package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/basic")
@Composable
fun BasicComposePage() {
    BlogLayout(
        PageProperties(
            "Jetpack Compose Basic",
            "Jetpack Compose UI toolkit uses a declarative programming model to define UI components."),
        BlogProperties("Jetpack Compose", "Basic"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Strong Skipping Mode (default since Compose Compiler 2.0) automatically memoizes lambdas that don’t capture unstable values and allows composables with unstable parameters to be skipped if the parameters are the same instance (===).",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            //https://abifarhan.medium.com/the-20-jetpack-compose-interview-questions-that-separate-seniors-from-everyone-else-2026-edition-a53a3d82265b
            KotlinCode(
                code = """
                    //Strong Skipping Mode
                    
                    android {
                        buildFeatures {
                            compose true
                        }
                        composeCompiler {
                            enableStrongSkippingMode = true
                        }
                    }
                    
                    //Enable strong skipping mode (Kotlin 1.9.22+)
                    targets.configureEach {
                        compilations.configureEach {
                            compileTaskProvider.get().compilerOptions {
                                freeCompilerArgs.addAll("-Xskippable-compose")
                            }
                         }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Jetpack Compose UI toolkit uses a declarative programming model to define UI components.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Developers define the UI components using Composable functions, which are functions annotated with the @Composable annotation. When a Composable function is called, it describes the UI component it represents, rather than building it directly.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Composable Function doesn’t return anything but it emits the UI. (Jetpack Compose is declarative programming, which means you can describe your user interface by invoking a set of composables, which is vastly different from the traditional way of imperative UI design)",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Compose Prototyping in Kotlin Notebooks",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://www.youtube.com/watch?v=bkd6EAPIVe0&t=140s
            KotlinCode(
                code = """
                    %use compose(platform = "macos-arm64")
                    @file:OptIn(ExperimentalLayoutApi::class)
                    
                    @Composable
                    fun simpleButton() {
                        Button(onClick = {}) 
                        {
                            Text("Compose Prototyping in Kotlin Notebooks")
                        }
                    }
                    
                    COMPOSE {
                        simpleButton()
                    }
                    
                    //Read Json File
                    val json = File("/Users/christian.melchior/colors.json").readText()
                    json
                    
                    val jsonColors = json.deserializeJson()
                    jsonColors
                    """.trimIndent()
            )

            //https://medium.com/@lee645521797/kotlin-compose-multiplatform-1-10-0-release-24ac85d1a398
            SpanText(
                text = "Jetpack Compose 1.10",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Veiled transitions, Modifier.scrollableArea(), Modifier.skipToLookaheadPosition()
            KotlinCode(
                code = """
                    // Compose 1.9: Verbose animation setup
                    val transition = updateTransition(targetState = isExpanded)
                    val height by transition.animateDp(label = "height") { state ->
                        if (state) 300.dp else 100.dp
                    }
                    val opacity by transition.animateFloat(label = "opacity") { state ->
                        if (state) 1f else 0.5f
                    }

                    // Compose 1.10: Clean, declarative animations
                    val isExpanded = remember { mutableStateOf(false) }
                    
                    val animatedHeight by animateDpAsState(
                        targetValue = if (isExpanded.value) 300.dp else 100.dp,
                        animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy),
                        label = "height"
                    )
                    
                    val animatedOpacity by animateFloatAsState(
                        targetValue = if (isExpanded.value) 1f else 0.5f,
                        animationSpec = tween(durationMillis = 300),
                        label = "opacity"
                    )

                    Box(
                        modifier = Modifier
                            .height(animatedHeight)
                            .alpha(animatedOpacity)
                            .clickable { isExpanded.value = !isExpanded.value }
                    ) {
                        Text("Smooth animation without boilerplate")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     AnimatedVisibility(
                        visible = isExpanded,
                        enter = slideInVertically(
                            initialOffsetY = { -40 },
                            animationSpec = tween(durationMillis = 300)
                        ) + fadeIn(),
                            exit = slideOutVertically(targetOffsetY = { -40 }) 
                          + fadeOut()
                    ) {
                         ExpandedContent()
                      } 
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    Box {
                        // This box will not be visible
                        // but will still occupy 200 dp × 200. dp of space.
                        Box(
                            modifier = Modifier
                            •size(size = 200.dp)
                            •background(Color.Red)
                            •visible(visible = false)
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Jetpack Compose 1.9",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "dropShadow(): Draws a shadow behind your content.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "innerShadow(): Draws a shadow inside the edges of a shape.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberTextFieldState Faster: Before, each key press made a new String, which messed with how things reloaded. With the new TextFieldState, things are more stable. Only stuff that needs to change gets updated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun ModernTextField() 
                    {
                        val state = rememberTextFieldState()
                        BasicTextField(
                            state = state,
                            modifier = Modifier.padding(16.dp),
                            outputTransformation = {
                                for(i in 4..length step 5) {
                                    insert(i, " ")
                                }
                            }
                        )
                    }        
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val purpleColor = Color (OxFF9c27b0)
                    
                    dropShadow(
                        RoundedCornerShape(20.dp),
                        dropShadow = DropShadow(
                            15.dp,
                            color = pinkColor,
                            spread = 10.dp,
                            alpha = 0.5f
                        )
                        
                    innerShadow(
                        RoundedCornerShape(20.dp),
                        innerShadow = InnerShadow(
                            15.dp,
                            color = Color.Black,
                            spread = 10.dp,
                            alpha = 0.5f
                        )
                    )    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    LazyColumn {
                        items(feedData) { video ->
                            VideoRow(
                                video,
                                Modifier.onVisibilityChanged(minDurationMs = 500, minFractionVisible = 1f) {
                                    visible ->
                                    // Track visibility state
                                    if (visible) video.play() else video.pause()
                                }
                            )
                        }
                    }
                    
                    //Use onFirstVisible to act when an item first appears, like logging an impression.
                    LazyColumn {
                        items (100) {
                            Box(
                                Modifier
                                // Log impressions when item has been visible for 500ms
                                .onFirstVisible(minDurationMs = 500) {/* log impression */ }
                                .clip(RoundedCornerShape (16.dp))
                                .drawBehind { drawRect (backgroundColor) }
                                .fillMaxWidth()
                                .height(100.dp)
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //OutputTransformation
                    
                    // Format a phone number and color the punctuation
                    val phoneTransformation = OutputTransformation {
                        // 1234567890 -> (123) 456-7890
                        if (length == 10) {
                            insert(0, "(")
                            insert(4, ") ")
                            insert(9, "-")
                            // Color the added punctuation
                            val gray = Color(0xFF666666)
                            addStyle(SpanStyle(color = gray), 0, 1)
                            addStyle(SpanStyle(color = gray), 4, 5)
                            addStyle(SpanStyle(color = gray), 9, 10)
                        }
                    }
                    
                    BasicTextField(
                        state = myTextFieldState,
                        outputTransformation = phoneTransformation
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    private val ttOSM = "https://multiplatformdeveloper.netlify.app",
                        
                    @Composable
                    fun Map() {
                        Box(
                            modifier = Modifier.fillMaxWidth().fillMaxHeight()
                        ) {
                            WebElementView(
                                factory = {
                                    (document.createElement("iframe")
                                            as HTMLIFrameElement)
                                        .apply { src = ttOSM }
                                },
                                modifier = Modifier.fillMaxSize(),
                                update = { iframe -> iframe.src = iframe.src }
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyLayout",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LazyLayoutMeasurePolicy: Defines how items are measured and placed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LazyLayoutItemProvider: Manages the list of items.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LazyLayoutPrefetchState: Handles prefetching for performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @OptIn(ExperimentalFoundationApi::class)
                    @Composable
                    private fun LazyColumnCacheWindowDemo() {
                        // Prefetch items 150.dp ahead and retain items 100.dp behind the visible viewport
                        val dpCacheWindow = LazyLayoutCacheWindow(ahead = 150.dp, behind = 100.dp)
                        // Alternatively, prefetch/retain items as a fraction of the list size
                        // val fractionCacheWindow = LazyLayoutCacheWindow(aheadFraction = 1f, behindFraction = 0.5f)
                        val state = rememberLazyListState(cacheWindow = dpCacheWindow)
                        LazyColumn(state = state) {
                            items(1000) { Text(text = "$it", fontSize = 80.sp) }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Improve crash analysis by adding source info to stack traces
                    
                    class App : Application() {
                       override fun onCreate() {
                            // Enable only for debug flavor to avoid perf regressions in release
                            Composer.setDiagnosticStackTraceEnabled(BuildConfig.DEBUG)
                       }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Jetpack Compose 1.9.0-alpha01",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Improved scrolling performance for shared elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "TabRow and ScrollableTabRow are deprecated in favor of Primary and Secondary variants of each.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Added LocalResources composition local to query Resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifier.keepScreenOn(): Video players, Long-form reading, Timers & countdowns, Live dashboards",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    //1
                    Modifier.onVisibilityChanged(
                        callback = {}
                    ).onFirstVisible(
                        callback = {
                            onFirstVisibleImage = "Image: $index"
                        }
                    )
                    
                    //2
                    LazyColumn {
                        items() -> 
                           Text(modifier = Modifier
                                .onFirstVisible {
                                    println("$name visible")
                                }
                           )
                    }
                    
                    """.trimIndent()
            )

            SpanText(
                text = "Jetpack Compose 1.8.0",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //AutoFill

                    TextField(
                        state = rememberTextFieldState(),
                        modifier = Modifier.semantics
                        {
                            contentType = ContentType.Username
                        }
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Smarter Text

                    BasicText(
                        text = "Hello World",
                        maxLines = 1,
                        autoSize = TextAutoSize.StepBased()
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //HTML Support
                    AnnotatedString.fromHtml()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Better Animations

                    Modifier.width(if(expanded) 180.dp else 110.dp)
                    •offset(x = if (expanded) 0.dp else 100. dp)
                    •animateBounds(lookaheadScope = this@LookaheadScope)
                    •background(Color.LightGray, shape = RoundedCornerShape(12.dp))
                    height(50.dp)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //TextOverflow

                    val text = "This is a long text that will overflow"
                    Column(Modifier.width(200.dp)) {
                      Text(text, maxLines = 1, overflow = TextOverflow.Ellipsis)
                      Text(text, maxLines = 1, overflow = TextOverflow.StartEllipsis)
                      Text(text, maxLines = 1, overflow = TextOverflow.MiddleEllipsis)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //ContextFlowRow and ContextualFlowColumn have now been deprecated for FlowRow and FlowColumn
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Pull To Refresh
                    @OptIn(ExperimentalMaterial3Api::class, ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    private fun Content(
                        modifier: Modifier = Modifier
                    ) {
                        val coroutineScope = rememberCoroutineScope()
                        val state = rememberPullToRefreshState()
                        var isRefreshing by remember {
                            mutableStateOf(false)
                        }
                        PullToRefreshBox(
                            indicator = {
                                PullToRefreshDefaults.LoadingIndicator(
                                    state = state,
                                    isRefreshing = isRefreshing,

                                    )
                            },
                            state = state,
                            isRefreshing = isRefreshing,
                            onRefresh = {
                                isRefreshing = true
                                coroutineScope.launch {
                                    delay(3000)
                                    isRefreshing = false
                                }
                            },
                            contentAlignment = Alignment.TopCenter,
                            modifier = modifier.fillMaxWidth()
                        ) {

                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "composables-cli",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Installation
                    curl -fsSL https://composables.com/get-composables.sh | bash
                    
                    //Create New Application & Run
                    composables init composeApp
                    cd composeApp
                    ./gradlew run
                    """.trimIndent()
            )

            SpanText(
                text = "Jetpack Compose Plugin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝐋𝐢𝐯𝐞 𝐓𝐞𝐦𝐩𝐥𝐚𝐭𝐞",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Localyze: Translate Android strings.xml files to 120+ languages directly from Android Studio using Localyze.app.", //https://plugins.jetbrains.com/plugin/30802-localyze
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "compose-stability-analyzer: You can trace the reason of your composable function is triggered recomposition with a TraceRecomposition annotation, and export stability compatibility reports using Gradle tasks for reviewing the new stability changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CodeMetrics With Kotlin",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMP Vector Asset",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "YARC: Fastest way to write Jetpack Compose UI using a smart abbreviation engine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "JSON Visualize Interactive Graphs",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "https://jsoncrack.com",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "Scratch Files",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is a temporary file in Android Studio where you can write and run code without adding it to your project.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://www.linkedin.com/in/kumarkomati/
            KotlinCode(
                code = """
                    macOS: cmd + shift + n
                    Windows / Linux: ctrl + alt + shift + insert
                    
                    val a = 10
                    val b = 20
                    val c = a + b
                    
                    https://play.kotlinlang.org
                    
                    //Select To Search
                    Windows/Linux: Ctrl + Alt + H 
                    Mac: Control + Option + H
                    
                    
                    """.trimIndent()
            )

            SpanText(
                text = "Settings: Optimize Imports",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.SETTING_OPTIMIZE_IMPORTS_URL,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Optimize imports"
            )

            SpanText(
                text = "Settings: Live Edit",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(
                src = Resources.Images.SETTING_LIVE_EDIT_URL,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Live Edit"
            )

            SpanText(
                text = "Comment code",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://www.linkedin.com/in/ritik-dhakate-104485202/
            KotlinCode(
                code = """
                    1. Network module configuration for HTTP client
                    2. Repository module for dependency injection
                    3. Use case module for business logic
                    4. ViewModel module for UI layer
                    5. Combined list of all modules
                    
                       Common response structure for all API calls.
                    1. @param success Indicates if the API call was successful
                    2. @param statusCode HTTP status code
                    3. @param message Optional message, typically used for error descriptions
                    4. @param data Optional data returned by the API

                        Network Error Messages
                    1. ⓘ   Network Error
                    2. Oops! Looks like you're offline.
                    3. Please check your internet connection and try again.
                    """.trimIndent()
            )

            SpanText(
                text = "Flipper",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flipper is a platform for debugging mobile apps that works with both iOS and Android. It allows developers to inspect, debug, and interact with their apps in real-time. Whether you are inspecting network traffic, managing local databases, or inspecting the React Native logs, Flipper offers a clean interface to manage it all. Flipper integrates seamlessly with React Native, allowing you to use its powerful suite of tools (such as the Network Inspector, Log Viewer, Database Viewer, and Crash Reporter) directly within your development environment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Thread Safety in Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose’s snapshot system also ensures thread safety when managing state. All updates are performed on a single thread (the main UI thread) to avoid concurrency issues. While Compose supports asynchronous tasks, state changes are carefully synchronized to prevent race conditions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Wireframe Draw",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "https://excalidraw.com/",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Multiplatform Library",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "https://klibs.io",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose Samples Application",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "https://github.com/android/compose-samples",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inside a Composable: stringResource (stringRes) - Cleanest, handles recomposition automatically",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inside a Helper Class: LocalResources.current - Provides Resources that Compose watches for changes",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inside a Button Click: LocalResources. current (but LocalContext. current will still work) - Safe for lazy actions, but you will get a lint warning if vou use LocalContext.current",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "datasize - (Stop Hardcoding Bytes)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                   repositories {
                       mavenCentral()
                   }

                   dependencies {
                       implementation("io.github.ardiien.datasize:datasize:<version>")
                   }
                   """.trimIndent()
            )


            KotlinCode(
                code = """
                    import io.github.ardiien.datasize.*

                    fun main() {
                        val fromInt: DataSize = 1.kibibytes
                        println(fromInt.inBytes)    // 1024

                        val fromDouble: DataSize = 1.0.kilobytes
                        println(fromDouble.inBytes) // 1000

                        val fromLong: DataSize = 1L.kibibytes
                        println(fromLong.inBytes)   // 1024
                    }
                    
                    fun main() {
                        // Arithmetic
                        val total: DataSize = 100.megabytes
                        val used: DataSize = 35.megabytes
                        val remaining: DataSize = total - used
                        println(remaining.inMegabytes) // 65.0

                        // Comparisons
                        val warningThreshold: DataSize = 80.megabytes
                        if (used > warningThreshold) {
                            println("Storage running low")
                        }

                        // Utilities
                        val peak: DataSize = max(used, 40.megabytes)
                        println(peak.inMegabytes) // 40.0
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "R. String 35 or Higher",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Module build.gradle
                    android {
                        namespace 'com. example moduleb'
                    }

                    //gradle.properties
                    //already true when targeting 35 or higher android.
                    nonTransitiveRClass=true

                    import com.example.moduleb.R as BRes
                    Text (getString (BRes.string.multiplatform))
                    """.trimIndent()
            )

            SpanText(
                text = "StringResource plurals - (Fetch static app resources) A composable function that can only be called from a Composable scope.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/matthieucoisne/YelpExplorer-CMP
            KotlinCode(
                code = $$"""
                    <plurals name="business_reviews_count">
                        <item quantity="one">%1$d review</item>
                        <item quantity="other">%1$d reviews</item>
                    </plurals>

                    <string name="business_name">%1$d. %2$s</string>
                    <string name="business_price">%1$s\u0020\u0020\u2022\u0020\u0020</string>

                    Text(
                        text = pluralStringResource(
                            resource = Res.plurals.business_reviews_count,
                            quantity = businessDetailsUiModel.reviewCount, //Int
                            businessDetailsUiModel.reviewCount //Int
                        ),
                        fontSize = 12.sp,
                    )

                    Text(
                        text = stringResource(
                        resource = Res.string.business_name,
                        1, //Int
                        "" //String
                    ),

                    val price = if (businessDetailsUiModel.price.isNotEmpty()) {
                        stringResource(resource = Res.string.business_price, businessDetailsUiModel.price)
                    } else ""
                    """.trimIndent()
            )

            //https://medium.com/@sivavishnu0705/stop-the-if-else-madness-a-better-way-to-handle-plurals-in-kotlin-for-android-cad9ad2657e3
            KotlinCode(
                code = """
                    <resources>
                        <plurals name="likes_received">
                            <item quantity="one">%d person liked this post. ❤️</item>
                            <item quantity="other">%d people liked this post. 🔥</item>
                        </plurals>
                    </resources>
                    
                    val likes = 100
                    val likeMessage = context.resources.getQuantityString(
                        R.plurals.likes_received,
                        likes,
                        likes
                    )
                    println(likeMessage)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Resource Access
                   
                    import androidx.annotation.DrawableRes
                    import androidx.annotation.StringRes
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.graphics.painter.Painter
                    import androidx.compose.ui.res.painterResource
                    import androidx.compose.ui.res.stringResource
                    
                    @Composable
                    fun @receiver:StringRes Int.string(vararg formatArgs: Any): String =
                    stringResource(id = this, formatArgs = formatArgs)

                    val @receiver:StringRes Int.string: String
                    @Composable get() = stringResource(id = this)

                    val @receiver:DrawableRes Int.painter: Painter
                    @Composable get() = painterResource(id = this)
                    """.trimIndent()
            )

            SpanText(
                text = "StringResource Locale-aware formatting - (Format dynamic values - dates, currency, numbers)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Tips Smart Work
            //https://github.com/patrykandpatrick/liftapp.git
            KotlinCode(
                code = $$"""
                    val formatter = LocalFormatter.current
                    
                    <string name="exercise_set_format_weight">%1$s × %2$s</string>
                    stringResource(
                        R.string.exercise_set_format_weight,
                        formatter.formatValue(weight, weightUnit),
                        formatter.formatValue(reps, RepsUnit),
                    )
                    
                    <string name="exercise_set_format_cardio">%1$s ꞏ %2$s ꞏ %3$s</string>
                    stringResource(
                        R.string.exercise_set_format_cardio,
                        formatter.formatDuration(duration),
                        formatter.formatValue(distance, distanceUnit),
                        formatter.formatValue(kcal, EnergyUnit.KiloCalorie),
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "getString",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A suspend function that can be called from any coroutine or suspend scope to access KMP resources. Neither Composable nor context required.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //stringResource() cannot be called inside LaunchedEffect
                    LaunchedEffect (Unit) {
                        val message = stringResource(Res.string.message)
                        println(message)
                    }
                    
                    //Call stringResource() in Composable scope
                    val message = stringResource(Res.string.message)
                    LaunchedEffect (Unit) {
                        println(message)
                    }
                    
                    //You can use getString() inside a suspend scope
                    LaunchedEffect (Unit) {
                        val message = getString(Res.string.message)
                        println(message)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Makefile",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """  
                    .PHONY: build clean run test test-verbose help

                    help:
                    @echo "Available targets for app-notebook:"
                    @echo "  build       - Build the notebook application"
                    @echo "  clean       - Clean build artifacts"
                    @echo "  run         - Run the notebook desktop app"
                    @echo "  test        - Run all tests"
                    @echo "  test-verbose - Run tests with verbose output"
                    @echo "  help        - Show this help message"

                    build:
                    ../gradlew :app-notebook:build

                    clean:
                    ../gradlew :app-notebook:clean

                    run:
                    ../gradlew :app-notebook:run

                    test:
                    ../gradlew :app-notebook:test

                    test-verbose:
                    ../gradlew :app-notebook:test --info   
                    """.trimIndent()
            )

            SpanText(
                text = "Google font",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    uiTextGoogleFonts = "1.7.8"

                    androidx-ui-text-google-fonts = { group = "androidx.compose.ui", name = "ui-text-google-fonts", version.ref = "uiTextGoogleFonts" }

                    dependencies {
                        implementation(libs.androidx.ui.text.google.fonts)
                    }

                    val provider = GoogleFont.Provider(
                        providerAuthority = "com.google.android.gms.fonts",
                        providerPackage = "com.google.android.gms",
                        certificates = R.array.com_google_android_gms_fonts_certs
                    )

                    val bodyFontFamily = FontFamily(
                        Font(
                            googleFont = GoogleFont("Roboto Serif"),
                            fontProvider = provider,
                        )
                    )

                    val displayFontFamily = FontFamily(
                        Font(
                            googleFont = GoogleFont("Roboto Serif"),
                            fontProvider = provider,
                        )
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Best Plugin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    1. http://github.com/skydoves/sealedx
                    """.trimIndent()
            )

            SpanText(
                text = "Layout Inspector",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Layout Insepctor support Child recomposition count. Android studio Narwhal Feature Drop | 2025.1.2",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Track Recompositions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "https://github.com/qamarelsafadi/JetpackComposeTracker",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun MyComposable() {
                        Text(
                            text = "Kotlin Multiplatform Developer",
                            modifier = Modifier.trackRecompositions()
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Validation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //1. https://github.com/zainulhassan815/formz-kotlin
            //2. https://www.shortandclear.de/2025/11/06/stop-writing-boilerplate-mastering-data-validation-with-konform-in-kotlin/
            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FromZ - https://multiplatformdeveloper.netlify.app/kmp_formz_validation",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Konform - https://www.konform.io",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Server-Driven UI with Compose Remote Layout",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "https://github.com/utsmannn",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://www.linkedin.com/in/abbas-haji-esfandiari-56a211244 (compositeOver, remember vs. rememberUpdatedState, SubcomposeLayout, CompositionLocal, drawWithCache)
            SpanText(
                text = "compositeOver",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stop hardcoding hex values for semi-transparent overlays! When implementing design systems (especially for Dark Mode or disabled states), we often use opacity (alpha) to create variations. But relying solely on runtime alpha blending can lead to unpredictability when the background changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Color.compositeOver. This function calculates the actual resulting opaque color that occurs when you place a semi-transparent color on top of a background color. It flattens the layer stack into a single solid color value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun getSafetyBackgroundColor(): Color {
                     val primary = MaterialTheme.colorScheme.primary
                     val surface = MaterialTheme.colorScheme.surface

                     // ❌ Bad: Just using alpha (Relies on rendering pipeline)
                     // val overlayColor = primary.copy(alpha = 0.12f)

                     // ✅ Good: Calculating the final flat color
                     // "Put 12% primary ON TOP OF surface and give me the result"
                     val finalColor = primary.copy(alpha = 0.12f).compositeOver(surface)

                     return finalColor
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Config create (build.gradle.kts)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/CagriOrcan/ChristmasGrapes.git
            KotlinCode(
                code = $$"""
                    val localProperties = Properties()
                    val localPropertiesFile = rootProject.file("local.properties")
                    if (localPropertiesFile.exists()) {
                        localPropertiesFile.inputStream().use { localProperties.load(it) }
                    }
                    val openAiApiKey = localProperties.getProperty("openai.api.key") ?: System.getenv("OPENAI_API_KEY") ?: ""
                    val revenueCatApiKey = localProperties.getProperty("revenuecat.api.key") ?: System.getenv("REVENUECAT_API_KEY") ?: ""
                    val supabaseApiKey = localProperties.getProperty("supabase.api.key") ?: System.getenv("SUPABASE_API_KEY") ?: ""
                    val supabaseUrlKey = localProperties.getProperty("supabase.url.key") ?: System.getenv("SUPABASE_URL_KEY") ?: ""

                    tasks.register("generateApiConfig") {
                        doLast {
                            val configDir = File("${projectDir}/src/commonMain/kotlin/org/lamysia/christmasgrapes/config")
                            configDir.mkdirs()

                            val configFile = File(configDir, "ApiConfig.kt")
                            configFile.writeText(""$${'"'}
                                package org.lamysia.christmasgrapes.config

                                object ApiConfig {
                                    const val OPENAI_API_KEY = ""
                                    const val REVENUECAT_API_KEY = ""
                                    const val SUPABASE_API_KEY = ""
                                    const val SUPABASE_URL_KEY = ""
                                }
                            ""$${'"'}.trimIndent())
                        }
                    }

                    tasks.named("preBuild") {
                        dependsOn("generateApiConfig")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "HIDL",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "HAL Interface Definition Language — allows Android framework to communicate with vendor hardware.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "HAL",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hardware Abstraction Layer — layer between hardware drivers and Android framework.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "AIDL",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android Interface Definition Language — for IPC (Inter-Process Communication) between different apps/processes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "At its core, AIDL is a contract. You define an interface in a .aidl file, specifying the methods a remote service will expose. The Android build tools then generate a Java interface and a special Stub class. Your service implements this Stub, and your client binds to the service to call its methods.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "AOSP",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android Open Source Project — the base project for Android OS development.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What loads first when Android boots?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Bootloader runs",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kernel is loaded",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Then Zygote process starts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "System server starts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Finally, Launcher app is loaded",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "archivesBaseName",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Android projects, archivesBaseName is a Gradle property that lets you customize the name of your build output files (e.g., APKs or AABs). By default, the generated file names might not be descriptive, especially if you’re managing multiple versions or builds. Using archivesBaseName, you can make your file names clearer and more meaningful.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = $$"""
                    android
                    {
                    	def tagName = ‘2.1.2’
                    	defaultConfig {
                    		archivesBaseName = "qrcode-v${tagName}"
                    	}
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "buildkonfig",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/pbclive-digital/pbc-web-app.git
            KotlinCode(
                code = $$"""
                    //app.sh
                    #!/bin/zsh

                    ## Referring static arguments
                    args=()
                    env=$2

                    ## Execute the operation according to provided environment
                    case $env in
                      "local")
                        ./gradlew clean -Papp.env=dev :composeApp:wasmJsBrowserDistribution
                        ;;
                      "staging")
                        ./gradlew clean -Papp.env=staging :composeApp:wasmJsBrowserDistribution
                        ;;
                      "prod")
                        ./gradlew clean -Papp.env=prod :composeApp:wasmJsBrowserDistribution
                        ;;
                      *)
                        ./gradlew clean -Papp.env=dev :composeApp:wasmJsBrowserDistribution
                        ;;
                    esac
                    
                    //gradle.properties
                    #App Config
                    app.env=staging
                    
                    //build.gradle.kts
                    buildkonfig {
                        packageName = "com.kavi.pbc.web.app"

                        val environment = project.findProperty("app.env")?.toString() ?: "dev"

                        // Default values
                        defaultConfigs {
                            when(environment) {
                                "dev" -> { // Development variant
                                    buildConfigField(FieldSpec.Type.STRING, "API_SCHEME", "http")
                                    buildConfigField(FieldSpec.Type.STRING, "API_DOMAIN", "localhost:8082")
                                    buildConfigField(FieldSpec.Type.STRING, "ENVIRONMENT", "dev")
                                }
                                "staging" -> { // Staging variant
                                    buildConfigField(FieldSpec.Type.STRING, "API_SCHEME", "https")
                                    buildConfigField(FieldSpec.Type.STRING, "API_DOMAIN", "pbc-api-staging-1f3fe32cb947.herokuapp.com")
                                    buildConfigField(FieldSpec.Type.STRING, "ENVIRONMENT", "staging")
                                }
                                "prod" -> { // Production variant
                                    buildConfigField(FieldSpec.Type.STRING, "API_SCHEME", "https")
                                    buildConfigField(FieldSpec.Type.STRING, "API_DOMAIN", "pbc-api-staging-1f3fe32cb947.herokuapp.com")
                                    buildConfigField(FieldSpec.Type.STRING, "ENVIRONMENT", "staging")
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Remove Cache Free Space",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    buildCache {
                        local {
                            //Automatically remove unused cache entries after 30 days.
                            removeUnusedEntriesAfterDays = 30
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "buildConfigField constants",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "buildConfigField is a Gradle feature in Android that allows you to define constants that will be included in the generated BuildConfig class. This is useful for storing values like API keys, base URLs, or any other configuration that might differ between build variants.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Type of the field (e.g., String, boolean, int).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    buildTypes {
                        debug {
                            buildConfigField "String", "API_KEY", "\"DEV_API_KEY\""
                        }
                        release {
                            buildConfigField "String", "API_KEY", "\"PROD_API_KEY\""
                        }
                    }

                    //Access
                    val apiKey = BuildConfig.API_KEY
                    """.trimIndent()
            )

            SpanText(
                text = "buildConfigField create",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/inlineTushar/TopCoins-KMM.git
            KotlinCode(
                code = $$"""
                    android {
                        signingConfigs {
                            create("debugSign") {
                                storeFile = file("${rootDir}/keystore/debug.keystore.jks")
                                keyAlias = "debugKey"
                                keyPassword = "android"
                                storePassword = "android"
                            }
                            create("releaseSign") {
                                storeFile = file("${rootDir}/keystore/release.keystore.jks")
                                // source from /Users/{user.name}/gradle.properties
                                keyAlias = project.findProperty("COIN_RELEASE_KEY_ALIAS") as String?
                                    ?: throw GradleException("COIN_RELEASE_KEY_ALIAS property not found in gradle.properties")
                                keyPassword = project.findProperty("COIN_RELEASE_KEY_PASSWORD") as String?
                                    ?: throw GradleException("COIN_RELEASE_KEY_PASSWORD property not found in gradle.properties")
                                storePassword = project.findProperty("COIN_RELEASE_STORE_PASSWORD") as String?
                                    ?: throw GradleException("COIN_RELEASE_STORE_PASSWORD property not found in gradle.properties")
                            }
                        }

                        buildTypes {
                            debug {
                                applicationIdSuffix = ".kmm.debug"
                                signingConfig = signingConfigs.getByName("debugSign")
                            }
                            release {
                                applicationIdSuffix = ".kmm.release"
                                signingConfig = signingConfigs.getByName("releaseSign")
                                isMinifyEnabled = true
                                proguardFiles(
                                    getDefaultProguardFile("proguard-android-optimize.txt"),
                                    "proguard-rules.pro"
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "buildConfigField dynamic",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/mccostic/NBRideMultiPlatform.git
            KotlinCode(
                code = """
                    buildKonfig = "0.17.1"
                    
                    [plugins]
                    buildKonfig = { id = "com.codingfeline.buildkonfig", version.ref = "buildKonfig" }
                    
                    plugins {
                        alias(libs.plugins.buildKonfig)
                    }

                    buildkonfig {
                        packageName = "Access package name"

                        defaultConfigs {
                            val baseUrl: String = project.findProperty("BASE_URL") as? String
                                ?: "https://dummyjson.com"

                            buildConfigField(
                                com.codingfeline.buildkonfig.compiler.FieldSpec.Type.STRING,
                                "BASE_URL",
                                baseUrl
                            )
                        }
                    }
                    
                    # gradle.properties
                    BASE_URL=https://dummyjson.com
                    BASE_URL_DEBUG=https://dummyjson.com
                    BASE_URL_RELEASE=https://dummyjson.com
                    
                    //ACCESS BASE_URL
                    BuildKonfig.BASE_URL
                    """.trimIndent()
            )

            //https://medium.com/@sarah.thoma.456
            SpanText(
                text = "Android Performance Testing Tools",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "BrowserStack",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android Profiler (Integrated with Android Studio)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Firebase Performance Monitoring",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AWS Device Farm",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "TestFairy",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kobiton",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dynatrace Mobile",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "New Relic Mobile",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Apache JMeter",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How do you handle orientation changes in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Orientation changes in Jetpack Compose are handled automatically by recomposing the UI based on the new configuration. Composable functions that define the UI layout and behavior will be recomposed with the updated configuration, allowing the UI to adapt to the new orientation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is TOML and How Does it Help?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "TOML stands for Tom’s Obvious Minimal Language. It’s a simple and human-readable way to organize configurations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using TOML with Gradle means:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You define all your dependencies and their versions in one file.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Your build.gradle files become cleaner and easier to read.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Updating a library version happens in a single place.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Android Studio .toml update using Gemini",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "libs.versions.toml open file -> Right Click -> Choose option Gemini -> Generate code -> Update all project dependencies to their latest stable versions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is Gradle Catalog?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Simply put, Gradle Catalog is a feature that helps you collect all the versions of libraries and plugins in one place in your project. Instead of editing these copies everywhere, you can specify the copies in just one file, called libs.versions.toml. Thus, all versions in the project remain compatible. I mean, you don't need to go and edit each build.gradle file every little while.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Do not load eagerly",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://proandroiddev.com/how-to-load-data-kotlin-898f9add9c6f
            KotlinCode(
                code = $$"""
                    interface HomeRepository {
                        suspend fun getArticles(): List<Article> // load articles from network, only possible while signed in
                        val user: Flow<User> // locally cached user, auto updated on refreshUser()
                        suspend fun refreshUser(): User // update user data from the BE
                    }

                    sealed interface HomeState {
                        data object Loading: HomeState
                        data class DisplayingFeed(val user: User, val feed: List<Article>): HomeState
                    }
                    
                    //Do not load eagerly
                    private val _state = MutableStateFlow<HomeState>(HomeState.Loading)
                    
                    //Instead Do
                    private val screenState = MutableStateFlow<HomeState>(HomeState())  
                    
                    //Do not roll your own onDataRefresh() callback
                    class HomeViewModel() : ViewModel() {
                        init {
                            refreshData()
                        }

                        fun onResume() = refreshData()
                        fun onPullToRefresh() = refreshData()

                        private fun refreshData() = viewModelScope.launch {
                            val user = repo.refreshUser()
                            val articles = repo.getArticles()
                            _state.value = DisplayingFeed(user, articles)
                        }
                    }
                    
                    //Do not observe data sources in the background
                    class HomeViewModel(
                       private val repo: HomeRepository,
                    ): ViewModel() {
                        init {
                            repo.user.onEach { user ->
                                _state.value = DisplayingFeed(user, repo.getArticles())
                            }.launchIn(viewModelScope)
                        }
                    }
                    
                    //Do not trigger loading from the UI
                    @Composable
                    fun HomeScreen(vm: HomeViewModel) {
                        LaunchedEffect(Unit) {
                            vm.observeData()
                        }
                    }
                    
                    //Do not
                    class HomeViewModel @Inject constructor() : ViewModel() {
                        
                        private val screenState = MutableStateFlow(value = ScreenState())  
                        
                        val uiState = screenState  
                            .onStart { fetchArticleList() }  // ❌ 
                            //Instead Do
                            .onStart {
                                emit(ScreenState(text = "Loading...")) 
                                fetchArticleList()                     
                            }
                            .stateIn(  
                                scope = viewModelScope,  
                                started = SharingStarted.WhileSubscribed(stopTimeoutMillis = 5000L),  
                                initialValue = screenState.value,  // ❌ 
                                //initialValue = ScreenState() //Instead Do
                            )  

                        suspend fun fetchArticleList() {  
                            delay(timeMillis = 2000L)  
                            screenState.update { state -> 
                                state.copy(  
                                    text = "Fetch Data ${state.counter}",  
                                    counter = state.counter + 1  
                                )  
                            }  
                        }  
                    }  
                    """.trimIndent()
            )

            SpanText(
                text = "Improve app performance with optimized resource shrinking",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //build.gradle.kts
                    android {
                      buildTypes {
                        release {
                          isMinifyEnabled = true
                          isShrinkResources = true
                          …
                        }
                      }
                    }
                    
                    //gradle.properties 
                    android.r8.optimizedResourceShrinking=true
                    """.trimIndent()
            )

            SpanText(
                text = "Arrow plugin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/xephosbot/AnilibriaRefresh.git
            KotlinCode(
                code = """
                    sealed class NetworkError {
                        data class HttpError(val code: Int, val message: String?) : NetworkError()
                        data class SerializationError(val cause: Throwable) : NetworkError()
                        data class NetworkException(val cause: Throwable) : NetworkError()
                    }
                    
                    internal suspend inline fun <reified T> HttpClient.request(
                        block: HttpClient.() -> HttpResponse
                    ): Either<NetworkError, T> = either {
                        try {
                            val response = this@request.block()
                            when (response.status) {
                                HttpStatusCode.OK -> response.body<T>()
                                else -> {
                                    raise(NetworkError.HttpError(response.status.value, response.status.description))
                                }
                            }
                        } catch (e: SerializationException) {
                            raise(NetworkError.SerializationError(e))
                        } catch (e: UnresolvedAddressException) {
                            raise(NetworkError.NetworkException(e))
                        }
                    }
                    
                    @Serializable
                    data class LoginResponse(
                        @SerialName("token") val token: String?,
                    )
                    
                    interface AuthApi {
                        suspend fun login(login: String, password: String): Either<NetworkError, LoginResponse>
                    }
                    
                    internal class DefaultAuthApi(private val client: HttpClient) : AuthApi {
                        override suspend fun login(
                            login: String,
                            password: String
                        ): Either<NetworkError, LoginResponse> = client.request {
                            post("accounts/users/auth/login") {
                                contentType(ContentType.Application.Json)
                                setBody(mapOf("login" to login, "password" to password))
                            }
                        }
                    }
                    
                    class ProfileViewModel(
                        private val authRepository: AuthRepository
                    ) : ViewModel() {
                        private fun login(login: String, password: String) {
                                viewModelScope.launch {
                                    authRepository.login(login, password).fold(
                                        ifRight = { 
                                            println("Login successful")
                                        },
                                        ifLeft = {
                                            showErrorMessage(it.toString().orEmpty())
                                        }
                                    )
                                }
                            }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Password encode",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import org.springframework.security.crypto.argon2.Argon2PasswordEncoder

                    class Argon2() {
                        val encoder = Argon2PasswordEncoder(16, 32, 1, 60000,10)

                        fun hash(text: String): String {
                            return encoder.encode(text)
                        }

                        fun verify(raw: String, encoded: String): Boolean {
                            return encoder.matches(raw, encoded)
                        }
                    }
                """.trimIndent()
            )

            SpanText(
                text = "Faster Configuration Cache with parallel load and store",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Gradle's Configuration Cache improves build performance by caching the result of the configuration phase. Gradle uses the Configuration Cache to skip the configuration phase entirely when nothing that affects the build configuration has changed. According to tests in the community, the results look very good. It can be used to improve build times. the configuration time in Gradle projects increases as the project structure grows. When enabled, the Configuration Cache can greatly increase build performance. However, for large and complex builds, storing and loading cache entries can still incur a noticeable overhead.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //gradle.properties
                    //gradle 8.11

                    org.gradle.configuration-cache.parallel=true
                    """.trimIndent()
            )

            SpanText(
                text = "Why is ISP Important in Android Development?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Android, classes like Activity, View, and various listeners often implement multiple interfaces. Violating ISP can lead to bloated and hard-to-maintain code. Adhering to ISP makes your classes smaller, focused, and more flexible, especially when working with dependency injection, event-driven architectures, or testable components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compilation Process to .dex",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The compilation process converts the .class files and .jar libraries into a single classes.dex file containing Dalvik byte-codes. This is possible with the dx command.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The dx command turns all of the .class and .jar files together into a single classes.dex file is written in Dalvik bytecode format.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "AIDL Files",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android Interface Definition Language (AIDL) allows you to define the programming interface for client and service to communicate using IPC.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "IPC is interprocess communication.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AIDL can be used between any process in Android.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Library Modules",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Library module contains Java or Kotlin classes, Android components, and resources though assets are not supported.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The code and resources of the library project are compiled and packaged together with the application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Therefore a library module can be considered to be a compile-time artifact.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "AAR Libraries",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android library compiles into an Android Archive (AAR) file that you can use as a dependency for an Android app module.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AAR files can contain Android resources and a manifest file, which allows you to bundle in shared resources like layouts and drawables in addition to Java or Kotlin classes and methods.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is Zygote?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Zygote process is started by the Android system during boot time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It acts as a pre-initialized process that holds: The Android Runtime (ART) or Dalvik VM, Core Java libraries, Common framework classes, Shared resources needed by most apps",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How It Works Zygote?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you launch an app:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The ActivityManagerService (AMS) sends a request to Zygote.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead of creating a brand-new process, Zygote forks itself (using the fork() system call).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The new child process inherits the preloaded resources and becomes your app’s process.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The new child process inherits the preloaded resources and becomes your app’s process.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Android Asset Packaging Tool (AAP2)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android Asset Packaging Tool (aapt2) compiles the AndroidManifest and resource files into a single APK.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "At this point, it is divided into two steps, compiling and linking. It improves performance, since if only one file changes, you only need to recompile that one file and link all the intermediate files with the ‘link’ command.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AAPT2 supports the compilation of all Android resource types, such as drawables and XML files.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you invoke AAPT2 for compilation, you should pass a single resource file as an input per invocation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AAPT2 then parses the file and generates an intermediate binary file with a .flat extension.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The link phase merges all the intermediate files generated in the compile phase and outputs one .apk file. You can also generate R.java and proguard-rules at this time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ART over Dalvik",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Since Android 4.4, Android migrated to ART, the Android runtime from Dalvik. This execution environment executes .dex as well.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The benefit of ART over Dalvik is that the app runs and launches faster on ART, this is because DEX bytecode has been translated into machine code during installation, no extra time is needed to compile it during the runtime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ART and Dalvik are compatible runtimes running Dex bytecode, so apps developed for Dalvik should work when running with ART.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The JIT based compilation in the previously used Dalvik has disadvantages of poor battery life, application lag, and performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is the reason Google created Android Runtime(ART).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ART is based on Ahead — Of — Time (AOT) based compilation process where compilation happens before application starts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In ART, the compilation process happens during the app installation process itself. Even though this leads to higher app installation time, it reduces app lag, increases battery usage efficiency, etc.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Even though dalvik was replaced as the default runtime, dalvik bytecode format is still in use (.dex)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Android version 7.0, JIT came back. The hybrid environment combining features from both a JIT compiler and ART was introduced.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The bytecode execution environment of Android is important as it is involved in the application startup and installation process.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Imperative UI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the past, we used imperative UI in Android. We retrieve the element and its contents first, then modify and finally update it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The connection between Android and XML is also not performant with findViewById() or even kotlinx.synthetics. Although the introduction of ViewBinding and DataBinding is good, the setup required is tiresome.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Declarative UI (What you want)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A declarative UI defines what the UI should look like based on the state. The framework automatically updates the UI when the state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declarative UI occurs when the screen’s state is pre-defined and the UI elements reload when any changes to this state are detected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The declarative approach specifies from the start a blueprint that contains all the needed setup instead of starting from base instances and mutating them. This allows the developers to design the user interface based on the data received.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No direct view manipulation (e.g., no findViewById), setText)).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State is managed via a Single Source of Truth (SSOT) (e.g. mutableStateOf().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose Leverages “Virtual Views” for Layouts",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike traditional Android views, which are backed by actual UI elements, Jetpack Compose uses virtual views that only exist in memory.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The UI components you define in Compose don’t directly map to traditional Android views or widgets.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead, Compose works in a virtual space and only creates actual UI elements during the final rendering pass. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This allows for more flexible and efficient layouts, especially when dealing with complex UIs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Declarative vs Imperative",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Declarative (What you want)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This pattern is an emerging trend that allows developers to design the user interface based on the data received.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This design paradigm makes use of one programming language to create an entire application.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This on the other hand focuses on the what",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imperative (How to do)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is the most common paradigm; it involves having a separate prototype/model of the application’s UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A good example is XML layouts in Android. We design the widgets and components which are then rendered for the user to see and interact with.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This design focuses on the how rather than the what.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Why Microbenchmark in Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Microbenchmarking in Compose helps you measure the performance of your composable functions. This can be crucial for identifying performance bottlenecks and ensuring smooth Ul interactions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Annotation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Annotations means attaching some extra information, Annotation with composable function tells kotlin compiler that this function is composable function, we use @Composable annotation with composable functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There is another annotation we use to preview our code, we wrap our composable function with @Preview annotation which tells compiler to show the functionality in our UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Composable",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Recomposition: Will be evaluated again when inputs change or a state is updated.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Should either emit content into the composition or return a value, but not both.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Can only be called from a composable context",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A Composable function is a fundamental building block in Jetpack Compose. It’s a special function that defines a piece of UI in a declarative way.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "By marking a function with @Composable, you make it possible for Jetpack Compose to track and manage the UI it represents, automatically handling updates whenever the underlying data or state changes.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When we annotate a function with @Composable, we're essentially telling the compiler that this function is responsible for creating a UI element and inserting it into the Compose UI tree. its purpose is to describe how to build a UI component and doesn't return anything. Composable functions don’t build and return UI. They simply emit data to build or update an in-memory structure. That makes them blazing fast and allows the runtime to execute them multiple times without fear.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Executing composable function: The only purpose of executing our Composable functions is to build or update the in-memory representation of the tree. That will keep it always up to date with the structure it represents since Composable functions will re-execute whenever the data they read changes.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This annotation specifies the following method using Jetpack compose for creating views.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It informs the Compose compiler that this function aims to transform data into a user interface.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Composable functions can be called in another composable function.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Jetpack Compose uses a Kotlin compiler plugin to transform the created composable functions into UI elements in our application.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Composables are also immutable, you can’t hold a reference to them. You need to pass the data as parameter when you call them. As the function is created in Kotlin you can use all the power of Kotlin.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Avoid Side Effects: Composable functions should be pure; they should only describe the UI and not perform actions like network calls or database operations.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "They are lightweight and reusable.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Ol {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "They don’t return any value; instead, they define how the UI should look and behave.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .textAlign(TextAlign.Start)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "They can be combined and nested to build complex interfaces.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .textAlign(TextAlign.Start)
                                )
                            }
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Preview",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Any method annotated with @Preview will be shown in the studio’s design section. Preview is just for development, when we run on an actual device it’s not gonna be useful.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "setContent - (keeps UI alive, reactive, and self-updating)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The setContent function is part of the ComponentActivity class. The setContent() function is one of the fundamental building blocks of the Compose framework, and it allows you to create sophisticated UIs by composing simple building blocks, or composables, together. It starts the Compose runtime.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "internally: Creates a Composition → Attaches it to the Activity lifecycle → Tracks state changes → Automatically recomposes only what’s needed",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Animation Preview",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Using this menu you can inspect animations defined using Jetpack Compose animation framework frame-by-frame.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Currently, limited APIs are supported to view animation previews like updateTransition, AnimatedVisibility, CrossFade etc.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Run Preview",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Using this menu, we run individually composable in isolation mode on the testing device to see how that particular composable looks on a real device/emulator.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Interactive Mode",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Using this menu we can make a live preview of gestures like click events and check composable in a different state like the check button in enabled/disable mode.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .textAlign(TextAlign.Start)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "Composer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose compiler analyzes your code and adds extra information to it. One key modification is the addition of a hidden parameter called Composer to every composable function. This parameter is automatically managed by the Compose runtime and passed down through the composable hierarchy. It's essential for tracking the UI tree and managing recompositions efficiently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An instance of Composer is injected at runtime, and forwarded to all the child Composable calls so it is accessible from all levels of the tree.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Composer is forwarded to all the Composable calls within the body. The Composer is the connection between the Composable code we write as developers and the Compose runtime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable functions will use it to emit their changes for the tree and therefore inform the runtime about its shape in order to build its in-memory representation or update it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If I wanna say it in simple words The Composer is The Bridge Between Code and UI The Composer parameter is passed down through every level of a composable function hierarchy. This ensures that all UI elements are connected and managed by the Compose runtime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Composer is responsible for:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Building and updating the UI tree based on the composable functions' instructions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Tracking changes to data and triggering necessary UI updates.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Optimizing the UI rendering process.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Primary patterns",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Slot pattern: This uses slots to group components together in a flexible way, making it easy to organize and rearrange UI elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compound Component pattern: This shares the state of a parent component with its children to reduce coupling between components and is useful for separating UI and business logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Composition Ideas 1 — Slot Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Slot pattern is one way to loosen the tight connection between components and allow for more flexible UI component composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is a design pattern that enables specific areas of a component to be easily customized from the outside. Composable lambdas (in the form of @Composable () -> Unit) can be used as function parameters in compose, allowing a parent component to define the UI structure without depending on the specific implementation of the child component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    @Composable
                    fun UserProfile(
                        user: User,
                        // Receive the UI itself as a parameter via composable lambda (Slot)
                        bottomContent: @Composable () -> Unit,
                    ) {
                        Column(...) {
                            ...
                            // The UserProfile component does not know any incoming UI components
                            bottomContent(...)
                        }
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Composition Ideas 2 — Compound Component Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compound Component pattern, known as one of the UI design patterns in React, involves breaking down a single component into multiple pieces and then combining them externally.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The parent controls the state passed to child components for rendering UI. This separates child components from managing state, allowing them to focus on displaying the user interface. In React, this is typically implemented via the Context API, but in Compose, it can be implemented using Lambda Receivers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.COMPOSITION_PATTERNS,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Composition Patterns"
            )

            SpanText(
                text = "Slot API (Slotting) (Constraints for Slot Parameters)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Slots API Pattern enables us to create flexible components by providing generic composable lambda -slots- parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Slot API in Jetpack Compose provides a mechanism for composing UI components that can accommodate varying content. It allows you to define slots within a composable function where different content can be inserted dynamically at runtime. Slots act as placeholders for content, providing a flexible way to customize and adapt UI components based on specific requirements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Material components make heavy use of slot APIs, a pattern Compose introduces to bring in a layer of customization on top of composables. This approach makes components more flexible, as they accept a child element which can configure itself rather than having to expose every configuration parameter of the child. Slots leave an empty space in the UI for the developer to fill as they wish. For example, these are the slots that you can customize in a TopAppBar.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scaffold has slots like topBar, bottomBar, drawerContent, and content, allowing you to structure a screen layout with consistent regions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AlertDialog has slots for title, text, confirmButton, and dismissButton, enabling the use of dialogs with different configurations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composables usually take a content composable lambda ( content: @Composable () -> Unit). Slot APIs expose multiple content parameters for specific uses. For example, TopAppBar allows you to provide the content for title, navigationIcon, and actions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, Scaffold allows you to implement a UI with the basic Material Design layout structure. Scaffoldprovides slots for the most common top-level Material components, such as TopAppBar, BottomAppBar, FloatingActionButton, and Drawer. By using Scaffold, it's easy to make sure these components are properly positioned and work together correctly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multiple Slots: The Slot API also supports multiple slots within a single composable function. This enables more complex composition scenarios.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun TopAppBar(
                        title: String,
                        iconPainter: Painter,
                        iconTint: Color = DefaultTintColor,
                    ) {
                        // ...
                        Icon(iconPainter, iconTint)
                        // ...
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class TopAppBarIcon(
                        val painter: Painter,
                        val tint: Color = DefaultTintColor,
                    )

                    @Composable
                    fun TopAppBar(
                        title: String,
                        icon: TopAppBarIcon,
                    ) {
                        // ...
                        Icon(icon.painter, icon.tint)
                        // ...
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Compose Layout subsystem has a thing called layoutId — a parameter that every LayoutNode can have (implemented using ParentDataModifier).

                    //First, it is set using a Modifier.layoutId, then — read in a layout (measuring) phase.

                    //Applying this knowledge to our problem, firstly we use Modifier.layoutId inside an Icon like this:

                    @Composable
                    fun Icon(painter: Painter, tint: Color = DefaultTintColor) {
                        Box(Modifier.layoutId(IconLayoutId)) {
                            Icon(
                                painter = painter,
                                tint = tint,
                                contentDescription = null
                            )
                        }
                    }

                    private object IconLayoutId
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable fun NavigationBar(
                        content: @Composable () -> Unit,
                        modifier: Modifier = Modifier,
                        leftButton: (@Composable () -> Unit)? = null,
                        rightButton: (@Composable () -> Unit)? = null,
                    ) {
                        Row(modifier) { }
                    }

                    val IconSize = 40.dp
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Multiple Slots
                     @Composable
                     fun ComplexComponent(headerSlot: @Composable () -> Unit, bodySlot: @Composable () -> Unit) {
                         // UI code for ComplexComponent

                         headerSlot()

                         // More UI code for ComplexComponent

                         bodySlot()

                         // More UI code for ComplexComponent
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Toolbar(
                        modifier: Modifier = Modifier,
                        navigationIcon: @Composable (() -> Unit)? = null,
                        actions: (@Composable () -> Unit)? = null,
                        /* other optional parameters */
                        body: @Composable () -> Unit,
                    ){
                      /*...*/
                    }

                    // subcomponents...
                    @Composable
                    fun ToolbarIcon(
                        imageVector: ImageVector,
                        onClick: () -> Unit,
                        tint: Color = LocalContentColor.current,
                    ) {
                      /*...*/
                    }

                    @Composable
                    fun ToolbarBackToCloseIconAnimation(
                        onClick: () -> Unit,
                        rotate: Boolean,
                        tint: Color = LocalContentColor.current,
                    ) {
                      /*...*/
                    }

                    @Composable
                    fun ToolbarAnimatedVisibility(
                        visible: Boolean,
                        content: @Composable AnimatedVisibilityScope.() -> Unit,
                    ) {
                      /*...*/
                    }

                    @Composable
                    fun ToolbarButton(
                        onClick: () -> Unit,
                        text: String,
                    ){
                      /*...*/
                    }

                    // any other subcomponents like a custom AnimatedVisibility or a popup...
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                   //The Toolbar accepts any composable. To restrict this, we can use the layoutId modifier to assign a unique ID to each subcomponent, and then validate these IDs in the Toolbar.

                   @Composable
                   fun ToolbarIcon(/*...*/) {
                     Icon(
                       modifier = Modifier.layoutId(ToolbarIconLayoutId)
                       /*...*/
                     )
                   }

                   private object ToolbarIconLayoutId
                    """.trimIndent()
            )

            SpanText(
                text = "DSL-based slots",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "constrain UI components, but we will also touch on a technique called DSL-based slots, which uses scopes to define child components and prevent them from being used in unintended places.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Stable
                    object ListItemLeadingScope {
                       @Composable
                      fun LeadingIconThumbnail(
                        icon: B43Icon,
                        contentDescription: String ? = null ,
                      ) {
                        IconThumbnail(
                          icon = icon,
                          contentDescription = contentDescription,
                          size = ThumbnailSize.Small,
                          background = IconThumbnailBackground.Accent,
                        )
                      }
                    }

                    @Composable
                    fun ListItem(
                      body: @Composable () ->  Unit ,
                      modifier: Modifier = Modifier,
                      leading: @Composable (ListItemLeadingScope.() ->  Unit )? = null ,
                      trailing: (ListItemTrailingScope.() ->  Unit )? = null ,
                    ) { ... }
                    """.trimIndent()
            )

            SpanText(
                text = "Named Slots for Better Customization",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun CustomDialog(
                        title: @Composable (() -> Unit)? = null,
                        content: @Composable (() -> Unit)? = null,
                        actions: @Composable (() -> Unit)? = null
                    ) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            title?.invoke()
                            Spacer(modifier = Modifier.height(8.dp))
                            content?.invoke()
                            Spacer(modifier = Modifier.height(8.dp))
                            actions?.invoke()
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable Slots as Parameters: In a slot-based layout, you define parameters in a composable function that accept other composables. These slots are commonly declared as @Composable () -> Unit parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Customizable Content: Each slot can be filled with custom content, so the component becomes versatile, with layout and style determined by its parent composable. This is similar to passing children in UI frameworks like React or SwiftUI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flexible Structure: Slots allow for more flexibility in structuring and managing the layout. You can decide which parts of the layout are customizable by exposing them as slots, while other parts of the layout remain fixed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Separation of Concerns: Keeps logic and UI structure separate by delegating UI content to the caller.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "FIR",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FIR stands for Front-end Intermediate Representation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The new Kotlin compiler front-end used in the K2 compiler.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Jetpack Compose Compiler is a Kotlin compiler that works on FIR trees.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FIR helps to improve building performance, stability inference, and future compatibility with new Kotlin features.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "IR",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "During the IR stage, the Compose compiler plugin transforms your composable into something conceptually like this:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This transformation allows the runtime to call your composable functions, track state, and schedule recompositions efficiently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun Screen($composer: Composer, $changed: Int) { ... }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Compiler",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A compiler plugin is a tool that extends how the Kotlin compiler processes your code. It hooks into the compilation pipeline and can analyze, transform, or generate additional code before the final program output is produced.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Compiler plugin interacts with the Intermediate Representation (IR) when the kotlin source code is halfway compiled. At this stage, compiler plugins step in to modify the code as needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose Compiler integrates with the Kotlin compiler through the ComponentRegistrar interface, which acts as the entry point for any compiler plugin. It registers a series of extensions that simplify using the Compose library.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose Compiler analyses the source code during compile time to ensure everything is in place for the next steps. This involves checks like verifying Kotlin compiler dependencies since the Composer Compiler depends on specific Kotlin versions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the Compose Compiler identifies any function annotated with @Composable, it rewrites it slightly to enable the desired runtime behaviour. This transformation process, where higher-level constructs like lambdas and inline functions are converted into, lower-level representations, is known as “Lowering.” The Compose Compiler modifies elements in the IR tree during this phase to make them compatible with the runtime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In summary, the Compose Compiler modifies the source code during the IR phase by adding and altering metadata, which ultimately supports the functionality of the Compose Runtime. Afterwards, the IR is compiled into the native binaries for JVM, JS, LLVM (for iOS), or WASM, allowing Compose to be multi-platform. The IR modifications made by the Compose Compiler ease the work of the Compose Runtime. While I won’t go into the details of every lowering, I’ll briefly touch on a few important transformations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.COMPOSE_COMPILER_URL,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Compose Compiler"
            )

            SpanText(
                text = "Infer Class Stability",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose infers the stability of classes to help the runtime with smart recompositions. It allows the runtime to skip recomposing @Composable functions whose inputs haven’t changed. The Compose Compiler infers stability based on certain criteria2, and this algorithm is constantly evolving.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Primitive types are stable by default in Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For custom types such as data classes, you need to annotate them with @Stable or @Immutable if they should be treated as stable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Enabling Live Literals",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Live literals enable the Compose tooling to generate live previews of @Composables functions without needing recompilation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This transformation is intended to improve developer experience and should never be enabled in a release build as it will significantly slow down performance-conscious code",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Compose Lambda Memoization",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Compiler teaches the runtime how to handle the lambdas passed to the @Composable functions. There are two kinds of lambdas: composable and non-composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If a non-composable lambda passed to a @Composable function doesn’t capture any value at the call site, the Kotlin compiler optimises it by modelling it as a singleton. However, if a non-composable lambda captures a value, the Compose Compiler optimises it by wrapping it in a remember call.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For composable lambdas, the Compose Compiler memoizes them by storing and retrieving them from a slot table, allowing the runtime to efficiently recompose only the parts of the UI tree that need updating. This technique, originally called “donut-hole skipping”3 because it allows for a lambda to be updated “high” in the tree, and for Compose to only need to recompose at the very “low” portion of the tree where this value is actually read.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Inject the composer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose compiler injects a special composer parameter into all @Composable functions and passes it to their sub-composables. The compiler wraps the body of each @Composable function with composer.start(key) and composer.end() calls, where key represents an arbitrary integer that serves as a hash of the function’s name and call site.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose Compiler performs additional optimisations & lowerings on the sources by inserting metadata and modifying the source code. These changes will ultimately help the Compose Runtime in its operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is the Compose Compiler and what does it do",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose Compiler is a crucial component of Jetpack Compose that translates Kotlin code written using Compose’s declarative UI framework into the corresponding View code that Android can render. It analyzes Compose code and generates optimized and efficient UI rendering logic for Android applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Slot Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A slot is typically defined as a parameter in a composable function that accepts other composables. This parameter can be filled with any UI elements you like, allowing you to inject content dynamically. Slots are usually defined with the type @Composable () -> Unit.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "onFinishInflate",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "onFinishInflate() is a lifecycle callback method in Android, specifically used in custom views. It is called after a view and all of its children have been inflated from XML.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Kotlin (and Java), one overrides onFinishInflate() when creating a custom view class and need to perform operations after the XML layout inflation is complete—for example, accessing child views or initializing view-related logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "AndroidView",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AndroidView is an external object from the Android View system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Views maintain their own internal state, such as focus status, cursor position, and scroll position.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reusing these objects to insert different data can lead to bugs, such as the previous user's scroll position or text being input, so they are not reused.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, if you directly specify the onReset lambda function for the AndroidView parameter, the node becomes reusable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How can you handle user input and events in Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "User input and events can be handled using event callbacks and state variables in Jetpack Compose. Composable functions can define event callbacks that are triggered when a user interacts with the UI. These callbacks can modify the state variables, leading to UI recomposition and updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun InputExample() {
                        var text by remember { mutableStateOf("") }
                        TextField(value = text, onValueChange = { text = it })
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Padding",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "statusBarsPadding() - Adds padding at the top to avoid overlap with the status bar (like the clock and battery icons).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "navigationBarsPadding() - Adds padding at the bottom to prevent your UI from being covered by the navigation bar.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "systemBarsPadding() - A convenient combo of both top and bottom padding for full-screen layouts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "imePadding() - Dynamically adds bottom padding when the keyboard (IME) appears, so text fields or buttons stay visible.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "displayCutoutPadding() - Handles notches and camera holes, ensuring your content respects device cutouts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "safeDrawingPadding(), equivalent to windowInsetsPadding(WindowInsets.safeDrawing)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "safeContentPadding(), equivalent to windowInsetsPadding(WindowInsets.safeContent)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "safeGesturesPadding(), equivalent to windowInsetsPadding(WindowInsets.safeGestures)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """ 
                    // Edge-to-edge: outer Box paints the theme background under status bar and home indicator,
                    // inner content respects safe drawing insets.
                    val safeInsets = WindowInsets.safeDrawing.asPaddingValues()
                    val safeTop = safeInsets.calculateTopPadding()
                    val safeBottom = safeInsets.calculateBottomPadding()
                    
                    Column(
                        modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .padding(horizontal = spacing.xl)
                        .padding(top = safeTop + spacing.xl, bottom = safeBottom + 120.dp),
                        verticalArrangement = Arrangement.spacedBy(spacing.lg),
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Keyboard",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    expect object KeyboardManager {
                        @Composable
                        fun keyboardAsState(): State<Boolean>
                        @Composable
                        fun getKeyboardController(): SoftwareKeyboardController?
                    }
                    
                    //androidMain
                    import androidx.compose.foundation.layout.WindowInsets
                    import androidx.compose.foundation.layout.ime
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.State
                    import androidx.compose.runtime.rememberUpdatedState
                    import androidx.compose.ui.platform.LocalDensity
                    import androidx.compose.ui.platform.LocalSoftwareKeyboardController
                    import androidx.compose.ui.platform.SoftwareKeyboardController

                    actual object KeyboardManager {
                        @Composable
                        actual fun keyboardAsState(): State<Boolean> {
                            val isImeVisible = WindowInsets.ime.getBottom(LocalDensity.current) > 0
                            return rememberUpdatedState(isImeVisible)
                        }

                        @Composable
                        actual fun getKeyboardController(): SoftwareKeyboardController? {
                            return LocalSoftwareKeyboardController.current
                        }
                    }
                    
                    //NativeMain
                    import androidx.compose.foundation.layout.WindowInsets
                    import androidx.compose.foundation.layout.ime
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.State
                    import androidx.compose.runtime.rememberUpdatedState
                    import androidx.compose.ui.platform.LocalDensity
                    import androidx.compose.ui.platform.LocalSoftwareKeyboardController
                    import androidx.compose.ui.platform.SoftwareKeyboardController

                    actual object KeyboardManager {
                        @Composable
                        actual fun keyboardAsState(): State<Boolean> {
                            val isImeVisible = WindowInsets.ime.getBottom(LocalDensity.current) > 0
                            return rememberUpdatedState(isImeVisible)
                        }

                        @Composable
                        actual fun getKeyboardController(): SoftwareKeyboardController? {
                            return LocalSoftwareKeyboardController.current
                        }
                    }
                    
                    val isKeyboardOpened by KeyboardManager.keyboardAsState() //Check the keyboard open or not
                    val keyboardController = LocalSoftwareKeyboardController.current //Hide keyboard
                    """.trimIndent()
            )

            SpanText(
                text = "Split Button Layout",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/xephosbot/AnilibriaRefresh.git
            KotlinCode(
                code = """
                    @ExperimentalMaterial3ExpressiveApi
                    @Composable
                    fun SplitButtonLayout(
                        leadingButton: @Composable () -> Unit,
                        trailingButton: @Composable () -> Unit,
                        modifier: Modifier = Modifier,
                        spacing: Dp = SplitButtonDefaults.Spacing,
                    ) {
                        Layout(
                            {
                                // Override min component size enforcement to avoid create extra padding internally
                                // Enforce it on the parent instead
                                CompositionLocalProvider(LocalMinimumInteractiveComponentSize provides 0.dp) {
                                    Box(
                                        modifier = Modifier.layoutId(LeadingButtonLayoutId),
                                        contentAlignment = Alignment.Center,
                                        content = { leadingButton() }
                                    )
                                    Box(
                                        modifier = Modifier.layoutId(TrailingButtonLayoutId),
                                        contentAlignment = Alignment.Center,
                                        content = { trailingButton() }
                                    )
                                }
                            },
                            modifier.minimumInteractiveComponentSize(),
                            measurePolicy = { measurables, constraints ->
                                val looseConstraints = constraints.copy(minWidth = 0, minHeight = 0)

                                val trailingWidth = measurables
                                    .fastFirst { it.layoutId == TrailingButtonLayoutId }
                                    .maxIntrinsicWidth(0)

                                val leadingButtonPlaceable =
                                    measurables
                                        .fastFirst { it.layoutId == LeadingButtonLayoutId }
                                        .measure(
                                            looseConstraints.copy(
                                                minWidth = (constraints.maxWidth - trailingWidth).coerceAtLeast(0),
                                                maxWidth = (constraints.maxWidth - trailingWidth).coerceAtLeast(0)
                                            )
                                        )

                                val trailingButtonPlaceable =
                                    measurables
                                        .fastFirst { it.layoutId == TrailingButtonLayoutId }
                                        .measure(
                                            looseConstraints
                                                .offset(
                                                    horizontal = -(leadingButtonPlaceable.width + spacing.roundToPx())
                                                )
                                                .copy(
                                                    minHeight = leadingButtonPlaceable.height,
                                                    maxHeight = leadingButtonPlaceable.height
                                                )
                                        )

                                val placeables = listOf(leadingButtonPlaceable, trailingButtonPlaceable)

                                val contentWidth = placeables.fastSumBy { it.width } + spacing.roundToPx()
                                val contentHeight = placeables.fastMaxOfOrNull { it.height } ?: 0

                                val width = constraints.constrainWidth(contentWidth)
                                val height = constraints.constrainHeight(contentHeight)

                                layout(width, height) {
                                    leadingButtonPlaceable.placeRelative(0, 0)
                                    trailingButtonPlaceable.placeRelative(
                                        x = leadingButtonPlaceable.width + spacing.roundToPx(),
                                        y = 0
                                    )
                                }
                            }
                        )
                    }
                    
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    internal fun PlayButton(
                        modifier: Modifier = Modifier,
                        onLeadingClick: () -> Unit,
                        onTrailingClick: () -> Unit,
                        colors: ButtonColors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.tertiaryContainer,
                            contentColor = MaterialTheme.colorScheme.onTertiaryContainer,
                            disabledContainerColor = MaterialTheme.colorScheme.tertiaryContainer.copy(alpha = 0.1f),
                            disabledContentColor = MaterialTheme.colorScheme.onTertiaryContainer.copy(alpha = 0.38f),
                        ),
                        trailingEnabled: Boolean = true,
                        contentPadding: PaddingValues = PaddingValues(8.dp),
                    ) {
                        SplitButtonLayout(
                            modifier = modifier.fillMaxWidth(),
                            leadingButton = {
                                SplitButtonDefaults.LeadingButton(
                                    modifier = Modifier.fillMaxSize(),
                                    contentPadding = contentPadding,
                                    colors = colors,
                                    onClick = onLeadingClick
                                ) {
                                }
                            },
                            trailingButton = {
                                SplitButtonDefaults.TrailingButton(
                                    modifier = Modifier.fillMaxSize(),
                                    enabled = trailingEnabled,
                                    contentPadding = contentPadding,
                                    colors = colors,
                                    onClick = onTrailingClick
                                ) {
                                    
                                }
                            }
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Lazy composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lazy composition refers to the concept of deferring the composition of UI elements until they are actually needed or visible on the screen. This approach is particularly useful for handling large collections of UI elements, like lists or grids, by only composing the items that are currently in view. Lazy composition helps optimize performance and memory usage by minimizing the number of composable functions that are composed at any given time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Difference between Column and LazyColumn?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Column lays out all children at once, LazyColumn renders only visible items for better performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "rememberLazyListState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LazyListState is a creating a stateful LazyListState instance with rememberLazyListState() and passing it to your LazyColumn. It’s just a one liner that calls rememberSaveable() and creates an instance of LazyListState.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberLazyListState() accepts two optional parameters for the initial index and scroll offset.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     val rowState = rememberLazyListState(
                        prefetchStrategy = LazyListPrefetchStrategy(
                            nestedPrefetchItemCount = 6)
                     )
                    """.trimIndent()
            )

            //https://github.com/mheerwaarden/CMPEventDemo
            KotlinCode(
                code = $$"""
                    //stickyHeader
                    
                    //Serializing Abstract Classes with Kotlinx.serialization
                    @Serializable
                    data class Event(
                        override val id: String = "",
                        val eventType: EventType = EventType.ACTIVITY,
                        val startDateTime: LocalDateTime = now(),
                        val endDateTime: LocalDateTime = startDateTime.plus(1, DateTimeUnit.HOUR)
                    ) : ModelItem() {
                        companion object {
                            val typeNameResId = Res.string.event
                        }

                        override fun getTypeNameResId() = typeNameResId
                        override fun getDisplayName() = "${eventType.text} $title"
                    }
                    
                    abstract class ModelItem {
                        abstract val id: String
                        abstract fun getTypeNameResId(): StringResource
                        abstract fun getDisplayName(): String
                    }
                    
                    enum class EventType(val text: StringResource, val htmlColor: HtmlColors) {
                        ACTIVITY(Res.string.event_type_activity, HtmlColors.AQUAMARINE)
                    }
                    
                    @Composable
                    private fun EventOverviewBody(
                        eventList: List<Event>,
                        isReadOnly: Boolean,
                        deleteEvent: (String) -> Unit,
                        navigateToEvent: (String) -> Unit,
                        navigateToAddEvent: (LocalDate) -> Unit,
                        navigateToEditEvent: (String) -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        // Return early if the event list is empty
                        if (eventList.isEmpty()) return

                        // While grouping events by date, determine start index of overview
                        var currentIndex = 0
                        var startIndex = -1
                        val now = now()
                        val groupedEvents: Map<LocalDate, List<Event>> = buildMap {
                            eventList.forEach { event ->
                                val startDate = event.startDateTime
                                val endDate = event.endDateTime

                                if (startDate <= now && startIndex == -1) {
                                    // Start the overview at the number of events plus the number of headers
                                    startIndex = currentIndex + size
                                }
                                currentIndex++

                                // Iterate through each date from start to end, adding the event for each date
                                var currentDate = startDate
                                while (currentDate <= endDate) {
                                    val currentStart =
                                        if (currentDate == startDate) event.startDateTime else currentDate
                                    val currentEnd =
                                        if (currentDate == endDate) event.endDateTime else currentDate.plus(
                                            1, DateTimeUnit.DAY
                                        )

                                    // Add the event to the list for this date, creating the list if it doesn't exist
                                    val currentEvents = getOrElse(currentDate.date) { emptyList() }.toMutableList()
                                    currentEvents.add(
                                        event.copy(
                                            startDateTime = currentStart,
                                            endDateTime = currentEnd
                                        )
                                    )
                                    put(currentDate.date, currentEvents)

                                    // Move to the next day
                                    currentDate = currentDate.plus(1, DateTimeUnit.DAY)
                                }
                            }
                        }

                        val lazyListState = rememberLazyListState(initialFirstVisibleItemIndex = max(startIndex, 0))
                        LazyColumn(state = lazyListState, modifier = modifier) {
                            groupedEvents.forEach { entry ->

                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyListState Extension",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Robert-P-M/Stormbreaker_KMP.git
            KotlinCode(
                code = """
                    val LazyListState.isFirstFullyVisible: State<Boolean>
                        @Composable
                        get() = remember(this) {
                            derivedStateOf {
                                isFirstItemFullyVisible
                            }
                        }

                    val LazyListState.isLastFullyVisible: State<Boolean>
                        @Composable
                        get() = remember(this) {
                            derivedStateOf {
                                isLastItemFullyVisible
                            }
                        }

                    val LazyListState.areAllItemsFullyVisible: State<Boolean>
                        @Composable
                        get() = remember(this) {
                            derivedStateOf {
                                if (layoutInfo.totalItemsCount == 0) {
                                    true
                                } else {
                                    isFirstItemFullyVisible && isLastItemFullyVisible
                                }
                            }
                        }

                    val LazyListState.firstAndLastVisibleItemIndex: State<Pair<Int?, Int?>>
                        @Composable
                        get() = remember(this) {
                            derivedStateOf {
                                calculateFirstAndLastIndex()
                            }
                        }

                    private val LazyListState.isFirstItemFullyVisible: Boolean
                        get() {
                            val firstItem = layoutInfo.visibleItemsInfo.firstOrNull() ?: return false
                            if (firstItem.index != 0) return false

                            val viewportStart = layoutInfo.viewportStartOffset
                            val viewportEnd = layoutInfo.viewportEndOffset

                            val itemStart = firstItem.offset
                            val itemEnd = firstItem.offset + firstItem.size

                            return itemStart >= viewportStart && itemEnd <= viewportEnd
                        }

                    private val LazyListState.isLastItemFullyVisible: Boolean
                        get() {
                            val lastItem = layoutInfo.visibleItemsInfo.lastOrNull() ?: return false
                            val total = layoutInfo.totalItemsCount
                            if (lastItem.index != total - 1) return false

                            val viewportStart = layoutInfo.viewportStartOffset
                            val viewportEnd = layoutInfo.viewportEndOffset

                            val itemStart = lastItem.offset
                            val itemEnd = lastItem.offset + lastItem.size

                            return itemStart >= viewportStart && itemEnd <= viewportEnd
                        }

                    private fun LazyListState.calculateFirstAndLastIndex(): Pair<Int?, Int?> {
                        val visibleItems = layoutInfo.visibleItemsInfo
                        if (visibleItems.isEmpty()) return Pair(null, null)

                        val firstIndex = visibleItems.first().index
                        val lastIndex = visibleItems.last().index

                        return Pair(firstIndex, lastIndex)
                    }
                    
                    //Previous & Next icon visible/hide
                    @Composable
                    fun BoxScope.GraphLegendScrollIcons(
                        rowState: LazyListState,
                    ) {
                        val SCROLL_STEP = 3

                        val scope = rememberCoroutineScope()
                        val totalItems by remember(rowState) {
                            derivedStateOf {
                                rowState.layoutInfo.totalItemsCount
                            }
                        }

                        val areAllItemsFullyVisible by rowState.areAllItemsFullyVisible
                        val isFirstFullyVisible by rowState.isFirstFullyVisible
                        val isLastFullyVisible by rowState.isLastFullyVisible

                        AnimatedVisibility(
                            modifier = Modifier.align(Alignment.CenterStart),
                            visible = !areAllItemsFullyVisible && !isFirstFullyVisible,
                            enter = slideInHorizontally(
                                initialOffsetX = { fullWidth -> -fullWidth }
                            ) + fadeIn(),
                            exit = slideOutHorizontally(
                                targetOffsetX = { fullWidth -> -fullWidth }
                            ) + fadeOut()
                        ) {
                            Column(
                                modifier = Modifier.Companion,
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center,
                            ) {

                                FilledTonalIconButton(
                                    colors = IconButtonDefaults.filledTonalIconButtonColors(
                                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                                    ),
                                    onClick = {
                                        scope.launch {
                                            val currentFirst = rowState.firstVisibleItemIndex
                                            val newIndex = max(0, currentFirst - SCROLL_STEP)
                                            rowState.animateScrollToItem(index = newIndex)
                                        }
                                    },
                                ) {
                                    Icon(
                                        imageVector = Icons.Outlined.KeyboardDoubleArrowLeft,
                                        contentDescription = stringResource(Res.string.lazy_row_scroll_left_arrow),
                                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            }
                        }
                        AnimatedVisibility(
                            modifier = Modifier.align(Alignment.CenterEnd),
                            visible = !areAllItemsFullyVisible && !isLastFullyVisible,
                            enter = slideInHorizontally(
                                initialOffsetX = { fullWidth -> +fullWidth }
                            ) + fadeIn(),
                            exit = slideOutHorizontally(
                                targetOffsetX = { fullWidth -> +fullWidth }
                            ) + fadeOut()
                        ) {
                            Column(
                                modifier = Modifier.Companion,
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center,
                            ) {

                                FilledTonalIconButton(
                                    colors = IconButtonDefaults.filledTonalIconButtonColors(
                                        containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                                    ),
                                    onClick = {
                                        scope.launch {
                                            val currentFirst = rowState.firstVisibleItemIndex
                                            val newIndex = min(
                                                totalItems - 1,
                                                currentFirst + SCROLL_STEP
                                            )
                                            rowState.animateScrollToItem(index = newIndex)
                                        }
                                    },
                                ) {
                                    Icon(
                                        imageVector = Icons.Outlined.KeyboardDoubleArrowRight,
                                        contentDescription = stringResource(Res.string.lazy_row_scroll_right_arrow),
                                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Viewport-Aware Composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As you scroll, Compose measures the viewport — what’s visible on screen. As you scroll, Compose measures the viewport — what’s visible on screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //RecyclerView, LazyColumn doesn’t recycle views in the same sense. Instead, it uses a slot reuse policy, where it tries to reuse composed content slots when possible.

            KotlinCode(
                code = $$"""
                    LazyListIntervalContent(
                        startIndex = 0,
                        itemCount = 1000,
                        itemContent = { index -> Text("Item $index") }
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun UserListScreen(users: List<User>) {
                        LazyColumn {
                            items (users) { user -> Text(user.name) }
                        }
                    }
                    
                    @Composable
                    fun ParentScreen() {
                        var count by remember { mutableIntState0f(0) }
                        val users = remember { listOf(User("Kamlesh"), User ("Sachin" )) }
                        
                        Column {
                            Button (onClick = { count++ }) { Text ("Counter: $count") }
                            UserListScreen(users = users)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn rendering",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, each item in a LazyColumn is rendered through subcomposition. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s a separate child composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It has its own slot table for storing state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "That slot table can be reused later if the item scrolls back in (as long as the key is stable).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "LazyListState Retained",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/j-roskopf/Phoebe.git
            KotlinCode(
                code = $$"""
                    import androidx.compose.foundation.lazy.LazyListState
                    import androidx.compose.foundation.lazy.grid.LazyGridState
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember

                    /**
                     * Keeps [LazyListState] instances alive across navigation so back restores scroll position.
                     */
                    object RetainedLazyListStates {
                        private val cache = mutableMapOf<String, LazyListState>()

                        @Composable
                        fun remember(key: String): LazyListState =
                            remember(key) { cache.getOrPut(key) { LazyListState() } }
                    }

                    /**
                     * Keeps [LazyGridState] instances alive across navigation so back restores scroll position.
                     */
                    object RetainedLazyGridStates {
                        private val cache = mutableMapOf<String, LazyGridState>()

                        @Composable
                        fun remember(key: String): LazyGridState =
                            remember(key) { cache.getOrPut(key) { LazyGridState() } }
                    }
                    
                    //1
                    val listState = RetainedLazyListStates.remember("artist-detail:${artist.id}")
                    LazyColumn(
                        state = listState
                    )
                    
                    //2
                    val listState = RetainedLazyListStates.remember("library-songs")
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn contentType",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    LazyColumn {
                        items(
                            items = messages,
                            key = { message -> message.id },
                            contentType = { message -> 
                                when (message) {
                                    is Message.TextMessage -> "text"
                                    is Message.ImageMessage -> "image"
                                    is Message.VideoMessage -> "video"
                                }
                            }
                        ) { message ->
            
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn contentPadding",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(16.dp), //Item animations & overscroll behave correctly
                        
                        modifier = Modifier
                        .fillMaxSize()
                        .padding(16.dp), //Outer padding - NOT scrollable
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn itemsIndexed",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/surajpsp/CustomList.git
            KotlinCode(
                code = """
                    //hashCode() may change if object state changes
                    
                    itemsIndexed(section.items, key = { _, item -> item.hashCode() }) { index, item  }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn Keys",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Your list data has a unique identifier (id, uuid, etc.) for each item.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You support dynamic list updates (insert, remove, reorder)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Keys allow Compose to manage list items efficiently, reducing unnecessary recompositions and improving scroll performance. important. Wrong key usage in LazyColumn can cause full recomposition or even list flickering.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unique keys ensure each item maintains its state, even during list reordering, preventing potential bugs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You apply animations like animateItemPlacement() has been deprecated. New is animateItem()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        //Animate item reordering of items in a list use animateItemPlacement().
                        
                        LazyColumn {
                            items (books, key = { it.id }) {
                                    Row (Modifier.animateItemPlacement(
                                        tween (durationMillis = 250)
                                    )) {
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Without keys, reordering items can lead to wrong association of item states, causing inconsistent UI behavior. Absence of keys may result in redundant recompositions, negatively impacting performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun UserList(users: List<User>) {
                        LazyColumn {
                            items (users, key = {it.id}) { user ->

                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Cairo-Squad/Yalla-Tasks.git
            KotlinCode(
                code = """
                    //LazyRow Center Item 
                    
                    val listState = rememberLazyListState()
                    val coroutineScope = rememberCoroutineScope()

                    val itemWidth = with(LocalDensity.current) { 56.dp.toPx() }
                    val screenCenter = with(LocalDensity.current) {
                        LocalConfiguration.current.screenWidthDp.dp.toPx() / 2
                    }
                    
                    LaunchedEffect(Unit, selectedDay) {
                        coroutineScope.launch {
                            val currentScroll =
                                listState.firstVisibleItemIndex * itemWidth + listState.firstVisibleItemScrollOffset
                            val directionSign = if (isGoingRight) 1 else -1
                            val targetCenter = selectedDay * itemWidth + (itemWidth * directionSign)
                            val scrollValue = targetCenter - screenCenter - currentScroll

                            listState.animateScrollBy(
                                value = scrollValue,
                                animationSpec = tween(
                                    durationMillis = 600,
                                    easing = FastOutSlowInEasing
                                )
                            )
                        }
                    }
                    
                    LazyRow(
                        state = listState
                    ) {}    
                """.trimIndent()
            )

            //https://github.com/DeveloperRejaul/todo_app_kmp
            KotlinCode(
                code = """
                    //LazyColumn Generic T Type 
                    
                    internal fun LazyListState.reachedBottom(buffer: Int = 1): Boolean {
                        val lastVisibleItem = this.layoutInfo.visibleItemsInfo.lastOrNull()
                        return lastVisibleItem?.index != 0 && lastVisibleItem?.index == this.layoutInfo.totalItemsCount - buffer
                    }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    internal fun <T> ListView(
                        modifier: Modifier = Modifier,
                        isLoadingMore: Boolean = false,
                        listState: LazyListState = rememberLazyListState(),
                        items: List<T>,
                        itemKey: (T) -> Any,
                        itemContent: @Composable (T) -> Unit,
                        loadingItem: @Composable () -> Unit,
                        loadMore: () -> Unit,
                        itemGap: Dp = 8.dp,
                        topPadding: Dp = 10.dp,
                        bottomPadding: Dp = 80.dp,
                        isRefreshing: Boolean = false,
                        onRefresh: () -> Unit = {},
                    ) {
                        val state = rememberPullToRefreshState()

                        // observe list scrolling
                        val reachedBottom: Boolean by remember { derivedStateOf { listState.reachedBottom() } }

                        // load more if scrolled to bottom
                        LaunchedEffect(reachedBottom) {
                            if (reachedBottom) {
                                loadMore()
                            }
                        }

                        PullToRefreshBox(
                            isRefreshing = isRefreshing,
                            onRefresh = onRefresh,
                            modifier = modifier,
                            state = state,
                            indicator = {
                                Indicator(
                                    modifier = Modifier.align(Alignment.TopCenter),
                                    isRefreshing = isRefreshing,
                                    containerColor = MaterialTheme.colorScheme.primaryContainer,
                                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                                    state = state
                                )
                            }
                        ){
                            LazyColumn(
                                modifier = modifier,
                                state = listState,
                                verticalArrangement = Arrangement.spacedBy(itemGap),
                                contentPadding = PaddingValues(
                                    top = topPadding,
                                    bottom = bottomPadding
                                ),
                            ) {
                                items(
                                    items = items,
                                    key = { item: T -> itemKey(item) },
                                ) { item ->
                                    itemContent(item)
                                }
                                if (isLoadingMore) {
                                    item {
                                        loadingItem()
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/kanha321/Upastithi
            KotlinCode(
                code = $$"""
                    //LazyColumn Generic T Type With Border Animation
                     
                    //UI 
                    GroupedLazyColumn(
                        groups = listOf(),
                        keySelector = { it.id },
                        contentPadding = innerPadding,
                        listState = listState,
                        onItemClick = { gIndex, iIndex, item ->
                            item.onClick()
                        },
                    ) { gIndex, iIndex, item ->
                            SettingsCard(item)
                    }
        
                    //GroupedLazyColumn.kt    
                    object ListState {
                        lateinit var value: LazyListState
                    }

                    data class Group<T>(
                        val header: String,
                        val items: List<T>
                    )
                    
                    @Composable
                    fun <T> GroupedLazyColumn(
                        groups: List<Group<T>>,
                        contentPadding: PaddingValues = PaddingValues(0.dp),
                        listState: LazyListState = ListState.value,
                        modifier: Modifier = Modifier,
                        keySelector: ((T) -> Any)? = null,
                        onItemClick: ((groupIndex: Int, itemIndex: Int, item: T) -> Unit)? = null,
                        enabled: Boolean = true,
                        colors: CardColors = CardDefaults.outlinedCardColors(),
                        elevation: CardElevation = CardDefaults.outlinedCardElevation(),
                        border: BorderStroke = BorderStroke(2.dp, MaterialTheme.colorScheme.primary),
                        interactionSource: MutableInteractionSource? = null,
                        externalCornerSize: Dp = 24.dp,
                        internalCornerSize: Dp = 8.dp,
                        itemSpacing: Dp = 4.dp,
                        columnPadding: Dp = 16.dp,
                        topPadding: Dp = 16.dp,
                        headerContent: @Composable (String) -> Unit = {
                            if (it.isNotBlank())
                                Text(
                                    text = it,
                                    style = MaterialTheme.typography.titleLarge,
                                    modifier = Modifier.padding(vertical = 8.dp, horizontal = 12.dp)
                                )
                            else Spacer(modifier = Modifier.padding(vertical = 4.dp))
                        },
                        cardContent: @Composable (groupIndex: Int, itemIndex: Int, item: T) -> Unit
                    ) {
                        var hoveredItem by remember { mutableStateOf<Pair<Int, Int>?>(null) }
                        // Pair<groupIndex, itemIndex>

                        LazyColumn(
                            modifier = modifier
                                .fillMaxSize()
                                .padding(horizontal = columnPadding),
                            contentPadding = PaddingValues(
                                top = contentPadding.calculateTopPadding() + topPadding,
                                bottom = contentPadding.calculateBottomPadding(),
                                start = contentPadding.calculateStartPadding(LayoutDirection.Ltr),
                                end = contentPadding.calculateEndPadding(LayoutDirection.Ltr)
                            ),
                            state = listState
                        ) {
                            groups.forEachIndexed { groupIdx, group ->

                                // Group header
                                item(key = "header-$groupIdx-${group.header}") {
                                    headerContent(group.header)
                                }

                                // Group items
                                items(
                                    count = group.items.size,
                                    key = { idx ->
                                        if (keySelector != null) {
                                            keySelector(group.items[idx])
                                        } else {
                                            "$groupIdx-$idx-${group.header}"
                                        }
                                    }
                                ) { idx ->
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(itemSpacing)
                                            .wrapContentHeight()
                                            .animateItem(),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        val source = interactionSource ?: remember { MutableInteractionSource() }
                                        val isHovered = source.collectIsHoveredAsState().value

                                        LaunchedEffect(isHovered) {
                                            hoveredItem = if (isHovered) groupIdx to idx else null
                                        }

                                        val scale = animateScale(isHovered = isHovered, hoverScale = 1.039f)

                                        val animatedShape = animateCornerRadius(
                                            isHovered = isHovered,
                                            itemCount = group.items.size,
                                            index = idx,
                                            hoveredIndex = if (hoveredItem?.first == groupIdx) hoveredItem?.second else null,
                                            externalCornerSize = externalCornerSize,
                                            internalCornerSize = internalCornerSize
                                        )

                                        OutlinedCard(
                                            onClick = { onItemClick?.invoke(groupIdx, idx, group.items[idx]) },
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .scale(scale),
                                            enabled = enabled,
                                            shape = animatedShape,
                                            colors = colors,
                                            elevation = elevation,
                                            border = border,
                                            interactionSource = source
                                        ) {
                                            cardContent(groupIdx, idx, group.items[idx])
                                        }
                                    }
                                }
                            }
                        }
                    }
                    
                    //Animation.kt
                    @Composable
                    fun animateCornerRadius(
                        isHovered: Boolean,
                        itemCount: Int,
                        index: Int,
                        hoveredIndex: Int?,
                        externalCornerSize: Dp,
                        internalCornerSize: Dp
                    ): RoundedCornerShape {
                        val isAboveHovered = hoveredIndex != null && index == hoveredIndex - 1
                        val isBelowHovered = hoveredIndex != null && index == hoveredIndex + 1

                        val topStartRadius = animateDpAsState(
                            targetValue = when {
                                isHovered -> externalCornerSize
                                isBelowHovered -> externalCornerSize
                                itemCount == 1 -> externalCornerSize
                                index == 0 -> externalCornerSize
                                else -> internalCornerSize
                            },
                            animationSpec = tween(300)
                        ).value

                        val topEndRadius = animateDpAsState(
                            targetValue = when {
                                isHovered -> externalCornerSize
                                isBelowHovered -> externalCornerSize
                                itemCount == 1 -> externalCornerSize
                                index == 0 -> externalCornerSize
                                else -> internalCornerSize
                            },
                            animationSpec = tween(300)
                        ).value

                        val bottomStartRadius = animateDpAsState(
                            targetValue = when {
                                isHovered -> externalCornerSize
                                isAboveHovered -> externalCornerSize
                                itemCount == 1 -> externalCornerSize
                                index == itemCount - 1 -> externalCornerSize
                                else -> internalCornerSize
                            },
                            animationSpec = tween(300)
                        ).value

                        val bottomEndRadius = animateDpAsState(
                            targetValue = when {
                                isHovered -> externalCornerSize
                                isAboveHovered -> externalCornerSize
                                itemCount == 1 -> externalCornerSize
                                index == itemCount - 1 -> externalCornerSize
                                else -> internalCornerSize
                            },
                            animationSpec = tween(300)
                        ).value

                        return RoundedCornerShape(
                            topStart = topStartRadius,
                            topEnd = topEndRadius,
                            bottomStart = bottomStartRadius,
                            bottomEnd = bottomEndRadius
                        )
                    }
                    @Composable
                    fun animateScale(
                        isHovered: Boolean,
                        hoverScale: Float = 1.015f,
                        normalScale: Float = 1f,
                        durationMs: Int = 300
                    ): Float {
                        return animateFloatAsState(
                            targetValue = if (isHovered) hoverScale else normalScale,
                            animationSpec = tween(durationMs)
                        ).value
                    }
                    """.trimIndent()
            )

            //https://github.com/3wiida/Taskii
            /*
            AnimatedVisibility(
                    visible = ,
                    enter = scaleIn() + fadeIn(),
                    exit = scaleOut() + fadeOut(),
                    modifier = Modifier.padding(bottom = 60.dp)
                ) { }
            */
            KotlinCode(
                code = """
                    //SnapshotStateList Create Dynamic LazyColumn Items
                    
                    private val _uiState = MutableStateFlow(CreateTaskScreenUIState())
                    val uiState = _uiState.asStateFlow()

                    fun onDone(value: String) {
                        _uiState.update {
                            it.copy(
                                checkList = uiState.value.checkList.apply { add(value) },
                                checkItem = ""
                            )
                        }
                    }

                    data class CreateTaskScreenUIState(
                        val checkList: SnapshotStateList<String> = mutableStateListOf()
                    )
                    """.trimIndent()
            )

            ////https://github.com/kanha321/Upastithi
            KotlinCode(
                code = """
                    //LazyColumn itemsIndexed
                    
                    data class RadioItem(
                        val label: String,
                        val onClick: () -> Unit
                    )

                    @Composable
                    fun RadioSelectionDialog(
                        title: String,
                        options: List<RadioItem>,
                        initialSelection: Int? = null,
                        icon: ImageVector = Icons.Outlined.Add,
                        onConfirm: () -> Unit,
                        onDismiss: () -> Unit
                    ) {
                        var selectedIndex by rememberSaveable { mutableStateOf(initialSelection) }

                        val dismissInteractionSource = remember { MutableInteractionSource() }
                        val confirmInteractionSource = remember { MutableInteractionSource() }

                        AlertDialog(
                            onDismissRequest = onDismiss,
                            icon = {
                                Icon(
                                    imageVector = icon,
                                    contentDescription = "Icon",
                                    modifier = Modifier.size(32.dp)
                                )
                            },
                            title = {
                                Text(
                                    text = title,
                                    textAlign = TextAlign.Center,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            },
                            text = {
                                LazyColumn(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentPadding = PaddingValues(top = 16.dp, bottom = 8.dp),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    itemsIndexed(options) { index, item ->
                                        val interactionSource = remember { MutableInteractionSource() }
                                        val radioInteractionSource = remember { MutableInteractionSource() }

                                        Surface(
                                            onClick = {
                                                selectedIndex = index
                                                item.onClick()
                                            },
                                            interactionSource = interactionSource,
                                            color = Color.Transparent,
                                            shape = RoundedCornerShape(16.dp),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(vertical = 4.dp)
                                                    .clickable(
                                                        interactionSource = interactionSource,
                                                        indication = null
                                                    ) {
                                                        selectedIndex = index
                                                        item.onClick()
                                                    },
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                RadioButton(
                                                    selected = selectedIndex == index,
                                                    onClick = {
                                                        selectedIndex = index
                                                        item.onClick()
                                                    },
                                                    interactionSource = radioInteractionSource,
                                                    colors = RadioButtonDefaults.colors(
                                                        unselectedColor = MaterialTheme.colorScheme.onSurfaceVariant,
                                                        selectedColor = MaterialTheme.colorScheme.primary
                                                    )
                                                )
                                                Text(
                                                    text = item.label,
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .padding(8.dp),
                                                    fontSize = 16.sp
                                                )
                                            }
                                        }
                                    }
                                }
                            },
                            confirmButton = {
                                
                            },
                            dismissButton = {

                            }
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How Does Compose Actually Use key()?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Without keys, reordering items can lead to wrong association of item states, causing inconsistent UI behavior. Absence of keys may result in redundant recompositions, negatively impacting performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Without key (), it uses the index to map UI slots.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With key(), it uses the ID — so identity is preserved across recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "LazyLayoutCacheWindow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, LazyLayoutCacheWindow is the mechanism used to control which items are kept in memory (cached) around the visible area in lazy layouts like LazyColumn, LazyRow, LazyVerticalGrid, LazyStaggeredGrid or custom LazyLayouts. LazyLayoutCacheWindow deliberately expands the viewport boundaries of LazyColumn/LazyGrid. Compose normally disposes items that go off-screen immediately; with this API, items ahead of the viewport are pre-composed/measured, while items behind the viewport are kept in memory without being disposed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is a sliding window that determines:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "How many items before and after the visible ones should be kept in the composition (cached).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This helps avoid recomposing too often as the user scrolls.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you scroll through a LazyColumn, Compose doesn’t keep all items composed, it only composes what’s visible plus a buffer (the cache window). LazyLayoutCacheWindow controls the size and behavior of that buffer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Internally:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It works with the layout’s viewport and current scroll position.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It’s part of performance optimizations: reducing unnecessary recompositions while not holding onto too many items.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You typically don’t use it directly. But if you’re building a custom LazyLayout, or diving deep into Compose internals, it’s useful to understand how caching and composition are managed around the viewport.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun SmoothMediaFeed(items: List<FeedItem>) {
                        // We use viewport fractions to ensure consistent behavior across device classes
                        val cacheWindow = remember {
                            LazyLayoutCacheWindow(
                                ahead = ViewportFraction(0.7f),  // 70% of screen height pre-loaded
                                behind = ViewportFraction(0.3f) // 30% kept for quick reverse-scroll
                            )
                        }

                        val state = rememberLazyListState(cacheWindow = cacheWindow)

                        LazyColumn(
                            state = state,
                            modifier = Modifier.fillMaxSize()
                        ) {
                            // Always provide a stable key for better performance
                            items(items, key = { it.id }) { item ->
                                MediaCard(item)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How the Cache Window Works",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )


            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To understand why this matters, you need to visualize what Compose is keeping “alive” in memory:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "[Behind Buffer] < → [Visible Viewport] < → [Ahead Buffer]",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Remain composed and measured: The layout work is done before the item hits the glass.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Skip “cold” recomposition: Swiping back up doesn’t trigger a “first-time” render.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Instant Visibility: They are ready to be drawn the instant the user’s scroll brings them into view.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Does LazyLayoutCacheWindow affect recomposition?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It doesn’t change how a component recomposes, but it changes when. It moves the composition work “off-screen” so it’s finished before the user ever sees the item.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Is it safe for low-end devices?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Yes, but be conservative. Because Compose 1.10 uses Pausable Composition, prefetch work is interruptible. However, the memory used to hold those extra items in the buffer remains occupied.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Track Visible Items (real-time viewport awareness) with LazyListState.layoutInfo.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Need to know which LazyColumn items are on screen?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "listState layoutInfo.visibleltemsinfo gives you their indexes and offsets - perfect for timers, analytics, or infinite scroll triggers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "visibleltemsinfo - list of currently visible item indexes and their offsets.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "viewportStartOffset & viewportEndOffset - the pixel range of what's on screen.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "totalltemsCount - total items in the list.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    LaunchedEffect (listState) {
                        while (isActive) {
                            listState.layoutInfo.visibleItemsInfo
                            .map { it.index }
                            .forEach { timers[it].value++ }
                                delay (1000)    
                        }
                    }    
                    """.trimIndent()
            )

            SpanText(
                text = "LazyRow Center View Find using layoutInfo",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // add this to your commonMain.dependencies 
                    // implementation("org.jetbrains.kotlinx:kotlinx-datetime:0.7.1")

                    @OptIn(ExperimentalTime::class)
                    @Composable
                    fun CarouselCalendar() {
                        //// Clock.System.now() -> Output: 2025-11-15T14:30:45.123456789Z
                        ////                       (Year-Month-Day T Hour:Minute:Second.Nanoseconds Z for UTC)

                        //// Output: 2025-11-15T20:00:45.123456789 (if you're in IST, UTC+5:30)
                        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

                        // Output: 2025-11-15

                        val year = today.year // 2025

                        // Always start from 1st January
                        val startDate = LocalDate(year, 1, 1)
                        val endDate = LocalDate(year, 12, 31)

                        // Generate full year date list
                        val dates = remember {
                            generateSequence(startDate) { date ->
                                val next = date.plus(1, DateTimeUnit.DAY)
                                if (next <= endDate) next else null
                            }.toList()
                        }

                        // Selected item is today's date (even though list starts at Jan 1)
                        var selectedDate by remember { mutableStateOf(today) }

                        BoxWithConstraints(
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            DialerWeekCalendar(
                                dates = dates,
                                selectedDate = selectedDate,
                                onDateSelected = { selectedDate = it },
                                maxWidth = maxWidth
                            )
                        }
                    }

                    @Composable
                    fun DialerWeekCalendar(
                        modifier: Modifier = Modifier,
                        dates: List<LocalDate>,
                        selectedDate: LocalDate,
                        onDateSelected: (LocalDate) -> Unit,
                        maxWidth: Dp
                    ) {
                        val listState = rememberLazyListState()
                        var isInitialLoad by remember { mutableStateOf(true) }

                        // Auto-scroll to center the selected date
                        LaunchedEffect(selectedDate) {
                            val selectedIndex = dates.indexOf(selectedDate)
                            if (selectedIndex != -1) {
                                if (isInitialLoad) {
                                    listState.scrollToItem(selectedIndex)
                                    isInitialLoad = false
                                } else {
                                    listState.animateScrollToItem(selectedIndex)
                                }
                            }
                        }

                        LazyRow(
                            modifier = modifier
                                .fillMaxWidth()
                                .height(120.dp),
                            state = listState,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            contentPadding = PaddingValues(horizontal = (maxWidth / 2) - 46.dp)
                        ) {
                            items(dates.size) { index ->
                                val date = dates[index]

                                // Center calculation
                                val layoutInfo = listState.layoutInfo
                                val viewportCenter =
                                    layoutInfo.viewportStartOffset +
                                            (layoutInfo.viewportEndOffset - layoutInfo.viewportStartOffset) / 2

                                val itemInfo = layoutInfo.visibleItemsInfo.find { it.index == index }
                                val itemCenter = itemInfo?.let { it.offset + it.size / 2 } ?: 0

                                // Distance from center
                                val distanceFromCenter = if (itemInfo != null) {
                                    abs(viewportCenter - itemCenter).toFloat() / layoutInfo.viewportSize.width
                                } else 1f

                                val scale = (1f - (distanceFromCenter * 0.3f)).coerceIn(0.7f, 1f)
                                val rotationY = (distanceFromCenter * 40f).coerceAtMost(45f)
                                val alpha = (1f - (distanceFromCenter * 0.5f)).coerceIn(0.5f, 1f)

                                Box(
                                    modifier = Modifier
                                        .graphicsLayer {
                                            scaleX = scale
                                            scaleY = scale
                                            this.rotationY =
                                                if (itemCenter < viewportCenter) rotationY else -rotationY
                                            this.alpha = alpha
                                        }
                                        .clip(RoundedCornerShape(12.dp))
                                        .background(
                                            if (date == selectedDate) Color(0xFF1E88E5) else Color.White
                                        )
                                        .clickable { onDateSelected(date) }
                                        .width(80.dp)
                                        .height(100.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Text(
                                            text = date.month.name.take(3),
                                            style = MaterialTheme.typography.bodySmall.copy(
                                                color = if (date == selectedDate) Color.White.copy(alpha = 0.8f)
                                                else Color.DarkGray
                                            )
                                        )

                                        Spacer(modifier = Modifier.height(2.dp))

                                        Text(
                                            text = date.dayOfMonth.toString(),
                                            style = MaterialTheme.typography.headlineSmall.copy(
                                                fontWeight = FontWeight.Bold,
                                                color = if (date == selectedDate) Color.White else Color.Black
                                            )
                                        )

                                        Spacer(modifier = Modifier.height(2.dp))

                                        Text(
                                            text = date.dayOfWeek.name.take(3),
                                            style = MaterialTheme.typography.bodySmall.copy(
                                                color = if (date == selectedDate) Color.White.copy(alpha = 0.8f)
                                                else Color.Gray
                                            )
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How to Detect Clicks on a Jetpack Compose TextField",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can use MutableInteractionSource to listen for PressInteraction.Release events. This allows us to detect clicks while maintaining the native behavior of the TextField.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        @Composable
                        fun ClickableTextField(
                            label: String,
                            modifier: Modifier = Modifier,
                            height: Dp = 60.dp,
                            onClick: () -> Unit,
                            text: String,
                            onTextChange: (String) -> Unit,
                        ) {
                            var isFocused by remember { mutableStateOf(false) }
                            val interactionSource = remember { MutableInteractionSource() }

                            // Collect interaction events
                            LaunchedEffect(interactionSource) {
                                interactionSource.interactions.collect { interaction ->
                                    if (interaction is PressInteraction.Release) {
                                        onClick.invoke()
                                    }
                                }
                            }

                            TextField(
                                value = text,
                                onValueChange = { newText -> onTextChange(newText) },
                                keyboardOptions = KeyboardOptions(
                                    keyboardType = KeyboardType.Text,
                                    imeAction = ImeAction.Next
                                ),
                                modifier = modifier
                                    .fillMaxWidth()
                                    .height(height)
                                    .onFocusChanged { focusState ->
                                        isFocused = focusState.isFocused
                                    }
                                    .border(
                                        width = 1.dp,
                                        color = Color.Gray,
                                        shape = RoundedCornerShape(12.dp)
                                    ),
                                label = {
                                    Text(
                                        text = label,
                                        color = Color.Black,
                                        fontSize = if (text.isNotEmpty()) 12.sp else 16.sp,
                                    )
                                },
                                textStyle = TextStyle(fontSize = 16.sp),
                                interactionSource = interactionSource
                            )
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We create a MutableInteractionSource and pass it to the TextField.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We use LaunchedEffect to listen for interactions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a PressInteraction.Release event is detected, we invoke onClick.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "TextFieldValue",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A class holding information about the editing state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The input service updates text selection, cursor, text and text composition. This class represents those values and it is possible to observe changes to those values in the text editing composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This class stores a snapshot of the input state of the edit buffer and provide utility functions for answering IME requests such as getTextBeforeCursor, getSelectedText.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/FilipeGuerreiro/app-controle-de-caixa.git
            KotlinCode(
                code = $$"""
                    @Composable
                    fun CurrencyAmountInput(
                        amountInCents: Long,
                        onAmountChange: (Long) -> Unit,
                        modifier: Modifier = Modifier,
                        prefix: String = "R$ ",
                        textStyle: TextStyle = MaterialTheme.typography.displayMedium.copy(fontWeight = FontWeight.Bold),
                        textColor: Color = MaterialTheme.colorScheme.onSurface,
                        maxDigits: Int = 11 // Max value safely fits in Long and typical currency limits
                    ) {
                        val formattedAmount = amountInCents.toCurrencyStringWithoutPrefix()
                        
                        // Create TextFieldValue with cursor always at the end
                        val textFieldValue = TextFieldValue(
                            text = formattedAmount,
                            selection = TextRange(formattedAmount.length)
                        )

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = modifier
                        ) {
                            Text(
                                text = prefix,
                                style = MaterialTheme.typography.headlineMedium,
                                color = textColor.copy(alpha = 0.6f),
                                modifier = Modifier.padding(top = 8.dp, end = 4.dp)
                            )

                            Box(contentAlignment = Alignment.Center) {
                                if (amountInCents == 0L) {
                                    Text(
                                        text = "0,00",
                                        style = textStyle,
                                        color = textColor.copy(alpha = 0.3f),
                                        textAlign = TextAlign.Center
                                    )
                                }
                                
                                BasicTextField(
                                    value = textFieldValue,
                                    onValueChange = { newValue ->
                                        // Logic to handle input:
                                        // 1. Filter only digits
                                        // 2. Convert to Long
                                        // 3. Update state
                                        val digitsOnly = newValue.text.filter { it.isDigit() }
                                        
                                        // Prevent overflow or excessive digits
                                        if (digitsOnly.length <= maxDigits) {
                                            val newAmount = digitsOnly.toLongOrNull() ?: 0L
                                            onAmountChange(newAmount)
                                        }
                                    },
                                    textStyle = textStyle.copy(
                                        color = if (amountInCents == 0L) Color.Transparent else textColor,
                                        textAlign = TextAlign.Center
                                    ),
                                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
                                    singleLine = true,
                                    cursorBrush = SolidColor(textColor)
                                )
                            }
                        }
                    }

                    fun Long.toCurrencyString(): String {
                        val negative = this < 0
                        val absValue = abs(this)
                        val reais = absValue / 100
                        val cents = absValue % 100
                        val centsStr = cents.toString().padStart(2, '0')
                        val reaisStr = reais.formatWithThousandsSeparator()
                        val prefix = if (negative) "-R$ " else "R$ "
                        return "$prefix$reaisStr,$centsStr"
                    }

                    fun Long.toCurrencyStringWithoutPrefix(): String {
                        val absValue = abs(this)
                        val reais = absValue / 100
                        val cents = absValue % 100
                        val centsStr = cents.toString().padStart(2, '0')
                        val reaisStr = reais.formatWithThousandsSeparator()
                        return "$reaisStr,$centsStr"
                    }

                    private fun Long.formatWithThousandsSeparator(): String {
                        val str = this.toString()
                        val result = StringBuilder()
                        var count = 0
                        for (i in str.length - 1 downTo 0) {
                            if (count > 0 && count % 3 == 0) {
                                result.append('.')
                            }
                            result.append(str[i])
                            count++
                        }
                        return result.reverse().toString()
                    }

                    fun Double.toCents(): Long = round(this * 100).toLong()

                    fun String.toCents(): Long {
                        if (this.isBlank()) return 0L
                        // Remove tudo que não for dígito
                        val digitsOnly = this.filter { it.isDigit() }
                        return digitsOnly.toLongOrNull() ?: 0L
                    }
                    """.trimIndent()
            )

            //https://github.com/pbclive-digital/pbc-web-app.git (contentText)
            KotlinCode(
                code = """
                    //TextFieldValue Search/Filter

                    @Composable
                    @Preview
                    fun App() {
                        MaterialTheme {
                            var searchText by remember { mutableStateOf(TextFieldValue("")) }

                            val commands = listOf(
                                "Open App",
                                "Search Files",
                                "View System Status",
                                "Run Command",
                                "Settings"
                            )

                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(16.dp)
                            ) {
                                SearchBox(searchText) { searchText = it }
                                CommandList(commands.filter { it.contains(searchText.text, ignoreCase = true) })
                            }
                        }
                    }
                    
                    /*
                        val firstName = remember { mutableStateOf(TextFieldValue()) }
                        contentText = firstName
                    */

                    @Composable
                    fun SearchBox(
                        //contentText: MutableState<TextFieldValue>,
                        searchText: TextFieldValue, 
                        onSearchTextChanged: ((newValue: TextFieldValue) -> Unit)? = null) 
                     {
                        OutlinedTextField(
                            value = searchText,
                            //value = contentText.value,
                            onValueChange = { newValue ->
                                onValueChange?.invoke(newValue)
                            },
                            label = { Text("Search") },
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFF2C2C2C))
                                .padding(16.dp),
                            singleLine = true,
                            textStyle = MaterialTheme.typography.h6.copy(color = Color.White),
                            colors = TextFieldDefaults.outlinedTextFieldColors(
                                backgroundColor = Color(0xFF2C2C2C),
                                focusedBorderColor = Color(0xFF4CAF50),
                                unfocusedBorderColor = Color.Gray
                            )
                        )
                    }

                    @Composable
                    fun CommandList(commands: List<String>) {
                        val scrollState = rememberScrollState()

                        Column(modifier = Modifier.verticalScroll(scrollState)) {
                            commands.forEach { command ->
                                CommandItem(command)
                                Spacer(modifier = Modifier.height(10.dp))
                            }
                        }
                    }

                    @Composable
                    fun CommandItem(command: String) {
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(8.dp)
                                .background(Color(0xFF333333)),
                            elevation = 4.dp
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(command, style = MaterialTheme.typography.body1, color = Color.Black)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ComponentActivity",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComponentActivity is a lightweight class introduced as part of the AndroidX libraries. It is optimized for Jetpack Compose and does not require XML layouts. The UI is built inside setContent{} using @Composable functions. No need to call setContentView(). It provides basic lifecycle management and is tightly integrated with modern Jetpack components like ViewModel, LiveData, and LifecycleOwner.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you have some kind of let’s say permission related to the main XML things or like main Fragment things as well or like some kind of things related to the like you the things that we do we do not require in the like compose. So at that time you can use app compact activity. ComponentActivity() is child activity. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose, LocalContext is a CompositionLocal that allows you to access the current Context within a @Composable function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike traditional Android development, where Context management could sometimes lead to memory leaks or lifecycle issues, LocalContext simplifies and safeguards its usage by integrating it directly into the composition system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Use LocalContext?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Safe Context Access: LocalContext ensures that the Context is properly scoped to the lifecycle of the composable where it is used. This reduces the risk of common errors, such as holding references to an expired Activity context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Interacting with Resources and Services: It is particularly useful for tasks like retrieving resources (e.g., strings, dimensions, or colors), showing system-level Ul components like Toasts, or interacting with system services.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Seamless Integration: By leveraging LocalContext, you can start activities, access services, or interact with APls that depend on Context, all within the declarative paradigm of Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "LocalActivity",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Provides direct access to the current Activity, simplifying and securing Compose development. With the new LocalActivity composition local, you can directly retrieve the current Activity within a composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val activity = LocalActivity.current
                    val context = LocalContext.current.applicationContext
                    val activity = LocalActivity.current!! as ComponentActivity
                    """.trimIndent()
            )

            SpanText(
                text = "Gradient",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val cardGlowBorder = Brush.verticalGradient(
                        colors = listOf(Color(0xFFFF00FF), Color(0xFF00FFFF))
                    )

                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(brush = Brush.verticalGradient(colors = gradientColors))
                            .padding(horizontal = 20.dp, vertical = if (isPortrait) 32.dp else 16.dp)
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "builder",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun Format.Companion.builder(): FormatBuilder = FormatBuilder()

                    class FormatBuilder {
                        private var id: String = ""
                        private var name: String = ""
                        private var time: Int = 0

                        fun id(id: String) = apply { this.id = id }
                        fun name(name: String) = apply { this.name = name }
                        fun time(time: Int) = apply { this.time = time }

                        fun build(): Format = Format(
                            id = id,
                            name = name,
                            time = time
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "typealias Composable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/stoyan-vuchev/squircle-shape
            KotlinCode(
                code = """
                       // Select the menu animation 
                       
                       @Composable
                       fun NavigationList(
                           modifier: Modifier = Modifier,
                           selectedItemIndex: Int,
                           itemsCount: Int,
                           items: @Composable NavigationListScope.() -> Unit
                       ) {

                           Box(
                               modifier = Modifier
                                   .then(modifier)
                                   .floatingComponentHazeEffect(Theme.shapes.medium)
                           ) {

                               var maxHeight by remember { mutableIntStateOf(0) }
                               var maxWidth by remember { mutableIntStateOf(0) }
                               val targetItem by remember(itemsCount, selectedItemIndex) {
                                   derivedStateOf {
                                       (maxHeight / itemsCount) * selectedItemIndex
                                   }
                               }

                               val layoutHeight = remember(itemsCount) { 56.dp * itemsCount }
                               val offsetY by animateIntAsState(
                                   targetValue = targetItem,
                                   animationSpec = spring(
                                       dampingRatio = Spring.DampingRatioLowBouncy,
                                       stiffness = Spring.StiffnessMediumLow
                                   )
                               )

                               Box(
                                   modifier = Modifier
                                       .graphicsLayer { this.translationY = offsetY.toFloat() }
                                       .height(56.dp)
                                       .width(with(LocalDensity.current) { maxWidth.toDp() })
                                       .padding(4.dp)
                                       .clip(shape = Theme.shapes.small)
                                       .background(color = Theme.colorScheme.accent.copy(alpha = .16f))
                                       .innerShadow(
                                           shape = Theme.shapes.small,
                                           shadow = Shadow(
                                               radius = 16.dp,
                                               brush = Brush.verticalGradient(
                                                   colors = listOf(
                                                       Theme.colorScheme.accent.copy(.0f),
                                                       Theme.colorScheme.accent.copy(.1f)
                                                   )
                                               ),
                                               offset = DpOffset(x = 0.dp, y = (-2).dp)
                                           )
                                       )
                                       .border(
                                           width = 1.dp,
                                           brush = Brush.verticalGradient(
                                               colors = listOf(
                                                   Theme.colorScheme.accent.copy(.1f),
                                                   Theme.colorScheme.accent.copy(.0f)
                                               )
                                           ),
                                           shape = Theme.shapes.small
                                       )
                               )

                               Column(
                                   modifier = Modifier
                                       .height(layoutHeight)
                                       .onSizeChanged {
                                           maxHeight = it.height
                                           maxWidth = it.width
                                       },
                                   horizontalAlignment = Alignment.Start,
                                   content = items
                               )
                           }
                       }

                       typealias NavigationListScope = ColumnScope
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //typedef VoidBoolCallback = void Function(bool value); //Human readable
                    typealias ComposableFactory = @Composable () -> Unit

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun AppBar(
                        title: String = "",
                        navIcon: AppBarIconModel? = null,
                        containerColor: Color = MaterialTheme.colorScheme.primary,
                        titleContentColor: Color = MaterialTheme.colorScheme.onPrimary,
                        actions: @Composable RowScope.() -> Unit = {},
                    ) {
                        TopAppBar(
                            title = { Text(text = title) },
                            navigationIcon = if (navIcon == null) {
                                composableFactory { }
                            } else {
                                composableFactory { AppBarIcon(navIcon) }
                            },
                            colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                                containerColor = containerColor,
                                titleContentColor = titleContentColor,
                                navigationIconContentColor = titleContentColor,
                                actionIconContentColor = titleContentColor,
                            ),
                            actions = actions
                        )
                    }

                    @Composable
                    internal fun AppBarIcon(navIcon: AppBarIconModel) {
                        IconButton(onClick = { navIcon.action() }) {
                            Icon(imageVector = navIcon.icon, contentDescription = navIcon.contentDescription)
                        }
                    }

                    fun composableFactory(create: @Composable () -> Unit): ComposableFactory {
                        val factory: @Composable () -> Unit = {
                            create()
                        }
                        return factory
                    }

                    data class AppBarIconModel(
                        val icon: ImageVector,
                        val contentDescription: String,
                        val action: () -> Unit,
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "ColorProducer Text",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //if condition ColorProducer Lambda
                    
                    //New ColorProducer support in Text updates only the paint phase instead of triggering a full recomposition on color changes. This leads to improved performance, especially for color animations and frequent color updates
                    var isRed by remember { mutableStateOf(true) }
                    
                    Text(
                        text = "Kotlin Multiplatform Developer",
                        color = { if (isRed) Color.Red else Color.Blue }, // ColorProducer Lambda
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "TextField",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Cj-Rodriguez101/Vicinity-KMP
            KotlinCode(
                code = """
                    //TextField Hint Animation
                    
                    @Composable
                    fun RadiusAuthTextField(
                        hintContent: (@Composable () -> Unit)? = null,
                    ) {
                        RadiusTextField(
                            hintContent = {
                                hintContent?.let { it() } ?: run {
                                    AnimatedVisibility(visible = state != null && !state.isValid) {
                                        state?.toUiText()?.asString()?.let {
                                            Text(
                                                text = it,
                                                color = Color.Red,
                                                modifier = Modifier.padding(top = XXSmall.dp)
                                            )
                                        }
                                    }
                                }
                            }
                        )
                    }
                    
                    @Composable
                    fun RadiusTextField(
                        hintContent: (@Composable () -> Unit)? = null,
                    ) {
                        Column {
                            TextField(
                                
                            )
                            hintContent?.let { it() } ?: run {
                                AnimatedVisibility(visible = true) {
                                    toUiText()?.asString()?.let {
                                        Text(
                                            text = it,
                                            color = Color.Red,
                                            modifier = Modifier.padding(top = XXSmall.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Stable
                    @Serializable
                    enum class InputType {
                    	COMMON,
                    	PASSWORD;

                    	internal val toVisualTransformation: VisualTransformation get() = when (this) {
                    		COMMON -> VisualTransformation.None
                    		PASSWORD -> PasswordVisualTransformation()
                    	}

                    	internal val toKeyboardOptions: KeyboardOptions get() = when (this) {
                    		COMMON -> KeyboardOptions.Default
                    		PASSWORD -> KeyboardOptions(keyboardType = KeyboardType.Password)
                    	}
                    }

                    @Stable
                    class TextInputState(str: String = "") {
                    	var text: String by mutableStateOf(str)
                    	var overflow: Boolean by mutableStateOf(false)

                    	val ok: Boolean by derivedStateOf { !overflow && text.isNotEmpty() }
                    }

                    @Composable
                    fun TextInput(
                    	state: TextInputState,
                    	hint: String,
                    	inputType: InputType = InputType.COMMON,
                    	readOnly: Boolean = false,
                    	maxLength: Int = 0,
                    	maxLines: Int = 1,
                    	minLines: Int = maxLines,
                    	clearButton: Boolean = true,
                    	modifier: Modifier = Modifier
                    ) {
                    	OutlinedTextField(
                    		value = state.text,
                    		onValueChange = {
                    			state.text = it
                    			state.overflow = maxLength > 0 && it.length > maxLength
                    		},
                    		label = {
                    			Text(
                    				text = hint,
                    				style = MaterialTheme.typography.titleMedium
                    			)
                    		},
                    		trailingIcon = if (clearButton) {
                    			{
                    				ClickIcon(
                    					icon = Icons.Outlined.Clear,
                    					onClick = {
                    						state.text = ""
                    						state.overflow = false
                    					}
                    				)
                    			}
                    		} else null,
                    		readOnly = readOnly,
                    		visualTransformation = remember(inputType) { inputType.toVisualTransformation },
                    		keyboardOptions = remember(inputType) { inputType.toKeyboardOptions },
                    		singleLine = maxLines.coerceAtLeast(1) == 1,
                    		minLines = minLines.coerceAtLeast(1),
                    		maxLines = maxLines.coerceAtLeast(1),
                    		isError = state.overflow,
                    		modifier = modifier
                    	)
                    }
                    """.trimIndent()
            )

            //https://github.com/thejohnsondev/PassGuard-Multiplatform
            KotlinCode(
                code = """
                    //Password Random Generate, Password Level Indicator
                    
                    @Composable
                    fun StrengthLevelIndicator(
                        modifier: Modifier = Modifier,
                        level: Float,
                    ) {
                        var oldLevelState by remember { mutableIntStateOf(level.normalizeToInt()) }
                        val newLevel = level.normalizeToInt()
                        SideEffect {
                            oldLevelState = level.normalizeToInt()
                        }

                        val animatedLevelState by animateFloatAsState(
                            targetValue = level,
                            animationSpec = tween(durationMillis = DEFAULT_ANIM_DURATION),
                            label = "Strength Level Animation"
                        )

                        val animatedColor by animateColorAsState(
                            targetValue = level.mapToStrengthLevelColor(),
                            animationSpec = tween(durationMillis = DEFAULT_ANIM_DURATION),
                            label = "Strength Level Color Animation"
                        )

                        Box(
                            modifier = modifier
                                .size(Size32),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(
                                progress = { animatedLevelState },
                                color = animatedColor
                            )
                            AnimatedContent(
                                targetState = level,
                                transitionSpec = {
                                    if (oldLevelState > newLevel) {
                                        slideInVertically { -it } togetherWith slideOutVertically { it }
                                    } else {
                                        slideInVertically { it } togetherWith slideOutVertically { -it }
                                    }
                                }
                            ) {
                                Text(
                                    text = newLevel.toString(),
                                    color = animatedColor,
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }

                    }

                    private fun Float.normalizeToInt(): Int {
                        return (this * 10).toInt()
                    }
                    
                    interface PasswordGenerationRepository {
                        fun generatePassword(
                            type: PasswordGenerationType,
                            length: Int = 12,
                            includeLower: Boolean = true,
                            includeUpper: Boolean = true,
                            includeDigits: Boolean = true,
                            includeSpecial: Boolean = true,
                        ): PasswordGeneratedResult

                        fun evaluateStrength(password: String): PasswordStrength
                        fun isCommonPassword(password: String): Boolean
                    }
                    
                    internal class PasswordGeneratorRepositoryImpl(private val commonPasswords: Set<String>) :
                        PasswordGenerationRepository {

                        private val lowerCase = "abcdefghijklmnopqrstuvwxyz"
                        private val upperCase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
                        private val digits = "0123456789"
                        private val specialChars = "!@#\$%^&*()-_=+[]{}|;:'\".<>?/"

                        private val passwordRankMap: Map<String, Int> = commonPasswords
                            .withIndex()
                            .associate { it.value to it.index }

                        override fun generatePassword(
                            type: PasswordGenerationType,
                            length: Int,
                            includeLower: Boolean,
                            includeUpper: Boolean,
                            includeDigits: Boolean,
                            includeSpecial: Boolean,
                        ): PasswordGeneratedResult {
                            val password = when (type) {
                                PasswordGenerationType.RANDOM -> generateRandomPassword(
                                    length,
                                    includeLower,
                                    includeUpper,
                                    includeDigits,
                                    includeSpecial
                                )

                                PasswordGenerationType.HUMAN -> generateHumanReadablePassword(length)
                                PasswordGenerationType.UUID -> generateUUID()
                            }
                            val strength = evaluateStrength(password)
                            return PasswordGeneratedResult(password, strength.level, strength.suggestion)
                        }

                        private fun generateRandomPassword(
                            length: Int,
                            includeLower: Boolean,
                            includeUpper: Boolean,
                            includeDigits: Boolean,
                            includeSpecial: Boolean,
                        ): String {
                            val charPool = buildString {
                                if (includeLower) append(lowerCase)
                                if (includeUpper) append(upperCase)
                                if (includeDigits) append(digits)
                                if (includeSpecial) append(specialChars)
                            }
                            if (charPool.isEmpty()) return ""
                            return (1..length).map { charPool[Random.nextInt(charPool.length)] }.joinToString("")
                        }

                        private fun generateHumanReadablePassword(length: Int): String {
                            val wordList = HumanPasswordWords.wordList
                            val words = mutableListOf<String>()
                            while (words.joinToString("").length < length) {
                                words.add(wordList[Random.nextInt(wordList.size)])
                            }
                            return words.joinToString("-").take(length)
                        }

                        @OptIn(ExperimentalUuidApi::class)
                        private fun generateUUID(): String = Uuid.random().toString()

                        override fun evaluateStrength(password: String): PasswordStrength {
                            val rank = passwordRankMap[password.lowercase()]

                            if (rank != null) {
                                val suggestion = when {
                                    rank < 10 -> "This password is in the TOP 10 most common. Avoid it at all costs."
                                    rank < 100 -> "This password is in the top 100 most common. It's very unsafe."
                                    rank < 1000 -> "This password is in the top 1000 most common. It's easy to guess."
                                    else -> "This password is very common. Choose a more unique one."
                                }
                                return PasswordStrength(0.1f, suggestion)
                            }

                            val lengthScore = when {
                                password.length >= 16 -> 10
                                password.length >= 12 -> 8
                                password.length >= 10 -> 6
                                password.length >= 8 -> 4
                                password.length >= 6 -> 3
                                password.length >= 4 -> 2
                                else -> 0
                            }

                            val diversityScore = (listOf(
                                password.any { it.isLowerCase() },
                                password.any { it.isUpperCase() },
                                password.any { it.isDigit() },
                                password.any { !it.isLetterOrDigit() }
                            ).count { it } * 0.75).toInt()

                            val score = (lengthScore + diversityScore).coerceAtMost(10) * 0.1f

                            val suggestion = when (score) {
                                in 0.0..0.1 -> "Extremely weak! Use at least 8 characters with mixed cases and symbols."
                                in 0.2..0.3 -> "Too weak, add more characters and mix uppercase, lowercase, and numbers."
                                in 0.4..0.5 -> "Weak. Consider adding special symbols and increasing length."
                                0.6f -> "Moderate. Try using a longer password with symbols."
                                in 0.7..0.8 -> "Strong. Ensure it's not a common phrase."
                                0.9f -> "Very strong! Nearly unbreakable but avoid using personal information."
                                1f -> "Extremely strong! This password is highly secure."
                                else -> "Moderate. Try using a longer password with symbols."
                            }

                            return PasswordStrength(score, suggestion)
                        }

                        override fun isCommonPassword(password: String): Boolean {
                            return commonPasswords.contains(password)
                        }
                    }
                    
                    data class PasswordGeneratedResult(
                        val password: String,
                        val strengthLevel: Float,
                        val suggestion: String?
                    )
                    
                    enum class PasswordGenerationType {
                        RANDOM, HUMAN, UUID
                    }
                    
                    data class PasswordStrength(
                        val level: Float,
                        val suggestion: String?
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "BadgedBox",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    private fun NotificationBadge() {
                        BadgedBox(
                            badge = {
                                Badge(
                                    containerColor = PrimaryBlue,
                                    contentColor = White,
                                    modifier = Modifier
                                        .offset(
                                            y = (-4).dp
                                        )
                                ) {
                                    Text(
                                        text = "3",
                                        fontFamily = IBMPlexSansArabicFontFamily,
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 10.sp,
                                    )
                                }
                            },
                            content = {
                                Icon(
                                    modifier = Modifier
                                        .size(40.dp)
                                        .border(
                                            width = 1.dp,
                                            color = MediumGray,
                                            shape = RoundedCornerShape(12.dp)
                                        )
                                        .padding(10.dp),
                                    painter = R.drawable.ic_notification.toPainter(),
                                    contentDescription = "notification"
                                )
                            }
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/jcarlosvelasco/loopmusic.git
            KotlinCode(
                code = """
                    //Tab Select Background Animation
                    
                    enum class NavigationTab(
                        val titleRes: StringResource,
                        val selectedIcon: ImageVector,
                        val unselectedIcon: ImageVector
                    ) {
                        HOME(Res.string.home, Icons.Filled.Home, Icons.Outlined.Home),
                        SONGS(Res.string.songs, Icons.Filled.MusicNote, Icons.Outlined.MusicNote),
                        OTHER(Res.string.other, Icons.Filled.Menu, Icons.Outlined.Menu),
                        ALBUMS(Res.string.albums, Icons.Filled.Album, Icons.Outlined.Album),
                        SEARCH(Res.string.search, Icons.Filled.Search, Icons.Outlined.Search),
                        ARTISTS(Res.string.artists, Icons.Filled.Person, Icons.Outlined.Person),
                        FOLDERS(Res.string.folders, Icons.Filled.Folder, Icons.Outlined.Folder),
                        PLAYLISTS(Res.string.playlists, Icons.AutoMirrored.Filled.PlaylistPlay, Icons.AutoMirrored.Outlined.PlaylistPlay)
                    }
                    
                    @Composable
                    fun CustomNavigationItem(
                        tab: NavigationTab,
                        selected: Boolean,
                        onClick: () -> Unit,
                        onPositioned: (Float, Float) -> Unit
                    ) {
                        val contentColor = if (selected) {
                            MaterialTheme.colorScheme.onPrimaryContainer
                        } else {
                            MaterialTheme.colorScheme.onSurfaceVariant
                        }

                        val title = stringResource(tab.titleRes)

                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(16.dp))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = onClick
                                )
                                .onGloballyPositioned { coordinates ->
                                    val offsetX = coordinates.positionInParent().x
                                    val width = coordinates.size.width.toFloat()
                                    onPositioned(offsetX, width)
                                }
                        ) {
                            Row(
                                modifier = Modifier
                                    .padding(horizontal = 16.dp, vertical = 8.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center
                            ) {
                                Icon(
                                    imageVector = if (selected) tab.selectedIcon else tab.unselectedIcon,
                                    contentDescription = title,
                                    modifier = Modifier.size(24.dp),
                                    tint = contentColor
                                )

                                if (selected) {
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(
                                        text = title,
                                        fontSize = 14.sp,
                                        style = appTypography().bodyMedium,
                                        fontWeight = FontWeight.Bold,
                                        color = contentColor
                                    )
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun CustomBottomNavigationBar(
                        selectedTab: NavigationTab,
                        onTabSelected: (NavigationTab) -> Unit,
                        modifier: Modifier = Modifier,
                        navigationTabs: List<NavigationTab>
                    ) {
                        val density = LocalDensity.current

                        val allTabs = remember(navigationTabs) {
                            val shouldShowOther = navigationTabs.size < NavigationTab.entries.size - 1
                            if (shouldShowOther) navigationTabs + NavigationTab.OTHER else navigationTabs
                        }

                        var indicatorOffsetX by remember { mutableStateOf(0.dp) }
                        var indicatorWidth by remember { mutableStateOf(0.dp) }

                        val animatedOffsetX by animateDpAsState(
                            targetValue = indicatorOffsetX,
                            animationSpec = tween(durationMillis = 300),
                            label = "indicatorOffset"
                        )

                        val animatedWidth by animateDpAsState(
                            targetValue = indicatorWidth,
                            animationSpec = tween(durationMillis = 300),
                            label = "indicatorWidth"
                        )

                        Card(
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(bottom = 4.dp)
                                .shadow(
                                    elevation = 8.dp,
                                    shape = RoundedCornerShape(24.dp)
                                ),
                            shape = RoundedCornerShape(24.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.surface
                            )
                        ) {
                            Box {
                                if (animatedWidth > 0.dp) {
                                    Box(
                                        modifier = Modifier
                                            .offset(x = animatedOffsetX + 8.dp)
                                            .width(animatedWidth)
                                            .height(40.dp)
                                            .padding(horizontal = 4.dp, vertical = 4.dp)
                                            .background(
                                                color = MaterialTheme.colorScheme.primaryContainer,
                                                shape = RoundedCornerShape(16.dp)
                                            )
                                            .align(Alignment.CenterStart)
                                    )
                                }

                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(horizontal = 8.dp, vertical = 12.dp),
                                    horizontalArrangement = Arrangement.SpaceEvenly,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    allTabs.forEach { tab ->
                                        CustomNavigationItem(
                                            tab = tab,
                                            selected = selectedTab == tab,
                                            onClick = {
                                                onTabSelected(tab)
                                            },
                                            onPositioned = { offsetX, width ->
                                                if (selectedTab == tab) {
                                                    indicatorOffsetX = with(density) { offsetX.toDp() }
                                                    indicatorWidth = with(density) { width.toDp() }
                                                }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Tooltip",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                   //Tooltip Position
                      
                    @Composable
                    fun SkaterDistanceChart(title: String, skaters: List<TopSkaterUiModel>, modifier: Modifier = Modifier) {
                        val scope = rememberCoroutineScope()

                        Column(modifier = modifier.fillMaxWidth()) {
                            Text(
                                modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                                text = title,
                                color = Color.Black,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                textAlign = TextAlign.Center,
                                style = TextStyle(fontSize = 32.sp, fontFamily = FontFamily(Font(R.font.nhlitalic))),
                            )

                            Column(
                                Modifier
                                    .fillMaxWidth()
                                    .clip(RoundedCornerShape(12.dp)).background(Color.Black).padding(top = 26.dp, bottom = 26.dp, start = 16.dp, end = 16.dp),
                            ) {

                                Text(
                                    modifier = Modifier
                                        .align(Alignment.CenterHorizontally)
                                        .padding(bottom = 16.dp),
                                    text = skaters.first().subHeader,
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = Color.White,
                                )

                                skaters.forEachIndexed { index, skater ->
                                    val isTop = index == 0
                                    val barColor = if (isTop) Color(0xFF00B0FF) else Color(0xFF424242)

                                    // State for the tooltip popup
                                    positionProvider = rememberTooltipPositionProvider(TooltipAnchorPosition.Above),

                                    Column(modifier = Modifier.fillMaxWidth()) {
                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                        ) {
                                            Text(
                                                text = skater.formattedDistance,
                                                color = Color.White,
                                                fontWeight = FontWeight.Black,
                                                fontSize = 16.sp,
                                                modifier = Modifier.width(42.dp),
                                            )

                                            TeamLogoIcon(skater.playerTeamLogoUrl)

                                            TooltipBox(
                                                positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
                                                tooltip = {
                                                    PlainTooltip(
                                                        containerColor = Color.DarkGray,
                                                        contentColor = Color.White
                                                    ) {
                                                        Text("TIME ON ICE", style = MaterialTheme.typography.bodyMedium)
                                                    }
                                                },
                                                state = tooltipState
                                            ) {
                                                Text(
                                                    text = buildAnnotatedString {
                                                        append("${skater.name} ")
                                                        withLink(
                                                            LinkAnnotation.Clickable(
                                                                tag = "TOI_LINK",
                                                                styles = TextLinkStyles(
                                                                    style = SpanStyle(
                                                                        fontWeight = FontWeight.Bold,
                                                                        textDecoration = TextDecoration.Underline
                                                                    )
                                                                ),
                                                                linkInteractionListener = {
                                                                    scope.launch { tooltipState.show() }
                                                                }
                                                            )
                                                        ) {
                                                            append("TOI ${skater.toi}")
                                                        }
                                                        append(" ${skater.matchupLabel}")
                                                    },
                                                    color = Color.White,
                                                    style = MaterialTheme.typography.bodyMedium,
                                                    maxLines = 1,
                                                    overflow = TextOverflow.Ellipsis
                                                )
                                            }

                                            TeamLogoIcon(skater.opponentTeamLogoUrl)
                                        }

                                        Spacer(modifier = Modifier.height(6.dp))

                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth(skater.barFraction.coerceIn(0.01f, 1f))
                                                .height(20.dp)
                                                .background(barColor, RoundedCornerShape(2.dp)),
                                        )

                                        if (index != skaters.lastIndex) {
                                            Spacer(modifier = Modifier.height(16.dp))
                                        }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Tooltip and Popup Anchoring
                    
                    @Composable
                    fun popupAnchoring(viewModel: ViewModel) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 24.dp)
                                .onGloballyPositioned { coordinates ->
                                    // Get position relative to the window
                                    val position = coordinates.localToWindow(Offset.Zero)
                                    viewModel.anchorX = position.x
                                    viewModel.anchorY = position.y
                                }
                        ) {
                            Button(onClick = { viewModel.showTooltip() }) {
                                Text("Tooltip and Popup Anchoring")
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    TooltipAnchor(
                        tooltip = {
                            Text(
                                "This is a featured action!\nTap it to add to favorites.",
                                color = Color.White, fontSize = 13.sp
                            )
                        },
                        visible = ,
                        onDismiss = {  },
                        showSpotlight = true,
                        preferredPlacement = TooltipPlacement.Bottom
                    ) {
                        IconButton(onClick = { show = !show }) {
                            Icon(Icons.Default.Star, contentDescription = "Feature", tint = Color(0xFFFFA726))
                        }
                    }
                    
                    // ═══════════════════════════════════════════════════════════════════
                    // Data Models
                    // ═══════════════════════════════════════════════════════════════════

                    enum class TooltipPlacement { Top, Bottom, Start, End }

                    private data class TooltipPosition(
                        val placement: TooltipPlacement,
                        val offset: IntOffset,
                        val arrowX: Float,
                        val arrowY: Float
                    )

                    // ═══════════════════════════════════════════════════════════════════
                    // Theme & Style
                    // ═══════════════════════════════════════════════════════════════════

                    data class TooltipColors(
                        val background: Color,
                        val text: Color,
                        val arrowColor: Color,
                        val spotlightOverlay: Color
                    )

                    data class TooltipStyle(
                        val cornerRadius: Dp,
                        val padding: PaddingValues,
                        val arrowWidth: Dp,
                        val arrowHeight: Dp,
                        val elevation: Dp,
                        val maxWidth: Dp,
                        val screenPadding: Dp,
                        val spotlightCornerRadius: Dp
                    )

                    object TooltipDefaults {
                        fun colors(
                            background: Color = Color(0xFF2D2D2D),
                            text: Color = Color.White,
                            arrowColor: Color = Color(0xFF2D2D2D),
                            spotlightOverlay: Color = Color.Black.copy(alpha = 0.6f)
                        ) = TooltipColors(background, text, arrowColor, spotlightOverlay)

                        fun style(
                            cornerRadius: Dp = 8.dp,
                            padding: PaddingValues = PaddingValues(horizontal = 12.dp, vertical = 8.dp),
                            arrowWidth: Dp = 14.dp,
                            arrowHeight: Dp = 8.dp,
                            elevation: Dp = 4.dp,
                            maxWidth: Dp = 240.dp,
                            screenPadding: Dp = 12.dp,
                            spotlightCornerRadius: Dp = 8.dp
                        ) = TooltipStyle(
                            cornerRadius, padding, arrowWidth, arrowHeight,
                            elevation, maxWidth, screenPadding, spotlightCornerRadius
                        )
                    }

                    // ═══════════════════════════════════════════════════════════════════
                    // Positioning Algorithm
                    // ═══════════════════════════════════════════════════════════════════

                    private fun buildPlacementOrder(
                        preferred: TooltipPlacement,
                        spaceAbove: Float,
                        spaceBelow: Float,
                        spaceStart: Float,
                        spaceEnd: Float,
                        requiredVertical: Float,
                        requiredHorizontal: Float
                    ): List<TooltipPlacement> {
                        val fits = mapOf(
                            TooltipPlacement.Top to (spaceAbove >= requiredVertical),
                            TooltipPlacement.Bottom to (spaceBelow >= requiredVertical),
                            TooltipPlacement.Start to (spaceStart >= requiredHorizontal),
                            TooltipPlacement.End to (spaceEnd >= requiredHorizontal)
                        )

                        val opposite = when (preferred) {
                            TooltipPlacement.Top -> TooltipPlacement.Bottom
                            TooltipPlacement.Bottom -> TooltipPlacement.Top
                            TooltipPlacement.Start -> TooltipPlacement.End
                            TooltipPlacement.End -> TooltipPlacement.Start
                        }

                        val remaining = TooltipPlacement.entries
                            .filter { it != preferred && it != opposite }

                        val order = mutableListOf<TooltipPlacement>()
                        if (fits[preferred] == true) order.add(preferred)
                        if (fits[opposite] == true) order.add(opposite)
                        remaining.filter { fits[it] == true }.forEach { order.add(it) }
                        if (order.isEmpty()) order.add(preferred)

                        return order
                    }

                    private fun calculateTooltipPosition(
                        anchorBounds: Rect,
                        tooltipSize: IntSize,
                        screenSize: IntSize,
                        preferredPlacement: TooltipPlacement,
                        arrowSize: Float,
                        padding: Float
                    ): TooltipPosition {
                        val anchorCenterX = anchorBounds.left + anchorBounds.width / 2
                        val anchorCenterY = anchorBounds.top + anchorBounds.height / 2

                        val spaceAbove = anchorBounds.top - padding
                        val spaceBelow = screenSize.height - anchorBounds.bottom - padding
                        val spaceStart = anchorBounds.left - padding
                        val spaceEnd = screenSize.width - anchorBounds.right - padding

                        val tooltipW = tooltipSize.width.toFloat()
                        val tooltipH = tooltipSize.height.toFloat()

                        val placementOrder = buildPlacementOrder(
                            preferredPlacement, spaceAbove, spaceBelow, spaceStart, spaceEnd,
                            tooltipH + arrowSize, tooltipW + arrowSize
                        )

                        val placement = placementOrder.first()

                        val (x, y) = when (placement) {
                            TooltipPlacement.Top -> {
                                val tx = (anchorCenterX - tooltipW / 2)
                                    .coerceIn(padding, screenSize.width - tooltipW - padding)
                                val ty = anchorBounds.top - tooltipH - arrowSize
                                tx to ty
                            }

                            TooltipPlacement.Bottom -> {
                                val tx = (anchorCenterX - tooltipW / 2)
                                    .coerceIn(padding, screenSize.width - tooltipW - padding)
                                val ty = anchorBounds.bottom + arrowSize
                                tx to ty
                            }

                            TooltipPlacement.Start -> {
                                val tx = anchorBounds.left - tooltipW - arrowSize
                                val ty = (anchorCenterY - tooltipH / 2)
                                    .coerceIn(padding, screenSize.height - tooltipH - padding)
                                tx to ty
                            }

                            TooltipPlacement.End -> {
                                val tx = anchorBounds.right + arrowSize
                                val ty = (anchorCenterY - tooltipH / 2)
                                    .coerceIn(padding, screenSize.height - tooltipH - padding)
                                tx to ty
                            }
                        }

                        val (arrowRelX, arrowRelY) = when (placement) {
                            TooltipPlacement.Top, TooltipPlacement.Bottom ->
                                (anchorCenterX - x) to 0f

                            TooltipPlacement.Start, TooltipPlacement.End ->
                                0f to (anchorCenterY - y)
                        }

                        return TooltipPosition(
                            placement = placement,
                            offset = IntOffset(x.toInt(), y.toInt()),
                            arrowX = arrowRelX.coerceIn(arrowSize, tooltipW - arrowSize),
                            arrowY = arrowRelY.coerceIn(arrowSize, tooltipH - arrowSize)
                        )
                    }

                    // ═══════════════════════════════════════════════════════════════════
                    // Animation Helpers
                    // ═══════════════════════════════════════════════════════════════════

                    private fun placementToTransformOrigin(
                        placement: TooltipPlacement,
                        arrowX: Float,
                        arrowY: Float,
                        tooltipWidth: Float,
                        tooltipHeight: Float
                    ): TransformOrigin {
                        return when (placement) {
                            TooltipPlacement.Top -> TransformOrigin(
                                pivotFractionX = if (tooltipWidth > 0f) (arrowX / tooltipWidth).coerceIn(
                                    0f,
                                    1f
                                ) else 0.5f,
                                pivotFractionY = 1f
                            )

                            TooltipPlacement.Bottom -> TransformOrigin(
                                pivotFractionX = if (tooltipWidth > 0f) (arrowX / tooltipWidth).coerceIn(
                                    0f,
                                    1f
                                ) else 0.5f,
                                pivotFractionY = 0f
                            )

                            TooltipPlacement.Start -> TransformOrigin(
                                pivotFractionX = 1f,
                                pivotFractionY = if (tooltipHeight > 0f) (arrowY / tooltipHeight).coerceIn(
                                    0f,
                                    1f
                                ) else 0.5f
                            )

                            TooltipPlacement.End -> TransformOrigin(
                                pivotFractionX = 0f,
                                pivotFractionY = if (tooltipHeight > 0f) (arrowY / tooltipHeight).coerceIn(
                                    0f,
                                    1f
                                ) else 0.5f
                            )
                        }
                    }

                    // ═══════════════════════════════════════════════════════════════════
                    // Spotlight Overlay
                    // ═══════════════════════════════════════════════════════════════════

                    @Composable
                    private fun SpotlightOverlay(
                        anchorBounds: Rect,
                        alpha: Float,
                        overlayColor: Color,
                        cornerRadius: Float,
                        onClick: () -> Unit
                    ) {
                        if (alpha <= 0.01f) return

                        Canvas(
                            modifier = Modifier
                                .fillMaxSize()
                                .graphicsLayer {
                                    this.alpha = alpha
                                    compositingStrategy = androidx.compose.ui.graphics.CompositingStrategy.Offscreen
                                }
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null
                                ) { onClick() }
                        ) {
                            drawRect(overlayColor)
                            drawRoundRect(
                                color = Color.Transparent,
                                topLeft = Offset(
                                    anchorBounds.left - 4.dp.toPx(),
                                    anchorBounds.top - 4.dp.toPx()
                                ),
                                size = Size(
                                    anchorBounds.width + 8.dp.toPx(),
                                    anchorBounds.height + 8.dp.toPx()
                                ),
                                cornerRadius = CornerRadius(cornerRadius, cornerRadius),
                                blendMode = BlendMode.Clear
                            )
                        }
                    }

                    // ═══════════════════════════════════════════════════════════════════
                    // Arrow Drawing
                    // ═══════════════════════════════════════════════════════════════════

                    private fun androidx.compose.ui.graphics.drawscope.DrawScope.drawTooltipArrow(
                        placement: TooltipPlacement,
                        arrowX: Float,
                        arrowY: Float,
                        arrowWidth: Float,
                        arrowHeight: Float,
                        color: Color
                    ) {
                        val path = Path()
                        when (placement) {
                            TooltipPlacement.Top -> {
                                path.moveTo(arrowX - arrowWidth / 2, size.height)
                                path.lineTo(arrowX, size.height + arrowHeight)
                                path.lineTo(arrowX + arrowWidth / 2, size.height)
                                path.close()
                            }

                            TooltipPlacement.Bottom -> {
                                path.moveTo(arrowX - arrowWidth / 2, 0f)
                                path.lineTo(arrowX, -arrowHeight)
                                path.lineTo(arrowX + arrowWidth / 2, 0f)
                                path.close()
                            }

                            TooltipPlacement.Start -> {
                                path.moveTo(size.width, arrowY - arrowWidth / 2)
                                path.lineTo(size.width + arrowHeight, arrowY)
                                path.lineTo(size.width, arrowY + arrowWidth / 2)
                                path.close()
                            }

                            TooltipPlacement.End -> {
                                path.moveTo(0f, arrowY - arrowWidth / 2)
                                path.lineTo(-arrowHeight, arrowY)
                                path.lineTo(0f, arrowY + arrowWidth / 2)
                                path.close()
                            }
                        }
                        drawPath(path, color)
                    }

                    // ═══════════════════════════════════════════════════════════════════
                    // Main Components
                    // ═══════════════════════════════════════════════════════════════════

                    @Composable
                    fun AnimatedTooltip(
                        modifier: Modifier = Modifier,
                        visible: Boolean,
                        onDismiss: () -> Unit,
                        anchorBounds: LayoutCoordinates?,
                        preferredPlacement: TooltipPlacement = TooltipPlacement.Top,
                        showSpotlight: Boolean = false,
                        colors: TooltipColors = TooltipDefaults.colors(),
                        style: TooltipStyle = TooltipDefaults.style(),
                        content: @Composable () -> Unit
                    ) {
                        val density = LocalDensity.current
                        val view = LocalView.current

                        val scale by animateFloatAsState(
                            targetValue = if (visible) 1f else 0.7f,
                            animationSpec = if (visible) spring(dampingRatio = 0.65f, stiffness = 500f)
                            else tween(durationMillis = 150),
                            label = "tooltipScale"
                        )
                        val alpha by animateFloatAsState(
                            targetValue = if (visible) 1f else 0f,
                            animationSpec = tween(
                                durationMillis = if (visible) 200 else 150,
                                easing = FastOutSlowInEasing
                            ),
                            label = "tooltipAlpha"
                        )

                        val showPopup = visible || alpha > 0.01f
                        if (!showPopup || anchorBounds == null) return

                        val anchorRect = anchorBounds.boundsInWindow()
                        val screenWidth = view.width
                        val screenHeight = view.height

                        var tooltipSize by remember { mutableStateOf(IntSize.Zero) }

                        val arrowHeightPx = with(density) { style.arrowHeight.toPx() }
                        val arrowWidthPx = with(density) { style.arrowWidth.toPx() }
                        val screenPaddingPx = with(density) { style.screenPadding.toPx() }

                        val position = remember(anchorRect, tooltipSize, screenWidth, screenHeight) {
                            if (tooltipSize == IntSize.Zero) {
                                TooltipPosition(preferredPlacement, IntOffset.Zero, 0f, 0f)
                            } else {
                                calculateTooltipPosition(
                                    anchorBounds = anchorRect,
                                    tooltipSize = tooltipSize,
                                    screenSize = IntSize(screenWidth, screenHeight),
                                    preferredPlacement = preferredPlacement,
                                    arrowSize = arrowHeightPx,
                                    padding = screenPaddingPx
                                )
                            }
                        }

                        val transformOrigin = placementToTransformOrigin(
                            placement = position.placement,
                            arrowX = position.arrowX,
                            arrowY = position.arrowY,
                            tooltipWidth = tooltipSize.width.toFloat(),
                            tooltipHeight = tooltipSize.height.toFloat()
                        )

                        // Spotlight overlay
                        if (showSpotlight) {
                            Popup(
                                properties = PopupProperties(focusable = false)
                            ) {
                                SpotlightOverlay(
                                    anchorBounds = anchorRect,
                                    alpha = alpha,
                                    overlayColor = colors.spotlightOverlay,
                                    cornerRadius = with(density) { style.spotlightCornerRadius.toPx() },
                                    onClick = onDismiss
                                )
                            }
                        }

                        // Tooltip popup
                        Popup(
                            offset = position.offset,
                            onDismissRequest = onDismiss,
                            properties = PopupProperties(focusable = true)
                        ) {
                            Surface(
                                shape = RoundedCornerShape(style.cornerRadius),
                                color = colors.background,
                                shadowElevation = style.elevation,
                                modifier = modifier
                                    .sizeIn(maxWidth = style.maxWidth)
                                    .onSizeChanged { tooltipSize = it }
                                    .graphicsLayer {
                                        scaleX = scale
                                        scaleY = scale
                                        this.alpha = alpha
                                        this.transformOrigin = transformOrigin
                                    }
                                    .drawBehind {
                                        drawTooltipArrow(
                                            placement = position.placement,
                                            arrowX = position.arrowX,
                                            arrowY = position.arrowY,
                                            arrowWidth = arrowWidthPx,
                                            arrowHeight = arrowHeightPx,
                                            color = colors.arrowColor
                                        )
                                    }
                            ) {
                                Box(modifier = Modifier.padding(style.padding)) {
                                    content()
                                }
                            }
                        }
                    }

                    @Composable
                    fun TooltipAnchor(
                        modifier: Modifier = Modifier,
                        tooltip: @Composable () -> Unit,
                        visible: Boolean,
                        onDismiss: () -> Unit,
                        preferredPlacement: TooltipPlacement = TooltipPlacement.Top,
                        showSpotlight: Boolean = false,
                        colors: TooltipColors = TooltipDefaults.colors(),
                        style: TooltipStyle = TooltipDefaults.style(),
                        content: @Composable () -> Unit
                    ) {
                        var anchorCoordinates by remember { mutableStateOf<LayoutCoordinates?>(null) }

                        Box(
                            modifier = modifier.onGloballyPositioned { coordinates ->
                                anchorCoordinates = coordinates
                            }
                        ) {
                            content()
                        }

                        AnimatedTooltip(
                            visible = visible,
                            onDismiss = onDismiss,
                            anchorBounds = anchorCoordinates,
                            preferredPlacement = preferredPlacement,
                            showSpotlight = showSpotlight,
                            colors = colors,
                            style = style,
                            content = tooltip
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Parallax Scrolling Effects
                    
                    @Composable
                    fun ParallaxHeader(scrollState: ScrollState) {
                        var headerHeight by remember { mutableStateOf(0) }
                        
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(250.dp)
                                .onGloballyPositioned { headerHeight = it.size.height }
                                .graphicsLayer {
                                    // Move at 50% of scroll speed for parallax
                                    translationY = scrollState.value * 0.5f
                                    // Optional: fade out as user scrolls
                                    alpha = 1f - (scrollState.value.toFloat() / headerHeight).coerceIn(0f, 1f)
                                }
                        ) {
                            Image(
                                painter = painterResource(R.drawable.header_bg),
                                contentDescription = null,
                                modifier = Modifier.fillMaxSize()
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Nested Position Tracking
                    
                    @Composable
                    fun NestedPositionExample() {
                        var childPositionInParent by remember { mutableStateOf(Offset.Zero) }
                        
                        Column(
                            modifier = Modifier.fillMaxSize()
                        ) {
                            Spacer(modifier = Modifier.height(100.dp))
                            
                            Box(
                                modifier = Modifier
                                    .size(50.dp)
                                    .background(Color.Red)
                                    .onGloballyPositioned { coordinates ->
                                        // Position relative to immediate parent
                                        childPositionInParent = coordinates.positionInParent()
                                    }
                            )
                        }
                        // childPositionInParent.y will be ~100.dp in pixels
                    }
                    
                    //positionInParent() — relative to immediate parent
                    //positionInRoot() — relative to the Compose root
                    //localToWindow(Offset.Zero) — relative to the window
                    //localToScreen(Offset.Zero) — relative to the screen (API 33+)
                    """.trimIndent()
            )

            SpanText(
                text = "Custom Actions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A typical list item might have elements like a title, subtitle, and action icon. When using TalkBack, it first selects the whole item and then separately focuses on the action icon.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "customActions: Adds a custom action with a dynamic label (e.g., Mark unread or Read).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "clearAndSetSemantics {}: Ensures the icon is not separately selected by TalkBack, reducing repetitive focus.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun SettingsItem(
                        title: String,
                        isChecked: Boolean,
                        onCheckedChange: (Boolean) -> Unit
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { onCheckedChange(!isChecked) }
                                .semantics {
                                    stateDescription = if (isChecked) "Enabled" else "Disabled"
                                }
                                .padding(16.dp)
                        ) {
                            Text(
                                text = title,
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier.weight(1f)
                            )

                            Checkbox(
                                checked = isChecked,
                                onCheckedChange = onCheckedChange
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "RadioGroup",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/bonzaiferroni/pond-compose
            KotlinCode(
                code = """
                    @Composable
                    fun <T> RadioGroup(
                        selectedValue: T,
                        onOptionSelected: (T) -> Unit,
                        composeOptions: @Composable () -> List<RadioContent<T>>
                    ) {
                        val options = composeOptions()

                        Column(Modifier.selectableGroup()) {
                            options.forEachIndexed { index, (option, content) ->
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .selectable(
                                            selected = option.value == selectedValue,
                                            onClick = { onOptionSelected(option.value) }
                                        )
                                        .padding(8.dp)
                                        .animateInitialOffsetX(index),
                                ) {
                                    val circleColor = Pond.localColors.content.copy(.8f)
                                    val indicatorColor = Pond.colors.accent
                                    val size = 16
                                    val padding = 4
                                    val indicatorSize = when {
                                        option.value == selectedValue -> (size / 2f) - 3
                                        else -> 0f
                                    }.animate(spec = springSpec)

                                    Canvas(
                                        Modifier
                                            .size((size + padding * 2).dp)
                                            .padding(padding.dp)
                                    ) {
                                        drawCircle(
                                            color = circleColor,
                                            style = Stroke(2.dp.toPx())
                                        )
                                        drawCircle(
                                            radius = indicatorSize,
                                            color = indicatorColor,
                                            style = Fill
                                        )
                                    }
                                    Spacer(Modifier.width(8.dp))
                                    content()
                                }
                            }
                        }
                    }

                    @Composable
                    fun Modifier.animateInitialOffsetX(magnitude: Int): Modifier {
                        val initialValue = magnitude * 10
                        val translateX = 0f.animate(
                            initialValue = initialValue.toFloat().randomFlip(),
                            duration = initialValue * 20
                        )
                        return this.graphicsLayer { translationX = translateX; alpha = (100 - abs(translateX * 10)) / 100f }
                    }

                    fun Float.randomFlip(): Float = if ((0..1).random() == 0) -this else this
                    val springSpec = spring<Float>(Spring.DampingRatioMediumBouncy)

                    data class RadioContent<T>(
                        val option: RadioOption<T>,
                        val content: @Composable () -> Unit,
                    )

                    data class RadioOption<T>(
                        val label: String?,
                        val value: T
                    ) {
                        val labelOrValue get() = label ?: value.toString()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "SingleChoiceSegmentedButtonRow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/filin2hat/BikeCalcMP
            KotlinCode(
                code = """
                    @Serializable
                    enum class TubeType {
                        TUBES,
                        TUBELESS,
                    }

                    @Composable
                    fun TubeTypeChangeButton(
                        onClick: (TubeType) -> Unit,
                        enabled: Boolean,
                        selectedType: TubeType,
                        modifier: Modifier = Modifier,
                    ) {
                        var selectType by rememberSaveable { mutableStateOf(selectedType) }
                        var selectedIndex by rememberSaveable { mutableIntStateOf(0) }

                        val items =
                            listOf(
                                stringResource(Res.string.tubes),
                                stringResource(Res.string.tubeless),
                            )
                        val haptic = LocalHapticFeedback.current

                        SingleChoiceSegmentedButtonRow(
                            modifier = modifier.fillMaxWidth(),
                        ) {
                            items.forEachIndexed { index, label ->
                                SegmentedButton(
                                    enabled = enabled,
                                    shape = MaterialTheme.shapes.medium,
                                    border =
                                        BorderStroke(
                                            width = 2.dp,
                                            color = MaterialTheme.colorScheme.inversePrimary,
                                        ),
                                    colors =
                                        SegmentedButtonDefaults.colors(
                                            activeContainerColor = MaterialTheme.colorScheme.primaryContainer,
                                            activeContentColor = MaterialTheme.colorScheme.scrim,
                                            inactiveContainerColor = MaterialTheme.colorScheme.background,
                                            inactiveContentColor = MaterialTheme.colorScheme.primary,
                                            disabledActiveContainerColor =
                                                MaterialTheme.colorScheme.primaryContainer.copy(
                                                    alpha = 0.3f,
                                                ),
                                            disabledInactiveContainerColor =
                                                MaterialTheme.colorScheme.background.copy(
                                                    alpha = 0.4f,
                                                ),
                                            disabledActiveContentColor = MaterialTheme.colorScheme.scrim.copy(alpha = 0.3f),
                                            disabledInactiveContentColor =
                                                MaterialTheme.colorScheme.inversePrimary.copy(
                                                    alpha = 0.8f,
                                                ),
                                        ),
                                    onClick = {
                                        selectedIndex = index
                                        selectType =
                                            when (index) {
                                                0 -> TubeType.TUBES
                                                else -> TubeType.TUBELESS
                                            }
                                        onClick(selectType)
                                        haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                                    },
                                    selected = index == selectedIndex,
                                    label = {
                                        Text(
                                            text = label,
                                            style = MaterialTheme.typography.titleMedium,
                                        )
                                    },
                                    modifier =
                                        Modifier
                                            .fillMaxWidth()
                                            .height(75.dp)
                                            .padding(bottom = 16.dp),
                                )
                                if (index != items.lastIndex) {
                                    Spacer(modifier = Modifier.width(12.dp))
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ToggleButton",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/delacrixmorgan/kingscup-kmp.git
            KotlinCode(
                code = """
                    enum class ThemePreference {
                        System,
                        Light,
                        Dark;

                        companion object {
                            val Default = System
                        }
                    }
                    
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun ThemeScreen(
                        modifier: Modifier = Modifier,
                        state: ThemeUiState,
                        onAction: (ThemeAction) -> Unit,
                        haptic: HapticFeedback = LocalHapticFeedback.current,
                    ) {
                        Column(
                            modifier = modifier
                                .fillMaxSize()
                                .background(MaterialTheme.colorScheme.surface)
                                .verticalScroll(rememberScrollState())
                                .padding(horizontal = 16.dp)
                                .padding(bottom = 24.dp),
                        ) {
                            Row(
                                Modifier
                                    .background(ListItemDefaults.containerColor)
                                    .padding(horizontal = 16.dp)
                                    .padding(bottom = 16.dp),
                                horizontalArrangement = Arrangement.spacedBy(ButtonGroupDefaults.ConnectedSpaceBetween),
                            ) {
                                ThemePreference.entries.forEach { theme ->
                                    val (icon, name) = when (theme) {
                                        ThemePreference.System -> Icons.Rounded.SettingsSuggest to stringResource(Res.string.styleGuide_themeSystem)
                                        ThemePreference.Light -> Icons.Rounded.WbSunny to stringResource(Res.string.styleGuide_themeLight)
                                        ThemePreference.Dark -> Icons.Rounded.Bedtime to stringResource(Res.string.styleGuide_themeDark)
                                    }
                                    ToggleButton(
                                        modifier = Modifier.weight(1F).semantics { role = Role.RadioButton },
                                        checked = state.selectedTheme == theme,
                                        onCheckedChange = {
                                            haptic.performHapticFeedback(HapticFeedbackType.ContextClick)
                                            onAction(ThemeAction.OnThemeSelected(theme))
                                        },
                                        shapes = when (theme.ordinal) {
                                            0 -> ButtonGroupDefaults.connectedLeadingButtonShapes()
                                            ThemePreference.entries.lastIndex -> ButtonGroupDefaults.connectedTrailingButtonShapes()
                                            else -> ButtonGroupDefaults.connectedMiddleButtonShapes()
                                        }
                                    ) {
                                        Icon(imageVector = icon, contentDescription = name)
                                        Spacer(Modifier.size(ToggleButtonDefaults.IconSpacing))
                                        Text(name)
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "CheckBox / Radio Button Animation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/sai-charan2003/Habit-Diary.git
            KotlinCode(
                code = """
                    @Composable
                    fun RoundCheckbox(
                        checked: Boolean,
                        onCheckedChange: (Boolean) -> Unit,
                        modifier: Modifier = Modifier,
                        strokeColor: Color = MaterialTheme.colorScheme.outline,
                        fillColor: Color = MaterialTheme.colorScheme.primary
                    ) {
                        val targetBaseColor = if (checked) fillColor.copy(alpha = 0.35f) else strokeColor
                        val animatedBaseColor by animateColorAsState(
                            targetValue = targetBaseColor,
                            animationSpec = tween(300),
                            label = "baseColor"
                        )
                        val innerCircleScale by animateFloatAsState(
                            targetValue = if (checked) 1f else 0f,
                            animationSpec = tween(400, delayMillis = 50),
                            label = "innerScale"
                        )

                        Box(
                            modifier = modifier
                                .size(32.dp)
                                .clip(CircleShape)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null
                                ) { onCheckedChange(!checked) }
                                .padding(4.dp)
                        ) {
                            Canvas(modifier = Modifier.matchParentSize()) {
                                val strokeWidth = 2.dp.toPx()
                                drawCircle(
                                    color = animatedBaseColor,
                                    style = if (!checked) Stroke(width = strokeWidth) else Fill
                                )
                                if (innerCircleScale > 0f) {
                                    drawCircle(
                                        color = fillColor,
                                        radius = (size.minDimension * 0.32f) * innerCircleScale
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    /**
                     * Custom circular checkbox with animated check mark
                     * @param checked Whether the checkbox is currently checked
                     * @param onCheckedChange Callback invoked when checkbox state changes
                     * @param modifier Modifier to apply to the checkbox
                     */
                    @Composable
                    fun CircularCheckbox(
                        checked: Boolean,
                        onCheckedChange: (Boolean) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Box(
                            modifier = modifier
                                .size(24.dp)
                                .drawBehind {
                                    val strokeWidth = Stroke(width = 2.dp.toPx())
                                    val radius = size.minDimension / 2

                                    // Draw circle border
                                    drawCircle(
                                        color = if (checked) primaryColorVariant else Color.Gray.copy(alpha = 0.5f),
                                        radius = radius,
                                        style = if (checked) Stroke(width = strokeWidth.width) else strokeWidth
                                    )

                                    // Draw filled circle if checked
                                    if (checked) {
                                        drawCircle(
                                            color = primaryColorVariant,
                                            radius = radius - strokeWidth.width / 2
                                        )
                                    }
                                }
                                .clickable { onCheckedChange(!checked) },
                            contentAlignment = Alignment.Center
                        ) {
                            AnimatedVisibility(
                                visible = checked,
                                enter = fadeIn(animationSpec = tween(150)) + expandVertically(
                                    animationSpec = tween(150),
                                    expandFrom = Alignment.CenterVertically
                                ),
                                exit = fadeOut(animationSpec = tween(150)) + shrinkVertically(
                                    animationSpec = tween(150),
                                    shrinkTowards = Alignment.CenterVertically
                                )
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Check,
                                    contentDescription = "Checked",
                                    tint = Color.White,
                                    modifier = Modifier.size(14.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ContextualFlowRow and ContextualFlowColumn (deprecated)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FlowRow and FlowColumn have experimental overloads that were introduced in 1.7 that include an overflow parameter. The use of this parameter has been deprecated, and the overloads without this parameter can be used instead.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        maxItemsInEachRow = 2
                        horizontalArrangement = Arrangement
                        //horizontalArrangement = Arrangement.spacedBy(12.dp),
                        //verticalArrangement = Arrangement.spacedBy(12.dp),
                        .spacedBy(ButtonGroupDefaults.ConnectedSpaceBetween, Alignment.CenterHorizontally)
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "contentType",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The contentType parameter provides a way to categorize the content of individual items within a lazy layout. This allows Compose to optimize recomposition and improve scrolling performance, especially in scenarios with mixed item types.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you provide a contentType lambda, Compose uses the return value of this lambda for each item to identify its content type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is particularly beneficial when your LazyColumn displays items with significantly different layouts or content structures (e.g., text messages, image messages, video messages).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lazy composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lazy composition refers to the concept of deferring the composition of UI elements until they are actually needed or visible on the screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This approach is particularly useful for handling large collections of UI elements, like lists or grids, by only composing the items that are currently in view.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lazy composition helps optimize performance and memory usage by minimizing the number of composable functions that are composed at any given time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LazyColumn doesn’t recycle its children like the RecyclerView instead it will emit new Composables as we scroll up. Emitting Composable is cheap compared to instantiating new Android Views.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    sealed class FeedItem {
                        abstract val id: String
                        abstract val contentType: String
                        
                        data class TextPost(...) : FeedItem() {
                            override val contentType = "text"
                        }
                        
                        data class ImagePost(...) : FeedItem() {
                            override val contentType = "image"
                        }
                        
                        data class VideoPost(...) : FeedItem() {
                            override val contentType = "video"
                        }
                    }

                    LazyColumn {
                        items(
                            items = feedItems,
                            key = { it.id },
                            contentType = { it.contentType }  // Simple and maintainable
                        ) { item ->
                            when (item) {
                                is TextPost -> TextPostCard(item)
                                is ImagePost -> ImagePostCard(item)
                                is VideoPost -> VideoPostCard(item)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Combine Items List

                    @Composable
                    fun ListWithMultipleItems(messages: List<Any>) {
                        LazyColumn {
                            items(
                                1,
                                contentType = { it::class },
                                key = { it.hashCode() }
                            ) {
                                for (message in messages)
                                    when (message) {
                                        is Audio -> AudioMessage(message)
                                        is Message -> TextMessage(message)
                                    }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://newsletter.jorgecastillo.dev/p/movablecontentof-and-movablecontentwithreceivero
            SpanText(
                text = "𝗺𝗼𝘃𝗮𝗯𝗹𝗲𝗖𝗼𝗻𝘁𝗲𝗻𝘁𝗢𝗳",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝘛𝘩𝘪𝘴 𝘱𝘰𝘸𝘦𝘳𝘧𝘶𝘭 𝘧𝘶𝘯𝘤𝘵𝘪𝘰𝘯 𝘢𝘭𝘭𝘰𝘸𝘴 𝘺𝘰𝘶 𝘵𝘰 𝘮𝘰𝘷𝘦 𝘤𝘰𝘮𝘱𝘰𝘴𝘢𝘣𝘭𝘦 𝘤𝘰𝘯𝘵𝘦𝘯𝘵 𝘸𝘪𝘵𝘩𝘪𝘯 𝘺𝘰𝘶𝘳 𝘜𝘐 𝘸𝘪𝘵𝘩𝘰𝘶𝘵 𝘵𝘳𝘪𝘨𝘨𝘦𝘳𝘪𝘯𝘨 𝘳𝘦𝘤𝘰𝘮𝘱𝘰𝘴𝘪𝘵𝘪𝘰𝘯, 𝘭𝘦𝘢𝘥𝘪𝘯𝘨 𝘵𝘰 𝘴𝘪𝘨𝘯𝘪𝘧𝘪𝘤𝘢𝘯𝘵 𝘱𝘦𝘳𝘧𝘰𝘳𝘮𝘢𝘯𝘤𝘦 𝘪𝘮𝘱𝘳𝘰𝘷𝘦𝘮𝘦𝘯𝘵𝘴.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "movableContentOf and movableContentWithReceiverOf provides a means for tracking compositions in Jetpack Compose. They convert a lambda (or a lambda with a receiver) into one that moves all the remembered state and nodes created (within the block) in a previous call to any new location where it is called.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This might sound a bit confusing, but it is essentially about keeping track of the composition in a variable (formerly known as composition as a value) to allow inlining it in different places in our Composables, and avoid losing the state of all its Composables along the way.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val boxes = remember {
                        movableContentOf {
                            LetterBox(letter = 'A')
                            LetterBox(letter = 'B')
                        }
                    }

                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Button(onClick = { isRow = !isRow }) {
                            Text(text = "Switch")
                        }
                        if (isRow) {
                            Row(
                                Modifier.weight(1f),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                boxes()
                            }
                        } else {
                            Column(
                                Modifier.weight(1f),
                                verticalArrangement = Arrangement.Center
                            ) {
                                boxes()
                            }
                        }
                    }

                    @Composable
                    fun LetterBox(
                        modifier: Modifier = Modifier,
                        letter: Char
                    ) {
                        SideEffect {
                            Log.d("RECOMPOSE", "Recomposing ")
                        }

                        Box(
                            modifier = modifier
                                .padding(5.dp)
                                .size(150.dp)
                                .background(
                                    if (letter == 'A') Color(0xFFD13F3F) else Color(0xFF3F7BD6),
                                    shape = RoundedCornerShape(30.dp)
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = letter.toString(),
                                style = TextStyle(color = Color.White, fontSize = 80.sp, )
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun ColumnExample() {
                        val list = remember {
                            mutableStateListOf<String>().apply {
                                for (i in 0..20) add(i)
                            }
                        }

                        val listComposables = list.movable {
                            Counter(text = it)
                        }

                        Column {
                            Button(onClick = {
                                list.removeFirstOrNull()
                            }) {
                                Text(text = "Remove first")
                            }
                            Column(
                                modifier = Modifier
                                    .verticalScroll(state = rememberScrollState())
                                    .weight(1f)
                            ) {
                                list.forEach {
                                    listComposables(it)
                                }
                            }
                        }
                    }

                    @Composable
                    fun Counter(text: String) {

                        var counter by remember {
                            mutableStateOf(0)
                        }

                        SideEffect {
                            Log.d("RECOMPOSE", "Recomposing $text ($counter)")
                        }

                        Row(
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(text = text, fontWeight = FontWeight.Bold, fontSize = 20.sp)
                            Box(modifier = Modifier.weight(1f))
                            Button(onClick = { counter-- }) { Text(text = "-") }
                            Text(
                                text = "$counter",
                                fontWeight = FontWeight.Bold,
                                fontSize = 20.sp,
                                modifier = Modifier.padding(8.dp)
                            )
                            Button(onClick = { counter++ }) { Text(text = "+") }
                        }
                    }

                    @Composable
                    fun <T> List<T>.movable(
                        transform: @Composable (item: T) -> Unit
                    ): @Composable (item: T) -> Unit {
                        val composedItems = remember(this) { mutableMapOf<T, @Composable () -> Unit>() }
                        return { item: T ->
                            composedItems.getOrPut(item) {
                                movableContentOf { transform(item) }
                            }.invoke()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "fillMaxWidthRestricted",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun RestrictedWidthLayout(
                        restrictionWidth: Dp = 650.dp,
                        content: @Composable (constraints: Constraints) -> Unit,
                    ) {
                        BoxWithConstraints(
                            modifier = Modifier
                                .fillMaxSize()
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidthRestricted(scope = this, maxWidth = restrictionWidth)
                                    .align(Alignment.Center)
                            ) {
                                BoxWithConstraints {
                                    content(constraints)
                                }
                            }
                        }
                    }

                    @Composable
                    fun ApplicationFilterScreen(
                    ) {
                        Scaffold() { scaffoldPadding ->
                            RestrictedWidthLayout(
                                restrictionWidth = 800.dp
                            ) { constraints ->
                                val density = LocalDensity.current
                                val wideScreen = remember(constraints) {
                                    with(density) { constraints.maxWidth.toDp() } > 650.dp
                                }
                                val columnsCount = if (wideScreen) 2 else 1
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Box",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     //Box bottom shadow
                     
                     Box(
                        modifier = Modifier
                        .align(Alignment.BottomStart)
                        .fillMaxWidth()
                        .background(
                            Brush.verticalGradient(
                                listOf(
                                    Color.Transparent,
                                    Color.Black.copy(alpha = 0.1f),
                                    Color.Black.copy(alpha = 0.2f),
                                    Color.Black.copy(alpha = 0.4f),
                                    Color.Black.copy(alpha = 0.6f),
                                    Color.Black.copy(alpha = 0.8f),
                                    Color.Black
                                )
                            )
                        )
                        .height(100.dp)
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Why does Jetpack Compose not need DiffUtil?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose relies on a declarative UI model. It automatically updates the UI based on state changes, eliminating the need for manually calculated list differences like DiffUtil. Using keys in LazyColumn ensures efficient updates without external tools.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Theme",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/rachel-ylcs/ylcs-kmp.git
            KotlinCode(
                code = $$"""
                    // BaseValue
                    fun Offset.translate(x: Float = 0f, y: Float = 0f) = copy(x = this.x + x, y = this.y + y)

                    // condition Modifier
                    inline fun Modifier.condition(value: Boolean, callback: Modifier.() -> Modifier): Modifier =
                    	if (value) this.callback() else this

                    inline fun Modifier.condition(value: Boolean, ifTrue: Modifier.() -> Modifier, ifFalse: Modifier.() -> Modifier): Modifier =
                    	if (value) this.ifTrue() else this.ifFalse()


                    // clickableNoRipple
                    @Composable
                    fun Modifier.clickableNoRipple(enabled: Boolean = true, onClick: () -> Unit): Modifier = this.clickable(
                    	interactionSource = remember { MutableInteractionSource() },
                    	indication = null,
                    	enabled = enabled,
                    	onClick = onClick
                    )

                    // rememberState
                    @Composable
                    inline fun <T> rememberState(crossinline init: () -> T) =
                    	remember { mutableStateOf(init()) }
                    @Composable
                    inline fun <T> rememberState(key1: Any?, crossinline init: () -> T) =
                    	remember(key1) { mutableStateOf(init()) }
                    @Composable
                    inline fun <T> rememberState(key1: Any?, key2: Any?, crossinline init: () -> T) =
                    	remember(key1, key2) { mutableStateOf(init()) }
                    @Composable
                    inline fun <T> rememberState(key1: Any?, key2: Any?, key3: Any?, crossinline init: () -> T) =
                    	remember(key1, key2, key3) { mutableStateOf(init()) }
                    @Composable
                    inline fun <T> rememberState(vararg keys: Any?, crossinline init: () -> T) =
                    	remember(*keys) { mutableStateOf(init()) }

                    @Composable
                    fun rememberValueState(value: Int, vararg keys: Any?) = remember(*keys) { mutableIntStateOf(value) }

                    @Composable
                    fun rememberValueState(value: Long, vararg keys: Any?) = remember(*keys) { mutableLongStateOf(value) }

                    @Composable
                    fun rememberValueState(value: Float, vararg keys: Any?) = remember(*keys) { mutableFloatStateOf(value) }

                    @Composable
                    fun rememberValueState(value: Double, vararg keys: Any?) = remember(*keys) { mutableDoubleStateOf(value) }

                    @Composable
                    fun <T> rememberDerivedState(calculation: () -> T) =
                    	remember { derivedStateOf(calculation) }
                    @Composable
                    fun <T> rememberDerivedState(key1: Any?, calculation: () -> T) =
                    	remember(key1) { derivedStateOf(calculation) }
                    @Composable
                    fun <T> rememberDerivedState(key1: Any?, key2: Any?, calculation: () -> T) =
                    	remember(key1, key2) { derivedStateOf(calculation) }
                    @Composable
                    fun <T> rememberDerivedState(key1: Any?, key2: Any?, key3: Any?, calculation: () -> T) =
                    	remember(key1, key2, key3) { derivedStateOf(calculation) }
                    @Composable
                    fun <T> rememberDerivedState(vararg keys: Any?, calculation: () -> T) =
                    	remember(*keys) { derivedStateOf(calculation) }

                    // rememberOffScreenState
                    @Composable
                    fun rememberOffScreenState(): Boolean {
                    	var value by rememberState { false }
                    	LifecycleStartEffect(Unit) {
                    		value = true
                    		onStopOrDispose {
                    			value = false
                    		}
                    	}
                    	return value
                    }

                    @Composable
                    inline fun OffScreenEffect(crossinline block: (isForeground: Boolean) -> Unit) {
                    	LifecycleStartEffect(Unit) {
                    		block(true)
                    		onStopOrDispose {
                    			block(false)
                    		}
                    	}
                    }

                    // LaunchFlag
                    @OptIn(ExperimentalAtomicApi::class)
                    @JvmInline
                    value class LaunchFlag(val value: AtomicBoolean = AtomicBoolean(false))

                    @OptIn(ExperimentalAtomicApi::class)
                    fun launchFlag(): LaunchFlag = LaunchFlag()

                    @OptIn(ExperimentalAtomicApi::class)
                    @Composable
                    inline fun LaunchOnce(flag: LaunchFlag, crossinline block: suspend CoroutineScope.() -> Unit) {
                    	LaunchedEffect(Unit) {
                    		if (flag.value.compareAndSet(expectedValue = false, newValue = true)) block()
                    	}
                    }

                    // Composition Local
                    fun <T> localComposition() = staticCompositionLocalOf<T> { error("CompositionLocal not present") }


                    // Debounce
                    @Composable
                    fun Debounce(delay: Duration = Duration.ZERO, onClick: () -> Unit): () -> Unit {
                    	var lastTime = remember { Instant.fromEpochMilliseconds(0L) }
                    	return {
                    		val currentTime = Clock.System.now()
                    		val diff = currentTime - lastTime
                    		if (diff >= delay) {
                    			lastTime = currentTime
                    			onClick()
                    		}
                    	}
                    }

                    //Device.kt
                    @Stable
                    @Serializable
                    class Device private constructor(
                        val size: Size,
                        val type: Type
                    ) {
                        @Stable
                        @Serializable
                        enum class Size { SMALL, MEDIUM, LARGE; }
                        @Stable
                        @Serializable
                        enum class Type { PORTRAIT, LANDSCAPE, SQUARE; }

                        constructor(width: Dp, height: Dp) : this(
                            size = when {
                                width <= 420.dp -> Size.SMALL
                                width <= 900.dp -> Size.MEDIUM
                                else -> Size.LARGE
                            },
                            type = when {
                                height >= width * 1.3f -> Type.PORTRAIT
                                width >= height * 1.3f -> Type.LANDSCAPE
                                else -> Type.SQUARE
                            }
                        )

                        override fun toString(): String = "[size=$size, type=$type]"

                        override fun equals(other: Any?): Boolean = when {
                            other == null || other !is Device -> false
                            other.size == this.size && other.type == this.type -> true
                            else -> false
                        }

                        override fun hashCode(): Int = type.hashCode() + size.hashCode()
                    }

                    val LocalDevice = localComposition<Device>()

                    //ImmersivePadding.kt
                    @Stable
                    data class ImmersivePadding(
                        val start: Dp,
                        val end: Dp,
                        val top: Dp,
                        val bottom: Dp
                    ) : PaddingValues {
                        constructor(padding: PaddingValues) : this(
                            start = padding.calculateStartPadding(LayoutDirection.Ltr),
                            end = padding.calculateEndPadding(LayoutDirection.Ltr),
                            top = padding.calculateTopPadding(),
                            bottom = padding.calculateBottomPadding()
                        )
                        override fun calculateLeftPadding(layoutDirection: LayoutDirection): Dp = start
                        override fun calculateRightPadding(layoutDirection: LayoutDirection): Dp = end
                        override fun calculateTopPadding(): Dp = top
                        override fun calculateBottomPadding(): Dp = bottom

                        @Stable
                        val withoutStart: ImmersivePadding get() = this.copy(start = 0.dp)
                        @Stable
                        val withoutEnd: ImmersivePadding get() = this.copy(end = 0.dp)
                        @Stable
                        val withoutTop: ImmersivePadding get() = this.copy(top = 0.dp)
                        @Stable
                        val withoutBottom: ImmersivePadding get() = this.copy(bottom = 0.dp)
                        @Stable
                        val withoutVertical: ImmersivePadding get() = this.copy(top = 0.dp, bottom = 0.dp)
                    }

                    val LocalImmersivePadding = localComposition<ImmersivePadding>()

                    //ThemeMode.kt
                    @Stable
                    @Serializable
                    enum class ThemeMode {
                        SYSTEM, LIGHT, DARK;

                        override fun toString(): String = when (this) {
                            SYSTEM -> "System"
                            LIGHT -> "Light"
                            DARK -> "Dark"
                        }
                    }

                    val LocalDarkMode = localComposition<Boolean>()

                    //Theme.kt
                    @Stable
                    object Colors {
                    	val Unspecified = Color.Unspecified
                    	val Transparent = Color(0x00000000)
                    	val White = Color(0xffffffff)
                    	val Ghost = Color(0xfff8f8ff)
                    	val Black = Color(0xff000000)
                    	val Dark = Color(0xff181818)
                    	val Gray1 = Color(0xfff8fafc)
                    	val Gray2 = Color(0xfff1f5f9)
                    	val Gray3 = Color(0xffe2e8f0)
                    	val Gray4 = Color(0xffcbd5e1)
                    	val Gray5 = Color(0xff94a3b8)
                    	val Gray6 = Color(0xff64748b)
                    	val Gray7 = Color(0xff475569)
                    	val Gray8 = Color(0xff262626)
                    	val Red = Color(255, 0, 0)
                    	val Red1 = Color(255, 226, 226)
                    	val Red2 = Color(255, 201, 201)
                    	val Red3 = Color(255, 162, 162)
                    	val Red4 = Color(255, 100, 103)
                    	val Red5 = Color(251, 44, 54)
                    	val Red6 = Color(231, 0, 11)
                    	val Red7 = Color(193, 0, 7)
                    	val Red8 = Color(159, 7, 18)
                    	val Orange1 = Color(255, 237, 212)
                    	val Orange2 = Color(255, 214, 167)
                    	val Orange3 = Color(255, 184, 106)
                    	val Orange4 = Color(255, 137, 4)
                    	val Orange5 = Color(255, 105, 0)
                    	val Orange6 = Color(245, 73, 0)
                    	val Orange7 = Color(202, 53, 0)
                    	val Orange8 = Color(159, 45, 0)
                    	val Yellow = Color(255, 255, 0)
                    	val Yellow1 = Color(254, 249, 194)
                    	val Yellow2 = Color(255, 240, 133)
                    	val Yellow3 = Color(255, 223, 32)
                    	val Yellow4 = Color(253, 199, 0)
                    	val Yellow5 = Color(240, 177, 0)
                    	val Yellow6 = Color(208, 135, 0)
                    	val Yellow7 = Color(166, 95, 0)
                    	val Yellow8 = Color(137, 75, 0)
                    	val Green = Color(0, 255, 0)
                    	val Green1 = Color(0xffbbf7d0)
                    	val Green2 = Color(0xff86efac)
                    	val Green3 = Color(0xff4ade80)
                    	val Green4 = Color(0xff22c55e)
                    	val Green5 = Color(0xff16a34a)
                    	val Green6 = Color(0xff15803d)
                    	val Green7 = Color(0xff166534)
                    	val Green8 = Color(0xff14532d)
                    	val Cyan = Color(0, 255, 255)
                    	val Cyan1 = Color(0xffa5f3fc)
                    	val Cyan2 = Color(0xff67e8f9)
                    	val Cyan3 = Color(0xff22d3ee)
                    	val Cyan4 = Color(0xff06b6d4)
                    	val Cyan5 = Color(0xff0891b2)
                    	val Cyan6 = Color(0xff0e7490)
                    	val Cyan7 = Color(0xff155e75)
                    	val Cyan8 = Color(0xff164e63)
                    	val Blue = Color(0xff0000ff)
                    	val Blue1 = Color(219, 234, 254)
                    	val Blue2 = Color(190, 219, 255)
                    	val Blue3 = Color(142, 197, 255)
                    	val Blue4 = Color(80, 162, 255)
                    	val Blue5 = Color(43, 127, 255)
                    	val Blue6 = Color(21, 93, 252)
                    	val Blue7 = Color(20, 71, 230)
                    	val Blue8 = Color(25, 60, 184)
                    	val Fuchsia = Color(255, 0, 255)
                    	val Purple1 = Color(237, 233, 254)
                    	val Purple2 = Color(221, 214, 255)
                    	val Purple3 = Color(218, 178, 255)
                    	val Purple4 = Color(194, 122, 255)
                    	val Purple5 = Color(173, 70, 255)
                    	val Purple6 = Color(152, 16, 250)
                    	val Purple7 = Color(130, 0, 219)
                    	val Purple8 = Color(110, 17, 176)
                    	val Pink1 = Color(252, 231, 243)
                    	val Pink2 = Color(252, 206, 232)
                    	val Pink3 = Color(253, 165, 213)
                    	val Pink4 = Color(251, 100, 182)
                    	val Pink5 = Color(246, 51, 154)
                    	val Pink6 = Color(230, 0, 118)
                    	val Pink7 = Color(198, 0, 92)
                    	val Pink8 = Color(163, 0, 76)
                    	val Steel1 = Color(0xffd4e6f1)
                    	val Steel2 = Color(0xffa9cce3)
                    	val Steel3 = Color(0xff7fb3d5)
                    	val Steel4 = Color(0xff4682b4)
                    	val Steel5 = Color(0xff2980b9)
                    	val Steel6 = Color(0xff2471a3)
                    	val Steel7 = Color(0xff1f618d)
                    	val Steel8 = Color(0xff1a5276)
                    }

                    @Stable
                    object ThemeColor {
                    	val warning: Color @Composable get() = if (LocalDarkMode.current) Colors.Yellow4 else Colors.Yellow5
                    	val onWarning: Color @Composable get() = if (LocalDarkMode.current) Colors.Dark else Colors.White
                    	val backgroundVariant: Color @Composable get() = if (LocalDarkMode.current) Colors.Dark else Colors.Ghost
                    	val onBackgroundVariant: Color @Composable get() = if (LocalDarkMode.current) Colors.Ghost else Colors.Dark
                    }

                    private val LightColorScheme = lightColorScheme(
                    	primary = Colors.Steel4,
                    	onPrimary = Colors.Ghost,
                    	primaryContainer = Colors.Steel6,
                    	onPrimaryContainer = Colors.Ghost,
                    	secondary = Color(0xff76c1c6),
                    	onSecondary = Colors.Ghost,
                    	secondaryContainer = Color(0xff1c8d95),
                    	onSecondaryContainer = Colors.Ghost,
                    	tertiary = Color(0xffef91a1),
                    	onTertiary = Colors.Ghost,
                    	tertiaryContainer = Color(0xffc48b92),
                    	onTertiaryContainer = Colors.Ghost,
                    	background = Colors.Ghost,
                    	onBackground = Colors.Black,
                    	surface = Colors.Gray2,
                    	onSurface = Colors.Black,
                    	onSurfaceVariant = Colors.Gray5,
                    	error = Colors.Red5,
                    	onError = Colors.White,
                    	scrim = Colors.Dark
                    )

                    private val DarkColorScheme = darkColorScheme(
                    	primary = Color(0xffb0d5de),
                    	onPrimary = Colors.Ghost,
                    	primaryContainer = Color(0xff7da1aa),
                    	onPrimaryContainer = Colors.Ghost,
                    	secondary = Color(0xff9ac84b),
                    	onSecondary = Colors.Ghost,
                    	secondaryContainer = Color(0xff608c46),
                    	onSecondaryContainer = Colors.Ghost,
                    	tertiary = Color(0xffd6c8ff),
                    	onTertiary = Colors.Ghost,
                    	tertiaryContainer = Color(0xff7a89ce),
                    	onTertiaryContainer = Colors.Ghost,
                    	background = Colors.Dark,
                    	onBackground = Colors.White,
                    	surface = Colors.Gray8,
                    	onSurface = Colors.White,
                    	onSurfaceVariant = Colors.Gray4,
                    	error = Colors.Red4,
                    	onError = Colors.Ghost,
                    	scrim = Colors.Black
                    )

                    @Composable
                    private fun rachelShapes(device: Device): Shapes = remember(device) { when (device.size) {
                    	Device.Size.SMALL -> Shapes(
                    		extraSmall = RoundedCornerShape(3.dp),
                    		small = RoundedCornerShape(5.dp),
                    		medium = RoundedCornerShape(7.dp),
                    		large = RoundedCornerShape(9.dp),
                    		extraLarge = RoundedCornerShape(11.dp)
                    	)
                    	Device.Size.MEDIUM -> Shapes(
                    		extraSmall = RoundedCornerShape(3.5.dp),
                    		small = RoundedCornerShape(5.5.dp),
                    		medium = RoundedCornerShape(7.5.dp),
                    		large = RoundedCornerShape(9.5.dp),
                    		extraLarge = RoundedCornerShape(11.5.dp)
                    	)
                    	Device.Size.LARGE -> Shapes(
                    		extraSmall = RoundedCornerShape(4.dp),
                    		small = RoundedCornerShape(6.dp),
                    		medium = RoundedCornerShape(8.dp),
                    		large = RoundedCornerShape(10.dp),
                    		extraLarge = RoundedCornerShape(12.dp)
                    	)
                    } }

                    private fun baseTextStyle(
                    	font: Font,
                    	size: TextUnit,
                    	isBold: Boolean = false
                    ): TextStyle = TextStyle(
                    	fontFamily = FontFamily(font),
                    	fontWeight = if (isBold) FontWeight.SemiBold else FontWeight.Light,
                    	fontSize = size,
                    	lineHeight = size * 1.5f,
                    	letterSpacing = size / 16f
                    )

                    @Composable
                    private fun rachelTextStyle(
                    	size: TextUnit,
                    	isBold: Boolean = false
                    ): TextStyle {
                    	val font = Font(Res.font.xwwk)
                    	return remember(font, size, isBold) { baseTextStyle(font, size, isBold) }
                    }

                    @Composable
                    private fun rachelTypography(device: Device): Typography {
                    	val font = Font(Res.font.xwwk)
                    	return remember(device, font) { when (device.size) {
                            Device.Size.SMALL -> Typography(
                    			displayLarge = baseTextStyle(font, 28.sp, true),
                    			displayMedium = baseTextStyle(font, 24.sp, true),
                    			displaySmall = baseTextStyle(font, 20.sp, true),
                    			headlineLarge = baseTextStyle(font, 28.sp, false),
                    			headlineMedium = baseTextStyle(font, 24.sp, false),
                    			headlineSmall = baseTextStyle(font, 20.sp, false),
                    			titleLarge = baseTextStyle(font, 17.sp, true),
                    			titleMedium = baseTextStyle(font, 15.sp, true),
                    			titleSmall = baseTextStyle(font, 13.sp, true),
                    			bodyLarge = baseTextStyle(font, 16.sp, false),
                    			bodyMedium = baseTextStyle(font, 14.sp, false),
                    			bodySmall = baseTextStyle(font, 12.sp, false),
                    			labelLarge = baseTextStyle(font, 16.sp, true),
                    			labelMedium = baseTextStyle(font, 14.sp, true),
                    			labelSmall = baseTextStyle(font, 12.sp, true),
                    		)
                            Device.Size.MEDIUM -> Typography(
                    			displayLarge = baseTextStyle(font, 30.sp, true),
                    			displayMedium = baseTextStyle(font, 26.sp, true),
                    			displaySmall = baseTextStyle(font, 22.sp, true),
                    			headlineLarge = baseTextStyle(font, 30.sp, false),
                    			headlineMedium = baseTextStyle(font, 26.sp, false),
                    			headlineSmall = baseTextStyle(font, 22.sp, false),
                    			titleLarge = baseTextStyle(font, 18.sp, true),
                    			titleMedium = baseTextStyle(font, 16.sp, true),
                    			titleSmall = baseTextStyle(font, 14.sp, true),
                    			bodyLarge = baseTextStyle(font, 17.sp, false),
                    			bodyMedium = baseTextStyle(font, 15.sp, false),
                    			bodySmall = baseTextStyle(font, 13.sp, false),
                    			labelLarge = baseTextStyle(font, 17.sp, true),
                    			labelMedium = baseTextStyle(font, 15.sp, true),
                    			labelSmall = baseTextStyle(font, 13.sp, true),
                    		)
                            Device.Size.LARGE -> Typography(
                    			displayLarge = baseTextStyle(font, 32.sp, true),
                    			displayMedium = baseTextStyle(font, 28.sp, true),
                    			displaySmall = baseTextStyle(font, 24.sp, true),
                    			headlineLarge = baseTextStyle(font, 32.sp, false),
                    			headlineMedium = baseTextStyle(font, 28.sp, false),
                    			headlineSmall = baseTextStyle(font, 24.sp, false),
                    			titleLarge = baseTextStyle(font, 19.sp, true),
                    			titleMedium = baseTextStyle(font, 17.sp, true),
                    			titleSmall = baseTextStyle(font, 15.sp, true),
                    			bodyLarge = baseTextStyle(font, 18.sp, false),
                    			bodyMedium = baseTextStyle(font, 16.sp, false),
                    			bodySmall = baseTextStyle(font, 14.sp, false),
                    			labelLarge = baseTextStyle(font, 18.sp, true),
                    			labelMedium = baseTextStyle(font, 16.sp, true),
                    			labelSmall = baseTextStyle(font, 14.sp, true),
                    		)
                        } }
                    }

                    @Stable
                    object ThemeStyle {
                    	val bodyExtraSmall: TextStyle @Composable get() = when (LocalDevice.current.size) {
                    		Device.Size.SMALL -> rachelTextStyle(10.sp, false)
                    		Device.Size.MEDIUM -> rachelTextStyle(11.sp, false)
                    		Device.Size.LARGE -> rachelTextStyle(12.sp, false)
                    	}
                    	val DisplayExtraLarge: TextStyle @Composable get() = when (LocalDevice.current.size) {
                            Device.Size.SMALL -> rachelTextStyle(32.sp, true)
                            Device.Size.MEDIUM -> rachelTextStyle(34.sp, true)
                            Device.Size.LARGE -> rachelTextStyle(36.sp, true)
                        }
                    }

                    @Stable
                    object ThemeValue {
                    	@Stable
                    	object Size {
                    		val MicroIcon: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 18.dp
                    			Device.Size.MEDIUM -> 20.dp
                    			Device.Size.LARGE -> 22.dp
                    		}
                    		val Icon: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 22.dp
                    			Device.Size.MEDIUM -> 24.dp
                    			Device.Size.LARGE -> 26.dp
                    		}
                    		val ExtraIcon: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 26.dp
                    			Device.Size.MEDIUM -> 28.dp
                    			Device.Size.LARGE -> 30.dp
                    		}
                    		val SmallImage: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 48.dp
                    			Device.Size.MEDIUM -> 56.dp
                    			Device.Size.LARGE -> 64.dp
                    		}
                    		val MediumImage: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 64.dp
                    			Device.Size.MEDIUM -> 72.dp
                    			Device.Size.LARGE -> 80.dp
                    		}
                    		val LargeImage: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 100.dp
                    			Device.Size.MEDIUM -> 125.dp
                    			Device.Size.LARGE -> 150.dp
                    		}
                    		val ExtraLargeImage: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 200.dp
                    			Device.Size.MEDIUM -> 225.dp
                    			Device.Size.LARGE -> 250.dp
                    		}
                    		val ProgressHeight: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 4.dp
                    			Device.Size.MEDIUM -> 5.dp
                    			Device.Size.LARGE -> 6.dp
                    		}
                    		val SliderHeight: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 8.dp
                    			Device.Size.MEDIUM -> 10.dp
                    			Device.Size.LARGE -> 12.dp
                    		}
                    		val RefreshHeaderHeight: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 70.dp
                    			Device.Size.MEDIUM -> 75.dp
                    			Device.Size.LARGE -> 80.dp
                    		}
                    		val RefreshFooterHeight: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 50.dp
                    			Device.Size.MEDIUM -> 55.dp
                    			Device.Size.LARGE -> 60.dp
                    		}
                    		val MicroCellWidth: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 75.dp
                    			Device.Size.MEDIUM -> 90.dp
                    			Device.Size.LARGE -> 115.dp
                    		}
                    		val CellWidth: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 150.dp
                    			Device.Size.MEDIUM -> 180.dp
                    			Device.Size.LARGE -> 200.dp
                    		}
                    		val CardWidth: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 300.dp
                    			Device.Size.MEDIUM -> 320.dp
                    			Device.Size.LARGE -> 350.dp
                    		}
                    		val DialogWidth: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 300.dp
                    			Device.Size.MEDIUM -> 400.dp
                    			Device.Size.LARGE -> 500.dp
                    		}
                    		val SheetWidth: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 360.dp
                    			Device.Size.MEDIUM -> 450.dp
                    			Device.Size.LARGE -> 500.dp
                    		}
                    		val PanelWidth: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 360.dp
                    			Device.Size.MEDIUM -> 380.dp
                    			Device.Size.LARGE -> 400.dp
                    		}
                    	}

                    	@Stable
                    	object Padding {
                    		val ZeroSpace: Dp = 0.dp
                    		val LittleSpace: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 2.5.dp
                    			Device.Size.MEDIUM -> 3.dp
                    			Device.Size.LARGE -> 3.5.dp
                    		}
                    		val EqualSpace: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 7.5.dp
                    			Device.Size.MEDIUM -> 9.dp
                    			Device.Size.LARGE -> 10.5.dp
                    		}
                    		val HorizontalSpace: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 10.dp
                    			Device.Size.MEDIUM -> 12.dp
                    			Device.Size.LARGE -> 14.dp
                    		}
                    		val VerticalSpace: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 5.dp
                    			Device.Size.MEDIUM -> 6.dp
                    			Device.Size.LARGE -> 7.dp
                    		}
                    		val EqualExtraSpace: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 11.25.dp
                    			Device.Size.MEDIUM -> 13.125.dp
                    			Device.Size.LARGE -> 15.dp
                    		}
                    		val HorizontalExtraSpace: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 15.dp
                    			Device.Size.MEDIUM -> 17.5.dp
                    			Device.Size.LARGE -> 20.dp
                    		}
                    		val VerticalExtraSpace: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 7.5.dp
                    			Device.Size.MEDIUM -> 8.75.dp
                    			Device.Size.LARGE -> 10.dp
                    		}
                    		val InnerIcon: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 2.5.dp
                    			Device.Size.MEDIUM -> 3.dp
                    			Device.Size.LARGE -> 3.5.dp
                    		}
                    		val ZeroValue: PaddingValues = PaddingValues(0.dp)
                    		val LittleValue: PaddingValues @Composable get() = PaddingValues(horizontal = LittleSpace, vertical = LittleSpace * 0.8f)
                    		val EqualValue: PaddingValues @Composable get() = PaddingValues(horizontal = EqualSpace, vertical = EqualSpace)
                    		val Value: PaddingValues @Composable get() = PaddingValues(horizontal = HorizontalSpace, vertical = VerticalSpace)
                    		val EqualExtraValue: PaddingValues @Composable get() = PaddingValues(horizontal = EqualExtraSpace, vertical = EqualExtraSpace)
                    		val ExtraValue: PaddingValues @Composable get() = PaddingValues(horizontal = HorizontalExtraSpace, vertical = VerticalExtraSpace)
                    	}

                    	@Stable
                    	object Border {
                    		val Small: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 1.dp
                    			Device.Size.MEDIUM -> 1.5.dp
                    			Device.Size.LARGE -> 2.dp
                    		}
                    		val Medium: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 2.dp
                    			Device.Size.MEDIUM -> 2.5.dp
                    			Device.Size.LARGE -> 3.dp
                    		}
                    		val Large: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 3.dp
                    			Device.Size.MEDIUM -> 4.dp
                    			Device.Size.LARGE -> 5.dp
                    		}
                    	}

                    	@Stable
                    	object Shadow {
                    		val Icon: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 2.5.dp
                    			Device.Size.MEDIUM -> 3.dp
                    			Device.Size.LARGE -> 3.5.dp
                    		}
                    		val Item: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 1.dp
                    			Device.Size.MEDIUM -> 1.5.dp
                    			Device.Size.LARGE -> 2.dp
                    		}
                    		val Surface: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 4.dp
                    			Device.Size.MEDIUM -> 4.5.dp
                    			Device.Size.LARGE -> 5.dp
                    		}
                    		val Tonal: Dp @Composable get() = when (LocalDevice.current.size) {
                    			Device.Size.SMALL -> 1.dp
                    			Device.Size.MEDIUM -> 1.5.dp
                    			Device.Size.LARGE -> 2.dp
                    		}
                    	}
                    }

                    @Composable
                    fun RachelTheme(content: @Composable () -> Unit) {
                    	MaterialTheme(
                    		colorScheme = if (LocalDarkMode.current) DarkColorScheme else LightColorScheme,
                    		shapes = rachelShapes(LocalDevice.current),
                    		typography = rachelTypography(LocalDevice.current)
                    	) {
                    		CompositionLocalProvider(LocalTextStyle provides MaterialTheme.typography.bodyMedium) {
                    			content()
                    		}
                    	}
                    }

                    @Composable
                    fun AppWrapper(content: @Composable () -> Unit) {
                    	BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
                    		val device by rememberDerivedState(maxWidth, maxHeight) { Device(maxWidth, maxHeight) }
                    		CompositionLocalProvider(
                    			LocalDevice provides device,
                    			LocalDarkMode provides when (app.config.themeMode) {
                    				ThemeMode.SYSTEM -> isSystemInDarkTheme()
                    				ThemeMode.LIGHT -> false
                    				ThemeMode.DARK -> true
                    			}
                    		) {
                    			RachelTheme(content)
                    		}
                    	}
                    }

                    @Composable
                    fun App(modifier: Modifier = Modifier.fillMaxSize()) {
                    	val navController = rememberNavController()

                    	Box(modifier = modifier.background(MaterialTheme.colorScheme.background)) { }
                    }
                    """.trimIndent()
            )

            //https://github.com/zawhtetnaing10/Sanctuary-CMP
            KotlinCode(
                code = """
                    //Typography
                     
                    @Composable
                    fun fontFamilyList() =  FontFamily(
                        Font(Res.font.be_vietnam_pro_regular, FontWeight.Normal),
                        Font(Res.font.be_vietnam_pro_semi_bold, FontWeight.SemiBold),
                        Font(Res.font.be_vietnam_pro_bold, FontWeight.Bold),
                        Font(Res.font.be_vietnam_pro_medium, FontWeight.Medium)
                    )

                    @Composable
                    fun typographyFontFamily() = Typography().run {
                        val fontFamily = fontFamilyList()
                        copy(
                            displayLarge = displayLarge.copy(fontFamily = fontFamily),
                            displayMedium = displayMedium.copy(fontFamily = fontFamily),
                            displaySmall = displaySmall.copy(fontFamily = fontFamily),
                            headlineLarge = headlineLarge.copy(fontFamily = fontFamily),
                            headlineMedium = headlineMedium.copy(fontFamily = fontFamily),
                            headlineSmall = headlineSmall.copy(fontFamily = fontFamily),
                            titleLarge = titleLarge.copy(fontFamily = fontFamily),
                            titleMedium = titleMedium.copy(fontFamily = fontFamily),
                            titleSmall = titleSmall.copy(fontFamily = fontFamily),
                            bodyLarge = bodyLarge.copy(fontFamily =  fontFamily),
                            bodyMedium = bodyMedium.copy(fontFamily = fontFamily),
                            bodySmall = bodySmall.copy(fontFamily = fontFamily),
                            labelLarge = labelLarge.copy(fontFamily = fontFamily),
                            labelMedium = labelMedium.copy(fontFamily = fontFamily),
                            labelSmall = labelSmall.copy(fontFamily = fontFamily)
                        )
                    }
                    
                     MaterialTheme(
                        typography = typographyFontFamily()
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "LocalConfiguration",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Amsterdam-Team/Aflami
            KotlinCode(
                code = """
                    val configuration = LocalConfiguration.current
                    
                    val deviceWidth = configuration.screenWidthDp
                    val deviceHeightDp = configuration.screenHeightDp.dp
                     
                     val upcomingMoviesSectionHeightDp = calculateUpcomingMoviesSectionHeightDp(
                        deviceHeightDp, appBarHeight, bottomNavigationBarPadding
                     )
                     
                     private fun calculateUpcomingMoviesSectionHeightDp(
                         contentHeightDp: Dp, appBarHeight: Dp, bottomNavigationBarPadding: Dp
                     ) = contentHeightDp - appBarHeight - bottomNavigationBarPadding
                     
                     LazyColumn(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(max = upcomingMoviesSectionHeightDp)
                            .animateContentSize(tween(500))
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Click Handle",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    private fun HomeContent(
                        filteredMeals: List<FilteredMeal>,
                        onDetailClick: (String) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        .clickable {
                             onDetailClick.invoke("")
                        }
                    }
                    """.trimIndent()
            )

            //https://www.linkedin.com/in/jonathan-souza-6b570338/
            SpanText(
                text = "Button Reusable",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Size
                    enum class ButtonSize(val padding: PaddingValues) {
                        SMALL (PaddingValues(horizontal = 16. dp, vertical = 8.dp)),
                        MEDIUM(PaddingValues(horizontal = 24.dp, vertical = 12.dp)),
                        LARGE (PaddingValues(horizontal = 32.dp, vertical = 16.dp))
                    }

                    //Colors
                    enum class ButtonColor {
                        PRIMARY, SECONDARY, TERTIARY
                    }

                    @Composable
                    fun CustomButton(
                        label: String,
                        onClick: () -> Unit,
                        size: ButtonSize = ButtonSize. MEDIUM,
                        color: ButtonColor = ButtonColor.PRIMARY
                    ) {
                        val buttonColors = when (color) {
                            PRIMARY -> ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.primary,
                                contentColor = MaterialTheme.colorScheme.onPrimary
                            )

                            SECONDARY -> ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.secondary,
                                contentColor = MaterialTheme.colorScheme.onSecondary
                            )

                            TERTIARY -> ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.tertiary,
                                contentColor = MaterialTheme.colorScheme.onTertiary
                            )
                        }

                        Button(
                            onClick = onClick,
                            contentPadding = size.padding,
                            colors = buttonColors) {
                                Text(label)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "TextField Reusable",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        val textFieldColor = TextFieldDefaults.colors().copy(
                            focusedIndicatorColor = SpikartTheme.colors.greenAccentColor,
                            unfocusedIndicatorColor = MaterialTheme.colorScheme.secondary,
                            focusedTextColor = Color.Black,
                            unfocusedTextColor = Color.Black,
                            focusedContainerColor = Color.Transparent,
                            unfocusedContainerColor = Color.Transparent,
                        )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    var email by remember { mutableStateOf(TextFieldValue()) }
                    """.trimIndent()
            )

            //https://github.com/sjesinc/amazon-connect-chat-kmp.git
            KotlinCode(
                code = $$"""
                    //Chat Type Indicator / Send Message With Typing Indicator / View
                    
                    /**
                     * Message input component with send button
                     */
                    @Composable
                    fun SendMessage(
                        onSendMessage: (String) -> Unit,
                        onTyping: () -> Unit = {},
                        enabled: Boolean = true,
                        modifier: Modifier = Modifier
                    ) {
                        var inputText by remember { mutableStateOf("") }
                        var lastTypingTime by remember { mutableStateOf(0L) }

                        val sendMessage = {
                            if (inputText.isNotBlank()) {
                                onSendMessage(inputText.trim())
                                inputText = ""
                            }
                        }

                        Surface(
                            modifier = modifier.fillMaxWidth(),
                            elevation = 8.dp,
                            color = ChatColors.BACKGROUND
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(8.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                TextField(
                                    value = inputText,
                                    onValueChange = { newValue ->
                                        inputText = newValue
                                        // Send typing indicator (throttled)
                                        val now = Clock.System.now().toEpochMilliseconds()
                                        if (now - lastTypingTime > 2000) {
                                            lastTypingTime = now
                                            onTyping()
                                        }
                                    },
                                    enabled = enabled,
                                    modifier = Modifier
                                        .weight(1f)
                                        .padding(end = 8.dp)
                                        .onPreviewKeyEvent { event ->
                                            // Handle Enter key (without Shift) to send message
                                            if (event.type == KeyEventType.KeyDown &&
                                                event.key == Key.Enter &&
                                                !event.isShiftPressed) {
                                                sendMessage()
                                                true  // Consume the event to prevent newline
                                            } else {
                                                false
                                            }
                                        },
                                    placeholder = {
                                        Text(
                                            text = if (enabled) "Type a message..." else "Connecting...",
                                            color = ChatColors.TEXT_SECONDARY
                                        )
                                    },
                                    colors = TextFieldDefaults.textFieldColors(
                                        backgroundColor = ChatColors.SURFACE,
                                        focusedIndicatorColor = Color.Transparent,
                                        unfocusedIndicatorColor = Color.Transparent,
                                        disabledIndicatorColor = Color.Transparent,
                                        cursorColor = ChatColors.PRIMARY
                                    ),
                                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Send),
                                    keyboardActions = KeyboardActions(onSend = { sendMessage() }),
                                    shape = RoundedCornerShape(24.dp),
                                    singleLine = false,
                                    maxLines = 4
                                )

                                // Send button
                                SendButton(
                                    onClick = { sendMessage() },
                                    enabled = enabled && inputText.isNotBlank()
                                )
                            }
                        }
                    }

                    /**
                     * Circular send button
                     */
                    @Composable
                    private fun SendButton(
                        onClick: () -> Unit,
                        enabled: Boolean
                    ) {
                        val backgroundColor = if (enabled) ChatColors.PRIMARY else ChatColors.OFFLINE
                        val contentColor = Color.White

                        Surface(
                            modifier = Modifier
                                .size(48.dp)
                                .pointerHoverIcon(if (enabled) PointerIcon.Hand else PointerIcon.Default)
                                .clickable(enabled = enabled, onClick = onClick),
                            shape = androidx.compose.foundation.shape.CircleShape,
                            color = backgroundColor,
                            elevation = if (enabled) 4.dp else 0.dp
                        ) {
                            Box(
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Send,
                                    contentDescription = "Send message",
                                    tint = contentColor,
                                    modifier = Modifier.size(24.dp)
                                )
                            }
                        }
                    }
                    
                    SendMessage(
                        onSendMessage = { text ->
                            val user = state.currentUser ?: return@SendMessage
                            val message = Message(user, text)
                            store.send(Action.SendMessage(message))

                            // If connected to agent, send via Connect
                            if (state.chatMode == ChatMode.HUMAN_AGENT) {
                                scope.launch {
                                    try {
                                        connectRepository.sendMessage(text)
                                    } catch (e: Exception) {
                                        store.send(Action.SetError("Failed to send message: ${e.message}"))
                                    }
                                }
                            }
                        },
                        onTyping = {
                            if (state.chatMode == ChatMode.HUMAN_AGENT) {
                                scope.launch {
                                    try {
                                        connectRepository.sendTypingIndicator()
                                    } catch (e: Exception) {
                                        // Ignore typing indicator failures
                                    }
                                }
                            }
                        },
                        enabled = state.chatMode != ChatMode.CONNECTING_TO_AGENT
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //TextField Animated Border

                    object FieldValidators {
                        fun isValidEmail(email: String): Boolean {
                            val regex = "[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}".toRegex()
                            return regex.matches(email)
                        }
                    }

                    @Composable
                    fun EmailTextField(
                        modifier: Modifier = Modifier,
                        onChangeEmail: (String) -> Unit
                    ) {
                        var email by rememberSaveable { mutableStateOf("") }
                        val emailIsError by remember {
                            derivedStateOf {
                                email.isNotEmpty() && !FieldValidators.isValidEmail(email)
                            }
                        }
                        val path = remember { Path() }
                        val pathMeasure = remember { PathMeasure() }
                        val progress = remember { Animatable(0f) }
                        val borderColor = remember { mutableStateOf<EmailTextFieldValidationState>(EmailTextFieldValidationState.OTHER) }
                        val textColor = remember(borderColor.value) { borderColor.value.color }

                        LaunchedEffect(emailIsError) {
                            borderColor.value = when {
                                email.isNotEmpty() and emailIsError -> EmailTextFieldValidationState.INVALID
                                email.isNotEmpty() and !emailIsError -> EmailTextFieldValidationState.VALID
                                else -> EmailTextFieldValidationState.OTHER
                            }

                            progress.snapTo(0f)
                            progress.animateTo(
                                targetValue = 1f,
                                animationSpec = tween(2000, easing = EaseInOutCubic)
                            )
                        }

                        LaunchedEffect(email) {
                            if(email.isNotEmpty()) {
                                onChangeEmail.invoke(email)
                            }
                        }

                        TextField(
                            value = email,
                            onValueChange = { email = it },
                            label = {
                                Text(
                                    text = stringResource(id = R.string.email),
                                    fontFamily = medium,
                                    color = textColor
                                )
                            },
                            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
                            colors = TextFieldDefaults.colors(
                                focusedTextColor = MaterialTheme.colorScheme.onBackground,
                                disabledTextColor = Color.Transparent,
                                focusedIndicatorColor = Color.Transparent,
                                unfocusedIndicatorColor = Color.Transparent,
                                disabledIndicatorColor = Color.Transparent,
                            ),
                            shape = RoundedCornerShape(16.dp),
                            trailingIcon = {
                                Row(
                                    modifier = Modifier
                                        .wrapContentSize(),
                                    verticalAlignment = CenterVertically,
                                    horizontalArrangement = Arrangement.Center
                                ) {
                                    AnimatedContent(targetState = borderColor.value, label = "", transitionSpec = {
                                        slideIntoContainer(towards = AnimatedContentTransitionScope.SlideDirection.End, initialOffset = {
                                            it
                                        }, animationSpec = spring(
                                            dampingRatio = Spring.DampingRatioMediumBouncy,
                                            stiffness = Spring.StiffnessVeryLow
                                        )).togetherWith(
                                            ExitTransition.None
                                        )
                                    }, modifier = Modifier.size(24.dp)) { state ->
                                        when(state) {
                                            EmailTextFieldValidationState.VALID -> {
                                                Icon(
                                                    painter = painterResource(id = R.drawable.ic_completed),
                                                    contentDescription = "",
                                                    tint = EmailTextFieldValidationState.VALID.color,
                                                    modifier = Modifier.size(24.dp)
                                                )
                                            }
                                            EmailTextFieldValidationState.INVALID -> {
                                                Icon(
                                                    painter = painterResource(id = R.drawable.ic_cancel),
                                                    contentDescription = "",
                                                    tint = EmailTextFieldValidationState.INVALID.color,
                                                    modifier = Modifier.size(24.dp)
                                                )
                                            }
                                            EmailTextFieldValidationState.OTHER -> {}
                                        }
                                    }
                                }
                            },
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(16.dp)
                                .drawWithContent {
                                    drawContent()
                                    val strokeWidth = 2.dp.toPx()
                                    val cornerRadius = 16.dp.toPx()

                                    path.apply {
                                        reset()
                                        addRoundRect(
                                            RoundRect(
                                                rect = Rect(0f, 0f, size.width, size.height),
                                                CornerRadius(cornerRadius, cornerRadius)
                                            )
                                        )
                                    }

                                    pathMeasure.setPath(path, false)
                                    val pathLength = pathMeasure.length

                                    val animatedPath = Path()
                                    pathMeasure.getSegment(0f, progress.value * pathLength, animatedPath, true)

                                    drawPath(
                                        path = animatedPath,
                                        color = borderColor.value.color,
                                        style = Stroke(width = strokeWidth)
                                    )
                                }
                        )
                    }

                    enum class EmailTextFieldValidationState(val color: Color) {
                        VALID( Color.Green.copy(green = 0.7f)),
                        INVALID(Color.Red),
                        OTHER(Color.Unspecified)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    object FieldValidators {
                              fun isValidPasswordCheck(input: String): Boolean {
                                  return when {
                                      input.length < 6 -> false
                                      !isStringContainNumber(input) -> false
                                      !isStringLowerAndUpperCase(input) -> false
                                      !isStringContainSpecialCharacter(input) -> false
                                      else -> true
                                  }
                              }

                              fun isValidPasswordChecksStatus(input: String): Int {
                                  return when {
                                      input.length < 6 -> R.string.password_length
                                      !isStringContainNumber(input) -> R.string.required_at_least_1_digit
                                      !isStringLowerAndUpperCase(input) -> R.string.password_must_contain_upper_and_lower_case_letters
                                      !isStringContainSpecialCharacter(input) -> R.string.one_special_character_required
                                      else -> 0
                                  }
                              }
                          }
                    }

                    @Composable
                    fun PasswordTextField(modifier: Modifier = Modifier, onChangePassword: (String) -> Unit) {

                        var password by rememberSaveable { mutableStateOf("") }

                        var passwordVisibility by remember { mutableStateOf(false) }

                        val icon: Painter = if (passwordVisibility) painterResource(id = R.drawable.eye_password_hide)
                        else painterResource(id = R.drawable.eye_password_show)

                        val keyboardController = LocalSoftwareKeyboardController.current //Hide keyboard

                        val passwordIsError by remember {
                            derivedStateOf {
                                password.isNotEmpty() && !FieldValidators.isValidPasswordCheck(password)
                            }
                        }

                        val passwordErrorMessage by remember {
                            derivedStateOf {
                                FieldValidators.isValidPasswordChecksStatus(password)
                            }
                        }


                        val path = remember { Path() }
                        val pathMeasure = remember { PathMeasure() }
                        val progress = remember { Animatable(0f) }
                        val borderColor = remember { mutableStateOf<PasswordTextFieldValidationState>(PasswordTextFieldValidationState.OTHER) }
                        val textColor = remember(borderColor.value) { borderColor.value.color }

                        LaunchedEffect(passwordIsError) {
                            borderColor.value = when {
                                password.isNotEmpty() && !passwordIsError -> PasswordTextFieldValidationState.VALID
                                password.isNotEmpty() && passwordIsError -> PasswordTextFieldValidationState.INVALID
                                else -> PasswordTextFieldValidationState.OTHER
                            }

                            progress.snapTo(0f)
                            progress.animateTo(
                                targetValue = 1f,
                                animationSpec = tween(2000, easing = EaseInOutCubic)
                            )
                        }

                        LaunchedEffect(password) {
                            if(password.isNotEmpty()) {
                                onChangePassword.invoke(password)
                            }
                        }

                        Column (
                            modifier = Modifier
                                .fillMaxWidth(),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            TextField(
                                value = password,
                                onValueChange = { password = it },
                                label = {
                                    Text(
                                        text = stringResource(id = R.string.password),
                                        fontFamily = medium,
                                        color = textColor
                                    )
                                },
                                colors = TextFieldDefaults.colors(
                                    focusedTextColor = MaterialTheme.colorScheme.onBackground,
                                    disabledTextColor = Color.Transparent,
                                    focusedIndicatorColor = Color.Transparent,
                                    unfocusedIndicatorColor = Color.Transparent,
                                    disabledIndicatorColor = Color.Transparent,
                                ),
                                shape = RoundedCornerShape(16.dp),
                                trailingIcon = {
                                    Row(
                                        modifier = Modifier
                                            .wrapContentSize()
                                            .padding(horizontal = 8.dp),
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.Center
                                    ) {
                                        IconButton(onClick = {
                                            passwordVisibility = !passwordVisibility
                                        }, modifier = Modifier.animateContentSize(tween(1000))) {
                                            Icon(
                                                painter = icon, contentDescription = "Visibility Icon"
                                            )
                                        }
                                        if (password.isNotEmpty()) {
                                            AnimatedContent(targetState = borderColor.value, label = "", transitionSpec = {
                                                slideIntoContainer(towards = AnimatedContentTransitionScope.SlideDirection.End, initialOffset = {
                                                    it
                                                }, animationSpec = spring(
                                                    dampingRatio = Spring.DampingRatioMediumBouncy,
                                                    stiffness = Spring.StiffnessVeryLow
                                                )
                                                ).togetherWith(
                                                    ExitTransition.None
                                                )
                                            }, modifier = Modifier
                                                .size(24.dp)
                                                .animateContentSize(tween(1000))) { state ->
                                                when(state) {
                                                    PasswordTextFieldValidationState.VALID -> {
                                                        Icon(
                                                            painter = painterResource(id = R.drawable.ic_completed),
                                                            contentDescription = "",
                                                            tint = EmailTextFieldValidationState.VALID.color,
                                                            modifier = Modifier.size(24.dp)
                                                        )
                                                    }
                                                    PasswordTextFieldValidationState.INVALID -> {
                                                        Icon(
                                                            painter = painterResource(id = R.drawable.ic_cancel),
                                                            contentDescription = "",
                                                            tint = EmailTextFieldValidationState.INVALID.color,
                                                            modifier = Modifier.size(24.dp)
                                                        )
                                                    }
                                                    PasswordTextFieldValidationState.OTHER -> {}
                                                }
                                            }
                                        }
                                    }
                                },
                                keyboardOptions = KeyboardOptions(
                                    keyboardType = KeyboardType.Password, imeAction = ImeAction.Done
                                ),
                                keyboardActions = KeyboardActions(onDone = { keyboardController?.hide() }),
                                visualTransformation = if (passwordVisibility) VisualTransformation.None
                                else PasswordVisualTransformation(),
                                modifier = modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 16.dp)
                                    .drawWithContent {
                                        drawContent()
                                        val strokeWidth = 2.dp.toPx()
                                        val cornerRadius = 16.dp.toPx()

                                        path.apply {
                                            reset()
                                            addRoundRect(
                                                RoundRect(
                                                    rect = Rect(0f, 0f, size.width, size.height),
                                                    CornerRadius(cornerRadius, cornerRadius)
                                                )
                                            )
                                        }

                                        pathMeasure.setPath(path, false)
                                        val pathLength = pathMeasure.length

                                        val animatedPath = Path()
                                        pathMeasure.getSegment(0f, progress.value * pathLength, animatedPath, true)

                                        drawPath(
                                            path = animatedPath,
                                            color = borderColor.value.color,
                                            style = Stroke(width = strokeWidth)
                                        )
                                    }
                            )
                            AnimatedVisibility(
                                visible = passwordIsError,
                                enter = expandVertically(spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessVeryLow)),
                                exit = shrinkVertically(spring(dampingRatio = Spring.DampingRatioLowBouncy, stiffness = Spring.StiffnessVeryLow)),
                                modifier = Modifier
                            ) {
                                if (passwordErrorMessage != 0) {
                                    Card(
                                        colors = CardDefaults.cardColors(
                                            containerColor = MaterialTheme.colorScheme.onPrimary,
                                        ),
                                        shape = RoundedCornerShape(16.dp),
                                        modifier = modifier
                                            .fillMaxWidth()
                                            .padding(vertical = 4.dp, horizontal = 16.dp)
                                    ) {
                                        AnimatedContent(
                                            targetState = passwordErrorMessage,
                                            label = "Password Error Message",
                                            transitionSpec = {
                                                if (initialState == targetState) {
                                                    scaleIn() togetherWith scaleOut()
                                                } else {
                                                    slideInVertically(initialOffsetY = { -it }) togetherWith slideOutVertically(targetOffsetY = { it })
                                                }
                                            }
                                        ) { currentErrorMessage ->
                                            Text(
                                                text = stringResource(id = currentErrorMessage),
                                                color = MaterialTheme.colorScheme.onBackground,
                                                fontSize = 12.sp,
                                                fontFamily = medium,
                                                maxLines = 1,
                                                textAlign = TextAlign.Center,
                                                modifier = Modifier.padding(8.dp)
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    enum class PasswordTextFieldValidationState(val color: Color) {
                        VALID( Color.Green.copy(green = 0.7f)),
                        INVALID(Color.Red),
                        OTHER(Color.Unspecified)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //TextField gradient round border

                    Modifier
                        .weight(1f)
                        .padding(top = 6.dp, bottom = 8.dp, start = 8.dp)
                        .heightIn(0.dp, 400.dp)
                        .border(1.5.dp, gradientBrushColor(), RoundedCornerShape(32.dp)),

                    val Blue = Color(0xFF2965C9)
                    val Purple = Color(0xFF6F4CAD)
                    val DarkOrange = Color(0xFFFF5900)

                    fun gradientBrushColor(
                        vararg colorStops: Pair<Float, Color> = arrayOf(
                            0f to Blue,
                            0.4f to Purple,
                            1f to DarkOrange,
                        )
                    ) = Brush.linearGradient(
                        colorStops = colorStops,
                        start = Offset.Zero,
                        end = Offset.Infinite
                    )

                    @Preview
                    @Composable
                    private fun GradientColorPreview() {
                        MyBrainTheme(useDynamicColors = false) {
                            Box(
                                Modifier
                                    .size(100.dp)
                                    .drawBehind {
                                        drawRect(gradientBrushColor())
                                    }

                            )
                        }
                    }

                    //IconButton gradient
                    IconButton(
                        onClick = {  },
                    ) {
                        Icon(
                            painter = painterResource(R.drawable.ic_send),
                            contentDescription = "Send",
                            modifier = Modifier
                                .size(24.dp)
                                .graphicsLayer {
                                    compositingStrategy = CompositingStrategy.Offscreen
                                }
                                .drawWithCache {
                                    onDrawWithContent {
                                        drawContent()
                                        if (enabled) {
                                            drawRect(
                                                gradientBrushColor(),
                                                blendMode = BlendMode.SrcAtop
                                            )
                                        } else {
                                            drawRect(
                                                Color.Gray,
                                                blendMode = BlendMode.SrcAtop
                                            )
                                        }
                                    }
                                },
                        )
                    }


                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //TextField Material3

                    @Composable
                    fun TextField(
                        value: String,
                        onValueChanged: (String) -> Unit,
                        modifier: Modifier = Modifier,
                        title: String? = null,
                        visualTransformation: VisualTransformation = VisualTransformation.None,
                        keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
                        textStyle: TextStyle = MaterialTheme.typography.bodySmall,
                        singleLine: Boolean = true,
                        enabled: Boolean = true,
                        isError: Boolean = false,
                        errorMessage: String = "",
                        placeHolder: String = "",
                        trailingIcon: @Composable() (() -> Unit) = {}
                    ) {
                        Column(
                            modifier = modifier
                                .fillMaxWidth()
                        ) {
                            title?.let {
                                Text(text = it, style = textStyle.copy(fontWeight = FontWeight(600)))
                                VerticalSpacer(height = 9.dp)
                            }
                            TextFieldCompose(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(color = Color.White, shape = MaterialTheme.shapes.large),
                                value = value,
                                onValueChange = onValueChanged,
                                singleLine = singleLine,
                                textStyle = textStyle,
                                visualTransformation = visualTransformation,
                                keyboardOptions = keyboardOptions,
                                enabled = enabled,
                                isError = isError,
                                placeholder = {
                                    Text(text = placeHolder, style = MaterialTheme.typography.bodySmall)
                                },
                                colors = TextFieldDefaults.colors(
                                    disabledContainerColor = Color.Transparent,
                                    focusedContainerColor = Color.Transparent,
                                    errorContainerColor = MaterialTheme.colorScheme.errorContainer,
                                    unfocusedContainerColor = Color.Transparent,
                                    selectionColors = TextSelectionColors(
                                        handleColor = Color.Red,
                                        backgroundColor = Color.Green
                                    ),
                                    focusedIndicatorColor = Color.Transparent,
                                    errorIndicatorColor = Color.Transparent,
                                    disabledIndicatorColor = Color.Transparent,
                                    unfocusedIndicatorColor = Color.Transparent
                                ),
                                trailingIcon = trailingIcon
                            )
                            if (isError) {
                                Text(
                                    text = errorMessage,
                                    style = MaterialTheme.typography.bodySmall.copy(color = Color.Red),
                                    modifier = Modifier.padding(start = 10.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //OTP Animation
                    
                    @OptIn(ExperimentalComposeUiApi::class)
                    @Composable
                    fun OtpTextField(
                    	modifier: Modifier = Modifier,
                    	value: String,
                    	length: Int = 4,
                    	digitsHorizontalArrangement: Arrangement.HorizontalOrVertical = Arrangement.SpaceBetween,
                    	onDigitsChange: (value: String, isFilled: Boolean) -> Unit,
                    ) {
                    	val focusRequester = remember { FocusRequester() }
                    	val keyboardController = LocalSoftwareKeyboardController.current
                    	val isInputFocused = remember { mutableStateOf(false) }
                    	
                    	BasicTextField(
                    		modifier = modifier
                    			.focusRequester(focusRequester)
                    			.focusable(true)
                    			.onFocusChanged {
                    				isInputFocused.value = it.hasFocus
                    			}
                    			.fillMaxWidth(),
                    		value = value,
                    		onValueChange = {
                    			if (it.length <= length) {
                    				if (it.length == length) {
                    					focusRequester.freeFocus()
                    					keyboardController?.hide()
                    				}
                    				onDigitsChange(it, it.length == length)
                    			}
                    		},
                    		keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
                    		decorationBox = {
                    			Row(
                    				horizontalArrangement = digitsHorizontalArrangement,
                    				verticalAlignment = Alignment.CenterVertically
                    			) {
                    				repeat(length) { index ->
                    					val currentChar = value.takeIf { it.length > index }?.get(index)
                    					DigitView(
                    						modifier = Modifier
                    							.weight(1f)
                    							.padding(horizontal = 4.dp),
                    						char = "${currentChar ?: ""}",
                    						hasFocus = value.length == index && isInputFocused.value,
                    					)
                    				}
                    			}
                    		},
                    	)
                    }

                    @Composable
                    private fun DigitView(
                    	modifier: Modifier = Modifier,
                    	char: String,
                    	hasFocus: Boolean,
                    ) {
                    	val padding = remember { mutableStateOf(48) }
                    	val animatedPadding by animateIntAsState(
                    		targetValue = padding.value,
                    		label = "otp-padding-animation",
                    		animationSpec = TweenSpec(
                    			durationMillis = 300,
                    			easing = LinearEasing,
                    		)
                    	)
                    	LaunchedEffect(key1 = hasFocus) {
                    		if (hasFocus) {
                    			padding.value = 0
                    		}
                    		else {
                    			padding.value = 48
                    		}
                    	}
                    	Box(
                    		modifier = modifier,
                    		contentAlignment = Alignment.Center,
                    	) {
                    		Box(
                    			modifier = Modifier
                    				.size(40.dp)
                    				/*.background(
                    					color = MaterialTheme.colorScheme.surface,
                    					shape = RoundedCornerShape(15),
                    				)*/
                    				.border(
                    					width = 1.dp,
                    					color = if (hasFocus) {
                    						Color.Transparent
                    					}
                    					else MaterialTheme.colorScheme.onSurfaceVariant,
                    					shape = RoundedCornerShape(15),
                    				),
                    			contentAlignment = Alignment.Center,
                    		) {
                    			Text(
                    				text = char,
                    				style = MaterialTheme.typography.bodyMedium,
                    				textAlign = TextAlign.Center,
                    				color = Color.White
                    			)
                    		}
                    		Box(
                    			modifier = Modifier
                    				.size((40.plus(animatedPadding)).dp)
                    				.border(
                    					width = if (hasFocus) 2.dp else 1.dp,
                    					color = if (hasFocus) {
                    					 	Color.White
                    					}
                    					else Color.Transparent,
                    					shape = RoundedCornerShape(15),
                    				)
                    		)
                    	}
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //OTP
                    
                    @Composable
                    fun OTPTextField(value: String, onValueChange: (String) -> Unit) {
                        val focusRequester = remember { FocusRequester() }
                        LaunchedEffect(Unit) {
                            focusRequester.requestFocus()
                        }

                        BasicTextField(
                            value = value,
                            onValueChange = { raw ->
                                val digitsOnly = raw.filter { it.isDigit() }
                                val clipped =
                                    if (digitsOnly.length > OTP_LENGTH) digitsOnly.take(OTP_LENGTH) else digitsOnly
                                onValueChange(clipped)
                            },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier
                                .fillMaxWidth()
                                .focusRequester(focusRequester)
                        ) { innerTextField ->
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceEvenly
                            ) {
                                repeat(OTP_LENGTH) { index ->
                                    Box(
                                        modifier = Modifier
                                            .size(48.dp)
                                            .background(
                                                color = MaterialTheme.colorScheme.surfaceVariant,
                                                shape = RoundedCornerShape(6.dp)
                                            ),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = value.getOrNull(index)?.toString() ?: "",
                                            style = MaterialTheme.typography.titleLarge,
                                            color = MaterialTheme.colorScheme.onSurface
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                      //BasicTextField Validation

                      data class Product(
                        var name: String,
                        var price: Double,
                        var pvp: Double,
                        var stock: Int,
                        var photo: Photo,
                        var type: String
                      )

                      val pattern = remember { Regex("^\\d*\\.?\\d*\$") }
                      var stock by remember { mutableStateOf(product.stock.toString()) }

                      CustomTextField(
                        text = stock,
                        valueChange = { if (it.matches(Regex("^[0-9]{0,6}?"))) stock = it },
                        modifier = Modifier.height(50.dp).fillMaxWidth().padding(end = 10.dp)
                            .background(color = Color.White, shape = RoundedCornerShape(10.dp)),
                        centered = false,
                        placeholderText = "0u"
                    )

                    @Composable
                    fun CustomTextField(
                        text: String,
                        valueChange: (String) -> Unit,
                        modifier: Modifier = Modifier,
                        leadingIcon: (@Composable () -> Unit)? = null,
                        trailingIcon: (@Composable () -> Unit)? = null,
                        placeholderText: String = "0.0€",
                        centered: Boolean = true
                    ) {
                        val fontSize = 16.sp
                        val align = if (centered) TextAlign.Center else TextAlign.Start

                        BasicTextField(value = text,
                            onValueChange = valueChange,
                            singleLine = true,
                            cursorBrush = SolidColor(MaterialTheme.colors.primary),
                            textStyle = LocalTextStyle.current.copy(
                                color = MaterialTheme.colors.onSurface, fontSize = fontSize, textAlign = align
                            ),
                            decorationBox = { innerTextField ->
                                Row(
                                    verticalAlignment = Alignment.CenterVertically, modifier = modifier.fillMaxWidth(0.15F)
                                ) {
                                    if (leadingIcon != null) leadingIcon()
                                    Box {
                                        if (text.isEmpty()) Text(
                                            placeholderText, style = LocalTextStyle.current.copy(
                                                color = Colores.color2,
                                                fontSize = fontSize,
                                                textAlign = align,
                                            ), modifier = Modifier.fillMaxWidth()
                                        )
                                        innerTextField()
                                    }
                                    if (trailingIcon != null) trailingIcon()
                                }
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        interface UiState

                        abstract class BaseViewModel<T>(
                        ): ViewModel() where T: UiState{
                            private lateinit var navController: NavController

                            fun initializeNavController(navController: NavController) {
                                this.navController = navController
                            }
                            abstract fun initialState(): T

                            private val _uiState = mutableStateOf(this.initialState())
                            val uiState: State<T> = _uiState

                            protected fun updateUiState(uiState: T) {
                                _uiState.value = uiState
                            }

                            protected fun navigateToDestination(destinationUrl: String) {
                                navController.navigate(destinationUrl)
                            }
                        }

                        data class RegisterUiState(
                            val email: String = "",
                            val nickname: String = "",
                            val password: String = "",
                            val error: String? = null
                        ): UiState

                        class RegisterViewModel(
                        ): BaseViewModel<RegisterUiState>() {

                            override fun initialState() = RegisterUiState()

                            fun onEmailChange(email: String) {
                                updateUiState(
                                    uiState.value.copy(
                                        email = email
                                    )
                                )
                            }
                        }

                        @Composable
                        fun RegisterScreen(
                            viewModel: RegisterViewModel,
                        ) {
                            val uiState = viewModel.uiState.value

                            Column(
                                modifier = Modifier.fillMaxSize(),
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {

                                TextField(
                                    value = uiState.email,
                                    onValueChange = viewModel::onEmailChange,
                                    label = { Text(text = stringResource(R.string.email)) }
                                )
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                           //BasicTextField2
                            BasicTextField2(
                                value = password,
                                onValueChange = { password = it },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = 8.dp)
                                    .background(Color.LightGray, RoundedCornerShape(4.dp))
                                    .padding(horizontal = 8.dp, vertical = 12.dp),
                                textStyle = TextStyle(color = Color.Black, fontSize = 16.sp),
                                //lineLimits = TextFieldLineLimits.SingleLine
                                //lineLimits = TextFieldLineLimits.Multiline(5, 10)
                                visualTransformation = PasswordVisualTransformation()
                            )
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //moko

                    class LoginUserViewModel : ViewModel() {

                        private val _uiState = MutableStateFlow(LoginUserData())
                        val uiState = _uiState.asStateFlow()

                        private val _emailError = MutableStateFlow(false)
                        val emailError = _emailError.asStateFlow()

                        fun onEmailChange(newValue: String) {
                            _uiState.update { it.copy(email = newValue) }
                            //reset error when the user types another character
                            if (newValue.isNotBlank()) _emailError.value = false
                        }

                        fun validateEmail(email: String): String {
                            val emailRegex = Regex("^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}\$")
                            if(!emailRegex.matches(email)) {
                                _emailError.value = true
                            }
                            return "Verifique o email digitado"
                        }
                    }

                    data class LoginUserData(
                        val email: String = "",
                        val password: String = "",
                    )

                    val viewModel = getViewModel(
                        key = "login-screen",
                        factory = viewModelFactory {
                            LoginUserViewModel()
                        }
                    )

                    val uiState by viewModel.uiState.collectAsState()
                    val emailError by viewModel.emailError.collectAsState()

                    OutlinedTextField(
                        modifier = Modifier.fillMaxWidth(),
                        keyboardOptions = KeyboardOptions(
                            capitalization = KeyboardCapitalization.None,
                            autoCorrect = true,
                            keyboardType = KeyboardType.Email,
                            imeAction = ImeAction.Next),
                        shape = RoundedCornerShape(20.dp),
                        value = uiState.email,
                        isError = emailError,
                        supportingText = {
                            if (emailError) Text(text = viewModel.validateEmail(uiState.email))
                        },
                        placeholder = { Text(text = "Email") },
                        onValueChange = { viewModel.onEmailChange(it) },
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val defaultTextFieldColors: TextFieldColors
                        @Composable get() = TextFieldDefaults.colors(
                            unfocusedContainerColor = Color.Transparent,
                            focusedContainerColor = Color.Transparent,
                            focusedIndicatorColor = Color.Transparent,
                            unfocusedIndicatorColor = Color.Transparent,
                            disabledIndicatorColor = Color.Transparent,
                            errorIndicatorColor = Color.Transparent,
                        )

                    @Composable
                    fun DLLTextField(
                        modifier: Modifier = Modifier,
                        value: String,
                        onValueChange: (String) -> Unit,
                        label: String,
                        focusManager: FocusManager = LocalFocusManager.current,
                        keyboardActions: KeyboardActions = KeyboardActions(
                            onNext = { focusManager.moveFocus(FocusDirection.Down) },
                        ),
                        keyboardOptions: KeyboardOptions = KeyboardOptions.Default.copy(
                            imeAction = ImeAction.Next,
                        ),
                        colors: TextFieldColors = defaultTextFieldColors,
                        trailingIcon: @Composable (() -> Unit)? = null,
                        readOnly: Boolean = false,
                        textStyle: TextStyle = LocalTextStyle.current,
                        leadingIcon: @Composable (() -> Unit)? = null,
                    ) {
                        val textFieldValue = remember(value) {
                            TextFieldValue(
                                text = value,
                                selection = if (value.isEmpty()) TextRange.Zero else TextRange(value.length),
                            )
                        }

                        var isFocused by remember { mutableStateOf(false) }

                        TextField(
                            value = textFieldValue,
                            modifier = modifier
                                .onFocusChanged { isFocused = it.isFocused }
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(24.dp))
                                .border(
                                    width = 1.dp,
                                    shape = RoundedCornerShape(24.dp),
                                    color = if (isFocused) {
                                        MaterialTheme.colorScheme.primary.copy(alpha = .4f)
                                    } else {
                                        MaterialTheme.colorScheme.surfaceVariant
                                    },
                                ),
                            onValueChange = {
                                onValueChange(it.text)
                            },
                            readOnly = readOnly,
                            textStyle = textStyle,
                            label = {
                                Text(
                                    text = label,
                                    style = MaterialTheme.typography.labelMedium.copy(
                                        fontWeight = FontWeight.SemiBold,
                                        color = MaterialTheme.colorScheme.secondary,
                                    ),
                                )
                            },
                            colors = colors,
                            keyboardOptions = keyboardOptions,
                            leadingIcon = leadingIcon,
                            trailingIcon = trailingIcon,
                            keyboardActions = keyboardActions,
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "WebView",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AndroidMapView() {
                        var webView: WebView? = remember {
                            null
                        }
                        var isLoading by remember {
                            mutableStateOf(true)
                        }
                        AndroidView(
                            modifier = Modifier
                                .fillMaxSize()
                                .statusBarsPadding(),
                            factory = { context ->
                                webView ?: WebView(context).apply {
                                    layoutParams = ViewGroup.LayoutParams(
                                        ViewGroup.LayoutParams.MATCH_PARENT,
                                        ViewGroup.LayoutParams.MATCH_PARENT
                                    )
                                    settings.javaScriptEnabled = true
                                    settings.useWideViewPort = true
                                    settings.allowFileAccess = true
                                    settings.allowContentAccess = true
                                    settings.builtInZoomControls = true
                                    settings.displayZoomControls = false
                                    settings.useWideViewPort = true
                                    settings.loadWithOverviewMode = true
                                    settings.domStorageEnabled = true
                                    settings.blockNetworkLoads = false
                                    settings.blockNetworkImage = false
                                    settings.databaseEnabled = true
                                    settings.setSupportZoom(true)
                                    scrollBarStyle = WebView.SCROLLBARS_OUTSIDE_OVERLAY
                                    isScrollbarFadingEnabled = false
                                    webViewClient = LoadingWebViewClient { isLoading = it }
                                    loadUrl("https://map.astrainteractive.ru")
                                }.also { webView = it }
                            },
                            update = {
                                webView = it
                            }
                        )

                        Crossfade(
                            targetState = isLoading,
                            label = "loading indicator crossfade"
                        ) { localIsLoading ->
                            if (localIsLoading) {
                                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                   //Loading Add
                                }
                            }
                        }
                    }

                    internal class LoadingWebViewClient(
                        private val onLoading: (Boolean) -> Unit
                    ) : WebViewClient() {

                        init {
                            onLoading.invoke(true)
                        }

                        override fun shouldOverrideUrlLoading(view: WebView, url: String): Boolean {
                            onLoading.invoke(true)
                            return super.shouldOverrideUrlLoading(view, url)
                        }

                        override fun onPageFinished(view: WebView, url: String) {
                            onLoading.invoke(false)
                            return super.onPageFinished(view, url)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Surface",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //CircleShape Image

                    Surface(
                        color = Color.Black.copy(alpha = 0.6f),
                        modifier = modifier.size(50.dp),
                        shape = CircleShape
                    ) {
                        Image(
                            painter = painterResource(id = R.drawable.play_button),
                            contentDescription = null,
                            modifier = modifier
                                .padding(12.dp)
                                .align(Alignment.Center)
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Image",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ShimmeringText(
                        text: String,
                        shimmerColor: Color,
                        modifier: Modifier = Modifier,
                        textStyle: TextStyle = LocalTextStyle.current,
                        animationSpec: DurationBasedAnimationSpec<Float> = tween(1000, 500, LinearEasing)
                    ) {
                        val infiniteTransition = rememberInfiniteTransition(label = "ShimmeringTextTransition")

                        val shimmerProgress by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = infiniteRepeatable(animationSpec),
                            label = "ShimmerProgress"
                        )

                        val brush = remember(shimmerProgress) {
                            object : ShaderBrush() {
                                override fun createShader(size: Size): Shader {
                                    val initialXOffset = -size.width
                                    val totalSweepDistance = size.width * 2
                                    val currentPosition = initialXOffset + totalSweepDistance * shimmerProgress

                                    return LinearGradientShader(
                                        colors = listOf(Color.Transparent, shimmerColor, Color.Transparent),
                                        from = Offset(currentPosition, 0f),
                                        to = Offset(currentPosition + size.width, 0f)
                                    )
                                }
                            }
                        }

                        Text(
                            text = text,
                            modifier = modifier,
                            style = textStyle.copy(brush = brush)
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Image scale with pulse animation

                        @Composable
                        fun PulseAnimation(modifier: Modifier = Modifier) {
                            val transition = rememberInfiniteTransition()
                            val progress by transition.animateFloat(
                                initialValue = 0f,
                                targetValue = 1f,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(1000),
                                    repeatMode = RepeatMode.Restart
                                )
                            )

                            Box(
                                modifier = modifier
                                    .graphicsLayer {
                                        scaleX = progress
                                        scaleY = progress
                                        alpha = 1f - progress
                                    }
                                    .border(
                                        width = 5.dp,
                                        color = SandYellow,
                                        shape = CircleShape
                                    )
                            )
                        }

                        Box(
                            modifier = Modifier.height(100.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            var imageLoadResult by remember {
                                mutableStateOf<Result<Painter>?>(null)
                            }
                            val painter = rememberAsyncImagePainter(
                                model = book.imageUrl,
                                onSuccess = {
                                    imageLoadResult =
                                        if (it.painter.intrinsicSize.width > 1 && it.painter.intrinsicSize.height > 1) {
                                            Result.success(it.painter)
                                        } else {
                                            Result.failure(Exception("Invalid image size"))
                                        }
                                },
                                onError = {
                                    it.result.throwable.printStackTrace()
                                    imageLoadResult = Result.failure(it.result.throwable)
                                }
                            )

                            val painterState by painter.state.collectAsStateWithLifecycle()
                            val transition by animateFloatAsState(
                                targetValue = if(painterState is AsyncImagePainter.State.Success) {
                                    1f
                                } else {
                                    0f
                                },
                                animationSpec = tween(durationMillis = 800)
                            )

                            when (val result = imageLoadResult) {
                                null -> PulseAnimation(
                                    modifier = Modifier.size(60.dp)
                                )
                                else -> {
                                    Image(
                                        painter = if (result.isSuccess) painter else {
                                            painterResource(Res.drawable.book_error_2)
                                        },
                                        contentDescription = book.title,
                                        contentScale = if (result.isSuccess) {
                                            ContentScale.Crop
                                        } else {
                                            ContentScale.Fit
                                        },
                                        modifier = Modifier
                                            .aspectRatio(
                                                ratio = 0.65f,
                                                matchHeightConstraintsFirst = true
                                            )
                                            .graphicsLayer {
                                                rotationX = (1f - transition) * 30f
                                                val scale = 0.8f + (0.2f * transition)
                                                scaleX = scale
                                                scaleY = scale
                                            }
                                    )
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Circle loading image

                    CoilImage(
                        imageModel = { "" },
                        imageOptions = ImageOptions(
                            contentScale = ContentScale.Crop,
                            alignment = Alignment.Center,
                            contentDescription = "Movie item",
                            colorFilter = null,
                        ),
                        component = rememberImageComponent {
                            +CircularRevealPlugin(
                                duration = 800
                            )
                        },
                        modifier = Modifier.height(250.dp).fillMaxWidth().cornerRadius(10).shimmerBackground(
                            RoundedCornerShape(5.dp)
                        ).clickable {

                        }
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Avatar List

                     Row {
                       [].forEachIndexed { index, user ->
                         GlideImage(
                           modifier = Modifier
                             .size(52.dp)
                             .zIndex((users.size - index).toFloat())
                             .offset(x = -(index.dp * 16))
                             .clip(CircleShape)
                             .border(width = 1.dp, color = Color.White, shape = CircleShape),
                           imageModel = { user.image }
                         )
                       }
                     }
                    """.trimIndent()
            )

            SpanText(
                text = "pullRefreshState",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        val pullRefreshState = rememberPullRefreshState(
                        refreshing = uiState.refreshing,
                        onRefresh = {  })

                        Box(
                            modifier = modifier
                            .fillMaxSize()
                            .pullRefresh(state = pullRefreshState)
                        ) {

                            PullRefreshIndicator(
                                refreshing = uiState.refreshing,
                                state = pullRefreshState,
                                modifier = modifier.align(Alignment.TopCenter)
                            )
                        }

                        data class HomeState(
                            var refreshing: Boolean = false
                        )
                    """.trimIndent()
            )

            //rijksmuseum-kmp
            KotlinCode(
                code = """
                    //Material3 PullToRefreshContainer

                    data object ListScreen : Screen {
                        @OptIn(ExperimentalMaterial3Api::class)
                        @Composable
                        override fun Content() {
                            val navigator = LocalNavigator.currentOrThrow

                            val screenModel: ListScreenModel = koinScreenModel()

                            LaunchedEffect(Unit) {
                                screenModel.getPaintings()
                            }

                            val museumState by screenModel.state.collectAsState()

                            val refreshState = rememberPullToRefreshState()
                            if (refreshState.isRefreshing) {
                                LaunchedEffect(Unit) {
                                    screenModel.refreshPaintings()
                                    refreshState.endRefresh()
                                }
                            }

                            Box(modifier = Modifier.nestedScroll(refreshState.nestedScrollConnection)) {
                                when (val state = museumState) {
                                    is ListScreenModel.State.Data -> {

                                    }
                                    is ListScreenModel.State.Error -> {

                                    }
                                    else -> {

                                    }
                                }

                                PullToRefreshContainer(
                                    modifier = Modifier.align(Alignment.TopCenter),
                                    state = refreshState,
                                    indicator = {
                                        CircularProgressIndicator(
                                            modifier = Modifier.size(48.dp),
                                        )
                                    }
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "GridView",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        Box(
                            modifier = modifier
                                .fillMaxSize()
                                .background(color = MaterialTheme.colorScheme.background)
                        ) {
                            LazyVerticalGrid(
                                columns = GridCells.Fixed(2),
                                contentPadding = PaddingValues(16.dp),
                                horizontalArrangement = Arrangement.spacedBy(16.dp),
                                verticalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                itemsIndexed(
                                    uiState.movies,
                                    key = { _, movie -> movie.id }
                                ) { index, movie ->

                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val lazyGridState: LazyGridState = rememberLazyGridState()

                    LazyVerticalGrid(
                        state = lazyGridState,
                        columns = GridCells.Adaptive(160.dp),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 24.dp),
                        horizontalArrangement = Arrangement.Center
                    ) {
                        items(itemsSize) { i ->
                        val pokemon = uiState.studentList[i]

                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            modifier = Modifier
                                .clickable {
                                    onUiEvent(PokemonListUiEvent.OnPokemonClick(pokemon))
                                }
                                .padding(horizontal = 16.dp, vertical = 10.dp)
                        ) {

                        }
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "BottomBar Curve",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import androidx.compose.ui.geometry.Size
                    import androidx.compose.ui.graphics.Outline
                    import androidx.compose.ui.graphics.Path
                    import androidx.compose.ui.graphics.Shape
                    import androidx.compose.ui.unit.Density
                    import androidx.compose.ui.unit.LayoutDirection

                    class BottomNavCurve(
                        private val radius: Int = 90
                    ) : Shape {
                        // THE COORDINATES OF THE FIRST CURVE
                        private val mFirstCurveStartPoint = PointF()
                        private val mFirstCurveControlPoint1 = PointF()
                        private val mFirstCurveControlPoint2 = PointF()
                        private val mFirstCurveEndPoint = PointF()


                        private val mSecondCurveControlPoint1 = PointF()
                        private val mSecondCurveControlPoint2 = PointF()
                        private var mSecondCurveStartPoint = PointF()
                        private var mSecondCurveEndPoint = PointF()
                        override fun createOutline(
                            size: Size,
                            layoutDirection: LayoutDirection,
                            density: Density
                        ): Outline {
                            return Outline.Generic(path = Path().apply {
                                val curveDepth = radius + (radius / 4F)
                                // THE COORDINATES (X,Y) OF THE START POINT BEFORE CURVE
                                mFirstCurveStartPoint.set(
                                    (size.width / 2) - (radius * 2) - (radius / 3),
                                    curveDepth
                                )

                                // THE COORDINATES (X,Y) OF THE END POINT AFTER CURVE
                                mFirstCurveEndPoint.set(size.width / 2, 0F)

                                // SAME THING FOR THE SECOND CURVE
                                mSecondCurveStartPoint = mFirstCurveEndPoint;
                                mSecondCurveEndPoint.set(
                                    (size.width / 2) + (radius * 2) + (radius / 3),
                                    curveDepth
                                )

                                // THE COORDINATES (X,Y)  OF THE 1ST CONTROL POINT ON A CUBIC CURVE
                                mFirstCurveControlPoint1.set(
                                    mFirstCurveStartPoint.x + curveDepth,
                                    mFirstCurveStartPoint.y
                                )

                                // THE COORDINATES (X,Y)  OF THE 2ND CONTROL POINT ON A CUBIC CURVE
                                mFirstCurveControlPoint2.set(
                                    mFirstCurveEndPoint.x - (radius * 2) + radius,
                                    mFirstCurveEndPoint.y
                                )
                                mSecondCurveControlPoint1.set(
                                    mSecondCurveStartPoint.x + (radius * 2) - radius,
                                    mSecondCurveStartPoint.y
                                )
                                mSecondCurveControlPoint2.set(
                                    mSecondCurveEndPoint.x - (curveDepth),
                                    mSecondCurveEndPoint.y
                                )

                                moveTo(0f, curveDepth)
                                lineTo(mFirstCurveStartPoint.x, mFirstCurveStartPoint.y)
                                cubicTo(
                                    mFirstCurveControlPoint1.x, mFirstCurveControlPoint1.y,
                                    mFirstCurveControlPoint2.x, mFirstCurveControlPoint2.y,
                                    mFirstCurveEndPoint.x, mFirstCurveEndPoint.y
                                )
                                cubicTo(
                                    mSecondCurveControlPoint1.x, mSecondCurveControlPoint1.y,
                                    mSecondCurveControlPoint2.x, mSecondCurveControlPoint2.y,
                                    mSecondCurveEndPoint.x, mSecondCurveEndPoint.y
                                )
                                lineTo(size.width, curveDepth)
                                lineTo(size.width, size.height)
                                lineTo(0F, size.height)
                            })
                        }
                    }

                    @OptIn(ExperimentalResourceApi::class)
                    @Composable
                    @Preview
                    fun App() {
                        JtSalatTheme(darkTheme = false) {
                            val navController = rememberNavController()

                            Box(
                                modifier = Modifier.fillMaxSize()
                                    .windowInsetsPadding(WindowInsets.waterfall)
                            ) {
                                Image(
                                    modifier = Modifier.fillMaxSize(),
                                    painter = painterResource(Res.drawable.app_background),
                                    contentDescription = null,
                                    contentScale = ContentScale.FillBounds
                                )
                                Box(
                                    Modifier.fillMaxSize()
                                        .background(MaterialTheme.colors.onBackground.copy(alpha = 0.2f))
                                )

                                Column(
                                    Modifier.fillMaxSize(),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {

                                    AppHost(
                                        modifier = Modifier.fillMaxWidth().weight(1f),
                                        navController = navController
                                    )

                                    AppBottomBar(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clip(BottomNavCurve(radius = 80))
                                            .height(100.dp),
                                        navController = navController
                                    )

                                }
                            }
                        }
                    }

                    @Composable
                    fun AppHost(modifier: Modifier, navController: NavHostController) {
                        NavHost(
                            modifier = modifier,
                            navController = navController,
                            startDestination = Destinations.DEFAULT,
                    //        enterTransition = {
                    //            slideIntoContainer(towards = AnimatedContentTransitionScope.SlideDirection.Start, animationSpec =  tween(300))
                    //        },
                    //        exitTransition = {
                    //            slideOutOfContainer(towards = AnimatedContentTransitionScope.SlideDirection.Start, animationSpec = tween(300))
                    //        },
                    //        popEnterTransition = {
                    //            slideIntoContainer(towards = AnimatedContentTransitionScope.SlideDirection.Start, animationSpec = tween(300))
                    //        },
                    //        popExitTransition = {
                    //            slideOutOfContainer(towards = AnimatedContentTransitionScope.SlideDirection.Start, animationSpec = tween(300))
                    //        }
                        ) {
                            composable(Destinations.HOME) {
                                HomeScreen(modifier = Modifier.fillMaxSize())
                            }
                            composable(Destinations.COMPASS) {
                                CompassScreen(modifier = Modifier.fillMaxSize())
                            }
                            composable(Destinations.CALENDAR) {
                                CalendarScreen(modifier = Modifier.fillMaxSize())
                            }
                        }
                    }

                    @OptIn(ExperimentalResourceApi::class)
                    @Composable
                    fun AppBottomBar(modifier: Modifier, navController: NavHostController) {
                        BottomNavigation(
                            modifier = modifier,
                            elevation = 0.dp,
                            backgroundColor = MaterialTheme.colors.primary.copy(alpha = 0.7f),
                            contentColor = MaterialTheme.colors.secondary //Color(0xff222222)
                        ) {
                            BottomNavigationItem(
                                modifier = Modifier.padding(top = 40.dp),
                                alwaysShowLabel = true,
                                selected = false,
                                onClick = { navController.navigate(Destinations.HOME) },
                                icon = {
                                    Icon(
                                        modifier = Modifier
                                            .size(30.dp),
                                        painter = painterResource(Res.drawable.ic_home),
                                        contentDescription = null,
                                    )
                                }
                            )
                            val infiniteTransition = rememberInfiniteTransition()

                            BottomNavigationItem(
                                modifier = Modifier.padding(top = 10.dp),
                                selected = false,
                                onClick = { navController.navigate(Destinations.COMPASS) },
                                icon = {
                                    val animate = infiniteTransition.animateFloat(
                                        initialValue = -30f,
                                        targetValue = 30f,
                                        animationSpec = infiniteRepeatable(
                                            animation = tween(durationMillis = 2000),
                                            repeatMode = RepeatMode.Reverse
                                        )
                                    )

                                    Icon(
                                        modifier = Modifier
                                            .rotate(animate.value)
                                            .size(50.dp),
                                        painter = painterResource(Res.drawable.ic_qibla),
                                        contentDescription = null,
                                    )

                                }
                            )

                            BottomNavigationItem(
                                modifier = Modifier.padding(top = 40.dp),
                                selected = false,
                                onClick = { navController.navigate(Destinations.CALENDAR) },
                                icon = {
                                    Icon(
                                        modifier = Modifier
                                            .size(30.dp),
                                        painter = painterResource(Res.drawable.ic_calender),
                                        contentDescription = null,
                                    )
                                }
                            )
                        }
                    }

                    object Destinations {
                        const val HOME = "home"
                        const val COMPASS = "compass"
                        const val CALENDAR = "calendar"
                        const val SETTINGS = "settings"

                        const val DEFAULT = HOME
                    }
                    """.trimIndent()
            )

            //https://github.com/IssueSolverApp/IssueSolver
            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedNavigationBar(navController: NavController) {
                        val barColor: Color=Color.White
                        val circleColor: Color=Color(0xFF2981FF)
                        val selectedColor: Color=Color.White
                        val unselectedColor: Color=Color.White
                        val circleRadius = 28.dp
                        val buttons = listOf(
                            BottomBarScreen.Home,
                            BottomBarScreen.MyRequest,
                            BottomBarScreen.NewRequest,
                            BottomBarScreen.Profile
                        )
                        val navBackStackEntry by navController.currentBackStackEntryAsState()
                        val currentDestination = navBackStackEntry?.destination

                        var selectedItem by rememberSaveable { mutableIntStateOf(0) }
                        var barSize by remember { mutableStateOf(IntSize(0, 0)) }
                        val offsetStep = remember(barSize) {
                            barSize.width.toFloat() / (buttons.size * 2)
                        }
                        val offset = remember(selectedItem, offsetStep) {
                            offsetStep + selectedItem * 2 * offsetStep
                        }
                        val circleRadiusPx = LocalDensity.current.run { circleRadius.toPx().toInt() }
                        val offsetTransition = updateTransition(offset, "offset transition")
                        val animation = spring<Float>(dampingRatio = 0.9f, stiffness = Spring.StiffnessVeryLow)
                        val cutoutOffset by offsetTransition.animateFloat(
                            transitionSpec = {
                                if (this.initialState == 0f) {
                                    snap()
                                } else {
                                    animation
                                }
                            },
                            label = "cutout offset"
                        ) { it }
                        val circleOffset by offsetTransition.animateIntOffset(
                            transitionSpec = {
                                if (this.initialState == 0f) {
                                    snap()
                                } else {
                                    spring(animation.dampingRatio, animation.stiffness)
                                }
                            },
                            label = "circle offset"
                        ) {
                            IntOffset(it.toInt() - circleRadiusPx, -10)  // Smaller upward movement
                        }
                        val barShape = remember(cutoutOffset) {
                            BarShape(
                                offset = cutoutOffset,
                                circleRadius = 45.dp,
                                cornerRadius = 25.dp
                            )
                        }

                        Box (modifier = Modifier
                            .height(76.dp)
                        ){

                            Circle(
                                modifier = Modifier
                                    .offset {
                                        IntOffset(
                                            circleOffset.x,
                                            circleOffset.y - 5.dp.roundToPx()
                                        )
                                    } // Manually move up
                                    .zIndex(1f)
                                    .padding(bottom = 10.dp),  // Add padding to push it upward from the navbar
                                color = circleColor,
                                radius = circleRadius,
                                button = buttons[selectedItem],
                                iconColor = selectedColor,
                            )
                            Row(
                                modifier = Modifier
                                    .onPlaced { barSize = it.size }
                                    .graphicsLayer {
                                        shape = barShape
                                        clip = true
                                    }
                                    .fillMaxWidth()
                                    .background(barColor),
                                horizontalArrangement = Arrangement.SpaceAround,
                            ) {
                                buttons.forEachIndexed { index, button ->
                                    val isSelected = currentDestination?.hierarchy?.any {
                                        it.route == button.route
                                    } == true
                                    if (isSelected) selectedItem = index

                                    NavigationBarItem(
                                        selected = isSelected,
                                        onClick = {  navController.navigate(button.route) {
                                            popUpTo(navController.graph.findStartDestination().id)
                                            launchSingleTop = true
                                            restoreState = true
                                        } },
                                        icon = {
                                            val iconAlpha by animateFloatAsState(
                                                targetValue = if (isSelected) 0f else 1f,
                                                label = "Navbar item icon"
                                            )
                                            Image(
                                                painter = painterResource(id = button.iconId),
                                                contentDescription = button.title,
                                                modifier = Modifier.alpha(iconAlpha)
                                            )
                                        },
                                        colors = NavigationBarItemDefaults.colors(
                                            selectedIconColor = selectedColor,
                                            selectedTextColor = selectedColor,
                                            unselectedIconColor = unselectedColor,
                                            unselectedTextColor = unselectedColor,
                                            indicatorColor = barColor
                                        )
                                    )
                                }
                            }
                        }
                    }
                    @Composable
                    private fun Circle(
                        modifier: Modifier = Modifier,
                        color: Color = Color.White,
                        radius: Dp,
                        button: BottomBarScreen,
                        iconColor: Color,
                    ) {
                        val iconPainter = painterResource(id = button.iconId)  // Fetch the Painter here

                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = modifier

                                .size(radius * 2)
                                .clip(CircleShape)
                                .background(color),
                        ) {
                            Icon(
                                painter = iconPainter,
                                contentDescription = button.title,
                                tint = iconColor
                            )
                        }
                    }

                    class BarShape(
                        private val offset: Float,
                        private val circleRadius: Dp,
                        private val cornerRadius: Dp,
                        private val circleGap: Dp = 8.dp,
                    ) : Shape {
                        override fun createOutline(
                            size: Size,
                            layoutDirection: LayoutDirection,
                            density: Density
                        ): Outline {
                            return Outline.Generic(getPath(size, density))
                        }
                        private fun getPath(size: Size, density: Density): Path {
                            val cutoutCenterX = offset
                            val cutoutRadius = density.run { (circleRadius+circleGap).toPx() }
                            val cornerRadiusPx = density.run { (cornerRadius).toPx() }
                            val cornerDiameter = cornerRadiusPx * 2
                            return Path().apply {
                                val cutoutEdgeOffset = cutoutRadius * 1.3f //add corner here
                                val cutoutLeftX = cutoutCenterX - cutoutEdgeOffset
                                val cutoutRightX = cutoutCenterX + cutoutEdgeOffset
                                moveTo(x = 0F, y = (size.height))
                                if (cutoutLeftX > 0) {
                                    val realLeftCornerDiameter = if (cutoutLeftX >= cornerRadiusPx) {
                                        // there is a space between rounded corner and cutout
                                        cornerDiameter
                                    } else {
                                        cutoutLeftX * 2
                                    }
                                    arcTo(
                                        rect = Rect(
                                            left = 0f,
                                            top = 0f,
                                            right = 0f,
                                            bottom = 0f
                                        ),
                                        startAngleDegrees = 180.0f,
                                        sweepAngleDegrees = 90.0f,
                                        forceMoveTo = false
                                    )
                                }
                                lineTo(cutoutLeftX, 0f)
                                // cutout
                                cubicTo(
                                    x1 = cutoutCenterX - cutoutRadius/2,
                                    y1 = 0f,
                                    x2 = cutoutCenterX - cutoutRadius,
                                    y2 = cutoutRadius,
                                    x3 = cutoutCenterX,
                                    y3 = cutoutRadius,
                                )
                                cubicTo(
                                    x1 = cutoutCenterX + cutoutRadius,
                                    y1 = cutoutRadius,
                                    x2 = cutoutCenterX + cutoutRadius/2,
                                    y2 = 0f,
                                    x3 = cutoutRightX,
                                    y3 = 0f,
                                )
                                if (cutoutRightX < size.width) {

                                    arcTo(
                                        rect = Rect(
                                            left = 0f,
                                            top = 0f,
                                            right = size.width,
                                            bottom = 0f
                                        ),
                                        startAngleDegrees = -90.0f,
                                        sweepAngleDegrees = 90.0f,
                                        forceMoveTo = false
                                    )
                                }
                                lineTo(x = size.width, y = size.height)
                                close()
                            }
                        }
                    }

                    @SuppressLint("RememberReturnType")
                    @Composable
                    fun MainScreen(navController: NavHostController = rememberNavController()) {
                        val currentRoute = remember { mutableStateOf(BottomBarScreen.Profile.route) }
                        val navBackStackEntry by navController.currentBackStackEntryAsState()
                        val currentRouteValue = navBackStackEntry?.destination?.route

                        LaunchedEffect(currentRouteValue) {
                            currentRoute.value = currentRouteValue ?: BottomBarScreen.Profile.route
                        }

                        Scaffold(
                            modifier = Modifier
                                .navigationBarsPadding(),
                            bottomBar = {
                                if (currentRoute.value in listOf(
                                        BottomBarScreen.Home.route,
                                        BottomBarScreen.MyRequest.route,
                                        BottomBarScreen.NewRequest.route,
                                        BottomBarScreen.Profile.route
                                    )) {
                                    AnimatedNavigationBar(navController)
                                }
                            },

                            content = { padding ->
                                MainNavGraph(navController = navController, paddingValues = padding)
                            }
                        )
                    }

                    sealed class BottomBarScreen(
                        val route: String,
                        val title: String,
                        val iconId: Int
                    ) {
                        object Home : BottomBarScreen(
                            route = "HOME",
                            title = "Home",
                            iconId = R.drawable.home_ic
                        )

                        object MyRequest : BottomBarScreen(
                            route = "MY_REQUESTS",
                            title = "My Requests",
                            iconId = R.drawable.groupbottombar
                        )

                        object NewRequest : BottomBarScreen(
                            route = "NEW_REQUEST",
                            title = "New Request",
                            iconId = R.drawable.ph_plus
                        )

                        object Profile : BottomBarScreen(
                            route = "PROFILE",
                            title = "Profile",
                            iconId = R.drawable.profile
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "BottomBar Dot",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //https://github.com/djkovrik/Tackle

                    enum class TackleNavigationTab(val index: Int) {
                        HOME(0),
                        EXPLORE(1),
                        PUBLICATIONS(2),
                        NOTIFICATIONS(3),
                        PROFILE(4);
                    }

                    @Composable
                    internal fun BottomNavigationBar(
                        activeTab: TackleNavigationTab,
                        modifier: Modifier = Modifier,
                        config: NavigationBarConfig = NavigationBarConfig(
                            containerColor = MaterialTheme.colorScheme.background,
                            iconColorNormal = MaterialTheme.colorScheme.secondary,
                            iconColorSelected = MaterialTheme.colorScheme.primary,
                            iconSizeNormal = 32.dp,
                            iconSizeSelected = 36.dp,
                        ),
                        itemsCount: Int = TackleNavigationTab.entries.size,
                        onTabClick: (TackleNavigationTab) -> Unit = {},
                    ) {
                        BoxWithConstraints(modifier = modifier) {
                            val maxWidth: Dp = this.maxWidth

                            val animatedIndicatorOffset: Dp by animateDpAsState(
                                targetValue = (maxWidth / itemsCount) * activeTab.index,
                                animationSpec = spring(
                                    dampingRatio = 1f,
                                    stiffness = Spring.StiffnessMediumLow,
                                ),
                            )

                            Column(modifier = modifier.navigationBarsPadding()) {
                                NavigationBar(
                                    containerColor = config.containerColor,
                                    contentColor = config.containerColor,
                                    tonalElevation = 0.dp,
                                    modifier = modifier,
                                ) {
                                    BottomNavigationBarItem(
                                        baseTab = TackleNavigationTab.HOME,
                                        activeTab = activeTab,
                                        config = config,
                                        iconRes = Res.drawable.tab_home,
                                        contentDescriptionRes = Res.string.main_tab_home,
                                        onTabClick = onTabClick,
                                    )

                                    BottomNavigationBarItem(
                                        baseTab = TackleNavigationTab.EXPLORE,
                                        activeTab = activeTab,
                                        config = config,
                                        iconRes = Res.drawable.tab_explore,
                                        contentDescriptionRes = Res.string.main_tab_explore,
                                        onTabClick = onTabClick,
                                    )

                                    BottomNavigationBarItem(
                                        baseTab = TackleNavigationTab.PUBLICATIONS,
                                        activeTab = activeTab,
                                        config = config,
                                        iconRes = Res.drawable.tab_publications,
                                        contentDescriptionRes = Res.string.main_tab_publications,
                                        onTabClick = onTabClick,
                                    )

                                    BottomNavigationBarItem(
                                        baseTab = TackleNavigationTab.NOTIFICATIONS,
                                        activeTab = activeTab,
                                        config = config,
                                        iconRes = Res.drawable.tab_notifications,
                                        contentDescriptionRes = Res.string.main_tab_notifications,
                                        onTabClick = onTabClick,
                                    )

                                    BottomNavigationBarItem(
                                        baseTab = TackleNavigationTab.PROFILE,
                                        activeTab = activeTab,
                                        config = config,
                                        iconRes = Res.drawable.tab_profile,
                                        contentDescriptionRes = Res.string.main_tab_profile,
                                        onTabClick = onTabClick,
                                    )
                                }

                                BottomNavigationBarIndicator(
                                    indicatorOffset = animatedIndicatorOffset,
                                    indicatorColor = config.iconColorSelected,
                                )
                            }
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarHomePreviewLight() {
                        TackleScreenPreview {
                            BottomNavigationBar(
                                activeTab = TackleNavigationTab.HOME
                            )
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarHomePreviewDark() {
                        TackleScreenPreview(darkTheme = true) {
                            BottomNavigationBar(
                                activeTab = TackleNavigationTab.HOME
                            )
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarExplorePreviewLight() {
                        TackleScreenPreview {
                            BottomNavigationBar(
                                activeTab = TackleNavigationTab.EXPLORE
                            )
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarExplorePreviewDark() {
                        TackleScreenPreview(darkTheme = true) {
                            BottomNavigationBar(activeTab = TackleNavigationTab.EXPLORE)
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarPublicationsPreviewLight() {
                        TackleScreenPreview {
                            BottomNavigationBar(activeTab = TackleNavigationTab.PUBLICATIONS)
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarPublicationsPreviewDark() {
                        TackleScreenPreview(darkTheme = true) {
                            BottomNavigationBar(activeTab = TackleNavigationTab.PUBLICATIONS)
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarNotificationsPreviewLight() {
                        TackleScreenPreview {
                            BottomNavigationBar(activeTab = TackleNavigationTab.NOTIFICATIONS)
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarNotificationsPreviewDark() {
                        TackleScreenPreview(darkTheme = true) {
                            BottomNavigationBar(activeTab = TackleNavigationTab.NOTIFICATIONS)
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarProfilePreviewLight() {
                        TackleScreenPreview {
                            BottomNavigationBar(activeTab = TackleNavigationTab.PROFILE)
                        }
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarProfilePreviewDark() {
                        TackleScreenPreview(darkTheme = true) {
                            BottomNavigationBar(activeTab = TackleNavigationTab.PROFILE)
                        }
                    }

                    @Composable
                    internal fun BottomNavigationBarIndicator(
                        indicatorOffset: Dp,
                        modifier: Modifier = Modifier,
                        indicatorColor: Color = MaterialTheme.colorScheme.primary,
                        itemCount: Int = TackleNavigationTab.entries.size,
                    ) {
                        Box(
                            modifier = modifier
                                .fillMaxWidth(fraction = 1f / itemCount)
                                .offset(x = indicatorOffset, y = (-12).dp),
                        ) {
                            Column(modifier = Modifier.align(alignment = Alignment.BottomCenter)) {
                                Indicator(
                                    indicatorColor = indicatorColor,
                                    modifier = Modifier.size(size = 4.dp),
                                )
                            }
                        }
                    }

                    @Composable
                    private fun Indicator(
                        indicatorColor: Color,
                        modifier: Modifier = Modifier,
                    ) {
                        Box(
                            modifier = modifier
                                .background(color = indicatorColor, shape = CircleShape)
                                .clip(shape = CircleShape)
                        )
                    }

                    @Composable
                    @Preview
                    private fun BottomNavigationBarIndicatorPreview() {
                        TackleScreenPreview {
                            BottomNavigationBar(
                                activeTab = TackleNavigationTab.HOME
                            )
                        }
                    }

                    @Composable
                    internal fun RowScope.BottomNavigationBarItem(
                        baseTab: TackleNavigationTab,
                        activeTab: TackleNavigationTab,
                        config: NavigationBarConfig,
                        iconRes: DrawableResource,
                        contentDescriptionRes: StringResource,
                        modifier: Modifier = Modifier,
                        onTabClick: (TackleNavigationTab) -> Unit = {},
                    ) {
                        NavigationBarItem(
                            icon = {
                                val animatedIconSize: Dp by animateDpAsState(
                                    if (activeTab == baseTab) {
                                        config.iconSizeSelected
                                    } else {
                                        config.iconSizeNormal
                                    },
                                )
                                val animatedIconColor: Color by animateColorAsState(
                                    when {
                                        activeTab == baseTab -> config.iconColorSelected
                                        else -> config.iconColorNormal
                                    }
                                )

                                Icon(
                                    painter = painterResource(resource = iconRes),
                                    contentDescription = stringResource(resource = contentDescriptionRes),
                                    tint = animatedIconColor,
                                    modifier = modifier.size(size = animatedIconSize),
                                )
                            },
                            colors = NavigationBarItemDefaults.colors(indicatorColor = config.containerColor),
                            selected = activeTab == baseTab,
                            onClick = { onTabClick.invoke(baseTab) },
                            alwaysShowLabel = false,
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "For loop",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    Row(
                    modifier = Modifier
                        .padding(horizontal = 20.dp, vertical = 20.dp)
                        .align(Alignment.BottomCenter)
                    ) {
                        for ((index, images) in propertyInfo.images.withIndex()) { 
                            Image() 
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "AnnotatedString and buildAnnotatedString",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val annotatedString = AnnotatedString.fromHtml("HTML Syntax")
                    Text(text = annotatedString, fontSize = 24.sp)
                    """.trimIndent()
            )

            SpanText(
                text = "ConstraintLayout",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //ConstraintLayout
            KotlinCode(
                code = """
                    //ConstraintLayout Center Logo

                    @Composable
                    fun SplashScreen(
                        modifier: Modifier = Modifier
                    ) {
                        ConstraintLayout(
                            modifier = modifier.fillMaxSize()
                        ) {
                            val (logo) = createRefs()

                            Image(
                                painter = painterResource(id = R.drawable.logo),
                                contentDescription = null,
                                modifier = Modifier
                                    .size(140.dp, 240.dp)
                                    .constrainAs(logo) {
                                        top.linkTo(parent.top)
                                        bottom.linkTo(parent.bottom)
                                        start.linkTo(parent.start)
                                        end.linkTo(parent.end)
                                    }
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //ConstraintLayout Left Side Center Image and Right Name and Description (Description logn or sort left side image always center)

                    @Composable
                    fun EncryptedItem(
                        modifier: Modifier = Modifier
                    ) {
                        ConstraintLayout(
                            modifier = modifier
                                .clip(RoundedCornerShape(12.dp))
                                .clickable {

                                }
                        ) {
                            val (image, details) = createRefs()

                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clip(RoundedCornerShape(12.dp))
                                    .background(
                                        color = Biscay,
                                        shape = RoundedCornerShape(12.dp)
                                    )
                                    .padding(15.dp)
                                    .constrainAs(details) {
                                        top.linkTo(parent.top)
                                        bottom.linkTo(parent.bottom)
                                        start.linkTo(parent.start, 37.5.dp)
                                        end.linkTo(parent.end)
                                        width = Dimension.fillToConstraints
                                    }
                            ) {
                                Text(
                                    text = getValueOrNoData(value = "Name"),
                                    color = White,
                                    style = titleLarge.copy(fontWeight = FontWeight.Bold),
                                    modifier = Modifier
                                        .padding(start = 40.dp)
                                )

                                Spacer(modifier = Modifier.height(10.dp))

                                Text(
                                    text = "Document",
                                    color = White,
                                    style = titleLarge,
                                    modifier = Modifier
                                        .padding(start = 40.dp)
                                )
                            }

                            Image(
                                painter = painterResource(id = R.drawable.ic_type_document),
                                contentDescription = null,
                                modifier = Modifier
                                    .clip(CircleShape)
                                    .size(75.dp)
                                    .constrainAs(image) {
                                        top.linkTo(parent.top)
                                        bottom.linkTo(parent.bottom)
                                        start.linkTo(parent.start)
                                    }
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ExposedDropdownMenuBox",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/HITARTH-GOHEL15/klyr.git
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun BulletRewriterScreen(
                        onBackClick: () -> Unit = {}
                    ) {
                        var experienceLevel by remember { mutableStateOf("") }
                        var showExperienceMenu by remember { mutableStateOf(false) }

                        val experienceLevels = listOf(
                            "Entry Level",
                            "Junior",
                            "Mid Level",
                            "Senior",
                            "Lead",
                            "Executive"
                        )

                        Scaffold(
                            containerColor = NightBlack,
                            topBar = {
                                TopAppBar(
                                    title = {
                                        Text(
                                            text = "Bullet Rewriter",
                                            fontSize = 22.sp,
                                            fontFamily = poppins_regular,
                                            fontWeight = FontWeight.Bold,
                                            color = TextWhite
                                        )
                                    },
                                    navigationIcon = {
                                        IconButton(onClick = onBackClick) {
                                            Icon(
                                                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                                contentDescription = "Back",
                                                tint = TextWhite
                                            )
                                        }
                                    },
                                    colors = TopAppBarDefaults.topAppBarColors(
                                        containerColor = NightBlack
                                    )
                                )
                            }
                        ) { padding ->
                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(padding)
                                    .verticalScroll(rememberScrollState())
                                    .padding(horizontal = 24.dp)
                            ) {
                                ExposedDropdownMenuBox(
                                    expanded = showExperienceMenu,
                                    onExpandedChange = { showExperienceMenu = it }
                                ) {
                                    OutlinedTextField(
                                        value = experienceLevel,
                                        onValueChange = {},
                                        readOnly = true,
                                        placeholder = {
                                            Text(
                                                text = "Select experience level",
                                                color = TextMediumGrey
                                            )
                                        },
                                        trailingIcon = {
                                            Icon(
                                                imageVector = if (showExperienceMenu)
                                                    Icons.Default.KeyboardArrowUp
                                                else
                                                    Icons.Default.KeyboardArrowDown,
                                                contentDescription = null,
                                                tint = TextWhite
                                            )
                                        },
                                        modifier = Modifier
                                            .menuAnchor()
                                            .fillMaxWidth(),
                                        shape = RoundedCornerShape(16.dp),
                                        colors = OutlinedTextFieldDefaults.colors(
                                            focusedContainerColor = NightSurface,
                                            unfocusedContainerColor = NightSurface,
                                            focusedBorderColor = RedSecondary,
                                            unfocusedBorderColor = Color.Transparent,
                                            focusedTextColor = TextWhite,
                                            unfocusedTextColor = TextWhite
                                        )
                                    )

                                    ExposedDropdownMenu(
                                        expanded = showExperienceMenu,
                                        onDismissRequest = { showExperienceMenu = false },
                                        modifier = Modifier.background(NightSurface)
                                    ) {
                                        experienceLevels.forEach { level ->
                                            DropdownMenuItem(
                                                text = {
                                                    Text(
                                                        text = level,
                                                        color = TextWhite,
                                                        fontFamily = poppins_regular
                                                    )
                                                },
                                                onClick = {
                                                    experienceLevel = level
                                                    showExperienceMenu = false
                                                }
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterialApi::class)
                    @Composable
                    fun ComboBox(
                        text: String,
                        list: List<String>,
                        change: (String) -> Unit
                    ) {
                        var visible by remember { mutableStateOf(false) }

                        ExposedDropdownMenuBox(expanded = false, onExpandedChange = {}) {
                            Row(
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(Color.White, RoundedCornerShape(10.dp))
                                    .padding(start = 5.dp)
                            ) {
                                Text(text)
                                IconButton(onClick = { visible = true }) {
                                    Icon(Icons.Default.ArrowDropDown, contentDescription = "Abrir lista")
                                }
                            }
                            ExposedDropdownMenu(
                                expanded = visible,
                                onDismissRequest = { visible = false },
                                modifier = Modifier.width(IntrinsicSize.Min)
                            ) {
                                for (item in list) {
                                    DropdownMenuItem(onClick = { visible = false; change(item) }) {
                                        Text(item)
                                    }
                                    Divider()
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Loading",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     LazyRow {
                         if (listPopularMovies is Result.Loading)
                         {
                             listLoading(visible)
                         }
                         if (listPopularMovies is Result.Success)
                         {
                             itemsIndexed(listPopularMovies.data) { index, item ->
                                 if (index != 0) Spacer(modifier = Modifier.width(10.dp))
                             }
                         }
                     }

                    fun LazyListScope.listLoading(visible: Boolean)
                    {
                        items(3) {
                            if (it != 0) Spacer(modifier = Modifier.width(10.dp))

                            AnimatedVisibility(
                                    visible = visible,
                                    enter = fadeIn() + slideInHorizontally(),
                                    exit = fadeOut() + slideOutHorizontally()
                            ) {
                                Box(
                                        modifier = Modifier.size(
                                                100.dp,
                                                150.dp
                                        ).clip(shape = RoundedCornerShape(10.dp)).background(Color(0xFF2f466c)),
                                ) {
                                    CircularProgressIndicator(
                                            modifier = Modifier.align(Alignment.Center).size(20.dp),
                                            color = Color.White
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "AnimatedVectorDrawable",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedVectorDrawable() {
                        val image = AnimatedImageVector.animatedVectorResource(R.drawable.ic_hourglass_animated)
                        var atEnd by remember { mutableStateOf(false) }
                        Image(
                            painter = rememberAnimatedVectorPainter(image, atEnd),
                            contentDescription = "Timer",
                            modifier = Modifier.clickable {
                                atEnd = !atEnd
                            },
                            contentScale = ContentScale.Crop
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "KamelImage Animation",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val infiniteTransition = rememberInfiniteTransition()
                    val vibration by infiniteTransition.animateFloat(
                        initialValue = 0f,
                        targetValue = 10f,
                        animationSpec = InfiniteRepeatableSpec(
                            animation = tween(500),
                            repeatMode = RepeatMode.Reverse
                        )
                    )

                    KamelImage(
                        asyncPainterResource(Constant.IMG_BASE_URL + forecastDataResource.data.current.condition.icon),
                        "forecast icon",
                        modifier = Modifier
                            .size(80.dp)
                            .padding(horizontal = 10.dp)
                            .graphicsLayer {
                                translationX = vibration
                            },
                        contentScale = ContentScale.Crop
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    KamelImage(
                                resource = painterResource,
                                contentScale = ContentScale.Crop,
                                contentDescription = "Profile",
                                modifier = Modifier.size(
                                    if (getPlatform().name == PlatformType.IOS) {
                                        200.dp
                                    } else {
                                        200.dp
                                    }
                                ).clip(RoundedCornerShape(36.dp)),
                                onLoading = { progress ->
                                    CircularProgressIndicator(
                                        progress = { progress },
                                    )
                                }
                            )
                    """.trimIndent()
            )

            SpanText(
                text = "AsyncImage",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        //Blur ImageColor Background and Scale Animation

                        @Composable
                        fun MovieItem(movie: Movie, navigation: () -> Unit, viewModel: MovieViewModel) {
                            val visible = remember { mutableStateOf(false) }

                            val alpha by animateFloatAsState(
                                targetValue = if (visible.value) 1f else 0f,
                                animationSpec = tween(durationMillis = 2000),
                                label = ""
                            )

                            val scale by animateFloatAsState(
                                targetValue = if (visible.value) 1f else 0.9f,
                                animationSpec = tween(durationMillis = 2000),
                                label = ""
                            )

                            LaunchedEffect(Unit) {
                                visible.value = true
                            }

                            Column {
                                Image(
                                    painter = rememberAsyncImagePainter(model = ""),
                                    contentDescription = "",
                                    modifier = Modifier
                                        .graphicsLayer {
                                            this.alpha = alpha
                                            this.scaleX = scale
                                            this.scaleY = scale
                                        }
                                        .width(136.dp)
                                        .height(202.dp)
                                        .clickable {
                                            navigation()
                                            viewModel.updateDetailUiState(movie)
                                        },
                                    contentScale = ContentScale.Crop
                                )
                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/ShanuDevCodes/KITO.git (Best Navigation3 Animation, Find Dynamic Position, Bottom Hide/Show Animation, hazeEffect, Calendar, Auto Scroll Index)
            KotlinCode(
                code = $$"""
                    //Blur Image
                    
                    @OptIn(ExperimentalHazeMaterialsApi::class, ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun HolidayListScreen(

                    ) {
                        val listState = rememberLazyListState()
                        val hazeState = rememberHazeState()
                        
                        Box(modifier = Modifier.fillMaxSize()) {
                            AsyncImage(
                                model = getMonthImage(month),
                                contentDescription = null,
                                contentScale = ContentScale.FillWidth,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .layout { measurable, constraints ->
                                        val placeable = measurable.measure(
                                            constraints.copy(maxHeight = Int.MAX_VALUE)
                                        )
                                        layout(placeable.width, placeable.height) {
                                            placeable.place(0, 0)
                                        }
                                    }
                                    .graphicsLayer {
                                        // ✅ draw phase — zero recomposition
                                        val itemInfo = listState.layoutInfo.visibleItemsInfo
                                            .firstOrNull { it.key == "header_$month" }
                                        val viewportHeight = (
                                                listState.layoutInfo.viewportEndOffset -
                                                        listState.layoutInfo.viewportStartOffset
                                                ).toFloat()
                                        val cardOffset = itemInfo?.offset?.toFloat() ?: 0f
                                        val imageHeightPx = size.width * (21f / 9f)
                                        val translateY = (viewportHeight - imageHeightPx) - cardOffset
                                        scaleY = 1.2f
                                        translationY = translateY + 700f
                                    }
                            )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //1. https://github.com/MostafaMohamed2002/GameVault
                    //2. https://github.com/skydoves/Cloudy (Blur Image)

                    //Bluer ImageColor Background from network
                    suspend fun extractDominantColor(drawable: Drawable): Color {
                        val bitmap = (drawable as BitmapDrawable).bitmap.copy(Bitmap.Config.ARGB_8888, true)
                        return withContext(Dispatchers.Default) {
                            val palette = Palette.from(bitmap).generate()
                            val dominantColor = palette.getDarkVibrantColor(Color.Gray.toArgb()) // Default to gray if no color is found
                            Color(dominantColor)
                        }
                    }

                    suspend fun loadImage(context: Context, imageUrl: String): Drawable? {
                        val imageLoader = ImageLoader(context)
                        val request = ImageRequest.Builder(context)
                            .data(imageUrl)
                            .build()

                        val result = imageLoader.execute(request)
                        return if (result is SuccessResult) {
                            result.drawable
                        } else {
                            null
                        }
                    }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @SuppressLint("UnusedMaterial3ScaffoldPaddingParameter")
                    @Composable
                    fun FreeGamesScreen(
                        navController: NavController,
                        freeGamesScreenViewModel: FreeGamesScreenViewModel
                    ) {
                        val context = LocalContext.current
                        Scaffold(content = {
                            LaunchedEffect(key1 = Unit) {
                                freeGamesScreenViewModel.uiEffect.collectLatest {
                                    when (it) {
                                        is FreeGamesScreenUiEffect.ShowSnackbar -> {

                                        }
                                    }
                                }
                            }
                            if (state.isLoading) {
                                LinearProgressIndicator(
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                            LazyColumn(contentPadding = PaddingValues(8.dp),
                                content = {
                                items(state.games, key = {it.id}) { game ->
                                    var dominantColor by remember { mutableStateOf<Color>(Color.Transparent) }

                                    LaunchedEffect(game.thumbnail) {
                                        if (freeGamesScreenViewModel.colorCache.get(game.thumbnail) != null) {
                                            dominantColor = freeGamesScreenViewModel.colorCache[game.thumbnail]!!
                                        } else {
                                            val drawable = loadImage(context, game.thumbnail)
                                            val extractedColor =
                                                drawable?.let { imgDrawable -> extractDominantColor(imgDrawable) }
                                                    ?: Color.Transparent
                                            dominantColor = extractedColor
                                            freeGamesScreenViewModel.colorCache.put(game.thumbnail, extractedColor)
                                        }
                                    }
                                    var onImageLoading by remember { mutableStateOf(true) }

                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(8.dp)
                                            .clip(RoundedCornerShape(8.dp))
                                            .background(
                                                brush = Brush.horizontalGradient(
                                                    colors = listOf(
                                                        MaterialTheme.colorScheme.surface, dominantColor
                                                    ), tileMode = TileMode.Clamp
                                                )
                                            )
                                            .animateItem()
                                            .clickable {

                                            }, verticalAlignment = Alignment.CenterVertically,

                                    ) {
                                        Box {
                                            AsyncImage(model = game.thumbnail,
                                                contentDescription = null,
                                                contentScale = ContentScale.Fit,
                                                modifier = Modifier
                                                    .padding(8.dp)
                                                    .clip(RoundedCornerShape(8.dp)),
                                                onSuccess = {
                                                    onImageLoading = false
                                                },
                                                onError = {
                                                    onImageLoading = false
                                                })
                                            if (onImageLoading) {
                                                CircularProgressIndicator(
                                                    color = Color.Blue,
                                                    modifier = Modifier
                                                        .align(Alignment.Center)
                                                        .padding(8.dp)
                                                )
                                            }
                                        }
                                    }
                                }
                            })
                        })
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Create a custom ImageLoader with GIF decoding enabled

                    val imageLoader = ImageLoader.Builder(context)
                    .components {
                        // Use ImageDecoderDecoder for newer devices
                        if (android.os.Build.VERSION.SDK_INT >= 28) {
                            add(ImageDecoderDecoder.Factory())
                        } else {
                            add(GifDecoder.Factory())
                        }
                    }.build()

                    AsyncImage(
                        model = ImageRequest.Builder(context)
                        .data(gifUrl)
                        .crossfade(true)
                        .build(),
                    imageLoader = imageLoader,
                    contentDescription = badge.name,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier
                        .padding(10.sdp)
                        .size(70.sdp)
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() }) {
                            showDialog = true
                        }
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //AsyncImage onSuccess Set Background Color

                        private fun getPaletteFrom(result: SuccessResult): Palette {
                            val bitmap = (result.drawable as BitmapDrawable).bitmap
                                .copy(Bitmap.Config.ARGB_8888, false)
                            return Palette.from(bitmap).generate()
                        }

                        fun getDominantColor(result: SuccessResult): Int =
                            getPaletteFrom(result).getDominantColor(Color.Black.value.toInt())

                        fun getSecondDominantColor(result: SuccessResult): Int =
                            getPaletteFrom(result).swatches.getOrNull(1)?.rgb ?: getDominantColor(result)


                        var dominantColor by remember { mutableIntStateOf(Black.value.toInt()) }
                        var secondColor by remember { mutableIntStateOf(Color.White.value.toInt()) }

                        Column(modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 4.dp, vertical = 4.dp)
                            .alpha(alpha)
                            .scale(scale)
                            .background(
                                brush = Brush.linearGradient(
                                    colors = listOf(Color(dominantColor), Color(secondColor))
                                ), shape = RoundedCornerShape(10.dp)
                            )
                            .clickable {

                            }) {
                            AsyncImage(modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = 16.dp)
                                .aspectRatio(2.5f),
                                model = pokemon.url,
                                contentDescription = "Pokemon image",
                                onError = { error -> onError(error.result.toString()) },
                                onSuccess = { result ->
                                    dominantColor = getDominantColor(result.result)
                                    secondColor = getSecondDominantColor(result.result)
                                })
                            }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    var showLoading by remember { mutableStateOf(false) }
                    AsyncImage(
                            modifier = modifier.clip(shape = RoundedCornerShape(10.dp)),
                            onLoading = {
                                showLoading = true
                            },
                            onError = {
                                showLoading = false
                            },
                            onSuccess = {
                                showLoading = false
                            },
                            contentScale = ContentScale.Crop,
                            model = url,
                            contentDescription = null
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Slider Image Dot Indicator",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    LaunchedEffect(Unit) {
                        while (isActive)
                        {
                            delay(1500)
                            currentIndex = (currentIndex + 1) % listOfImages.size
                        }
                    }

                    MoviesHeader(
                        [],
                        currentIndex
                    )

                    @Composable fun MoviesHeader(
                        listOfImages: List<DrawableResource>,
                        currentIndex: Int
                    ) {
                        Box(modifier = Modifier.fillMaxWidth().height(250.dp)) {

                            Image(
                                    contentScale = ContentScale.Crop,
                                    alignment = Alignment.TopCenter,
                                    modifier = Modifier.fillMaxWidth().height(250.dp),
                                    painter = painterResource(listOfImages[currentIndex]),
                                    contentDescription = ""
                            )

                            LazyRow(modifier = Modifier.align(Alignment.BottomCenter)) {
                                items(listOfImages.size) {
                                    Dot(
                                            color = if (it == currentIndex) Color.Red else Color.Gray,
                                            selected = it == currentIndex
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    fun Dot (modifier: Modifier = Modifier, color: Color,selected:Boolean)
                    {
                        val animatableWidth = remember { Animatable(if (selected) 25f else 0f) }

                        LaunchedEffect(selected) {
                            animatableWidth.animateTo(
                                    if (selected) 25f else 0f,
                                    animationSpec = tween(durationMillis = 500, easing = LinearEasing)
                            )
                        }
                        Box(
                                modifier = modifier.zIndex(2f).padding(
                                        start = 5.dp,
                                        bottom = 10.dp
                                ).size(width=if(selected) animatableWidth.value.dp else 7.dp,height=7.dp).background(
                                        color = color,
                                        shape = RoundedCornerShape(10.dp)
                                )
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    @Preview
                    fun PageSwitcherButtons(
                        currentPage: Int,
                        onPageChange: (Int) -> Unit
                    ) {
                        val totalPages = 14 //Total size of list
                        val firstPage = 0

                        Box(Modifier.fillMaxSize()) {
                            // Previous Page Button.
                            PageSwitcherButton(
                                onClick = {
                                    val newPage: Int = (currentPage - 1).coerceAtLeast(firstPage)
                                    onPageChange(newPage)
                                },
                                enabled = currentPage != firstPage,
                                modifier = Modifier.align(Alignment.BottomStart).padding(4.dp),
                                label = "<"
                            )

                            // Slider for page selection
                            Slider(
                                value = currentPage.toFloat(),
                                onValueChange = { value: Float ->
                                    val newPage: Int = value.toInt()
                                        .coerceIn(firstPage, totalPages - 1)
                                    onPageChange(newPage)
                                },
                                valueRange = 0f..(totalPages - 1).toFloat(),
                                modifier = Modifier
                                    .padding(horizontal = 20.dp)
                                    .align(Alignment.BottomCenter)
                                    .padding(horizontal = 40.dp, vertical = 4.dp).then(
                                        Modifier.rotate(0f)
                                    )
                            )

                            // Next Page Button.
                            PageSwitcherButton(
                                onClick = {
                                    val newPage: Int = (currentPage + 1)
                                        .coerceAtMost(totalPages - 1)
                                    onPageChange(newPage)
                                },
                                enabled = currentPage < totalPages - 1,
                                modifier = Modifier.align(Alignment.BottomEnd).padding(4.dp),
                                label = ">"
                            )
                        }
                    }

                    @Composable
                    @Preview
                    fun PageSwitcherButton(
                        onClick: () -> Unit,
                        enabled: Boolean,
                        modifier: Modifier = Modifier,
                        label: String
                    ) {
                        Button(
                            onClick = onClick,
                            modifier = modifier.size(50.dp),
                            shape = CircleShape,
                            enabled = enabled,
                            colors = ButtonDefaults.buttonColors(
                                backgroundColor = Color(0xFFFF6F00),
                                contentColor = Color.White,
                                disabledBackgroundColor = Color(0xFFFF6F00)
                                    .copy(alpha = 0.3f),
                                disabledContentColor = Color.White.copy(alpha = 0.5f)
                            )
                        ) {
                            Text(label)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Image Slider from the right and slide out to the left

                    data class UrlExample(
                        val id: Int = IdGenerator().generatedId(),
                        val url: String = "",
                        val description: String = ""
                    )


                    @Composable
                    fun ShimmerPlaceholder() {
                        val shimmerColors = listOf(
                            Color.LightGray.copy(alpha = 0.6f),
                            Color.LightGray.copy(alpha = 0.2f),
                            Color.LightGray.copy(alpha = 0.6f)
                        )

                        val transition = rememberInfiniteTransition()
                        val translateAnim = transition.animateFloat(
                            initialValue = 0f,
                            targetValue = 1000f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(
                                    durationMillis = 1000,
                                    easing = LinearEasing
                                ),
                                repeatMode = RepeatMode.Restart
                            )
                        )

                        val brush = Brush.linearGradient(
                            colors = shimmerColors,
                            start = Offset(translateAnim.value - 500, translateAnim.value - 500),
                            end = Offset(translateAnim.value, translateAnim.value)
                        )

                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(brush = brush)
                        )
                    }

                    @Composable
                    fun ImageCarousel(
                        imageUrlExamples: List<UrlExample>
                    ) {
                        var currentExampleIndex by remember { mutableStateOf(0) }

                        // Automatically cycle through images every 3 seconds
                        LaunchedEffect(key1 = true) {
                            while (true) {
                                delay(5000)
                                currentExampleIndex = (currentExampleIndex + 1) % imageUrlExamples.size
                            }
                        }

                        // Use AnimatedContent for smooth transitions
                        AnimatedContent(
                            targetState = currentExampleIndex,
                            transitionSpec = {
                                // Slide in from the right and slide out to the left
                                slideInHorizontally { width -> width } togetherWith slideOutHorizontally { width -> -width }
                            },
                            label = "Image Carousel"
                        ) { index ->
                            val currentExample = imageUrlExamples[index]
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clip(RoundedCornerShape(16.dp))
                            ) {
                                // Use the onState parameter to handle loading and error states
                                var isLoading by remember { mutableStateOf(true) }
                                var hasError by remember { mutableStateOf(false) }

                                AsyncImage(
                                    model = currentExample.url,
                                    contentDescription = "",
                                    modifier = Modifier.fillMaxSize(),
                                    contentScale = ContentScale.FillBounds,
                                    onState = { state ->
                                        isLoading = state is AsyncImagePainter.State.Loading
                                        hasError = state is AsyncImagePainter.State.Error
                                    }
                                )

                                // Show shimmer placeholder while loading
                                if (isLoading) {
                                    ShimmerPlaceholder()
                                }

                                // Show error placeholder if loading fails
                                if (hasError) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxSize()
                                            .background(Color.LightGray),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = "Failed to load image",
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dialog Animation",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        //https://github.com/nandaiqbalh/muppi-kmp.git

                        class GeneralDialogState internal constructor() {
                        	var showDialog by mutableStateOf(false)
                        	var type by mutableStateOf(Constant.DialogType.ERROR)
                        	var title by mutableStateOf("")
                        	var description by mutableStateOf("")
                        	var buttonDismiss by mutableStateOf("")
                        	var buttonConfirm by mutableStateOf("")
                        	var onClickDismiss by mutableStateOf({})
                        	var onClickConfirm by mutableStateOf({})
                        }

                        @Composable
                        fun rememberGeneralDialogState(key1: Any? = null): GeneralDialogState {
                        	return remember(key1) {
                        		GeneralDialogState()
                        	}
                        }

                        @Composable
                        fun GeneralDialog(
                        	dialogType: Constant.DialogType,
                        	title: String = "",
                        	description: String = "",
                        	buttonDismiss: String = "",
                        	buttonConfirm: String = "",
                        	onClickDismiss: () -> Unit = {},
                        	onClickConfirm: () -> Unit = {},
                        ) {

                        	Box(
                        		modifier = Modifier
                        			.fillMaxWidth()
                        			.clip(RoundedCornerShape(24.dp))
                        			.background(primaryFont) // Custom background color
                        			.padding(16.dp)
                        			.imePadding() // Adjust for keyboard
                        	) {
                        		Column(
                        			modifier = Modifier.fillMaxWidth()
                        		) {
                        			Spacer(modifier = Modifier.height(13.dp))

                        			Image(
                        				painter = when (dialogType) {
                        					Constant.DialogType.SUCCESS -> painterResource(Res.drawable.ic_dialog_success)
                        					Constant.DialogType.WARNING -> painterResource(Res.drawable.ic_dialog_warning)
                        					else -> painterResource(Res.drawable.ic_dialog_error)
                        				},
                        				modifier = Modifier.height(60.dp),
                        				contentScale = ContentScale.FillHeight,
                        				contentDescription = "",
                        			)

                        			Spacer(modifier = Modifier.height(26.dp))

                        			Text(
                        				text = title,
                        				style = TextStyle(
                        					fontSize = 20.sp,
                        					lineHeight = 26.sp,
                        					fontFamily = FontFamily(Font(Res.font.nunito_semibold)),
                        					fontWeight = FontWeight.W600,
                        					color = primaryBackground,
                        				)
                        			)

                        			if (description.isNotEmpty()){
                        				Spacer(modifier = Modifier.height(8.dp))

                        				Text(
                        					text = description,
                        					style = TextStyle(
                        						fontSize = 14.sp,
                        						lineHeight = 18.sp,
                        						fontFamily = FontFamily(Font(Res.font.nunito_regular)),
                        						fontWeight = FontWeight.W400,
                        						color = onBackground,
                        					)
                        				)
                        			}

                        			Spacer(modifier = Modifier.height(16.dp))

                        			Row(
                        				modifier = Modifier
                        					.fillMaxWidth(),
                        				horizontalArrangement = Arrangement.spacedBy(16.dp)
                        			) {
                        				if (buttonDismiss.isNotEmpty())
                        					Button(
                        						onClick = {
                        							onClickDismiss()
                        						},
                        						modifier = Modifier.weight(1f).height(48.dp),
                        						enabled = true,
                        						shape = RoundedCornerShape(100.dp),
                        						colors = ButtonDefaults.buttonColors(
                        							containerColor = primaryFont
                        						),
                        						contentPadding = PaddingValues(
                        							horizontal = 16.dp,
                        							vertical = 12.dp
                        						),
                        						border = BorderStroke(1.dp, primaryColor),
                        					) {
                        						Text(
                        							buttonDismiss,
                        							maxLines = 1,
                        							overflow = TextOverflow.Ellipsis,
                        							style = TextStyle(
                        								fontSize = 18.sp,
                        								lineHeight = 24.sp,
                        								fontFamily = FontFamily(Font(Res.font.nunito_regular)),
                        								color = primaryColor,
                        								fontWeight = FontWeight.W600
                        							)
                        						)
                        					}

                        				if (buttonConfirm.isNotEmpty()) {
                        					GeneralTextButton(
                        						text = buttonConfirm,
                        						modifier = Modifier.weight(1f).height(48.dp),
                        						textColor = primaryFont,
                        						isEnable = true,
                        						onClick = {
                        							onClickConfirm()
                        						}
                        					)
                        				}
                        			}
                        		}
                        	}
                        }

                        @Composable
                        fun GeneralDialogItemBottom(dialogState: GeneralDialogState) {
                        	GeneralDialog(
                        		dialogType = dialogState.type,
                        		title = dialogState.title,
                        		description = dialogState.description,
                        		buttonConfirm = dialogState.buttonConfirm,
                        		onClickConfirm = dialogState.onClickConfirm,
                        		buttonDismiss = dialogState.buttonDismiss,
                        		onClickDismiss = dialogState.onClickDismiss
                        	)
                        }

                        @Composable
                        fun CustomAnimatedDialog(dialogState: GeneralDialogState) {

                        	if (dialogState.showDialog) {
                        		Box(
                        			modifier = Modifier
                        				.fillMaxSize()
                        				.background(Color.Black.copy(alpha = 0.7f)).then(
                        					if (dialogState.showDialog) {
                        						Modifier.blur(20.dp, BlurredEdgeTreatment.Unbounded)
                        					} else {
                        						Modifier
                        					},
                        				)
                        				.clickable(
                        					indication = null, // Removes any ripple effect
                        					interactionSource = remember { MutableInteractionSource() } // Prevents other interactions from being tracked
                        				) {
                        					// Action when the background is clicked (if needed)
                        				}
                        		)
                        	}

                        	// dialog result Code
                        	AnimatedVisibility(
                        		visible = dialogState.showDialog,
                        		enter = slideInVertically(
                        			initialOffsetY = { it },
                        			animationSpec = tween(durationMillis = 500)
                        		) + fadeIn(
                        			animationSpec = tween(durationMillis = 300) // Control animation duration
                        		),
                        		exit = fadeOut(
                        			animationSpec = tween(durationMillis = 300)
                        		)
                        	) {
                        		Box(
                        			modifier = Modifier.fillMaxSize().navigationBarsPadding()
                        				.padding(horizontal = 16.dp, vertical = 16.dp),
                        			contentAlignment = Alignment.BottomCenter
                        		) {
                        			GeneralDialogItemBottom(dialogState)
                        		}
                        	}
                        }

                        @Composable
                        fun PreventUserInteractionComponent(
                        	isPreventUserInteraction: Boolean = false,
                        	isNeedIndicator: Boolean = true
                        ) {
                        	if (
                        		isPreventUserInteraction
                        	) {
                        		Box(
                        			modifier = Modifier
                        				.fillMaxSize()
                        				.background(primaryBackground.copy(0.5f))
                        				.clickable(
                        					indication = null, // Removes any ripple effect
                        					interactionSource = remember { MutableInteractionSource() } // Prevents other interactions from being tracked
                        				) {
                        					// Action when the background is clicked (if needed)
                        				},
                        			contentAlignment = Alignment.Center
                        		){
                        			if (isNeedIndicator){
                        				PulseAnimation()
                        			}
                        		}
                        	}
                        }

                        @Composable
                        fun GeneralTextButton(
                        	onClick: () -> Unit,
                        	text: String,
                        	textColor: Color = primaryFont,
                        	textSize: Int = 18,
                        	lineHeight: Int = 24,
                        	fontWeight: FontWeight = FontWeight.W600,
                        	fontFamily: FontFamily = FontFamily(Font(Res.font.nunito_semibold)),
                        	bgColor: Color = primaryColor,
                        	bgColorPressed: Color = primaryColor200,
                        	contentPadding: PaddingValues = PaddingValues(horizontal = 16.dp),
                        	leftIcon: DrawableResource? = null,
                        	rightIcon: DrawableResource? = null,
                        	isEnable: Boolean = true,
                        	modifier: Modifier = Modifier.fillMaxWidth().height(48.dp).padding(horizontal = 16.dp),
                        ) {
                        	val interactionSource = remember { MutableInteractionSource() }
                        	val isPressed by interactionSource.collectIsPressedAsState()

                        	Button(
                        		modifier = modifier,
                        		onClick = onClick,
                        		enabled = isEnable,
                        		shape = RoundedCornerShape(50),
                        		colors = ButtonDefaults.buttonColors(
                        			containerColor = primaryColor,
                        			contentColor = primaryFont,
                        		),
                        		contentPadding = contentPadding,
                        		interactionSource = interactionSource,
                        	) {
                        		if (leftIcon != null) {
                        			Image(
                        				painter = painterResource(leftIcon),
                        				contentDescription = null,
                        				modifier = Modifier
                        					.padding(end = 8.dp)
                        			)
                        		}
                        		Text(
                        			text = text,
                        			maxLines = 1,
                        			overflow = TextOverflow.Ellipsis,
                        			style = TextStyle(
                        				fontSize = textSize.sp,
                        				lineHeight = lineHeight.sp,
                        				fontWeight = fontWeight,
                        				fontFamily = fontFamily,
                        				color = textColor,
                        				textAlign = TextAlign.Center
                        			)
                        		)
                        		if (rightIcon != null) {
                        			Image(
                        				painter = painterResource(rightIcon),
                        				contentDescription = null,
                        				modifier = Modifier
                        					.padding(start = 8.dp)
                        			)
                        		}
                        	}
                        }

                        val screenDialogState = rememberGeneralDialogState()

                        coroutineScope.launch {
				            screenDialogState.apply {
					            showDialog = true
					            type = Constant.DialogType.SUCCESS
					            title = getString(Res.string.movie_saved_title)
					            description = getString(Res.string.movie_saved_description)
					            buttonConfirm = getString(Res.string.ok)
					            onClickConfirm = {
						            showDialog = false
					            }
				            }
			            }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun BoxScope.DialogAnimation() {
                        var isVisible by remember { mutableStateOf(false) }
                        val backgroundColor = remember { Animatable(Color.Transparent) }

                        LaunchedEffect(isVisible) {
                            if (!isVisible) {
                                backgroundColor.animateTo(Color.Transparent, animationSpec = tween(300))
                            } else {
                                backgroundColor.animateTo(Color(0x99000000), animationSpec = tween(0))
                            }
                        }

                        FloatingActionButton(
                            onClick = {
                                isVisible = true
                            },
                            shape = CircleShape,
                            containerColor = MaterialTheme.colorScheme.primary,
                            modifier = Modifier
                                .padding(bottom = 20.sdp, end = 12.sdp)
                                .size(50.sdp)
                                .align(Alignment.BottomEnd),
                        ) {
                            Icon(Icons.Filled.Add, "", tint = Color.White)
                        }

                        Box(
                            modifier = if (isVisible) {
                                Modifier.clickable { }
                            } else {
                                Modifier
                            }
                                .fillMaxSize()
                                .background(backgroundColor.value),
                            contentAlignment = Alignment.BottomEnd
                        ) {
                            AnimatedVisibility(
                                isVisible,
                                enter = slideIn(tween(200, easing = LinearOutSlowInEasing)) { fullSize ->
                                    IntOffset(fullSize.width, fullSize.height)
                                },
                                exit = slideOut(tween(200, easing = LinearOutSlowInEasing)) { fullSize ->
                                    IntOffset(fullSize.width, fullSize.height)
                                },
                            ) {
                                ConstraintLayout(
                                    modifier = Modifier
                                        .padding(bottom = 20.sdp, end = 12.sdp)
                                        .align(Alignment.BottomEnd)
                                        .wrapContentSize()
                                ) {
                                    val (buySellView, ivClose) = createRefs()
                                    val minusPadding12 = (-12).sdp
                                    val minusPadding10 = (-10).sdp

                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth(0.8f)
                                            .background(Color.White, RoundedCornerShape(12.sdp))
                                            .clip(RoundedCornerShape(12.sdp))
                                            .constrainAs(buySellView) {
                                                linkTo(parent.top, parent.bottom, bias = 0f, topMargin = 50.dp)
                                                linkTo(parent.start, parent.end, bias = 0f)
                                            },
                                    ) {
                                        //UI
                                    }

                                    Box(
                                        modifier = Modifier
                                            .background(MaterialTheme.colorScheme.primary, CircleShape)
                                            .size(22.sdp)
                                            .padding(4.sdp)
                                            .clip(CircleShape)
                                            .constrainAs(ivClose) {
                                                top.linkTo(buySellView.top, minusPadding12)
                                                end.linkTo(buySellView.end, minusPadding10)
                                            }
                                            .clickable {
                                                isVisible = false
                                            }
                                            .wrapContentSize()
                                    ) {
                                        Icon(Icons.Filled.Close, "", tint = Color.White)
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Animated Dialog
                    
                    @Composable
                    fun ConfirmDialog(
                        visible: Boolean,
                        title: String,
                        message: String,
                        confirmText: String = "",
                        cancelText: String = "",
                        confirmColor: Color,
                        onConfirm: () -> Unit,
                        onDismiss: () -> Unit
                    ) {
                        if (visible) {
                            Dialog(
                                onDismissRequest = onDismiss,
                                properties = DialogProperties(
                                    dismissOnBackPress = true,
                                    dismissOnClickOutside = true
                                )
                            ) {
                                AnimatedVisibility(
                                    visible = visible,
                                    enter = fadeIn(animationSpec = tween(200)) +
                                            scaleIn(initialScale = 0.8f, animationSpec = tween(200)),
                                    exit = fadeOut(animationSpec = tween(150)) +
                                            scaleOut(targetScale = 0.8f, animationSpec = tween(150))
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(horizontal = 32.dp)
                                            .clip(RoundedCornerShape(24.dp))
                                            .background(Color.White)
                                    ) {
                                        Column(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .padding(24.dp),
                                            horizontalAlignment = Alignment.CenterHorizontally
                                        ) {
                                            
                                        }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Animated Dialog

                        /**
                         * A composable function that opens a dialog with a custom 3D animation and the given content.
                         *
                         * @param onDismiss A callback function to be executed when the dialog is dismissed.
                         * @param inAnimDuration The duration (in milliseconds) of the dialog's entry animation.
                         * @param outAnimDuration The duration (in milliseconds) of the dialog's exit animation.
                         * @param properties [DialogProperties] for further customization of this dialog's behavior.
                         * @param content The content to be displayed inside the dialog.
                         */

                        @Composable
                        fun AnimatedDialog(
                            onDismiss: () -> Unit,
                            inAnimDuration: Int = 720,
                            outAnimDuration: Int = 450,
                            properties: DialogProperties = DialogProperties(),
                            content: @Composable (triggerDismiss: () -> Unit) -> Unit,
                        ) {
                            val scope = rememberCoroutineScope()
                            var isDialogVisible by remember { mutableStateOf(false) }
                            val animationSpec = tween<Float>(if (isDialogVisible) inAnimDuration else outAnimDuration)

                            val dialogAlpha by animateFloatAsState(
                                targetValue = if (isDialogVisible) 1f else 0f,
                                animationSpec = animationSpec
                            )

                            val dialogRotationX by animateFloatAsState(
                                targetValue = if (isDialogVisible) 0f else -90f,
                                animationSpec = animationSpec
                            )

                            val dialogScale by animateFloatAsState(
                                targetValue = if (isDialogVisible) 1f else 0f,
                                animationSpec = animationSpec
                            )

                            val dismissWithAnimation: () -> Unit = {
                                scope.launch {
                                    isDialogVisible = false
                                    delay(outAnimDuration.toLong())
                                    onDismiss()
                                }
                            }

                            LaunchedEffect(Unit) {
                                isDialogVisible = true
                            }

                            Dialog(
                                onDismissRequest = dismissWithAnimation,
                                properties = properties
                            ) {
                                Box(
                                    modifier = Modifier
                                        .alpha(dialogAlpha)
                                        .scale(dialogScale)
                                        .graphicsLayer { rotationX = dialogRotationX },
                                    content = {
                                        content(dismissWithAnimation)
                                    }
                                )
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     var showAboutDialog by remember { mutableStateOf(false) }

                     AboutDialog(Modifier.fillMaxSize(0.4f), showAboutDialog) { showAboutDialog = false }

                     @Composable
                     fun AboutDialog(modifier: Modifier, showAboutDialog: Boolean, onClose: () -> Unit) {
                         var visible by remember { mutableStateOf(false) }
                         Dialog(modifier, showAboutDialog,
                             onClose = {
                                 visible = false
                                 onClose()
                             }) {
                             LaunchedEffect(Unit) {
                                 delay(500)
                                 visible = true
                             }
                             Box(modifier.padding(20.dp).fillMaxSize()) {
                                 Column(Modifier.fillMaxSize()) {
                                     AnimatedVisibility(
                                         modifier = Modifier.fillMaxWidth(),
                                         visible = visible,
                                         enter = fadeIn() + slideIn(animationSpec = tween(800), initialOffset = { i -> IntOffset(0, -20) })
                                     ) {

                                     }
                                 }
                             }
                         }
                     }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Dialog(
                        modifier: Modifier,
                        showDialog: Boolean,
                        onClose: () -> Unit,
                        enter: EnterTransition = scaleIn() + fadeIn(spring()),
                        exit: ExitTransition = scaleOut() + fadeOut(spring()),
                        content: @Composable () -> Unit
                    ) {
                        AnimatedVisibility(
                            visible = showDialog, enter = enter, exit = exit
                        ) {
                            val interactionSource = remember { MutableInteractionSource() }

                            Box(
                                modifier = Modifier.fillMaxSize()
                                    .clickable(interactionSource = interactionSource, indication = null, onClick = {
                                        onClose()
                                    })
                            ) {
                                Surface(
                                    modifier = modifier.shadow(2.dp, shape = RoundedCornerShape(10.dp)).align(Alignment.Center)
                                        .clickable(enabled = false, onClick = {}),
                                    shape = RoundedCornerShape(15.dp),
                                    border = BorderStroke(2.dp, Color.Gray)
                                ) {
                                    content()
                                }
                            }
                        }
                    }

                    //Use
                    @Composable
                    private fun FiltersDialog(
                        modifier: Modifier,
                        showFiltersDialog: Boolean,
                        component: DefaultVideoComponent,
                        onClose: () -> Unit
                    ) {
                        Dialog(
                            modifier
                                .fillMaxWidth(0.7f)
                                ."fillMaxHeight"(0.3f)
                                .defaultMinSize(minWidth = 100.dp)
                                .padding(20.dp), onClose = { onClose() }, showDialog = showFiltersDialog,
                            enter = fadeIn(),
                            exit = fadeOut()
                        ) {

                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Left to Right Animation",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun Splash() {
                        val animatableX = remember { Animatable(0f) }  // Initial animation state
                        val scope = rememberCoroutineScope()
                        var boxWidth by remember { mutableStateOf(0f) }

                        LaunchedEffect(Unit) {
                            scope.launch {
                                animatableX.animateTo(
                                    targetValue = 0.5f,
                                    animationSpec = spring(
                                        dampingRatio = Spring.DampingRatioLowBouncy,
                                        stiffness = Spring.StiffnessVeryLow
                                    )
                                )
                            }
                        }

                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .onGloballyPositioned { coordinates ->
                                    boxWidth = coordinates.size.width.toFloat()
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Row(
                                horizontalArrangement = Arrangement.Center,
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .graphicsLayer {
                                        translationX = animatableX.value * boxWidth - (boxWidth / 2)
                                    }
                            ) {
                                Image(
                                    painter = painterResource(R.drawable.ellipse_9),
                                    contentDescription = "Animated Circle",
                                    modifier = Modifier.size(32.dp)
                                )
                                Spacer(modifier = Modifier.width(16.dp))
                                Text(
                                    text = "Issue Solver",
                                    style = MaterialTheme.typography.headlineMedium,
                                    fontSize = 32.sp,
                                    fontWeight = FontWeight.W600,
                                    textAlign = TextAlign.Center,
                                    color = Color(0xFF2981FF)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Circle Radius Animation

                    @Composable
                    fun SplashScreen(modifier: Modifier = Modifier, onMove: () -> Unit = {}) {

                        var radius by remember { mutableStateOf(0f) }
                        var textVisible by remember { mutableStateOf(false) }

                        // The target radius based on the screen size
                        val targetRadius = 2000f // Set this to the full size you want (e.g., full screen width or height)

                        // Animate the radius
                        val animatedRadius by animateFloatAsState(
                            targetValue = radius,
                            animationSpec = tween(durationMillis = 30) // Duration for each animation step
                        )

                        // Use LaunchedEffect to update radius
                        LaunchedEffect(Unit) {
                            while (radius < targetRadius) {
                                radius += 50f // Increment the radius
                                delay(30) // Delay for 500ms
                            }
                            delay(500)
                            textVisible = true
                            delay(1000)
                            onMove()
                        }


                        Box(modifier = modifier.fillMaxSize(), contentAlignment = Alignment.Center) {

                            Canvas(modifier = Modifier.fillMaxSize()) {
                                drawCircle(
                                    color = TemplateBackground,
                                    radius = animatedRadius
                                )
                            }

                            Box(modifier = Modifier.fillMaxSize(0.5f) , contentAlignment = Alignment.Center) {

                                Column(modifier = Modifier.fillMaxSize(),
                                    horizontalAlignment = Alignment.CenterHorizontally ,
                                    verticalArrangement = Arrangement.Center) {

                                    Image(painter = rememberVectorPainter(image = vectorResource(Res.drawable.ic_nsaver)),
                                        contentDescription = "Logo",
                                        modifier = Modifier.size(150.dp)
                                    )

                                    AnimatedVisibility(
                                        visible = textVisible,
                                        enter = fadeIn(),
                                        exit = fadeOut(),
                                        modifier = Modifier.wrapContentSize()
                                    ) {
                                        Text(
                                            text = "Notification Saver",
                                            fontSize = 18.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = Color.White // Adjust the text color as needed
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "FadingEdge",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import androidx.compose.animation.core.AnimationSpec
                    import androidx.compose.animation.core.animateDpAsState
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.composed
                    import androidx.compose.ui.draw.drawWithContent
                    import androidx.compose.ui.geometry.Offset
                    import androidx.compose.ui.geometry.Size
                    import androidx.compose.ui.graphics.Brush
                    import androidx.compose.ui.graphics.Color
                    import androidx.compose.ui.unit.Dp
                    import androidx.compose.ui.unit.dp

                    fun Modifier.topFadingEdge(
                        color: Color,
                        isVisible: Boolean = true,
                        width: Dp = DefaultWidth,
                        spec: AnimationSpec<Dp>? = null
                    ) = fadingEdge(FadeSide.TOP, color = color, width = width, isVisible = isVisible, spec = spec)

                    fun Modifier.bottomFadingEdge(
                        color: Color,
                        isVisible: Boolean = true,
                        width: Dp = DefaultWidth,
                        spec: AnimationSpec<Dp>? = null
                    ) = fadingEdge(FadeSide.BOTTOM, color = color, width = width, isVisible = isVisible, spec = spec)

                    fun Modifier.rightFadingEdge(
                        color: Color,
                        isVisible: Boolean = true,
                        width: Dp = DefaultWidth,
                        spec: AnimationSpec<Dp>? = null
                    ) = fadingEdge(FadeSide.RIGHT, color = color, width = width, isVisible = isVisible, spec = spec)

                    fun Modifier.leftFadingEdge(
                        color: Color,
                        isVisible: Boolean = true,
                        width: Dp = DefaultWidth,
                        spec: AnimationSpec<Dp>? = null
                    ) = fadingEdge(FadeSide.LEFT, color = color, width = width, isVisible = isVisible, spec = spec)

                    fun Modifier.fadingEdge(
                        vararg sides: FadeSide,
                        color: Color,
                        width: Dp = DefaultWidth,
                        isVisible: Boolean = true,
                        spec: AnimationSpec<Dp>? = null
                    ) = composed {
                        require(width > 0.dp) { "Invalid fade width: Width must be greater than 0" }

                        val animatedWidth = spec?.let {
                            animateDpAsState(
                                targetValue = if (isVisible) width else 0.dp,
                                animationSpec = spec,
                                label = "Fade width"
                            ).value
                        }

                        drawWithContent {
                            this@drawWithContent.drawContent()

                            sides.forEach { side ->
                                val (start, end) = this.size.getFadeOffsets(side)

                                val staticWidth = if (isVisible) width.toPx() else 0f
                                val widthPx = animatedWidth?.toPx() ?: staticWidth

                                val fraction = when(side) {
                                    FadeSide.LEFT, FadeSide.RIGHT -> widthPx / this.size.width
                                    FadeSide.BOTTOM, FadeSide.TOP -> widthPx / this.size.height
                                }

                                drawRect(
                                    brush = Brush.linearGradient(
                                        0f to color,
                                        fraction to Color.Transparent,
                                        start = start,
                                        end = end
                                    ),
                                    size = this.size
                                )
                            }
                        }
                    }

                    enum class FadeSide {
                        LEFT, RIGHT, BOTTOM, TOP
                    }

                    private fun Size.getFadeOffsets(side: FadeSide): Pair<Offset, Offset> {
                        return when (side) {
                            FadeSide.LEFT -> Offset.Zero to Offset(width, 0f)
                            FadeSide.RIGHT -> Offset(width, 0f) to Offset.Zero
                            FadeSide.BOTTOM -> Offset(0f, height) to Offset.Zero
                            FadeSide.TOP -> Offset.Zero to Offset(0f, height)
                        }
                    }

                    private val DefaultWidth = 16.dp
                    """.trimIndent()
            )

            SpanText(
                text = "TimerBox",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import androidx.annotation.IntRange
                    import androidx.compose.animation.core.animateFloatAsState
                    import androidx.compose.foundation.layout.Arrangement
                    import androidx.compose.foundation.layout.Row
                    import androidx.compose.foundation.lazy.LazyColumn
                    import androidx.compose.foundation.lazy.rememberLazyListState
                    import androidx.compose.material3.MaterialTheme
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.derivedStateOf
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.setValue
                    import androidx.compose.ui.Alignment
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.draw.alpha
                    import androidx.compose.ui.draw.scale
                    import androidx.compose.ui.hapticfeedback.HapticFeedbackType
                    import androidx.compose.ui.platform.LocalHapticFeedback
                    import androidx.compose.ui.text.TextStyle
                    import androidx.compose.ui.text.font.FontWeight
                    import androidx.compose.ui.text.style.TextAlign
                    import androidx.compose.ui.unit.dp
                    import androidx.compose.ui.unit.sp
                    import com.ujizin.leafy.alarm.ui.components.AutoSizeText
                    import com.ujizin.leafy.core.ui.extensions.toDecimalFormat
                    import kotlin.math.floor

                    /**
                     * Timer box with hour (00-23) and minute (00-59)
                     * @param hour initial hour from timer, valid values (00-23)
                     * @param minute initial
                     * */
                    @Composable
                    fun TimerBox(
                        modifier: Modifier = Modifier,
                        @IntRange(0, 23) hour: Int? = null,
                        @IntRange(0, 59) minute: Int? = null,
                        onTimeChange: (hour: Int, minute: Int) -> Unit,
                    ) {
                        Row(
                            modifier = modifier,
                            horizontalArrangement = Arrangement.spacedBy(64.dp, Alignment.CenterHorizontally),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            var internalHour by remember(Unit) { mutableStateOf(hour.toDecimalFormat()) }
                            var internalMinute by remember(Unit) { mutableStateOf(minute.toDecimalFormat()) }
                            TimerUnitBox(
                                modifier = Modifier.weight(1F),
                                timeUnit = TimeUnit.Hour,
                                value = hour,
                                horizontalAlignment = Alignment.End,
                                onTimeChange = { internalHour = it },
                            )
                            TimerUnitBox(
                                modifier = Modifier.weight(1F),
                                timeUnit = TimeUnit.Minutes,
                                value = minute,
                                horizontalAlignment = Alignment.Start,
                                onTimeChange = { internalMinute = it },
                            )

                            LaunchedEffect(internalHour, internalMinute) {
                                onTimeChange(
                                    internalHour.toInt(),
                                    internalMinute.toInt(),
                                )
                            }
                        }
                    }

                    /**
                     * Timer Unit Box Composable
                     *
                     * @param quantity how many numbers it will appear
                     * @param timeUnit time unit to Box (hour or minute)
                     * @param value the initial value from time unit, if value is not valid then start with 00
                     * @param horizontalAlignment horizontal alignment from box
                     * @param onTimeChange callback for time unit
                     * */
                    @Composable
                    private fun TimerUnitBox(
                        modifier: Modifier = Modifier,
                        timeUnit: TimeUnit,
                        quantity: Int = 5,
                        value: Int? = null,
                        horizontalAlignment: Alignment.Horizontal = Alignment.Start,
                        onTimeChange: (String) -> Unit,
                    ) {
                        val state = rememberLazyListState()
                        var hapticInitialized by remember { mutableStateOf(false) }
                        val quantityMiddle = remember(quantity) { floor(quantity.toDouble() / 2).toInt() }

                        LazyColumn(
                            modifier = modifier,
                            state = state,
                            horizontalAlignment = horizontalAlignment,
                        ) {
                            items(Int.MAX_VALUE) { listIndex ->
                                val index = listIndex % timeUnit.numbers.size
                                val number = timeUnit.numbers[index]
                                val middle by remember {
                                    derivedStateOf { state.firstVisibleItemIndex + quantityMiddle }
                                }
                                val endIndex by remember {
                                    derivedStateOf { state.firstVisibleItemIndex + (quantity - 1) }
                                }

                                val alpha by remember(listIndex) {
                                    derivedStateOf {
                                        when (listIndex) {
                                            state.firstVisibleItemIndex, endIndex -> 0.25F
                                            middle -> 1F
                                            in state.firstVisibleItemIndex until middle,
                                            in middle until endIndex,
                                            -> 0.5F

                                            else -> 0F
                                        }
                                    }
                                }

                                val scaleMultiplier = remember(Unit) { 1.4F }
                                val animatedScale by animateFloatAsState(targetValue = alpha * scaleMultiplier,
                                    label = ""
                                )

                                AutoSizeText(
                                    modifier = Modifier
                                        .alpha(alpha)
                                        .scale(
                                            animatedScale.coerceAtLeast(0.5F),
                                        ),
                                    text = number,
                                    textStyle = TextStyle(
                                        fontSize = 48.sp,
                                        textAlign = TextAlign.Center,
                                        color = MaterialTheme.colorScheme.onBackground,
                                        fontWeight = FontWeight.Bold,
                                    ),
                                )

                                val haptic = LocalHapticFeedback.current
                                LaunchedEffect(remember { derivedStateOf { state.firstVisibleItemIndex } }) {
                                    if (hapticInitialized) {
                                        haptic.performHapticFeedback(HapticFeedbackType.TextHandleMove)
                                    }
                                }
                            }
                        }

                        LaunchedEffect(Unit) {
                            val half = Int.MAX_VALUE / 2 // Set Infinite loop
                            val index = half % timeUnit.numbers.size
                            val zeroIndex = half - index - quantityMiddle
                            val scrollToIndex = zeroIndex + timeUnit.numbers.indexOf(
                                value.toDecimalFormat(),
                            ).coerceAtLeast(0)

                            state.scrollToItem(scrollToIndex)
                        }

                        // Set correct position when scroll is not in progress and trigger on time change
                        LaunchedEffect(state.isScrollInProgress) {
                            if (!state.isScrollInProgress) {
                                state.animateScrollToItem(state.firstVisibleItemIndex)
                                val index = (state.firstVisibleItemIndex + quantityMiddle) % timeUnit.numbers.size
                                val number = timeUnit.numbers[index]
                                onTimeChange(number)
                                hapticInitialized = true
                            }
                        }
                    }

                    import java.text.DecimalFormat

                    private val decimalFormat = DecimalFormat("00")
                    fun Int?.toDecimalFormat() = this?.let { decimalFormat.format(it) } ?: "00"
                    """.trimIndent()
            )

            SpanText(
                text = "Animate Text Appearance with Icon",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    /**
                     * Reveal Text with Icon
                     *
                     * @param text Text to reveal
                     * @param icon Icon to show
                     * @param textStyle Text Style
                     * @param iconSize Size of the Icon
                     * @param spacing Spacing between Icon and Text
                     */
                    @Composable
                    fun RevealTextWithIcon(
                        modifier: Modifier = Modifier,
                        text: String,
                        icon: Painter,
                        textStyle: TextStyle = TextStyle(
                            fontSize = 24.sp,
                            color = Color.Black
                        ),
                        iconSize: Dp = 64.dp,
                        spacing: Dp = 8.dp,
                    ) {
                        val density = LocalDensity.current

                        val textMeasurer = rememberTextMeasurer()

                        var isRevealed by remember { mutableStateOf(false) }

                        // Calculate Text Layout Result
                        val textLayoutResult by remember(text, textStyle) {
                            mutableStateOf(
                                textMeasurer.measure(
                                    text = text,
                                    style = textStyle
                                )
                            )
                        }

                        // Calculate Canvas Size
                        val canvasSize by remember(textLayoutResult, iconSize) {
                            mutableStateOf(
                                with(density) {
                                    DpSize(
                                        width = textLayoutResult.size.width.toDp() + iconSize + spacing,
                                        height = max(textLayoutResult.size.height.toDp(), iconSize)
                                    )
                                }
                            )
                        }

                        // Icon Offset Animation on Reveal
                        val iconOffsetPercentage by animateFloatAsState(
                            targetValue = if (isRevealed) 0f else 1f,
                            animationSpec = tween(
                                durationMillis = 500,
                            ),
                            label = "Icon Offset Animation"
                        )

                        // Text Alpha Animation on Reveal - Delay = half of Icon Offset Animation Duration
                        val textAlpha by animateFloatAsState(
                            targetValue = if (isRevealed) 1f else 0f,
                            animationSpec = tween(
                                durationMillis = 500,
                                delayMillis = 250
                            ),
                            label = "Text Alpha Animation"
                        )

                        LaunchedEffect(Unit) {
                            delay(1000)
                            isRevealed = true
                        }

                        Canvas(
                            modifier = modifier
                                .size(canvasSize)
                                .clipToBounds()
                        ) {
                            // Calculate Icon Position based on Icon Offset Percentage
                            val iconPosition = (size.width / 2 - iconSize.toPx() / 2) * iconOffsetPercentage

                            // Offset Icon
                            translate(
                                left = iconPosition
                            ) {
                                with(icon) {
                                    // Draw Icon
                                    draw(
                                        size = Size(
                                            width = iconSize.toPx(),
                                            height = iconSize.toPx()
                                        )
                                    )
                                }
                            }

                            // Fade in Text
                            drawText(
                                textLayoutResult = textLayoutResult,
                                alpha = textAlpha,
                                topLeft = Offset(
                                    x = iconSize.toPx() + spacing.toPx(),
                                    y = size.height / 2 - textLayoutResult.size.height / 2
                                )
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //1. LazyColumn Animation
                        var isVisible by remember { mutableStateOf(false) }

                    	LaunchedEffect(key1 = Unit) {
                    		delay(index * 150L)
                    		isVisible = true
                    	}

                    	AnimatedVisibility(
                    		visible = isVisible, enter = slideInVertically(
                    			initialOffsetY = { -it }, animationSpec = tween(durationMillis = 500)
                    		) + fadeIn(animationSpec = tween(durationMillis = 1000))
                    	) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //2. LazyColumn Animation (Scale)

                    @Composable
                    private fun ItemPokemon(
                        navController: NavHostController,
                    ) {
                        var visible by remember { mutableStateOf(false) }
                        val dominantColor by remember { mutableIntStateOf(Black.value.toInt()) }
                        val secondColor by remember { mutableIntStateOf(Color.White.value.toInt()) }

                        val alpha by animateFloatAsState(
                            targetValue = if (visible) 1f else 0.25f,
                            animationSpec = tween(durationMillis = 1000),
                            label = "alphaAnim"
                        )

                        val scale by updateTransition(visible, label = "scaleTrans").animateFloat(
                            transitionSpec = { tween(durationMillis = 500) }, label = "scaleAnim"
                        ) { state -> if (state) 1f else 0.75f }

                        Column(modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 4.dp, vertical = 4.dp)
                            .alpha(alpha)
                            .scale(scale)
                            .background(
                                brush = Brush.linearGradient(
                                    colors = listOf(Color(dominantColor), Color(secondColor))
                                ), shape = RoundedCornerShape(10.dp)
                            )
                            .clickable {

                            }) {

                        }

                        LaunchedEffect(key1 = true) {
                            visible = true
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //3. LazyColum List Animation Left to Right

                    //https://github.com/thisishisham1/CoinTracker
                    @Composable
                    fun CoinListView(
                        modifier: Modifier = Modifier,
                        state: CoinListState,
                        onAction: (CoinListAction) -> Unit,
                    ) {
                        if (state.isLoading) {
                            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                CircularProgressIndicator()
                            }
                        } else {
                            LazyColumn(
                                modifier = modifier.fillMaxSize(),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                            ) {
                                items(state.coins,
                                    key = { it.id }
                                ) { coinUi ->
                                    Modifier
                                        .fillMaxWidth()
                                    CoinListItem(
                                        coinUi = coinUi,
                                        onClick = {
                                            onAction(CoinListAction.onCoinClicked(coinUi))
                                        },
                                        modifier = Modifier
                                            .animateItem(
                                                fadeInSpec = null, fadeOutSpec = null, placementSpec = spring(
                                                    dampingRatio = Spring.DampingRatioMediumBouncy,
                                                    stiffness = Spring.StiffnessMedium
                                                )
                                            )
                                            .scale(
                                                animateFloatAsState(
                                                    targetValue = 1f,
                                                    animationSpec = tween(
                                                        durationMillis = 300,
                                                        easing = FastOutSlowInEasing
                                                    ),
                                                    label = "scale"
                                                ).value
                                            )
                                    )
                                }
                            }
                        }
                    }

                    @PreviewLightDark
                    @Composable
                    private fun CoinListViewPreview() {
                        CoinTrackerTheme {
                            CoinListView(state = CoinListState(
                                coins = (0..100).map {
                                    coinPreview.copy(id = it.toString())
                                }
                            ),
                                modifier = Modifier.background(MaterialTheme.colorScheme.background),
                                onAction = { TODO() }
                            )
                        }
                    }

                    @Composable
                    fun CoinListItem(
                        modifier: Modifier = Modifier,
                        coinUi: CoinUi,
                        onClick: () -> Unit,
                    ) {
                        var isVisible by remember { mutableStateOf(false) }

                        LaunchedEffect(Unit) {
                            isVisible = true
                        }

                        val alpha by animateFloatAsState(
                            targetValue = if (isVisible) 1f else 0f,
                            animationSpec = tween(durationMillis = 300),
                            label = "alpha"
                        )
                        val translationX by animateFloatAsState(
                            targetValue = if (isVisible) 0f else -100f,
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessLow
                            ),
                            label = "translationX"
                        )

                        Row(
                            modifier = modifier
                                .shadow(
                                    4.dp,
                                    RoundedCornerShape(16.dp),
                                    clip = false,
                                    spotColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f)
                                )
                                .background(
                                    MaterialTheme.colorScheme.surface, shape = RoundedCornerShape(16.dp)
                                )
                                .clickable(onClick = onClick)
                                .padding(16.dp)
                                .graphicsLayer {
                                    this.alpha = alpha
                                    this.translationX = translationX
                                },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Icon(
                                imageVector = ImageVector.vectorResource(id = coinUi.iconRes),
                                contentDescription = coinUi.name,
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier
                                    .size(80.dp)
                                    .background(
                                        MaterialTheme.colorScheme.primary.copy(alpha = 0.08f),
                                        shape = RoundedCornerShape(16.dp)
                                    )
                                    .padding(8.dp)
                            )
                            Column(
                                modifier = Modifier.weight(1f),
                            ) {
                                Text(
                                    text = coinUi.symbol,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    style = MaterialTheme.typography.titleMedium.copy(
                                        fontWeight = FontWeight.Bold
                                    )
                                )
                                Text(
                                    text = coinUi.name,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    style = MaterialTheme.typography.bodyMedium.copy(
                                        fontWeight = FontWeight.Light
                                    )
                                )
                            }
                            Column(
                                horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                Text(
                                    text = "${coinUi.price.suffix}",
                                    color = MaterialTheme.colorScheme.onSurface,
                                    style = MaterialTheme.typography.titleSmall
                                )
                                PriceChange(
                                    change = coinUi.percentChange24h, modifier = Modifier.align(Alignment.End)
                                )
                            }
                        }
                    }

                    @PreviewLightDark
                    @Composable
                    private fun CoinListItemPreview() {
                        CoinTrackerTheme {
                            CoinListItem(
                                onClick = {}, coinUi = coinPreview, modifier = Modifier.background(
                                    MaterialTheme.colorScheme.surface
                                )
                            )
                        }
                    }

                    internal val coinPreview = Coin(
                        id = "BTC",
                        name = "Bitcoin",
                        rank = 1,
                        symbol = "BTC",
                        priceUsd = 50000.0,
                        changePercent24Hr = -0.6,
                        marketCapUsd = 12485965258.75
                    ).toCoinUi()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //5. LazyColumn Search Animation

                        LazyColumn {
                            items(
                                items = books,
                                key = {  it.key }
                            ) { item ->
                                Box(
                                    modifier = Modifier
                                        .animateItem(placementSpec = tween(500))
                                    ) { }
                                }
                            }
                """.trimIndent()
            )

            //https://github.com/AhmedOmara14/Multiplatform-Book-Manager
            KotlinCode(
                code = """
                    6. LazyColumn Delete Scale Animation
                    
                    @Composable
                    fun BookList(
                        books: List<Book> = emptyList(),
                        modifier: Modifier = Modifier,
                    ) {
                        var allItems by remember { mutableStateOf(persistentListOf<Book>()) }
                        val removingItemIds = remember { mutableStateOf(setOf<String>()) }
                        val lazyListState = rememberLazyListState()

                        LaunchedEffect(Unit) {
                            val initialItems = books.toPersistentList()
                            allItems = initialItems
                        }

                        LazyColumn(
                            modifier = modifier.fillMaxSize().padding(8.dp),
                            state = lazyListState,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            items(
                                items = allItems,
                                key = { it.id }
                            ) { book ->
                                val isBeingRemoved = book.id in removingItemIds.value
                                val animatedAlpha by animateFloatAsState(
                                    targetValue = if (isBeingRemoved) 0f else 1f,
                                    animationSpec = tween(durationMillis = 400)
                                )
                                val animatedScale by animateFloatAsState(
                                    targetValue = if (isBeingRemoved) 0.0f else 1f,
                                    animationSpec = tween(durationMillis = 400)
                                )

                                LaunchedEffect(isBeingRemoved) {
                                    if (isBeingRemoved) {
                                        delay(300)
                                        allItems = allItems.remove(book)
                                        removingItemIds.value -= book.id
                                    }
                                }

                                if (animatedAlpha > 0.01f) {
                                   // modifier = Modifier.fillMaxWidth().graphicsLayer { alpha = animatedAlpha scaleX = animatedScale scaleY = animatedScale }
                                   
                                   // Delete on Click
                                   // removingItemIds.value = removingItemIds.value + book.id
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                //https://github.com/nandaiqbalh/muppi-kmp.git
                //Search Button Click Search TexField Right to Left Animation

                Scaffold(
                    containerColor = primaryBackground,
                    contentColor = primaryBackground,
                    topBar = {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .statusBarsPadding()
                                .padding(start = 16.dp, end = 16.dp, top = 16.dp),
                        ) {
                            AnimatedVisibility(
                                modifier = Modifier.fillMaxWidth(),
                                visible = (!state.isSearchFieldVisible),
                                enter = fadeIn(
                                    animationSpec = tween(durationMillis = 500)
                                ),
                                exit = fadeOut(
                                    animationSpec = tween(durationMillis = 500)
                                )
                            ) {
                                Box(
                                    modifier = Modifier.fillMaxWidth(),
                                ){
                                    Text(
                                        modifier = Modifier.align(Alignment.Center),
                                        text = stringResource(Res.string.nav_item_explore),
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis,
                                        style = TextStyle(
                                            fontFamily = FontFamily(Font(Res.font.nunito_medium)),
                                            fontSize = 20.sp,
                                            color = primaryFont,
                                            textAlign = TextAlign.Center
                                        )
                                    )

                                    Image(
                                        modifier = Modifier
                                            .clip(CircleShape)
                                            .align(Alignment.CenterEnd)
                                            .clickable {

                                            },
                                        painter = painterResource(Res.drawable.ic_search),
                                        contentDescription = null,
                                    )
                                }

                            }

                            AnimatedVisibility(
                                visible = state.isSearchFieldVisible,
                                enter = slideInHorizontally(
                                    initialOffsetX = { it },
                                    animationSpec = tween(durationMillis = 1000)
                                ),
                                exit = slideOutHorizontally(
                                    targetOffsetX = { it },
                                    animationSpec = tween(durationMillis = 1000)
                                )
                            ) {
                                //Search TextField
                            }
                        }
                    }
                ) {

                }""".trimIndent()
            )

            KotlinCode(
                code = """
                        //Error Animation Left to Right

                        @Composable
                        fun ErrorComponent(
                        	modifier: Modifier = Modifier,
                        	title: String = stringResource(Res.string.tv_error_general),
                        	imageModifier: Modifier = Modifier
                        ) {

                        	var isVisible by remember { mutableStateOf(false) }

                        	LaunchedEffect(Unit){
                        		delay(200)
                        		isVisible = true
                        	}

                        	Box(
                        		modifier = modifier,
                        		contentAlignment = Alignment.Center
                        	) {
                        		// Animated visibility for the Column only (Slide-in from left + Fade-in)
                        		AnimatedVisibility(
                        			visible = isVisible, // Always visible but animated on first appearance
                        			enter = slideInHorizontally(
                        				initialOffsetX = { -it },  // Start from left side (negative X-axis)
                        				animationSpec = tween(durationMillis = 2000)  // Control speed
                        			) + fadeIn(animationSpec = tween(durationMillis = 1500))  // Add fade-in effect
                        		) {
                        			Column(
                        				modifier = Modifier.fillMaxWidth(),
                        				horizontalAlignment = Alignment.CenterHorizontally,
                        				verticalArrangement = Arrangement.Center
                        			) {
                        				// Error Icon
                        				Image(
                        					modifier = imageModifier,
                        					painter = painterResource(Res.drawable.ic_error_general),
                        					contentDescription = null,
                        				)

                        				Spacer(modifier = Modifier.height(4.dp))

                        				// Error Text
                        				Text(
                        					modifier = Modifier,
                        					text = title,
                        					style = TextStyle(
                        						fontFamily = FontFamily(Font(Res.font.nunito_medium)),
                        						fontSize = 13.sp,
                        						color = primaryFont,
                        						textAlign = TextAlign.Center
                        					)
                        				)
                        			}
                        		}
                        	}
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/oguzsout/WalliesCompose (TextField Border Animation)

                    //Left to Right Animation
                    @Composable
                    fun PasswordRuleSet(
                    ) {
                        AnimatedVisibility(
                            visible = true,
                            enter = slideInHorizontally(animationSpec = spring(stiffness = Spring.StiffnessMediumLow)) + fadeIn(
                                animationSpec = tween(durationMillis = 300)
                            ),
                            exit = slideOutHorizontally(animationSpec = spring(stiffness = Spring.StiffnessMediumLow)) + fadeOut(
                                animationSpec = tween(durationMillis = 300)
                            )
                        ) {

                        }
                    }

                    //Top to Bottom Animation
                    AnimatedVisibility(
                    visible = passwordIsError,
                    enter = expandVertically(spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessVeryLow)),
                    exit = shrinkVertically(spring(dampingRatio = Spring.DampingRatioLowBouncy, stiffness = Spring.StiffnessVeryLow)),
                    modifier = Modifier
                    ) {
                        Card(
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.onPrimary,
                            ),
                            shape = RoundedCornerShape(16.dp),
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(vertical = 4.dp, horizontal = 16.dp)
                        ) {
                            AnimatedContent(
                                targetState = passwordErrorMessage,
                                label = "Password Error Message",
                                transitionSpec = {
                                    if (initialState == targetState) {
                                        scaleIn() togetherWith scaleOut()
                                    } else {
                                        slideInVertically(initialOffsetY = { -it }) togetherWith slideOutVertically(targetOffsetY = { it })
                                    }
                                }
                            ) { currentErrorMessage ->

                            }
                        }
                    }

                    //Message/Notice Animation
                    @Composable
                    fun ColumnScope.AnimatedImageRotationCard(visible: Boolean) {
                        AnimatedVisibility(
                            visible = visible,
                            enter = expandVertically(tween(1250, easing = EaseOutBounce)) + scaleIn(tween(1000)),
                            exit = shrinkVertically(
                                tween(1250, easing = EaseOutBounce)
                            ) + scaleOut(tween(1000))
                        ) {
                            PhotoByOrientationCard()
                        }
                    }

                    @Composable
                    fun PhotoByOrientationCard(modifier: Modifier = Modifier) {
                        val containerColor = Brush.linearGradient(listOf(Color.Blue.copy(alpha = 0.5f), Color.Red.copy(alpha = 0.5f)))
                        val infiniteTransition = rememberInfiniteTransition(label = "")
                        val textRotation = infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 360f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(3000, easing = EaseInOutCubic, delayMillis = 2000),
                                repeatMode = RepeatMode.Reverse,
                                initialStartOffset = StartOffset(offsetMillis = 1, offsetType = StartOffsetType.Delay)
                            ), label = ""
                        )
                        Card(
                            modifier = modifier
                                .wrapContentHeight()
                                .fillMaxWidth()
                                .padding(8.dp)
                                .background(containerColor, shape = RoundedCornerShape(16.dp)),
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors().copy(containerColor = Color.Transparent)
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(12.dp)
                                    .background(Color.Transparent),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(space = 16.dp)
                            ) {
                                Icon(
                                    painter = painterResource(R.drawable.ic_rotate_image),
                                    contentDescription = "",
                                    tint = Color.Unspecified,
                                    modifier = Modifier.graphicsLayer {
                                        rotationZ = textRotation.value
                                    }
                                )
                               ExploreText()
                            }
                        }
                    }
                    @Composable
                    fun ExploreText(modifier: Modifier = Modifier) {
                        val portrait = stringResource(id = R.string.portrait)
                        val landscape = stringResource(id = R.string.landscape)
                        val exploreText = stringResource(id = R.string.explore_images, portrait, landscape)
                        val underlineColor = MaterialTheme.colorScheme.secondaryContainer
                        Text(
                            modifier = modifier,
                            text = buildAnnotatedString {
                                val portraitStart = exploreText.indexOf(portrait)
                                val landscapeStart = exploreText.indexOf(landscape)

                                append(exploreText)

                                addStyle(
                                    style = SpanStyle(color = underlineColor,fontWeight = FontWeight.Bold),
                                    start = portraitStart,
                                    end = portraitStart + portrait.length
                                )

                                addStyle(
                                    style = SpanStyle(color = underlineColor,fontWeight = FontWeight.Bold),
                                    start = landscapeStart,
                                    end = landscapeStart + landscape.length
                                )
                            },
                            color = Color.White,
                            fontSize = 16.sp,
                            fontFamily = regular,
                            textAlign = TextAlign.Start
                        )
                    }
                    @Preview(showSystemUi = true, showBackground = true)
                    @Composable
                    fun PhotoByOrientationCardPreview(modifier: Modifier = Modifier) {
                        PhotoByOrientationCard()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Chip Animation

                    fun Modifier.animatePlacement(
                        animationSpec: AnimationSpec<IntOffset> = spring(
                            stiffness = Spring.StiffnessMediumLow,
                            visibilityThreshold = IntOffset.VisibilityThreshold
                        )
                    ): Modifier = composed {
                        val scope = rememberCoroutineScope()
                        var targetOffset by remembr { mutableStateOf(IntOffset.Zero) }
                        var animatable by remember {
                            mutableStateOf<Animatable<IntOffset, AnimationVector2D>?>(null)
                        }
                        this
                            .onPlaced {
                                // Calculate the position in the parent layout
                                targetOffset = it.positionInParent().round()
                            }
                            .offset {
                                // Animate to the new target offset when alignment changes.
                                val anim =
                                    animatable
                                        ?: Animatable(targetOffset, IntOffset.VectorConverter).also {
                                            animatable = it
                                        }
                                if (anim.targetValue != targetOffset) {
                                    scope.launch {
                                        anim.animateTo(targetOffset, animationSpec)
                                    }
                                }
                                // Offset the child in the opposite direction to the targetOffset, and slowly catch
                                // up to zero offset via an animation to achieve an overall animated movement.
                                animatable?.let { it.value - targetOffset } ?: IntOffset.Zero
                            }
                    }
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    //Chip Animation
                    
                    @Composable
                    fun <T> MultiChoiceChipGroup(
                        items: List<T>,
                        selectedItems: List<T>,
                        modifier: Modifier = Modifier,
                        contentPadding: PaddingValues = ChipGroupDefaults.ContentPadding,
                        spacing: Dp = ChipGroupDefaults.ChipSpacing,
                        itemContent: @Composable ChipGroupScope.(selected: Boolean, item: T) -> Unit
                    ) {
                        ChipGroup(
                            items = items,
                            modifier = modifier,
                            contentPadding = contentPadding,
                            spacing = spacing
                        ) { item ->
                            itemContent(selectedItems.contains(item), item)
                        }
                    }
                    
                    @OptIn(ExperimentalLayoutApi::class)
                    interface ChipGroupScope : FlowRowScope

                    @OptIn(ExperimentalLayoutApi::class)
                    internal class DefaultChipGroupScope(scope: FlowRowScope) : ChipGroupScope, FlowRowScope by scope

                    object ChipGroupDefaults {
                        val ChipSpacing = 8.dp
                        val ContentPadding = PaddingValues(horizontal = 16.dp)
                    }
                    
                    MultiChoiceChipGroup(
                        items = ,
                        selectedItems = 
                    ) { selected, item ->
                        FilterChip(
                            modifier = Modifier.animatePlacement(),
                            selected = selected,
                            onClick = { onAgeRatingClick(item) },
                            label = { Text(stringResource(item.stringRes)) },
                            leadingIcon = if (selected) {
                                {
                                    Icon(AnilibriaIcons.Outlined.Check, null, Modifier.size(FilterChipDefaults.IconSize))
                                }
                            } else null
                        )
                    }
                    
                    fun Modifier.animatePlacement(
                        animationSpec: AnimationSpec<IntOffset> = spring(
                            stiffness = Spring.StiffnessMediumLow,
                            visibilityThreshold = IntOffset.VisibilityThreshold
                        )
                    ): Modifier = composed {
                        val scope = rememberCoroutineScope()
                        var targetOffset by remember { mutableStateOf(IntOffset.Zero) }
                        var animatable by remember {
                            mutableStateOf<Animatable<IntOffset, AnimationVector2D>?>(null)
                        }
                        this
                            .onPlaced {
                                // Calculate the position in the parent layout
                                targetOffset = it.positionInParent().round()
                            }
                            .offset {
                                // Animate to the new target offset when alignment changes.
                                val anim =
                                    animatable
                                        ?: Animatable(targetOffset, IntOffset.VectorConverter).also {
                                            animatable = it
                                        }
                                if (anim.targetValue != targetOffset) {
                                    scope.launch {
                                        anim.animateTo(targetOffset, animationSpec)
                                    }
                                }
                                // Offset the child in the opposite direction to the targetOffset, and slowly catch
                                // up to zero offset via an animation to achieve an overall animated movement.
                                animatable?.let { it.value - targetOffset } ?: IntOffset.Zero
                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Quiz Option Border Animation
                    //https://github.com/YazanAesmael/AndroidQuizApp

                    val Primary = Color(0xFF3F51B5)
                    val PrimaryVariant = Color(0xFF949BC2)
                    val Secondary = Color(0xFF1F7CE4)
                    val SecondaryVariant = Color(0xFFFFFFFF)

                    val RaisinBlack = Color(0xFF1E212B)
                    val TopColor = Color(0xFF2b4059)
                    val MainColor = Color(0xFF21273c)
                    val ButtonColor = Color(0xFFC8CFE7)
                    val SecondColor = Color(0xFFe6822f)

                    val fontColor = Color.White.copy(0.8f)

                    @Composable
                    fun OptionItem(text: String, index: Int, selected: Boolean, onClick: (Int) -> Unit) {
                        val fontSize by remember {
                            mutableStateOf(42.sp)
                        }
                        val colorListGradient = listOf(
                            SecondColor,
                            ButtonColor,
                        )
                        val currentFontSizePx = with(LocalDensity.current) { fontSize.toPx() }
                        val currentFontSizeDoublePx = currentFontSizePx * 2
                        val infiniteTransition = rememberInfiniteTransition()
                        val offset by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = currentFontSizeDoublePx,
                            animationSpec = infiniteRepeatable(animation = tween(1800, easing = LinearEasing))
                        )
                        val brush = Brush.linearGradient(
                            colorListGradient,
                            start = Offset(offset, offset),
                            end = Offset(offset + currentFontSizePx, offset + currentFontSizePx),
                            tileMode = TileMode.Mirror
                        )
                        var padding by remember {
                            mutableStateOf(8.dp)
                        }
                        val paddingValue by animateDpAsState(targetValue = padding)
                        padding = when (selected) {
                            true -> {
                                12.dp
                            }
                            else -> {
                                8.dp
                            }
                        }
                        Text(
                            text = text,
                            modifier = Modifier
                                .padding(top = 4.dp, start = 4.dp, end = 4.dp, bottom = 4.dp)
                                .shadow(elevation = 4.dp, RoundedCornerShape(10.dp))
                                .clickable {
                                    onClick.invoke(index)
                                }
                                .background(
                                    if (selected) SecondColor else Color(0xFFBDC3DA),
                                    RoundedCornerShape(10.dp)
                                )
                                .fillMaxWidth()
                                .border(
                                    width = if (selected) 1.dp else Dp.Unspecified,
                                    brush = brush,
                                    shape = RoundedCornerShape(12.dp)
                                )
                                .padding(paddingValue),
                            textAlign = TextAlign.Center,
                            color = if (selected) fontColor else RaisinBlack,
                            fontSize = 16.sp,
                            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import androidx.compose.animation.core.Animatable
                    import androidx.compose.animation.core.CubicBezierEasing
                    import androidx.compose.animation.core.tween
                    import androidx.compose.foundation.gestures.Orientation
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.remember
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.composed
                    import androidx.compose.ui.graphics.graphicsLayer
                    import kotlin.math.roundToInt

                    /**
                     * @param rotation in degrees
                     * */
                    fun Modifier.listItemWindAnimation(
                        isScrollingForward: Boolean,
                        orientation: Orientation = Orientation.Vertical,
                        rotation: Float = 15f,
                        durationMillis: Int = 400,
                    ): Modifier = composed {
                        val cameraAnimatable = remember { Animatable(initialValue = 7.0f) }
                        val scaleAnimatable = remember { Animatable(initialValue = 0.8f) }
                        val rotateAnimatable = remember(isScrollingForward) {
                            Animatable(initialValue = if (isScrollingForward) rotation else -rotation)
                        }

                        // Observe changes to scrollDirection and update rotateAnimatable accordingly
                        LaunchedEffect(isScrollingForward) {
                            // Animate from 0 to either 60 or -60
                            rotateAnimatable.animateTo(
                                if (isScrollingForward) rotation else -rotation,
                                animationSpec = tween(
                                    durationMillis = 100,
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                            // Animate from either 60 or -60 to 0
                            rotateAnimatable.animateTo(
                                targetValue = 0f,
                                animationSpec = tween(
                                    durationMillis = durationMillis,
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                        }

                        // Other animations (camera and scale) remain the same
                        LaunchedEffect(Unit) {
                            cameraAnimatable.animateTo(
                                8.0f,
                                animationSpec = tween(
                                    durationMillis = durationMillis.times(1.4f).roundToInt(),
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                        }

                        LaunchedEffect(Unit) {
                            scaleAnimatable.animateTo(
                                1f,
                                animationSpec = tween(
                                    durationMillis = durationMillis.times(1.4f).roundToInt(),
                                    easing = CubicBezierEasing(0f, 0.5f, 0.5f, 1f)
                                )
                            )
                        }

                        graphicsLayer {
                            scaleX = scaleAnimatable.value
                            scaleY = scaleAnimatable.value
                            cameraDistance = cameraAnimatable.value
                            when (orientation) {
                                Orientation.Vertical -> {
                                    rotationX = -rotateAnimatable.value
                                }

                                Orientation.Horizontal -> {
                                    rotationY = rotateAnimatable.value
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    typealias Material3 = androidx.compose.material3.MaterialTheme

                    val Material3.dimen: Dimen
                        @Composable
                        @ReadOnlyComposable
                        get() = LocalDimen.current

                    @Stable
                    fun Modifier.shimmerIf(condition: () -> Boolean) = if (condition()) this then shimmer() else this

                    val LazyPagingItems<*>.isRefreshing: Boolean
                        get() = loadState.refresh == LoadState.Loading

                    val LazyPagingItems<*>.isAppending: Boolean
                        get() = loadState.append == LoadState.Loading

                    val LazyPagingItems<*>.isEmpty: Boolean
                        get() = itemCount == 0

                    @Composable
                    fun LazyStaggeredGridState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex >= firstVisibleItemIndex
                                } else {
                                    previousScrollOffset >= firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }

                    @Composable
                    fun LazyGridState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex >= firstVisibleItemIndex
                                } else {
                                    previousScrollOffset >= firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }

                    @Composable
                    fun LazyListState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex > firstVisibleItemIndex
                                } else {
                                    previousScrollOffset > firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Select Chip",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun SelectableChip(
                        label: String,
                        selected: Boolean,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier,
                        icon: (@Composable () -> Unit)? = null,
                        selectedIcon: (@Composable () -> Unit)? = null,
                    ) {
                        val backgroundColor by animateColorAsState(
                            targetValue = if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surface,
                            label = "Chip Background Color",
                        )

                        Surface(
                            modifier = Modifier
                                .animateContentSize()
                                .then(modifier),
                            onClick = onClick,
                            color = backgroundColor,
                            contentColor = if (selected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurface,
                            tonalElevation = if (selected) 0.dp else 5.dp,
                            shape = CircleShape,
                        ) {
                            Box(
                                modifier = Modifier
                                    .padding(
                                        horizontal = MaterialTheme.spacing.large,
                                        vertical = MaterialTheme.spacing.small,
                                    ),
                                contentAlignment = Alignment.Center,
                            ) {
                                if (icon != null || selectedIcon != null) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(MaterialTheme.spacing.small),
                                    ) {
                                        ChipIcon(
                                            selected = selected,
                                            icon = icon,
                                            selectedIcon = selectedIcon,
                                        )

                                        ChipText(text = label)
                                    }
                                } else {
                                    ChipText(text = label)
                                }
                            }
                        }
                    }

                    @Composable
                    private fun ChipIcon(
                        selected: Boolean,
                        icon: (@Composable () -> Unit)?,
                        selectedIcon: (@Composable () -> Unit)?,
                    ) {
                        val sizeModifier = Modifier.size(18.dp)
                        when {
                            icon != null && selectedIcon != null -> {
                                AnimatedContent(
                                    targetState = selected,
                                    label = "Chip Icon",
                                    transitionSpec = {
                                        (scaleIn() togetherWith scaleOut())
                                    },
                                    modifier = sizeModifier,
                                ) { isSelected ->
                                    if (isSelected) {
                                        selectedIcon()
                                    } else {
                                        icon()
                                    }
                                }
                            }

                            selected && selectedIcon != null -> {
                                Box(sizeModifier) { selectedIcon() }
                            }

                            icon != null -> {
                                Box(sizeModifier) { icon() }
                            }
                        }
                    }

                    @Composable
                    private fun ChipText(
                        text: String,
                        modifier: Modifier = Modifier,
                    ) {
                        Text(
                            text = text,
                            style = MaterialTheme.typography.bodyMedium,
                            modifier = modifier,
                        )
                    }

                    @PreviewMultiTheme
                    @Composable
                    private fun SelectableChipWithIconPreview() {
                        NewsLayerTheme {
                            Surface(
                                modifier = Modifier
                                    .background(MaterialTheme.colorScheme.background)
                                    .padding(16.dp),
                            ) {
                                var selected by remember { mutableStateOf(false) }

                                SelectableChip(
                                    label = "Selectable Chip",
                                    selected = selected,
                                    onClick = { selected = !selected },
                                    icon = {
                                        Icon(
                                            imageVector = Icons.Default.Add,
                                            contentDescription = null,
                                        )
                                    },
                                    selectedIcon = {
                                        Icon(
                                            imageVector = Icons.Default.Check,
                                            contentDescription = null,
                                        )
                                    },
                                )
                            }
                        }
                    }

                    @PreviewMultiTheme
                    @Composable
                    private fun SelectableChipWithoutIconPreview() {
                        NewsLayerTheme {
                            Surface(
                                modifier = Modifier
                                    .background(MaterialTheme.colorScheme.background)
                                    .padding(16.dp),
                            ) {
                                var selected by remember { mutableStateOf(false) }

                                SelectableChip(
                                    label = "Selectable Chip",
                                    selected = selected,
                                    onClick = { selected = !selected },
                                )
                            }
                        }
                    }

                    data class Spacing(
                        val extraSmall: Dp = 4.dp,
                        val small: Dp = 8.dp,
                        val medium: Dp = 12.dp,
                        val large: Dp = 16.dp,
                        val extraLarge: Dp = 24.dp,
                    ) {
                        companion object {
                            val Default = Spacing()
                        }
                    }

                    internal val LocalSpacing = staticCompositionLocalOf { Spacing.Default }

                    val MaterialTheme.spacing: Spacing
                        @Composable
                        @ReadOnlyComposable
                        get() = LocalSpacing.current


                    @Composable
                    fun NewsLayerTheme(
                        darkTheme: Boolean = isSystemInDarkTheme(),
                        dynamicColor: Boolean = false,
                        content: @Composable () -> Unit,
                    ) {
                        val colorScheme = when {
                            dynamicColor && Build.VERSION.SDK_INT >= Build.VERSION_CODES.S -> {
                                val context = LocalContext.current
                                if (darkTheme) dynamicDarkColorScheme(context) else dynamicLightColorScheme(context)
                            }

                            darkTheme -> DarkColorScheme
                            else -> LightColorScheme
                        }

                        MaterialTheme(
                            colorScheme = colorScheme,
                            typography = NewsLayerTypography,
                            content = {
                                CompositionLocalProvider(
                                    value = LocalSpacing provides Spacing.Default,
                                    content = content
                                )
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/gcaguilar/beers-kmp

                    InfiniteStagedGridList(
                                    modifier = Modifier
                                        .padding(vertical = 16.dp)
                                        .weight(1f),
                                    listState = listState,
                                    canRequestMoreData = ,
                                    isRequestingMoreItems = ,
                                    scrollingDown = !listState.isScrollingUp(),
                                    onClick = {

                                    },
                                    onLoadMore = {

                                    }
                                )

                    const val InfiniteStagedGridListTag = "InfiniteStagedGridList"

                    @Composable
                    fun InfiniteStagedGridList(
                        scrollingDown: Boolean,
                        beerList: List<Beer>,
                        buffer: Int = 5,
                        canRequestMoreData: Boolean,
                        isRequestingMoreItems: Boolean,
                        onClick: (bid: String) -> Unit,
                        onLoadMore: () -> Unit,
                        listState: LazyStaggeredGridState,
                        modifier: Modifier = Modifier,
                    ) {
                        LazyVerticalStaggeredGrid(
                            modifier = modifier.fillMaxSize()
                                .testTag(InfiniteStagedGridListTag),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalItemSpacing = 16.dp,
                            state = listState,
                            columns = StaggeredGridCells.Adaptive(136.dp)
                        ) {
                            items(
                                count = beerList.size,
                            ) { index ->
                                Card {
                                    with(beerList[index]) {
                                        //UI SET
                                    }
                                }
                            }
                        }

                        if (scrollingDown && !isRequestingMoreItems) {
                            LaunchedEffect(listState) {
                                snapshotFlow { listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0 }
                                    .map { lastVisibleItem ->
                                        if (listState.layoutInfo.totalItemsCount > 0) {
                                            lastVisibleItem >= listState.layoutInfo.totalItemsCount - buffer
                                        } else {
                                            false
                                        }
                                    }
                                    .collectLatest { requestMore ->
                                        if (requestMore && canRequestMoreData) {
                                            onLoadMore()
                                        }
                                    }
                            }
                        }
                    }

                    @Composable
                    internal fun LazyStaggeredGridState.isScrollingUp(): Boolean {
                        var previousIndex by remember(this) { mutableStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableStateOf(firstVisibleItemScrollOffset) }
                        val scrollOffsetThreshold = 10.dp // Adjust as needed
                        val scrollOffsetThresholdInPx = with(LocalDensity.current) { scrollOffsetThreshold.toPx() }

                        return remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex > firstVisibleItemIndex
                                } else {
                                    firstVisibleItemScrollOffset - previousScrollOffset >= scrollOffsetThresholdInPx
                                }.also {
                                    previousIndex = firstVisibleItemIndex
                                    previousScrollOffset = firstVisibleItemScrollOffset
                                }
                            }
                        }.value
                    }
                    """.trimIndent()
            )

            //https://github.com/dalafiarisamuel/Unsplash-KMP
            KotlinCode(
                code = """
                    @ExperimentalFoundationApi
                    @Composable
                    fun UnsplashImageList(
                        modifier: Modifier,
                        imageList: Flow<PagingData<Photo>>,
                        lazyGridState: LazyStaggeredGridState,
                        nestedScrollConnection: NestedScrollConnection,
                        onItemClicked: (Photo?) -> Unit,
                        onItemLongClicked: (Photo?) -> Unit,
                    ) {
                        val list = imageList.collectAsLazyPagingItems()
                        val isListEmpty by remember { derivedStateOf { list.itemCount <= 0 } }
                        if (list.loadState.refresh is LoadState.Loading) {
                            LoadingView(modifier = Modifier.fillMaxSize())
                        } else {
                            Crossfade(targetState = isListEmpty, label = "") {
                                if (it) {
                                    EmptyListStateComponent(modifier = Modifier.fillMaxSize())
                                } else {
                                    PhotosList(
                                        modifier = modifier,
                                        imageList = list,
                                        lazyGridState = lazyGridState,
                                        nestedScrollConnection = nestedScrollConnection,
                                        onItemClicked = onItemClicked,
                                        onItemLongClicked = onItemLongClicked,
                                    )
                                }
                            }
                        }
                    }

                    @OptIn(ExperimentalMaterial3WindowSizeClassApi::class)
                    @ExperimentalFoundationApi
                    @Composable
                    private fun PhotosList(
                        modifier: Modifier,
                        imageList: LazyPagingItems<Photo>,
                        lazyGridState: LazyStaggeredGridState,
                        nestedScrollConnection: NestedScrollConnection,
                        onItemClicked: (Photo?) -> Unit,
                        onItemLongClicked: (Photo?) -> Unit,
                    ) {

                        val windowSizeClass = calculateWindowSizeClass()

                        val gridSize = when (windowSizeClass.widthSizeClass) {
                            WindowWidthSizeClass.Compact -> 2
                            WindowWidthSizeClass.Medium -> 3
                            else -> 4
                        }

                        LazyVerticalStaggeredGrid(
                            state = lazyGridState,
                            verticalItemSpacing = 8.dp,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier
                                .padding(top = 15.dp)
                                .nestedScroll(nestedScrollConnection)
                                .then(modifier),
                            columns = StaggeredGridCells.Fixed(gridSize)
                        ) {
                            lazyItems(imageList) { photo ->
                                UnsplashImageStaggered(
                                    modifier = Modifier,
                                    data = photo,
                                    onImageClicked = onItemClicked,
                                    onImageLongClicked = onItemLongClicked
                                )
                            }
                        }
                    }

                    @Preview
                    @Composable
                    fun EmptyListStateComponent(
                        modifier: Modifier = Modifier,
                        term: String = stringResource(Res.string.searched_term_not_found),
                    ) {

                        Column(
                            modifier = Modifier.then(modifier),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {

                            Image(
                                modifier = Modifier
                                    .size(100.dp)
                                    .background(color = appDark, shape = CircleShape),
                                painter = painterResource(Res.drawable.ic_image_search),
                                contentScale = ContentScale.Inside,
                                contentDescription = null
                            )

                            Text(
                                text = term,
                                color = appWhite,
                                fontSize = 13.sp,
                                fontStyle = FontStyle.Normal,
                                modifier = Modifier.padding(top = 10.dp)
                            )
                        }
                    }

                    @ExperimentalFoundationApi
                    @Composable
                    private fun UnsplashImageStaggered(
                        modifier: Modifier,
                        data: Photo?,
                        onImageClicked: (Photo?) -> Unit,
                        onImageLongClicked: (Photo?) -> Unit,
                    ) {

                        val aspectRatio: Float by remember {
                            derivedStateOf { (data?.width?.toFloat() ?: 1.0F) / (data?.height?.toFloat() ?: 1.0F) }
                        }

                        Card(
                            elevation = 4.dp,
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier
                                .fillMaxWidth()
                                .combinedClickable(
                                    onClick = { onImageClicked(data) },
                                    onLongClick = { onImageLongClicked(data) },
                                )
                                .then(modifier)

                        ) {
                            AsyncImage(
                                model = data?.urls?.small,
                                contentDescription = data?.description,
                                contentScale = ContentScale.FillBounds,
                                modifier = Modifier
                                    .aspectRatio(aspectRatio)
                                    .fillMaxWidth()
                                    .defaultMinSize(minHeight = 200.dp)
                            )
                        }

                    }

                    @ExperimentalFoundationApi
                    private fun <T : Any> LazyStaggeredGridScope.lazyItems(
                        lazyPagingItems: LazyPagingItems<T>,
                        itemContent: @Composable LazyStaggeredGridItemScope.(value: T?) -> Unit,
                    ) {
                        items(lazyPagingItems.itemCount) { index ->
                            itemContent(lazyPagingItems[index])
                        }
                    }

                    @ExperimentalFoundationApi
                    private fun <T : Any> LazyStaggeredGridScope.lazyItemsIndexed(
                        lazyPagingItems: LazyPagingItems<T>,
                        key: ((item: T?) -> Any)? = null,
                        itemContent: @Composable LazyStaggeredGridItemScope.(value: T?) -> Unit,
                    ) {
                        items(
                            lazyPagingItems.itemCount,
                            key?.let { { index -> key(lazyPagingItems[index]) } }
                        ) { index ->
                            itemContent(lazyPagingItems[index])
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Ratting Circle",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ChallengeRatingCircle(
                        challengeRating: String,
                        size: Dp,
                        modifier: Modifier = Modifier,
                        xp: String = "",
                        fontSize: TextUnit = 16.sp,
                        contentTopPadding: Dp = 0.dp,
                        xpFontSize: TextUnit = 10.sp,
                    ) = Box(
                        modifier = modifier
                            .widthIn(min = size)
                            .height(size + contentTopPadding)
                    ) {
                        DrawChallengeRatingCircle(
                            color = MaterialTheme.colors.surface,
                            canvasSize = size,
                            contentTopPadding = contentTopPadding
                        )
                        Column(
                            modifier = Modifier.background(
                                color = MaterialTheme.colors.surface,
                                shape = RoundedCornerShape(bottomEndPercent = 25)
                            )
                        ) {
                            Spacer(modifier = Modifier.height(4.dp))

                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .padding(vertical = 4.dp)
                                    .padding(top = contentTopPadding)
                            ) {
                                Text(
                                    challengeRating,
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = fontSize,
                                    color = MaterialTheme.colors.onSurface,
                                    textAlign = TextAlign.Center,
                                    maxLines = 1,
                                    modifier = Modifier
                                        .width(size - 16.dp)
                                )

                                if (xp.isNotBlank()) {
                                    Text(
                                        xp,
                                        fontWeight = FontWeight.Normal,
                                        fontSize = xpFontSize,
                                        color = MaterialTheme.colors.onSurface,
                                        textAlign = TextAlign.Center,
                                        maxLines = 1,
                                        modifier = Modifier
                                            .padding(start = 4.dp, end = 8.dp)
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    private fun DrawChallengeRatingCircle(
                        color: Color,
                        modifier: Modifier = Modifier,
                        canvasSize: Dp = 48.dp,
                        contentTopPadding: Dp = 0.dp
                    ) = Canvas(
                        modifier
                            .width(canvasSize)
                            .height(canvasSize + contentTopPadding)
                    ) {
                        val width = size.width - 8.dp.toPx()
                        val height = size.height - 8.dp.toPx()
                        val topPadding = contentTopPadding.toPx()
                        drawPath(
                            path = Path().apply {
                                lineTo(0f, 0f)
                                lineTo(width, 0f)
                                lineTo(width, topPadding)
                                cubicTo(
                                    x1 = width, y1 = height * 0.7f,
                                    x2 = width * 0.7f, y2 = height,
                                    x3 = 0f, y3 = height
                                )
                                close()
                            },
                            color = color
                        )
                    }

                    @Preview
                    @Composable
                    private fun ChallengeRatingWithXpPreview() {
                        HunterTheme {
                            ChallengeRatingCircle(
                                challengeRating = "10",
                                xp = "111k XP",
                                size = 62.dp,
                                fontSize = 18.sp,
                                xpFontSize = 14.sp,
                            )
                        }
                    }

                    @Preview
                    @Composable
                    private fun ChallengeRatingWithXpPreviewWithDifferentSize() {
                        HunterTheme {
                            ChallengeRatingCircle(
                                challengeRating = "10",
                                xp = "155k XP",
                                size = 56.dp,
                                fontSize = 16.sp,
                                contentTopPadding = 24.dp
                            )
                        }
                    }

                    @Preview
                    @Composable
                    private fun ChallengeRatingPreview() {
                        HunterTheme {
                            ChallengeRatingCircle(challengeRating = "10", size = 48.dp)
                        }
                    }

                    @Preview
                    @Composable
                    private fun ChallengeRatingPreviewWithDifferentSize() {
                        HunterTheme {
                            ChallengeRatingCircle(
                                challengeRating = "10",
                                size = 56.dp,
                                fontSize = 16.sp,
                                contentTopPadding = 24.dp
                            )
                        }
                    }

                    @Preview
                    @Composable
                    private fun DrawChallengeRatingCirclePreview() {
                        HunterTheme {
                            DrawChallengeRatingCircle(Color.Blue)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn Horizontal ProgressBar",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/XDdevv/PLMayMiniChallenges2025 (Tab Horizontal to Scroll Title)
            KotlinCode(
                code = """
                     val mainListState = rememberLazyListState()

                    @Composable
                    fun ListHorizontalProgressbar(modifier: Modifier = Modifier, scrollState: LazyListState) {
                        val totalItems = remember { derivedStateOf { scrollState.layoutInfo } }.value.totalItemsCount
                        val visibleItems = remember { derivedStateOf { scrollState.layoutInfo } }.value.visibleItemsInfo.size
                        val firstVisibleItem = remember { derivedStateOf { scrollState.firstVisibleItemIndex } }.value

                        if (totalItems > visibleItems && visibleItems > 0) {
                            val offset = firstVisibleItem.toFloat() / totalItems

                            Box(
                                modifier = modifier
                                    .height(2.dp)
                                    .fillMaxWidth()
                                    .drawBehind {
                                        val scrollbarWidth = size.width * offset

                                        drawRoundRect(
                                            color = LessonTopicStudyTheme.PRIMARY.toColor(),
                                            topLeft = Offset(x = 0f, y = 0f),
                                            size = Size(width = scrollbarWidth, height = size.height),
                                            cornerRadius = CornerRadius(2.dp.toPx())
                                        )
                                    }
                            ) {

                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn Scroll Observer",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Beb3r/OpalTest.git
            KotlinCode(
                code = """
                    @Composable
                    fun LazyColumnScrollObserver(
                        listState: LazyListState,
                        onScrollOffsetChanged: (Int) -> Unit = {}
                    ) {
                        val firstVisibleItemIndex by remember {
                            derivedStateOf { listState.firstVisibleItemIndex }
                        }
                        val scrollOffset by remember {
                            derivedStateOf { (listState.firstVisibleItemScrollOffset / 2f).toInt() }
                        }
                        var map: Map<Int, Int> by remember {
                            mutableStateOf(emptyMap())
                        }
                        map = map.toMutableMap().apply { put(firstVisibleItemIndex, scrollOffset) }

                        val totalOffset by remember {
                            derivedStateOf { map.entries.sumOf { it.value } }
                        }
                        onScrollOffsetChanged(totalOffset)
                    }
                    
                    @Composable
                    fun BoxScope.AmbientBackground(
                        modifier: Modifier = Modifier,
                        offset: Float = 0f,
                        imageScaleX: Float = 1.3f,
                        imageScaleY: Float = 1.5f,
                        content: @Composable () -> Unit,
                    ) {
                        val context = LocalContext.current
                        val density = LocalDensity.current

                        AsyncImage(
                            modifier = modifier
                                .fillMaxWidth()
                                .fillMaxHeight(fraction = 0.5f)
                                .graphicsLayer {
                                    scaleY = imageScaleY
                                    scaleX = imageScaleX
                                    with(density) {
                                        translationY = (-32).dp.toPx() + (-offset)
                                    }
                                },
                            model = ImageRequest.Builder(context)
                                .data(LocalAppThemeData.current.ambientBackgroundGemDrawableResId)
                                .allowHardware(false)
                                .apply {
                                    transformations(
                                        listOf(
                                            BlurTransformation(
                                                context = context,
                                                radius = 25f,
                                                sampling = 8f,
                                            )
                                        )
                                    )
                                    crossfade(true)
                                }.build(),
                            contentDescription = "ambient background",
                            contentScale = ContentScale.Crop,
                        )
                        content()
                    }
                    
                    var ambienBackgroundOffset by remember {
                        mutableFloatStateOf(0f)
                    }
                
                    AmbientBackground(
                        offset = ambienBackgroundOffset,
                        content = {
                            HomeScreen(
                                onScrolled = {
                                    ambienBackgroundOffset = it
                                }
                            )
                        }
                    )
                    
                    @Composable
                    fun HomeScreen(
                        viewModel: HomeViewModel = koinViewModel(),
                        onScrolled: (Float) -> Unit,
                    ) {
                        HomeScreenContent(
                            onScrolled = onScrolled,
                        )
                    }
                    
                    @SuppressLint("UnusedMaterial3ScaffoldPaddingParameter")
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun HomeScreenContent(
                        onScrolled: (Float) -> Unit
                    ) {
                        val listState = rememberLazyListState()
                        LazyColumnScrollObserver(
                            listState = listState,
                            onScrollOffsetChanged = { offset ->
                                onScrolled(offset.toFloat())
                            }
                        )

                        LazyColumn(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp),
                            state = listState,
                        ) {

                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Pull To Refresh",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://medium.com/@ramadan123sayed/building-a-custom-pull-to-refresh-in-jetpack-compose-spring-physics-custom-indicator-with-a2cd4837b2f4
            KotlinCode(
                code = """
                    //Custom Rotating Arrow Indicator
                    
                    @Composable
                    private fun CustomRefreshIndicator(
                        progress: Float,
                        isRefreshing: Boolean,
                        offset: Float,
                        modifier: Modifier = Modifier
                    ) {
                        val density = LocalDensity.current
                        val indicatorSize = 40.dp

                    // Arrow rotation: 0° at start, 180° at threshold
                        val arrowRotation = (progress * 180f).coerceIn(0f, 180f)
                        // Infinite rotation when refreshing
                        val infiniteTransition = rememberInfiniteTransition(label = "refresh")
                        val spinRotation by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 360f,
                            animationSpec = infiniteRepeatable(tween(800, easing = LinearEasing)),
                            label = "spin"
                        )
                        // Scale: small at start, full at threshold
                        val scale = (progress * 1.2f).coerceIn(0f, 1f)
                        Box(
                            modifier = modifier
                                .offset { IntOffset(0, (offset * 0.4f).roundToInt() - with(density) { 20.dp.toPx() }.roundToInt()) }
                                .size(indicatorSize)
                                .graphicsLayer {
                                    scaleX = scale
                                    scaleY = scale
                                    rotationZ = if (isRefreshing) spinRotation else arrowRotation
                                    alpha = progress.coerceIn(0f, 1f)
                                }
                                .background(Color.White, CircleShape)
                                .border(1.5.dp, Color(0xFFE0E0E0), CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            if (isRefreshing) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(22.dp),
                                    strokeWidth = 2.5.dp,
                                    color = Color(0xFF2563EB)
                                )
                            } else {
                                Icon(
                                    Icons.Default.ArrowDownward,
                                    contentDescription = "Pull to refresh",
                                    tint = Color(0xFF2563EB),
                                    modifier = Modifier.size(22.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://medium.com/@ramadan123sayed/building-a-custom-pull-to-refresh-in-jetpack-compose-spring-physics-custom-indicator-with-a2cd4837b2f4
            KotlinCode(
                code = """
                    @Composable
                    fun CustomPullToRefresh(
                        isRefreshing: Boolean,
                        onRefresh: () -> Unit,
                        modifier: Modifier = Modifier,
                        refreshThreshold: Dp = 80.dp,
                        maxDragDistance: Dp = 150.dp,
                        content: @Composable () -> Unit
                    ) {
                        val density = LocalDensity.current
                        val refreshThresholdPx = with(density) { refreshThreshold.toPx() }
                        val maxDragPx = with(density) { maxDragDistance.toPx() }

                    var dragOffset by remember { mutableFloatStateOf(0f) }
                        val animatedOffset = remember { Animatable(0f) }
                        var hasTriggeredRefresh by remember { mutableStateOf(false) }
                        val haptic = LocalHapticFeedback.current
                        // Sync animated offset with drag
                        LaunchedEffect(isRefreshing) {
                            if (!isRefreshing && hasTriggeredRefresh) {
                                hasTriggeredRefresh = false
                                animatedOffset.animateTo(
                                    0f,
                                    spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow)
                                )
                            }
                        }
                        val nestedScrollConnection = remember {
                            object : NestedScrollConnection {
                                override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                                    // When pulling back up while indicator is showing
                                    if (dragOffset > 0 && available.y < 0) {
                                        val consumed = available.y.coerceAtLeast(-dragOffset)
                                        dragOffset += consumed
                                        return Offset(0f, consumed)
                                    }
                                    return Offset.Zero
                                }
                                override fun onPostScroll(
                                    consumed: Offset, available: Offset, source: NestedScrollSource
                                ): Offset {
                                    // Content is at top and user pulls down
                                    if (available.y > 0 && !isRefreshing) {
                                        // Rubber band: diminishing returns as you pull further
                                        val dampedDelta = available.y * (1f - (dragOffset / maxDragPx).coerceIn(0f, 1f))
                                        dragOffset = (dragOffset + dampedDelta).coerceIn(0f, maxDragPx)
                                        // Haptic when crossing threshold
                                        if (dragOffset >= refreshThresholdPx && !hasTriggeredRefresh) {
                                            haptic.performHapticFeedback(HapticFeedbackType.TextHandleMove)
                                        }
                                        return Offset(0f, available.y)
                                    }
                                    return Offset.Zero
                                }
                                override suspend fun onPostFling(consumed: Velocity, available: Velocity): Velocity {
                                    if (dragOffset >= refreshThresholdPx && !isRefreshing) {
                                        hasTriggeredRefresh = true
                                        onRefresh()
                                        // Animate to resting position
                                        animatedOffset.snapTo(dragOffset)
                                        animatedOffset.animateTo(
                                            refreshThresholdPx * 0.6f,
                                            spring(stiffness = Spring.StiffnessMedium)
                                        )
                                    } else {
                                        // Snap back
                                        animatedOffset.snapTo(dragOffset)
                                        animatedOffset.animateTo(
                                            0f,
                                            spring(dampingRatio = Spring.DampingRatioMediumBouncy)
                                        )
                                    }
                                    dragOffset = 0f
                                    return available
                                }
                            }
                        }
                        val displayOffset = if (dragOffset > 0) dragOffset else animatedOffset.value
                        val progress = (displayOffset / refreshThresholdPx).coerceIn(0f, 1.5f)
                        Box(modifier = modifier.nestedScroll(nestedScrollConnection)) {
                            // Content moves down with the pull
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .graphicsLayer { translationY = displayOffset * 0.5f }
                            ) {
                                content()
                            }
                            // Custom indicator
                            CustomRefreshIndicator(
                                progress = progress,
                                isRefreshing = isRefreshing,
                                offset = displayOffset,
                                modifier = Modifier.align(Alignment.TopCenter)
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/love-yuri/journal-kmp.git
            KotlinCode(
                code = """
                    //Pull To Refresh Custom UI
                    
                    @OptIn(FlowPreview::class)
                    @Composable
                    fun JournalListScreen(journals: List<Journal>) {
                        val listState = rememberLazyListState()
                        var isLoadingMore by remember { mutableStateOf(false) }
                        var isRefreshing by remember { mutableStateOf(false) }
                       
                        var y by remember { mutableFloatStateOf(0f) }
                        val scope = rememberCoroutineScope()

                        val refreshThreshold = 140f
                        val maxPullDistance = 200f

                        val animatedOffset by animateFloatAsState(
                            targetValue = if (isRefreshing) refreshThreshold else y,
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioNoBouncy,
                                stiffness = Spring.StiffnessLow
                            )
                        )

                        LaunchedEffect(listState, journals) {
                            snapshotFlow { listState.layoutInfo.visibleItemsInfo }
                                .debounce(100)
                                .collect { visibleItems ->
                                    if (visibleItems.isNotEmpty() && !isLoadingMore) {
                                        val lastVisibleItem = visibleItems.last()
                                        if (lastVisibleItem.index == journals.size) {
                                            isLoadingMore = true
                                            JournalService.nextPage()
                                            isLoadingMore = false
                                        }
                                    }
                                }
                        }

                        val pullRefreshState = remember {
                            derivedStateOf {
                                when {
                                    isRefreshing -> RefreshState.Refreshing
                                    y >= refreshThreshold -> RefreshState.ReadyToRefresh
                                    y > 0 -> RefreshState.Pulling
                                    else -> RefreshState.Idle
                                }
                            }
                        }

                        LazyColumn(
                            state = listState,
                            modifier = Modifier
                                .fillMaxSize()
                                .pointerInput(Unit) {
                                    awaitPointerEventScope {
                                        while (true) {
                                            val down = awaitFirstDown(requireUnconsumed = false)
                                            var dragStartY = down.position.y

                                            do {
                                                val event = awaitPointerEvent()
                                                event.changes.forEach { change ->
                                                    if (listState.firstVisibleItemIndex == 0 &&
                                                        listState.firstVisibleItemScrollOffset <= 0) {

                                                        val dragAmount = change.position.y - dragStartY
                                                        val newY = (y + dragAmount * 0.2f).coerceAtLeast(0f)
                                                        y = newY.coerceAtMost(maxPullDistance)

                                                        dragStartY = change.position.y
                                                    }
                                                }
                                            } while (event.changes.any { it.pressed })

                                            if (y >= refreshThreshold && !isRefreshing) {
                                                scope.launch {
                                                    isRefreshing = true
                                                    JournalService.refresh()
                                                    delay(300)
                                                    isRefreshing = false
                                                    y = 0f
                                                }
                                            } else {
                                                y = 0f
                                            }
                                        }
                                    }
                                }
                        ) {
                            item {
                                ElegantPullRefreshIndicator(
                                    offset = animatedOffset,
                                    refreshState = pullRefreshState.value,
                                    refreshThreshold = refreshThreshold
                                )
                            }
                        }
                    }
                    
                    enum class RefreshState {
                        Idle, Pulling, ReadyToRefresh, Refreshing
                    }

                    @Composable
                    fun ElegantPullRefreshIndicator(
                        offset: Float,
                        refreshState: RefreshState,
                        refreshThreshold: Float
                    ) {
                        val progress = (offset / refreshThreshold).coerceIn(0f, 1f)

                        val iconRotation by animateFloatAsState(
                            targetValue = when (refreshState) {
                                RefreshState.Refreshing -> 360f * 10 
                                RefreshState.ReadyToRefresh -> 180f
                                else -> progress * 180f
                            },
                            animationSpec = if (refreshState == RefreshState.Refreshing) {
                                infiniteRepeatable(
                                    animation = tween(1000, easing = LinearEasing),
                                    repeatMode = RepeatMode.Restart
                                )
                            } else {
                                spring(dampingRatio = Spring.DampingRatioMediumBouncy)
                            }
                        )
                        
                        val gradientColors = when (refreshState) {
                            RefreshState.ReadyToRefresh -> listOf(
                                Color(0xFF6B73FF), Color(0xFF9B59B6)
                            )
                            RefreshState.Refreshing -> listOf(
                                Color(0xFF00C9FF), Color(0xFF92FE9D)
                            )
                            else -> listOf(
                                Color(0xFFE8E8E8), Color(0xFFBDBDBD)
                            )
                        }

                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(1.dp)
                                .height(offset.dp)
                                .background(
                                    brush = Brush.verticalGradient(
                                        colors = listOf(
                                            Color.Transparent,
                                            Color.White.copy(alpha = 0.1f)
                                        )
                                    )
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            AnimatedVisibility(
                                visible = offset > 10f,
                                enter = fadeIn() + scaleIn(),
                                exit = fadeOut() + scaleOut()
                            ) {
                                Column(
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    verticalArrangement = Arrangement.Center
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .size(42.dp)
                                            .background(
                                                brush = Brush.radialGradient(
                                                    colors = gradientColors,
                                                    radius = 24f
                                                ),
                                                shape = CircleShape
                                            )
                                            .padding(2.dp)
                                            .background(
                                                Color.White.copy(alpha = 0.9f),
                                                CircleShape
                                            ),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = when (refreshState) {
                                                RefreshState.Refreshing -> "⟳"
                                                RefreshState.ReadyToRefresh -> "↑"
                                                else -> "↓"
                                            },
                                            fontSize = 24.sp,
                                            modifier = Modifier.rotate(iconRotation)
                                        )
                                    }

                                    Spacer(modifier = Modifier.height(6.dp))
                                    
                                    Text(
                                        text = when (refreshState) {
                                            RefreshState.Idle -> ""
                                            RefreshState.Pulling -> "Pull down to refresh"
                                            RefreshState.ReadyToRefresh -> "Release"
                                            RefreshState.Refreshing -> "Refreshing..."
                                        },
                                        color = when (refreshState) {
                                            RefreshState.ReadyToRefresh -> Color(0xFF9B59B6)
                                            RefreshState.Refreshing -> Color(0xFF00C9FF)
                                            else -> Color.Gray
                                        },
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Medium
                                    )

                                    if (refreshState == RefreshState.Pulling) {
                                        Spacer(modifier = Modifier.height(4.dp))
                                        LinearProgressIndicator(
                                            progress = { progress },
                                            modifier = Modifier
                                                .width(60.dp)
                                                .height(2.dp),
                                            color = Color(0xFF6B73FF),
                                            trackColor = Color.Gray.copy(alpha = 0.2f)
                                        )
                                    }
                                }
                            }

                            if (offset > 20f && refreshState != RefreshState.Refreshing) {
                                WaterDropEffect(progress = progress)
                            }
                        }
                    }

                    @Composable
                    fun WaterDropEffect(progress: Float) {
                        val dropSize by animateFloatAsState(
                            targetValue = (8 + progress * 12),
                            animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy)
                        )

                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .alpha(progress * 0.6f),
                            contentAlignment = Alignment.TopCenter
                        ) {
                            Canvas(
                                modifier = Modifier
                                    .size(dropSize.dp)
                                    .offset(y = (-dropSize / 2).dp)
                            ) {
                                val radius = size.minDimension / 2
                                drawCircle(
                                    color = Color(0xFF6B73FF).copy(alpha = 0.3f),
                                    radius = radius,
                                    center = center
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "LazyColumn Animation List",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    AnimatedVisibility(
                        visible = isVisible,
                        enter = slideInVertically(
                            animationSpec = tween(durationMillis = 400),
                            initialOffsetY = { it / 2 }
                        ) + fadeIn(
                            animationSpec = tween(durationMillis = 300)
                        )
                    ) { }
                """.trimIndent()
            )

            //https://github.com/sai-charan2003/Habit-Diary.git
            KotlinCode(
                code = """
                    //Material3 MediumFlexibleTopAppBar
                    
                    val scrollBehavior = TopAppBarDefaults.exitUntilCollapsedScrollBehavior()
    
                    @OptIn(ExperimentalMaterial3Api::class, ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun CustomMediumTopBar(
                        title : String,
                        subTitle : String = "",
                        scrollBehavior: TopAppBarScrollBehavior,
                        showBackButton : Boolean = false,
                        onBackClick : () -> Unit = { },
                        actions: @Composable RowScope.() -> Unit = {},

                    ) {
                        MediumFlexibleTopAppBar(
                            title = {
                                Text(title)
                            },
                            subtitle = {
                                if(subTitle.isNotEmpty()){
                                    Text(subTitle)
                                }

                            },
                            scrollBehavior = scrollBehavior,
                            colors = TopAppBarDefaults.topAppBarColors(
                                containerColor = MaterialTheme.colorScheme.surface,
                                scrolledContainerColor = MaterialTheme.colorScheme.surface
                            ),
                            navigationIcon = {
                                if(showBackButton){
                                    BackButton {
                                        onBackClick()
                                    }
                                }
                            },
                            actions = actions
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/love-yuri/journal-kmp.git (LazyColumn Animation)
            KotlinCode(
                code = """
                     @OptIn(ExperimentalMaterial3Api::class)
                     @Composable
                     fun TopBarBackButton(isNavigable: Boolean, navController: NavController, title: String, scrollBehavior: TopAppBarScrollBehavior) {
                         CenterAlignedTopAppBar(scrollBehavior = scrollBehavior, title = { Text(text = title, style = MaterialTheme.typography.titleLarge) }, navigationIcon = {
                             if(isNavigable){
                                 IconButton(onClick = { navController.navigateUp() }) {
                                     Icon(modifier = Modifier.size(AssistChipDefaults.IconSize),
                                          painter = painterResource(id = R.drawable.left_btn) ,
                                          contentDescription = stringResource(R.string.back_content_description
                                          )
                                     )
                                 }
                             }
                         })
                     }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun CustomerListScreen(
                        navigationController: NavHostController
                    ) {
                        val scrollBehavior = TopAppBarDefaults.enterAlwaysScrollBehavior()
                        val listState = rememberLazyListState()
                        val isItemVisible = remember { mutableStateMapOf<Int, Boolean>() }
                        val animationFinishedHashMap = remember { mutableStateMapOf<Int, Boolean>() }

                        LaunchedEffect(Unit) {
                            animationFinishedHashMap[0] = true
                        }

                        Scaffold(modifier = Modifier
                            .nestedScroll(scrollBehavior.nestedScrollConnection)
                            .fillMaxSize(),
                            topBar = {
                                TopBarBackButton(
                                    isNavigable = false,
                                    navController = navigationController,
                                    title = stringResource(R.string.home_top_bar_title),
                                    scrollBehavior = scrollBehavior
                                )
                            },
                            content = { padding ->
                                val customerList = mockList()
                                LazyColumn(
                                    modifier = Modifier
                                        .padding(padding)
                                        .fillMaxSize(),
                                    state = listState
                                ) {
                                    /*item {
                                        Column {
                                            SearchBarComponent(
                                                iconDrawable = R.drawable.search_vector,
                                                text = "Search patients",
                                                contentDescription = "Search patients"
                                            )
                                        }
                                    }*/

                                    itemsIndexed(customerList) { actualIndex, customer ->
                                        AnimatedItemListComponent(
                                            actualIndex,
                                            customer,
                                            customerList.size,
                                            isItemVisible,
                                            listState,
                                            animationFinishedHashMap,
                                            navigationController
                                        )
                                    }
                                }
                            })
                    }

                    @Composable
                    private fun AnimatedItemListComponent(
                        actualIndex: Int,
                        customer: Customer,
                        customerListSize: Int,
                        isItemVisible: SnapshotStateMap<Int, Boolean>,
                        listState: LazyListState,
                        animationFinishedHashMap: SnapshotStateMap<Int, Boolean>,
                        navigationController: NavHostController
                    ) {
                        val difference =
                            if (listState.layoutInfo.totalItemsCount == 0) 0 else (listState.layoutInfo.totalItemsCount - customerListSize)
                        val correctedIndex =
                            if (actualIndex == listState.layoutInfo.totalItemsCount) listState.layoutInfo.totalItemsCount else actualIndex + difference
                        val isVisible = remember {
                            derivedStateOf {
                                listState.layoutInfo.visibleItemsInfo.firstOrNull { correctedIndex == it.index } != null
                            }
                        }

                        if (correctedIndex <= listState.firstVisibleItemIndex) {
                            isItemVisible[correctedIndex] = true
                            animationFinishedHashMap[correctedIndex - 1] = true
                        }

                        if (isVisible.value) {
                            isItemVisible[correctedIndex] = true
                            animationFinishedHashMap[listState.firstVisibleItemIndex] = true
                        } else {
                            isItemVisible[correctedIndex] = false
                        }

                        val initPx = with(LocalDensity.current) {
                            350.dp.toPx().roundToInt()
                        }
                        val targetValue =
                            if (isItemVisible[correctedIndex] == true && (correctedIndex == 0 || animationFinishedHashMap[correctedIndex - 1] == true)) {
                                0
                            } else {
                                initPx
                            }

                        val offset by animateIntAsState(targetValue = targetValue,
                            label = "offset",
                            animationSpec = tween(
                                durationMillis = if (listState.isScrollInProgress) 200 else 50
                            ),
                            finishedListener = {
                                animationFinishedHashMap[correctedIndex] = true
                            })

                        Box(modifier = Modifier
                            .fillMaxWidth()
                            .offset(x = offset.dp)
                            .clickable { navigationController.navigate("DetailScreen/") }) {
                            ItemCustomerComponent(customer, navigationController)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "SegmentedListItem",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/sai-charan2003/Habit-Diary.git
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun CustomListItem(
                        indexItem: IndexItem,
                        headLineContent: @Composable () -> Unit,
                        supportingContent : @Composable (() -> Unit)? = null,
                        trailingContent: @Composable (() -> Unit)? = null,
                        leadingContent : @Composable (() -> Unit)? = null,
                        modifier: Modifier = Modifier,
                        onClick: (() -> Unit)? = null
                    ) {

                        SegmentedListItem(
                            content = headLineContent,
                            onClick ={ onClick?.invoke() },
                            supportingContent = {
                                if (supportingContent != null) {
                                    Column(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        Spacer(Modifier.height(8.dp))
                                        supportingContent()
                                    }
                                }
                            },
                            trailingContent = trailingContent,
                            leadingContent = leadingContent,
                            shapes = customListItemShapes(indexItem),
                            colors = ListItemDefaults.colors(containerColor = MaterialTheme.colorScheme.surfaceContainer),
                            modifier = modifier.padding(ListItemDefaults.SegmentedGap),
                            contentPadding = PaddingValues(16.dp)

                            )
                    }
                    
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun customListItemShapes(
                        indexItem: IndexItem,
                        defaultShape : ListItemShapes = ListItemDefaults.shapes()
                    ): ListItemShapes {
                        val overrideShape = ShapeDefaults.Large
                        val shape = when (indexItem) {
                            IndexItem.FIRST -> {
                                val defaultBaseShape = defaultShape.shape
                                if (defaultBaseShape is CornerBasedShape) {
                                    defaultShape.copy(
                                        shape =
                                            defaultBaseShape.copy(
                                                topStart = overrideShape.topStart,
                                                topEnd = overrideShape.topEnd,
                                            )
                                    )
                                } else {
                                    defaultShape
                                }
                            }
                            IndexItem.LAST -> {
                                val defaultBaseShape = defaultShape.shape
                                if (defaultBaseShape is CornerBasedShape) {
                                    defaultShape.copy(
                                        shape =
                                            defaultBaseShape.copy(
                                                bottomStart = overrideShape.bottomStart,
                                                bottomEnd = overrideShape.bottomEnd,
                                            )
                                    )
                                } else {
                                    defaultShape
                                }
                            }

                            IndexItem.MIDDLE -> {
                                defaultShape
                            }

                            IndexItem.FIRST_AND_LAST -> {
                                defaultShape.copy(
                                    shape = ShapeDefaults.Large
                                )


                            }
                        }

                        return shape
                    }

                    fun <T> List<T>.indexItemFor(index: Int): IndexItem {
                        return when {
                            size == 1 -> IndexItem.FIRST_AND_LAST
                            index == 0 -> IndexItem.FIRST
                            index == size - 1 -> IndexItem.LAST
                            else -> IndexItem.MIDDLE
                        }
                    }

                    enum class IndexItem {
                        FIRST,
                        LAST,
                        MIDDLE,
                        FIRST_AND_LAST
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Shimmer",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //ShimmerBrush Continues Animation

                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            enableEdgeToEdge()
                            setContent {
                                ComposeShimmersTheme {
                                    Scaffold(modifier = Modifier.fillMaxSize()) { innerPadding ->
                                        Box(
                                            modifier = Modifier
                                                .fillMaxSize()
                                                .padding(innerPadding)
                                                .background(Color(0xFF535454)),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Column(
                                                verticalArrangement = Arrangement.spacedBy(30.dp),
                                                horizontalAlignment = Alignment.CenterHorizontally
                                            ) {
                                                val showShimmer = remember { mutableStateOf(true) }
                                                val imageUrl =
                                                    "https://banner2.cleanpng.com/20240204/wic/transparent-spider-man-intense-spider-man-in-red-hoodie-with-1710887687018.webp"
                                                AsyncImage(
                                                    modifier = Modifier
                                                        .clip(RoundedCornerShape(10.dp))
                                                        .background(
                                                            shimmerBrush(
                                                                targetValue = 1300f,
                                                                showShimmer = showShimmer.value
                                                            )
                                                        )
                                                        .padding(start = 0.dp, bottom = 2.dp)
                                                        .size(300.dp),
                                                    onSuccess = { showShimmer.value = false },
                                                    model = imageUrl,
                                                    contentDescription = null,
                                                    contentScale = ContentScale.Crop,
                                                )

                                                ImageShimmer(imageUrl, modifier = Modifier.size(300.dp))
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        @Composable
                        fun shimmerBrush(showShimmer: Boolean = true, targetValue: Float = 1000f): Brush {
                            val color = Color(0xFF535454)
                            return if (showShimmer) {
                                val shimmerColors = listOf(
                                    color.copy(alpha = 0.6f),
                                    color.copy(alpha = 0.2f),
                                    color.copy(alpha = 0.6f),
                                )
                                val transition = rememberInfiniteTransition(label = "")
                                val translateAnimation = transition.animateFloat(
                                    initialValue = 0f,
                                    targetValue = targetValue,
                                    animationSpec = infiniteRepeatable(
                                        animation = tween(800, easing = LinearEasing), repeatMode = RepeatMode.Restart
                                    ),
                                    label = ""
                                )
                                Brush.linearGradient(
                                    colors = shimmerColors,
                                    start = Offset.Zero,
                                    end = Offset(x = translateAnimation.value, y = translateAnimation.value)
                                )
                            } else {
                                Brush.linearGradient(
                                    colors = listOf(Color.Transparent, Color.Transparent),
                                    start = Offset.Zero,
                                    end = Offset.Zero
                                )
                            }
                        }

                        @Composable
                        fun ImageShimmer(imageUrl: String, modifier: Modifier = Modifier) {
                            val context = LocalContext.current
                            val shimmerColors = listOf(
                                Color.White.copy(.0f),
                                Color.White.copy(0.5f),
                                Color.White.copy(.0f)
                            )
                            val widthOfShadowBrush = 200
                            val transition = rememberInfiniteTransition(label = "")
                            val translateAnimation = transition.animateFloat(
                                initialValue = 0f,
                                targetValue = 1000f,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(
                                        durationMillis = 1000 + widthOfShadowBrush,
                                        easing = LinearEasing
                                    ),
                                    repeatMode = RepeatMode.Restart
                                ),
                                label = ""
                            )
                            val brush = Brush.horizontalGradient(
                                colors = shimmerColors,
                                startX = translateAnimation.value - widthOfShadowBrush,
                                endX = translateAnimation.value + widthOfShadowBrush
                            )
                            val bitmapState = remember { mutableStateOf<Bitmap?>(null) }
                            LaunchedEffect(imageUrl) {
                                val loader = ImageLoader(context)
                                val request = ImageRequest.Builder(context)
                                    //pass image url in data if required
                                    .data(R.drawable.spiderman)
                                    .allowHardware(false)
                                    .build()
                                val result = withContext(Dispatchers.IO) {
                                    loader.execute(request)
                                }

                                if (result is SuccessResult) {
                                    bitmapState.value = (result.drawable as BitmapDrawable).bitmap
                                }
                            }
                            bitmapState.value?.let { bitmap ->
                                Canvas(
                                    modifier = modifier
                                        .fillMaxWidth()
                                        .graphicsLayer(alpha = 0.99f)
                                ) {
                                    drawImage(
                                        image = bitmap.asImageBitmap(),
                                        dstSize = IntSize(size.width.roundToInt(), size.height.roundToInt())
                                    )
                                    drawRect(brush, blendMode = BlendMode.SrcAtop)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //MultiChoiceSegmentedButtonRow

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun BreviaryPartWithSelectionLayout(
                        title: String,
                        optionSelected: Int,
                        options: List<String>,
                        texts: List<AnnotatedString>,
                        onOptionSelected: (Int) -> Unit
                    ) {
                        Column {
                            MultiChoiceSegmentedButtonRow(modifier = Modifier.fillMaxWidth()) {
                                options.forEachIndexed { index, option ->
                                    SegmentedButton(
                                        shape = SegmentedButtonDefaults.itemShape(
                                            index = index,
                                            count = options.size
                                        ),
                                        icon = {
                                            SegmentedButtonDefaults.Icon(
                                                active = optionSelected == index,
                                                activeContent = {
                                                    Icon(
                                                        imageVector = Icons.Filled.Done,
                                                        contentDescription = null,
                                                        modifier = Modifier.size(SegmentedButtonDefaults.IconSize)
                                                    )
                                                },
                                                inactiveContent = null
                                            )
                                        },
                                        onCheckedChange = { onOptionSelected(index) },
                                        checked = optionSelected == index
                                    ) {
                                        Text(option)
                                    }
                                }
                            }
                            HeightSpacer(8.dp)
                            AnimatedContent(
                                targetState = optionSelected,
                                transitionSpec = {
                                    slideIntoContainer(
                                        animationSpec = tween(400, easing = EaseIn),
                                        towards = AnimatedContentTransitionScope.SlideDirection.Down
                                    ).togetherWith(
                                        slideOutOfContainer(
                                            animationSpec = tween(400, easing = EaseOut),
                                            towards = AnimatedContentTransitionScope.SlideDirection.Down
                                        )
                                    )
                                },
                                label = title
                            ) { targetState ->
                                Text(text = texts[targetState], fontSize = 15.sp)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    internal fun ImageGallery(
                        imagesList: List<DrawableResource>,
                        onDetailsClicked: (DrawableResource) -> Unit,
                        onImageLongClick: (DrawableResource) -> Unit
                    ) {
                        HorizontalMultiBrowseCarousel(
                            state = rememberCarouselState { imagesList.size },
                            modifier = Modifier.fillMaxWidth().height(221.dp).padding(16.dp),
                            preferredItemWidth = 186.dp,
                            itemSpacing = 8.dp,
                            contentPadding = PaddingValues(horizontal = 16.dp)
                        ) { i ->
                        
                            val item = imagesList[i]
                            Image(
                                painter = painterResource(item),
                                contentDescription = null,
                                modifier = Modifier.height(210.dp).maskClip(MaterialTheme.shapes.extraLarge)
                                    .pointerInput(Unit) {
                                        detectTapGestures(onLongPress = {
                                            // Notify client regarding the long press event.
                                            onImageLongClick(item)
                                        }, onTap = {
                                            // Notify client regarding the single click event.
                                            onDetailsClicked(item)
                                        })
                                    },
                                contentScale = ContentScale.Crop,
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //ParallaxCarouselImage
                    val pagerState = rememberPagerState(pageCount = {  })

                    @Composable
                    fun ParallaxCarouselImage(
                      state: PagerState,
                      currentPage: Int,
                      imageUrl: String?,
                      modifier: Modifier = Modifier,
                      shape: Shape = RectangleShape,
                      overlayContent: @Composable () -> Unit = {},
                    ) {
                      val currentPageOffset = calculatePageOffset(state, currentPage)
                      val cardTranslationX = lerp(100f, 0f, 1f - currentPageOffset)
                      val cardScaleX = lerp(0.8f, 1f, 1f - currentPageOffset.absoluteValue.coerceIn(0f, 1f))
                      val screenWidth = LocalConfiguration.current.screenWidthDp
                      val parallaxOffset = currentPageOffset * screenWidth * 2f

                      Box(
                        modifier = modifier
                          .fillMaxWidth()
                          .graphicsLayer {
                            scaleX = cardScaleX
                            translationX = cardTranslationX
                          },
                      ) {
                        AsyncImageComposable(
                          modifier = Modifier
                            .fillMaxSize()
                            .clip(shape)
                            .graphicsLayer {
                              translationX = lerp(10f, 0f, 1f - currentPageOffset) + parallaxOffset
                            },
                          model = imageUrl,
                          contentDescription = null,
                          contentScale = ContentScale.Crop,
                        )

                        overlayContent()
                      }
                    }

                    private fun calculatePageOffset(state: PagerState, currentPage: Int): Float {
                      return (state.currentPage + state.currentPageOffsetFraction - currentPage).coerceIn(-1f, 1f)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Full Screen Scroll (Top Slider Image and Bottom List of Items)

                    data class ChildLayout(
                        val contentType: String = "",
                        val content: @Composable (item: Any?) -> Unit = {},
                        val items: List<Any> = emptyList()
                    )

                    @Composable
                    fun VerticalScrollLayout(
                        modifier: Modifier = Modifier,
                        vararg childLayouts: ChildLayout
                    ) {
                        LazyColumn(modifier = modifier.fillMaxSize()) {
                            childLayouts.forEach { child ->
                                if (child.items.isEmpty()) {
                                    loadItem(child)
                                } else {
                                    loadItems(child)
                                }
                            }
                        }
                    }

                    /**
                     * Use single item compose if no scroll or only horizontal scroll needed
                     */
                    private fun LazyListScope.loadItem(childLayout: ChildLayout) {
                        item(contentType = childLayout.contentType) {
                            childLayout.content(null)
                        }
                    }

                    /**
                     * Use load multiple items to the lazy column when nested vertical scroll is needed
                     */
                    private fun LazyListScope.loadItems(childLayout: ChildLayout) {
                        items(items = childLayout.items) { item ->
                            childLayout.content(item)
                        }
                    }

                    /**
                     * Compose items only if general item is successfully casted to defined class
                     */
                    @Suppress("UNCHECKED_CAST")
                    @Composable
                    fun <T: Any> LoadItemAfterSafeCast(
                        generalItem: Any?,
                        composeWithSafeItem: @Composable (item: T) -> Unit
                    ) {
                        (generalItem as? T)?.let { safeItem ->
                            composeWithSafeItem(safeItem)
                        }
                    }

                    //UI
                     VerticalScrollLayout(
                        modifier = Modifier.fillMaxSize()
                            .background(color = MaterialTheme.colorScheme.background),
                        ChildLayout(
                            contentType = "Section Name",
                            content = {

                            }
                        )

                        ChildLayout(
                            contentType = HomeScreenContents.DESTINATION_SMALL_SECTION.name,
                            items = []],
                            content = { item ->
                                LoadItemAfterSafeCast<Destination>(item) {
                                    destinationSmallItem(it) {

                                    }
                                }
                            }
                        )
                     )

                     @Composable
                     fun destinationSmallItem(
                         destination: Destination,
                         onItemClicked: (Destination) -> Unit
                     ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Usecase Validation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    data class ValidationResult(
                        val successful: Boolean,
                        val errorMessage: String? = null
                    )

                    enum class ValidationRule {
                        Required,
                        MinLength,
                        MinValue,
                        NotEmpty,
                        NotNull
                    }

                    class ValidateEmail {
                        operator fun invoke(email: String): ValidationResult {
                            if (email.isBlank()) {
                                return ValidationResult(false, "Error: Data wajib diisi!")
                            }
                            if (!PatternsCompat.EMAIL_ADDRESS.matcher(email).matches()) {
                                return ValidationResult(false, "Error: Format email salah!")
                            }
                            return ValidationResult(true, null)
                        }
                    }

                    class ValidateNotBlank {
                        operator fun invoke(
                            input: String
                        ): ValidationResult {
                            if (input.isBlank()) {
                                return ValidationResult(false, "Error: Data wajib diisi!")
                            }
                            return ValidationResult(true, null)
                        }
                    }

                    class ValidateInput {
                        operator fun invoke(
                            input: Any?,
                            validationRule: ValidationRule,
                            requiredLength: Int? = null
                        ): ValidationResult {
                            return when (validationRule) {
                                ValidationRule.Required -> {
                                    if (input is String && input.isEmpty()) {
                                        ValidationResult(false, "Error: Data wajib diisi!")
                                    } else {
                                        ValidationResult(true, null)
                                    }
                                }

                                ValidationRule.MinLength -> {
                                    requiredLength?.let {
                                        if (input is String && input.length < it) {
                                            ValidationResult(false, it)
                                        } else {
                                            ValidationResult(true, null)
                                        }
                                    }
                                        ?: throw IllegalArgumentException("requiredLength must be provided for MinLength validation rule")
                                }

                                ValidationRule.MinValue -> {
                                    requiredLength?.let {
                                        if (input is String && input.toInt() < it) {
                                            ValidationResult(false, it)
                                        } else {
                                            ValidationResult(true, null)
                                        }
                                    }
                                        ?: throw IllegalArgumentException("requiredLength must be provided for MinValue validation rule")
                                }

                                ValidationRule.NotEmpty -> {
                                    if (input is List<*> && input.isNotEmpty()) {
                                        ValidationResult(true, null)
                                    } else {
                                        ValidationResult(false, "Silahkan pilih minimal 1 produk")
                                    }
                                }

                                ValidationRule.NotNull -> {
                                    if (input != null) {
                                        ValidationResult(true, null)
                                    } else {
                                        ValidationResult(false, "Silahkan isi semua data yang diperlukan")
                                    }
                                }
                            }
                        }
                    }

                    //UseCaseManager
                    class UseCaseManager() {
                        fun validateEmailUseCase() = ValidateEmail()

                        fun validateNotBlankUseCase() = ValidateNotBlank()

                        fun validateInput() = ValidateInput()
                    }

                    //Validation
                    class LoginViewModel(
                        useCaseManager: UseCaseManager
                    ) : ViewModel() {

                        var state by mutableStateOf(LoginState())
                        private val login: Login = useCaseManager.loginUseCase()
                        private val validateNotBlankUseCase = useCaseManager.validateNotBlankUseCase()
                        private val validateEmailUseCase = useCaseManager.validateEmailUseCase()

                        private fun validateInput() {
                            val validatedEmail = validateNotBlankUseCase(state.email)
                            val validatedEmail1 = validateEmailUseCase(state.email)

                            val hasError = listOf(
                                validatedEmail,
                                validatedEmail1
                            ).any { !it.successful }
                            if (hasError) {
                                state = state.copy(
                                    emailError = (validatedEmail.errorMessage ?: validatedEmail1.errorMessage) ?: ""
                                )
                                return
                            }
                            clearInputErrors()
                            processLogin()
                        }

                        private fun validateAllInput() {
                            val state = state.value
                            val validateCartItems = validateInputUseCase(state.cartItems, ValidationRule.NotEmpty)
                            val validateCustomer = validateInputUseCase(state.customer, ValidationRule.NotNull)

                            val resultError = listOf(
                                validateCartItems,
                                validateCustomer
                            ).any { !it.successful }

                            if (resultError) {
                                if (validateCustomer.errorMessage != null) {
                                    _state.value = _state.value.copy(
                                        validationErrors = state.validationErrors.plus("Anda wajib memilih 1 pelanggan")
                                    )
                                }

                                if (validateCartItems.errorMessage != null) {
                                    _state.value = _state.value.copy(
                                        validationErrors = state.validationErrors.plus("Anda harus memasukkan setidaknya 1 produk dalam keranjang")
                                    )
                                }
                            } else {
                                createTransaction()
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "sealed Validation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/pitampoudel/komposeauth.git
            KotlinCode(
                code = """
                    //ValidationResult.kt
                    sealed class ValidationResult {
                        data object Success : ValidationResult()
                        open class Error(val message: GeneralValidationError) : ValidationResult()

                        fun isSuccess() = this is Success
                        fun error(): GeneralValidationError? {
                            if (this is Error) {
                                return this.message
                            }
                            return null
                        }
                    }
                    
                    //GeneralValidationError.kt
                    enum class GeneralValidationError {
                        VALIDATION_ERROR_MUST_NOT_BE_BLANK,
                        VALIDATION_ERROR_MUST_BE_SELECTED,
                        VALIDATION_ERROR_TOO_SHORT,
                        VALIDATION_ERROR_INVALID_URL,
                        VALIDATION_ERROR_INVALID_EMAIL,
                        VALIDATION_ERROR_INVALID_PHONE_NUMBER,
                        VALIDATION_ERROR_PASSWORDS_DONT_MATCH,
                        VALIDATION_ERROR_MUST_NOT_BE_EMPTY
                    }
                    
                    //RegexUtils.kt
                    fun isUrlValid(url: String): Boolean {
                        val pattern = "^((https?|ftp)://)?" + // Protocol
                                "(([a-zA-Z0-9._-]+\\.[a-zA-Z]{2,6})|" + // Domain name
                                "([0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}))" + // OR IP address
                                "(:[0-9]{1,4})?" + // Port
                                "(/\\S*)?$" // Path
                        return Regex(pattern).matches(url)
                    }
                    fun String?.isValidEmail(): Boolean {
                        return this?.matches(Regex("^[\\w-.]+@([\\w-]+\\.)+[\\w-]{2,4}$")) == true
                    }
                    
                    //ValidateNotNull.kt
                    object ValidateNotNull {
                        operator fun <T> invoke(value: T?): ValidationResult {
                            return if (value == null) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_MUST_BE_SELECTED)
                            } else {
                                ValidationResult.Success
                            }
                        }
                    }
                    
                    //ValidateNotBlank.kt
                    object ValidateNotBlank {
                        operator fun invoke(value: String): ValidationResult {
                            return if (value.isBlank()) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_MUST_NOT_BE_BLANK)
                            } else {
                                ValidationResult.Success
                            }
                        }
                    }
                    
                    //ValidateNotEmpty.kt
                    object ValidateNotEmpty {
                        operator fun <T> invoke(value: List<T>): ValidationResult {
                            return if (value.isEmpty()) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_MUST_NOT_BE_EMPTY)
                            } else {
                                ValidationResult.Success
                            }
                        }
                    }
                    
                    //ValidateUrlOrBlank.kt
                    object ValidateUrlOrBlank {
                        operator fun invoke(value: String): ValidationResult {
                            return if (value.isNotBlank() && !isUrlValid(value)) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_INVALID_URL)
                            } else {
                                ValidationResult.Success
                            }

                        }
                    }
                    
                    //ValidateOtpCode.kt
                    object ValidateOtpCode {
                        const val OTP_LENGTH = 6
                        operator fun invoke(code: String): ValidationResult {
                            return if (code.isBlank()) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_MUST_NOT_BE_BLANK)
                            } else if (code.length != OTP_LENGTH || code.toIntOrNull() == null) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_TOO_SHORT)
                            } else {
                                ValidationResult.Success
                            }
                        }
                    }
                    
                    //ValidateEmail.kt
                    object ValidateEmail {
                        operator fun invoke(email: String): ValidationResult {
                            return if (email.isBlank()) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_MUST_NOT_BE_BLANK)
                            } else if (!matchesEmailRegex(email)) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_INVALID_EMAIL)
                            } else {
                                ValidationResult.Success
                            }
                        }

                        private fun matchesEmailRegex(email: CharSequence): Boolean {
                            val pattern = "^[\\w.-]+@([\\w\\-]+\\.)+[A-Z]{2,4}$"
                            return Regex(pattern, RegexOption.IGNORE_CASE).matches(email)
                        }
                    }
                    
                    //ValidatePassword.kt
                    object ValidatePassword {
                        operator fun invoke(password: String): ValidationResult {
                            return if (password.isBlank()) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_MUST_NOT_BE_BLANK)
                            } else if (!matchesPasswordRequirements(password)) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_TOO_SHORT)
                            } else {
                                ValidationResult.Success
                            }
                        }

                        private fun matchesPasswordRequirements(password: String): Boolean {
                            val pattern = "^(?=.*\\W)(?!.* ).{8,16}$"
                            return Regex(pattern).matches(password)
                        }
                    }
                    
                    //ValidateConfirmPassword.kt
                    object ValidateConfirmPassword {
                        operator fun invoke(password: String, confirmPassword: String): ValidationResult {
                            return if (confirmPassword.isBlank()) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_MUST_NOT_BE_BLANK)
                            } else if (confirmPassword != password) {
                                ValidationResult.Error(GeneralValidationError.VALIDATION_ERROR_PASSWORDS_DONT_MATCH)
                            } else {
                                ValidationResult.Success
                            }
                        }
                    }
                    
                    //ChangePasswordRequest.kt
                    @Serializable
                    data class ChangePasswordRequest(
                        @SerialName("newPassword")
                        val newPassword: String,
                        @SerialName("confirmPassword")
                        val confirmPassword: String
                    ) {
                        init {
                            require(ValidatePassword(newPassword) is ValidationResult.Success)
                            require(
                                ValidateConfirmPassword(
                                    password = newPassword,
                                    confirmPassword = confirmPassword
                                ) is ValidationResult.Success
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Usecase invoke() with flowOn()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class LoginUseCases(private val authRepository: AuthRepository) {
                        suspend fun invoke(email: String, password: String) = flow<Result<LoginResult>> {
                            emit (authRepository.login(email = email, password = password))
                        }.catch { error →
                            emit (Result. failure(error))
                        }.flowOn(Dispatchers. IO)
                    }

                    //Better Way
                    class LoginUseCases(private val authRepository: AuthRepository) {
                        fun invoke(email: String, password: String): Flow<Result<LoginResult>> =
                            flow {
                                    emit(runCatching { authRepository.login(email, password) })
                            }.flowOn(Dispatchers.IO)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //AutoComplete

                    @Composable
                    fun AutoCompleteSelectBar(entries: List<String>) {

                        var itemElement by remember { mutableStateOf("") }
                        val heightTextFields by remember { mutableStateOf(55.dp) }
                        var textFieldSize by remember { mutableStateOf(Size.Zero) }
                        var expanded by remember { mutableStateOf(false) }
                        val interactionSource = remember { MutableInteractionSource() }

                        Column(
                            modifier = Modifier
                                .padding(30.dp)
                                .fillMaxWidth()
                                .clickable(
                                    interactionSource = interactionSource,
                                    indication = null,
                                    onClick = {
                                        expanded = false
                                    }
                                )
                        ) {

                            Text(
                                text = "Words",
                                style = MaterialTheme.typography.titleSmall,
                                color = Color.Black
                            )

                            Column(modifier = Modifier.fillMaxWidth()) {
                                TextField(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(heightTextFields)
                                        .border(
                                            width = 1.5.dp,
                                            color = Color.Black,
                                            shape = RoundedCornerShape(8.dp)
                                        )
                                        .onGloballyPositioned { coordinates ->
                                            textFieldSize = coordinates.size.toSize()
                                        },
                                    value = itemElement,
                                    onValueChange = {
                                        itemElement = it
                                        expanded = true
                                    },
                                    // Perform action when the TextField is clicked
                                    interactionSource = remember { MutableInteractionSource() }
                                        .also { interactionSource ->
                                            LaunchedEffect(interactionSource) {
                                                interactionSource.interactions.collect { interaction ->
                                                    if (interaction is PressInteraction.Release) {
                                                        expanded = !expanded
                                                    }
                                                }
                                            }
                                        },
                                    placeholder = { Text("Enter word") },
                                    colors = TextFieldDefaults.colors(
                                        focusedContainerColor = Color.Transparent,
                                        unfocusedContainerColor = Color.Transparent,
                                        disabledContainerColor = Color.Transparent,
                                        cursorColor = Color.Black,
                                        focusedIndicatorColor = Color.Transparent,
                                        unfocusedIndicatorColor = Color.Transparent,
                                    ),
                                    textStyle = TextStyle(
                                        color = Color.Black,
                                        fontSize = 16.sp
                                    ),
                                    keyboardOptions = KeyboardOptions(
                                        keyboardType = KeyboardType.Text,
                                        imeAction = ImeAction.Done
                                    ),
                                    singleLine = true,
                                    trailingIcon = {
                                        Icon(
                                            modifier = Modifier.size(24.dp),
                                            imageVector = Icons.Rounded.KeyboardArrowDown,
                                            contentDescription = "arrow",
                                            tint = Color.Black
                                        )
                                    }
                                )

                                AnimatedVisibility(visible = expanded) {
                                    Card(
                                        modifier = Modifier
                                            .width(textFieldSize.width.dp),
                                        shape = RoundedCornerShape(8.dp)
                                        /* colors = CardDefaults.cardColors(
                                             containerColor = Color.Transparent
                                         )*/
                                    ) {
                                        LazyColumn(
                                            modifier = Modifier.heightIn(max = 200.dp),
                                        ) {

                                            if (itemElement.isNotEmpty()) {
                                                items(
                                                    entries.filter {
                                                        it.lowercase().contains(itemElement.lowercase())
                                                    }.sorted()
                                                ) {
                                                    ItemElement(title = it) { title ->
                                                        itemElement = title
                                                        expanded = false
                                                    }
                                                }
                                            } else {
                                                items(
                                                    entries.sorted()
                                                ) {
                                                    ItemElement(title = it) { title ->
                                                        itemElement = title
                                                        expanded = false
                                                    }
                                                }
                                            }

                                        }

                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    fun ItemElement(
                        title: String,
                        onSelect: (String) -> Unit
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {
                                    onSelect(title)
                                }
                                .padding(vertical = 12.dp, horizontal = 15.dp)
                        ) {
                            Text(text = title, fontSize = 16.sp)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Set Custom Duration for Material3 Snackbar
                    
                    sealed class SnackbarDurationWrapper {
                        data class Standard(val duration: SnackbarDuration) : SnackbarDurationWrapper()
                        data class Custom(val millis: Long) : SnackbarDurationWrapper()

                        companion object {
                            fun fromMillis(milliseconds: Long): SnackbarDurationWrapper {
                                require(milliseconds > 0) { "Duration must be positive" }
                                return Custom(milliseconds)
                            }

                            fun fromSeconds(seconds: Double): SnackbarDurationWrapper {
                                return Custom((seconds * 1000).toLong())
                            }
                        }

                        fun toMillis(): Long {
                            return when (this) {
                                is Standard -> when (duration) {
                                    SnackbarDuration.Short -> 4000L
                                    SnackbarDuration.Long -> 10000L
                                    SnackbarDuration.Indefinite -> Long.MAX_VALUE
                                }
                                is Custom -> millis
                            }
                        }
                    }
                    
                    import androidx.compose.material3.SnackbarDuration
                    import androidx.compose.material3.SnackbarHostState
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.selects.select

                    suspend fun SnackbarHostState.showCustomSnackbar(
                        message: String,
                        actionLabel: String? = null,
                        durationWrapper: SnackbarDurationWrapper = SnackbarDurationWrapper.Standard(SnackbarDuration.Short)
                    ): SnackbarResult {
                        return when (durationWrapper) {
                            is SnackbarDurationWrapper.Standard -> {
                                showSnackbar(message, actionLabel, durationWrapper.duration)
                            }

                            is SnackbarDurationWrapper.Custom -> {
                                coroutineScope {
                                    val snackbarDeferred = async {
                                        showSnackbar(
                                            message = message,
                                            actionLabel = actionLabel,
                                            duration = SnackbarDuration.Indefinite
                                        )
                                    }

                                    val timerDeferred = async {
                                        delay(durationWrapper.millis)
                                        SnackbarResult.Dismissed
                                    }

                                    val result = select<SnackbarResult> {
                                        snackbarDeferred.onAwait { it }
                                        timerDeferred.onAwait { it }
                                    }

                                    // If timer finishes first, dismiss Snackbar manually
                                    if (result == SnackbarResult.Dismissed) {
                                        currentSnackbarData?.dismiss()
                                    }

                                    snackbarDeferred.cancel()
                                    timerDeferred.cancel()

                                    result
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun CustomSnackbarExample(snackbarHostState: SnackbarHostState) {
                        val scope = rememberCoroutineScope()

                        Button(onClick = {
                            scope.launch {
                                snackbarHostState.showCustomSnackbar(
                                    message = "Item deleted",
                                    actionLabel = "Undo",
                                    durationWrapper = SnackbarDurationWrapper.fromMillis(5000) // 5 seconds
                                )
                            }
                        }) {
                            Text("Show Snackbar (5s)")
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //SnackBar

                    import androidx.compose.animation.AnimatedVisibility
                    import androidx.compose.animation.core.FastOutLinearInEasing
                    import androidx.compose.animation.core.LinearOutSlowInEasing
                    import androidx.compose.animation.core.tween
                    import androidx.compose.animation.slideInVertically
                    import androidx.compose.animation.slideOutVertically
                    import androidx.compose.desktop.ui.tooling.preview.Preview
                    import androidx.compose.foundation.clickable
                    import androidx.compose.foundation.layout.*
                    import androidx.compose.foundation.shape.RoundedCornerShape
                    import androidx.compose.material.icons.Icons
                    import androidx.compose.material.icons.outlined.Close
                    import androidx.compose.material3.Icon
                    import androidx.compose.material3.MaterialTheme
                    import androidx.compose.material3.Snackbar
                    import androidx.compose.material3.Text
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.collectAsState
                    import androidx.compose.ui.Alignment
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.text.style.TextAlign
                    import androidx.compose.ui.unit.dp
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.delay
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.launch
                    import kotlinx.coroutines.sync.Semaphore
                    import java.util.concurrent.ConcurrentLinkedQueue

                    object SnackBar {
                        private val msgQueue = ConcurrentLinkedQueue<String>()
                        private val msgList = MutableList(2) { MutableStateFlow("") }

                        private val CoroutineScope = CoroutineScope(Dispatchers.Default)
                        private var isRunningSemaphore = Semaphore(2)

                        init {
                            CoroutineScope.launch {
                                delay(100)
                                msgCheck()
                            }
                        }

                        private fun msgCheck() {
                            for (i in 0 until msgList.size) {
                                CoroutineScope.launch {
                                    isRunningSemaphore.acquire()
                                    while (true) {
                                        if (msgList[i].value.isBlank()) {
                                            val poll = msgQueue.poll() ?: break
                                            msgList[i].value = poll
                                            delay(3000)
                                            msgList[i].value = ""
                                            delay(200)
                                        }
                                    }
                                    isRunningSemaphore.release()
                                }
                            }
                        }

                        @Composable
                        fun SnackBarItem(index: Int) {
                            val msg = msgList[index].collectAsState()

                            LaunchedEffect(msg) {
                                if (msg.value.isNotBlank()) {
                                    delay(3000)
                                    msgList[index].value = ""
                                    delay(50)
                                    msgList[index].value = msgQueue.poll() ?: ""
                                }
                            }
                            AnimatedVisibility(
                                visible = msg.value.trim().isNotBlank(),
                                enter = slideInVertically(
                                    initialOffsetY = { fullHeight -> -fullHeight },
                                    animationSpec = tween(durationMillis = 150, easing = LinearOutSlowInEasing)
                                ),
                                exit = slideOutVertically(
                                    targetOffsetY = { fullHeight -> -fullHeight },
                                    animationSpec = tween(durationMillis = 250, easing = FastOutLinearInEasing)
                                )
                            ) {
                                Box(modifier = Modifier.fillMaxWidth()) {
                                    Snackbar(
                                        modifier = Modifier.padding(top = 5.dp, start = 10.dp, end = 10.dp)
                                            .height(60.dp)
                                            .wrapContentWidth()
                    //                      .width((35 * msg.value.length).dp)
                                            .align(Alignment.Center),
                                        shape = RoundedCornerShape(25),
                                        containerColor = MaterialTheme.colorScheme.secondaryContainer,
                                        contentColor = MaterialTheme.colorScheme.onSecondaryContainer,
                                        action = {
                                            Box(modifier = Modifier.fillMaxHeight().wrapContentWidth()) {
                                                Icon(
                                                    imageVector = Icons.Outlined.Close, contentDescription = "Close the SnackBar",
                                                    modifier = Modifier.clickable(onClick = { msgList[index].value = "" })
                                                        .align(Alignment.Center),
                                                    tint = MaterialTheme.colorScheme.onSecondaryContainer
                                                )
                                            }
                                        }
                                    ) {
                                        Text(msg.value, modifier = Modifier.align(Alignment.Center).fillMaxWidth(), textAlign = TextAlign.Center)
                                    }
                                }
                            }
                        }

                        @Composable
                        fun SnackBarList() {
                            Column(
                                modifier = Modifier.fillMaxSize().padding(bottom = 15.dp),
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.aligned(Alignment.Bottom)
                            ) {
                                repeat(msgList.size) {
                                    SnackBarItem(it)
                                }
                            }
                        }

                        fun postMsg(msg: String) {
                            if (msgQueue.size > 6) {
                                msgQueue.poll()
                            }
                            msgQueue.add(msg)
                            msgCheck()
                        }

                    }

                    @Composable
                    @Preview
                    fun previewSnackBar() {
                        AppTheme {
                            SnackBar.postMsg("TEST")
                            SnackBar.SnackBarItem(0)
                        }
                    }

                    private val LightColors = lightColorScheme(
                        primary = md_theme_light_primary,
                        onPrimary = md_theme_light_onPrimary,
                        primaryContainer = md_theme_light_primaryContainer,
                        onPrimaryContainer = md_theme_light_onPrimaryContainer,
                        secondary = md_theme_light_secondary,
                        onSecondary = md_theme_light_onSecondary,
                        secondaryContainer = md_theme_light_secondaryContainer,
                        onSecondaryContainer = md_theme_light_onSecondaryContainer,
                        tertiary = md_theme_light_tertiary,
                        onTertiary = md_theme_light_onTertiary,
                        tertiaryContainer = md_theme_light_tertiaryContainer,
                        onTertiaryContainer = md_theme_light_onTertiaryContainer,
                        error = md_theme_light_error,
                        errorContainer = md_theme_light_errorContainer,
                        onError = md_theme_light_onError,
                        onErrorContainer = md_theme_light_onErrorContainer,
                        background = md_theme_light_background,
                        onBackground = md_theme_light_onBackground,
                        surface = md_theme_light_surface,
                        onSurface = md_theme_light_onSurface,
                        surfaceVariant = md_theme_light_surfaceVariant,
                        onSurfaceVariant = md_theme_light_onSurfaceVariant,
                        outline = md_theme_light_outline,
                        inverseOnSurface = md_theme_light_inverseOnSurface,
                        inverseSurface = md_theme_light_inverseSurface,
                        inversePrimary = md_theme_light_inversePrimary,
                        surfaceTint = md_theme_light_surfaceTint,
                        outlineVariant = md_theme_light_outlineVariant,
                        scrim = md_theme_light_scrim,
                    )


                    private val DarkColors = darkColorScheme(
                        primary = md_theme_dark_primary,
                        onPrimary = md_theme_dark_onPrimary,
                        primaryContainer = md_theme_dark_primaryContainer,
                        onPrimaryContainer = md_theme_dark_onPrimaryContainer,
                        secondary = md_theme_dark_secondary,
                        onSecondary = md_theme_dark_onSecondary,
                        secondaryContainer = md_theme_dark_secondaryContainer,
                        onSecondaryContainer = md_theme_dark_onSecondaryContainer,
                        tertiary = md_theme_dark_tertiary,
                        onTertiary = md_theme_dark_onTertiary,
                        tertiaryContainer = md_theme_dark_tertiaryContainer,
                        onTertiaryContainer = md_theme_dark_onTertiaryContainer,
                        error = md_theme_dark_error,
                        errorContainer = md_theme_dark_errorContainer,
                        onError = md_theme_dark_onError,
                        onErrorContainer = md_theme_dark_onErrorContainer,
                        background = md_theme_dark_background,
                        onBackground = md_theme_dark_onBackground,
                        surface = md_theme_dark_surface,
                        onSurface = md_theme_dark_onSurface,
                        surfaceVariant = md_theme_dark_surfaceVariant,
                        onSurfaceVariant = md_theme_dark_onSurfaceVariant,
                        outline = md_theme_dark_outline,
                        inverseOnSurface = md_theme_dark_inverseOnSurface,
                        inverseSurface = md_theme_dark_inverseSurface,
                        inversePrimary = md_theme_dark_inversePrimary,
                        surfaceTint = md_theme_dark_surfaceTint,
                        outlineVariant = md_theme_dark_outlineVariant,
                        scrim = md_theme_dark_scrim,
                    )

                    @Composable
                    fun AppTheme(
                      useDarkTheme: Boolean = isSystemInDarkTheme(),
                      content: @Composable() () -> Unit
                    ) {
                      val colors = if (!useDarkTheme) {
                        LightColors
                      } else {
                        DarkColors
                      }

                      MaterialTheme(
                        colorScheme = colors,
                        content = content
                      )
                    }
                    """.trimIndent()
            )

            //https://github.com/flyfishxu/vetra-ui.git
            KotlinCode(
                code = """
                    //Multiple SnackBar With Mutex
                    
                    //Access
                    @Composable
                    fun NotifyScreen() {
                        val colors = VetraTheme.colors
                        val typography = VetraTheme.typography
                        val notifyHostState = rememberNotifyHostState()
                        val coroutineScope = rememberCoroutineScope()
                        
                        //onClick
                        coroutineScope.launch {
                            notifyHostState.showWarning("Please review your input")
                        }
                    }
                    
                    /**
                     * Vetra Notify Host State
                     *
                     * State holder for managing notifications in the application.
                     * Supports displaying multiple notifications simultaneously with height limit.
                     * Automatically removes oldest notifications when exceeding screen height limit.
                     *
                     */
                    @Stable
                    class NotifyHostState {
                        private val mutex = Mutex()
                        private val _notifications = mutableStateListOf<NotifyItem>()
                        private val _visibilityMap = mutableStateMapOf<String, Boolean>()
                        private val _heightMap = mutableStateMapOf<String, Int>()
                        private val internalScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

                        internal var maxHeightPx = 0

                        /**
                         * Single notification mode - when enabled, only one notification is shown at a time.
                         * New notifications will automatically dismiss the existing one with animation.
                         */
                        var singleNotificationMode: Boolean = false

                        /**
                         * All currently visible notifications
                         */
                        val notifications: List<NotifyItem>
                            get() = _notifications.toList()

                        /**
                         * Get visibility state for a notification
                         */
                        fun isVisible(id: String): Boolean = _visibilityMap[id] ?: false

                        /**
                         * Internal: Set notification height
                         */
                        internal fun setNotificationHeight(id: String, height: Int) {
                            _heightMap[id] = height
                        }

                        /**
                         * Calculate total height of all notifications
                         */
                        private fun calculateTotalHeight(): Int {
                            return _notifications.sumOf { item ->
                                _heightMap[item.id] ?: 80 // Use measured height or default estimate
                            }
                        }

                        /**
                         * Check if adding a new notification would exceed height limit
                         * and remove oldest notifications if needed
                         */
                        private suspend fun checkAndRemoveOldestIfNeeded() {
                            if (maxHeightPx <= 0) return

                            val estimatedNewHeight = 80 // Estimated height for new notification

                            // Remove oldest notifications until we have space for the new one
                            while (_notifications.isNotEmpty()) {
                                val totalHeight = calculateTotalHeight() + estimatedNewHeight
                                if (totalHeight <= maxHeightPx) break

                                val oldestId = _notifications.first().id
                                dismiss(oldestId)
                            }
                        }

                        /**
                         * Show a notification with the given parameters
                         *
                         * @param message The notification message
                         * @param type The type of notification (Info, Success, Warning, Danger)
                         * @param duration Duration in milliseconds before auto-dismiss (null for no auto-dismiss)
                         * @param dismissible Whether the notification can be manually dismissed
                         * @return The ID of the created notification
                         */
                        @OptIn(ExperimentalUuidApi::class)
                        suspend fun showNotify(
                            message: String,
                            type: NotifyType = NotifyType.Info,
                            duration: Long? = NotifyDefaultDuration,
                            dismissible: Boolean = true
                        ): String {
                            val id = Uuid.random().toString()
                            val notifyItem = NotifyItem(
                                id = id,
                                data = NotifyData(
                                    message = message,
                                    type = type,
                                    duration = duration,
                                    dismissible = dismissible
                                )
                            )

                            // If single notification mode is enabled, dismiss all existing notifications first
                            if (singleNotificationMode && _notifications.isNotEmpty()) {
                                val existingIds = _notifications.map { it.id }
                                
                                // Trigger exit animation for all existing notifications at once
                                mutex.withLock {
                                    existingIds.forEach { existingId ->
                                        _visibilityMap[existingId] = false
                                    }
                                }
                                
                                // Don't wait for animation, let them animate out while new one comes in
                                // Remove after animation completes (NotifyExitDuration = 250ms, using 300ms for safety)
                                internalScope.launch {
                                    delay(300)
                                    mutex.withLock {
                                        existingIds.forEach { existingId ->
                                            _notifications.removeAll { it.id == existingId }
                                            _visibilityMap.remove(existingId)
                                            _heightMap.remove(existingId)
                                        }
                                    }
                                }
                            } else {
                                // Check if we need to remove old notifications (multi-notification mode)
                                if (maxHeightPx > 0) {
                                    checkAndRemoveOldestIfNeeded()
                                }
                            }

                            mutex.withLock {
                                _notifications.add(notifyItem)
                                _visibilityMap[id] = false
                            }

                            // Delay to allow composition, then trigger enter animation
                            delay(50)
                            mutex.withLock {
                                _visibilityMap[id] = true
                            }

                            return id
                        }

                        /**
                         * Show an info notification
                         *
                         * @param message The notification message
                         * @param duration Auto-dismiss duration (null for no auto-dismiss)
                         * @param dismissible Whether manual dismiss is allowed
                         * @return The ID of the created notification
                         */
                        suspend fun showInfo(
                            message: String,
                            duration: Long? = NotifyDefaultDuration,
                            dismissible: Boolean = true
                        ): String = showNotify(message, NotifyType.Info, duration, dismissible)

                        /**
                         * Show a success notification
                         *
                         * @param message The notification message
                         * @param duration Auto-dismiss duration (null for no auto-dismiss)
                         * @param dismissible Whether manual dismiss is allowed
                         * @return The ID of the created notification
                         */
                        suspend fun showSuccess(
                            message: String,
                            duration: Long? = NotifyDefaultDuration,
                            dismissible: Boolean = true
                        ): String = showNotify(message, NotifyType.Success, duration, dismissible)

                        /**
                         * Show a warning notification
                         *
                         * @param message The notification message
                         * @param duration Auto-dismiss duration (null for no auto-dismiss)
                         * @param dismissible Whether manual dismiss is allowed
                         * @return The ID of the created notification
                         */
                        suspend fun showWarning(
                            message: String,
                            duration: Long? = NotifyDefaultDuration,
                            dismissible: Boolean = true
                        ): String = showNotify(message, NotifyType.Warning, duration, dismissible)

                        /**
                         * Show a danger/error notification
                         *
                         * @param message The notification message
                         * @param duration Auto-dismiss duration (null for no auto-dismiss)
                         * @param dismissible Whether manual dismiss is allowed
                         * @return The ID of the created notification
                         */
                        suspend fun showDanger(
                            message: String,
                            duration: Long? = NotifyDefaultDuration,
                            dismissible: Boolean = true
                        ): String = showNotify(message, NotifyType.Danger, duration, dismissible)

                        /**
                         * Dismiss a specific notification by ID
                         *
                         * @param id The ID of the notification to dismiss
                         */
                        suspend fun dismiss(id: String) {
                            // First, trigger exit animation
                            mutex.withLock {
                                _visibilityMap[id] = false
                            }

                            // Wait for exit animation to complete
                            delay(300)

                            // Then remove from lists
                            mutex.withLock {
                                _notifications.removeAll { it.id == id }
                                _visibilityMap.remove(id)
                                _heightMap.remove(id)
                            }
                        }

                        /**
                         * Clear all notifications
                         */
                        suspend fun clearAll() {
                            // Trigger exit animations for all
                            mutex.withLock {
                                _notifications.forEach { item ->
                                    _visibilityMap[item.id] = false
                                }
                            }

                            // Wait for animations
                            delay(300)

                            // Clear all data
                            mutex.withLock {
                                _notifications.clear()
                                _visibilityMap.clear()
                                _heightMap.clear()
                            }
                        }
                    }

                    /**
                     * Internal notification item with unique ID
                     */
                    data class NotifyItem(
                        val id: String,
                        val data: NotifyData
                    )

                    /**
                     * Remember a NotifyHostState instance
                     */
                    @Composable
                    fun rememberNotifyHostState(): NotifyHostState {
                        return remember { NotifyHostState() }
                    }

                    /**
                     * Vetra Notify Host
                     *
                     * Container for displaying notifications managed by NotifyHostState.
                     * Place this at the top level of your screen or app to show notifications.
                     * 
                     * Supports two display modes:
                     * - Multiple notifications: Shows multiple notifications stacked (max 50% of screen height)
                     * - Single notification mode: Shows only one notification at a time. When enabled via
                     *   `hostState.singleNotificationMode = true`, new notifications will automatically
                     *   dismiss existing ones with smooth animations.
                     *
                     * @param hostState The state holder managing notifications
                     * @param modifier Modifier for the host container
                     */
                    @Composable
                    fun VetraNotifyHost(
                        hostState: NotifyHostState,
                        modifier: Modifier = Modifier
                    ) {
                        val scope = rememberCoroutineScope()

                        BoxWithConstraints(modifier = modifier.fillMaxWidth()) {
                            val maxHeight = maxHeight
                            val density = LocalDensity.current

                            // Set max height limit (50% of screen height)
                            LaunchedEffect(maxHeight) {
                                hostState.maxHeightPx = with(density) { (maxHeight / 2).roundToPx() }
                            }

                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .heightIn(max = maxHeight / 2),
                                verticalArrangement = Arrangement.spacedBy(0.dp)
                            ) {
                                hostState.notifications.forEach { notifyItem ->
                                    key(notifyItem.id) {
                                        val isVisible = hostState.isVisible(notifyItem.id)

                                        VetraNotify(
                                            visible = isVisible,
                                            data = notifyItem.data,
                                            onDismiss = {
                                                scope.launch {
                                                    hostState.dismiss(notifyItem.id)
                                                }
                                            },
                                            modifier = Modifier.onSizeChanged { size ->
                                                hostState.setNotificationHeight(notifyItem.id, size.height)
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    // ============================================================================
                    // Previews
                    // ============================================================================

                    @Preview
                    @Composable
                    private fun VetraNotifyHostPreview() {
                        VetraTheme {
                            val notifyHostState = rememberNotifyHostState()

                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(VetraTheme.colors.canvas)
                            ) {
                                // Sample content
                                Column(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .background(VetraTheme.colors.canvas),
                                    verticalArrangement = Arrangement.Center,
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        "Main Content",
                                        style = VetraTheme.typography.headingLg.copy(
                                            color = VetraTheme.colors.textPrimary
                                        )
                                    )

                                    Column(
                                        modifier = Modifier.fillMaxWidth(),
                                        verticalArrangement = Arrangement.spacedBy(12.dp),
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        VetraButton(onClick = {
                                            kotlinx.coroutines.MainScope().launch {
                                                notifyHostState.showInfo("This is an info notification")
                                            }
                                        }) {
                                            Text("Show Info")
                                        }

                                        VetraButton(onClick = {
                                            kotlinx.coroutines.MainScope().launch {
                                                notifyHostState.showSuccess("Operation completed successfully!")
                                            }
                                        }) {
                                            Text("Show Success")
                                        }

                                        VetraButton(onClick = {
                                            kotlinx.coroutines.MainScope().launch {
                                                notifyHostState.showWarning("Please review your changes")
                                            }
                                        }) {
                                            Text("Show Warning")
                                        }

                                        VetraButton(onClick = {
                                            kotlinx.coroutines.MainScope().launch {
                                                notifyHostState.showDanger("An error occurred")
                                            }
                                        }) {
                                            Text("Show Error")
                                        }
                                    }
                                }

                                // Notify host overlay
                                VetraNotifyHost(
                                    hostState = notifyHostState,
                                    modifier = Modifier.align(Alignment.TopCenter)
                                )
                            }
                        }
                    }

                    @Preview
                    @Composable
                    private fun VetraNotifyHostDarkPreview() {
                        VetraTheme(darkMode = true) {
                            val notifyHostState = rememberNotifyHostState()

                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(VetraTheme.colors.canvas)
                            ) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .background(VetraTheme.colors.canvas),
                                    verticalArrangement = Arrangement.Center,
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        "Dark Mode Preview",
                                        style = VetraTheme.typography.headingLg.copy(
                                            color = VetraTheme.colors.textPrimary
                                        )
                                    )

                                    VetraButton(onClick = {
                                        kotlinx.coroutines.MainScope().launch {
                                            notifyHostState.showSuccess("Dark mode notification")
                                        }
                                    }) {
                                        Text("Show Notification")
                                    }
                                }

                                VetraNotifyHost(
                                    hostState = notifyHostState,
                                    modifier = Modifier.align(Alignment.TopCenter)
                                )
                            }
                        }
                    }
                    
                    private data class Tuple4<out A, out B, out C, out D>(
                        val first: A,
                        val second: B,
                        val third: C,
                        val fourth: D
                    )

                    private val NotifyHorizontalPadding = 16.dp
                    private val NotifyVerticalPadding = 12.dp
                    private val NotifyIconSize = 20.dp
                    private val NotifyCloseIconSize = 18.dp
                    private val NotifyEnterDuration = 350
                    private val NotifyExitDuration = 250
                    val NotifyDefaultDuration = 3000L

                    /**
                     * Notification type defining the visual style and semantic meaning
                     */
                    enum class NotifyType {
                        Info,
                        Success,
                        Warning,
                        Danger
                    }

                    /**
                     * Notify data class containing all notification information
                     *
                     * @param message The notification message text
                     * @param type The type of notification (Info, Success, Warning, Danger)
                     * @param duration Duration in milliseconds before auto-dismiss (null for no auto-dismiss)
                     * @param dismissible Whether the notification can be manually dismissed
                     */
                    data class NotifyData(
                        val message: String,
                        val type: NotifyType = NotifyType.Info,
                        val duration: Long? = NotifyDefaultDuration,
                        val dismissible: Boolean = true
                    )

                    /**
                     * Vetra Notify Component
                     *
                     * Displays a notification banner that slides in from the top of the screen.
                     * Automatically dismisses after the specified duration if configured.
                     *
                     * @param visible Whether the notification is currently visible
                     * @param data Notification data containing message, type, and behavior
                     * @param onDismiss Callback invoked when the notification is dismissed
                     * @param modifier Modifier for the notification container
                     */
                    @Composable
                    fun VetraNotify(
                        visible: Boolean,
                        data: NotifyData,
                        onDismiss: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val colors = VetraTheme.colors
                        val shapes = VetraTheme.shapes
                        VetraTheme.shadows
                        val typography = VetraTheme.typography

                        // Auto-dismiss logic
                        LaunchedEffect(visible, data) {
                            if (visible && data.duration != null) {
                                delay(data.duration)
                                onDismiss()
                            }
                        }

                        // Get colors and icon based on type
                        val notifyStyle = when (data.type) {
                            NotifyType.Info -> Tuple4(
                                colors.canvasElevated,
                                colors.textPrimary,
                                colors.info,
                                Icons.Default.Info
                            )

                            NotifyType.Success -> Tuple4(
                                colors.canvasElevated,
                                colors.textPrimary,
                                colors.success,
                                Icons.Default.Check
                            )

                            NotifyType.Warning -> Tuple4(
                                colors.canvasElevated,
                                colors.textPrimary,
                                colors.warning,
                                Icons.Default.Warning
                            )

                            NotifyType.Danger -> Tuple4(
                                colors.canvasElevated,
                                colors.textPrimary,
                                colors.danger,
                                Icons.Default.Close
                            )
                        }

                        val backgroundColor = notifyStyle.first
                        val contentColor = notifyStyle.second
                        val accentColor = notifyStyle.third
                        val icon = notifyStyle.fourth

                        AnimatedVisibility(
                            visible = visible,
                            enter = slideInVertically(
                                animationSpec = spring(
                                    dampingRatio = 0.8f,
                                    stiffness = 300f
                                ),
                                initialOffsetY = { -it }
                            ) + fadeIn(
                                animationSpec = tween(durationMillis = NotifyEnterDuration)
                            ),
                            exit = shrinkVertically(
                                animationSpec = tween(durationMillis = NotifyExitDuration),
                                shrinkTowards = Alignment.Top
                            ) + fadeOut(
                                animationSpec = tween(durationMillis = NotifyExitDuration)
                            ),
                            modifier = modifier
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 16.dp, vertical = 8.dp)
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clip(shapes.sm)
                                        .border(width = 1.dp, color = notifyStyle.third, shape = shapes.sm)
                                        .background(backgroundColor)
                                        .padding(
                                            horizontal = NotifyHorizontalPadding,
                                            vertical = NotifyVerticalPadding
                                        ),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    // Leading icon with colored background
                                    Box(
                                        modifier = Modifier
                                            .size(32.dp)
                                            .background(accentColor.copy(alpha = 0.12f), shapes.full)
                                            .padding(6.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Icon(
                                            imageVector = icon,
                                            contentDescription = null,
                                            modifier = Modifier.size(NotifyIconSize),
                                            tint = accentColor
                                        )
                                    }

                                    // Message text
                                    CompositionLocalProvider(
                                        LocalContentColor provides contentColor,
                                        LocalTextStyle provides typography.bodyMd.copy(color = contentColor)
                                    ) {
                                        Text(
                                            text = data.message,
                                            modifier = Modifier.weight(1f)
                                        )
                                    }

                                    // Dismiss button
                                    if (data.dismissible) {
                                        val interactionSource = remember { MutableInteractionSource() }
                                        Box(
                                            modifier = Modifier
                                                .size(28.dp)
                                                .clip(shapes.full)
                                                .clickable(
                                                    onClick = onDismiss,
                                                    role = Role.Button,
                                                    interactionSource = interactionSource,
                                                    indication = vetraPressIndication(
                                                        pressAlpha = 0.08f
                                                    )
                                                ),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Icon(
                                                imageVector = Icons.Default.Close,
                                                contentDescription = "Dismiss",
                                                modifier = Modifier.size(NotifyCloseIconSize),
                                                tint = colors.textTertiary
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    /**
                     * Info Notify - Informational message
                     *
                     * Convenience function for creating an informational notification.
                     * Uses the info color scheme (blue) by default.
                     *
                     * @param visible Whether the notification is visible
                     * @param message The notification message
                     * @param onDismiss Callback when dismissed
                     * @param duration Auto-dismiss duration in milliseconds (null for no auto-dismiss)
                     * @param dismissible Whether manual dismiss is allowed
                     * @param modifier Modifier for the notification
                     */
                    @Composable
                    fun VetraNotifyInfo(
                        visible: Boolean,
                        message: String,
                        onDismiss: () -> Unit,
                        modifier: Modifier = Modifier,
                        duration: Long? = NotifyDefaultDuration,
                        dismissible: Boolean = true
                    ) {
                        VetraNotify(
                            visible = visible,
                            data = NotifyData(
                                message = message,
                                type = NotifyType.Info,
                                duration = duration,
                                dismissible = dismissible
                            ),
                            onDismiss = onDismiss,
                            modifier = modifier
                        )
                    }

                    /**
                     * Success Notify - Success confirmation
                     *
                     * Convenience function for creating a success notification.
                     * Uses the success color scheme (green) by default.
                     *
                     * @param visible Whether the notification is visible
                     * @param message The notification message
                     * @param onDismiss Callback when dismissed
                     * @param duration Auto-dismiss duration in milliseconds (null for no auto-dismiss)
                     * @param dismissible Whether manual dismiss is allowed
                     * @param modifier Modifier for the notification
                     */
                    @Composable
                    fun VetraNotifySuccess(
                        visible: Boolean,
                        message: String,
                        onDismiss: () -> Unit,
                        modifier: Modifier = Modifier,
                        duration: Long? = NotifyDefaultDuration,
                        dismissible: Boolean = true
                    ) {
                        VetraNotify(
                            visible = visible,
                            data = NotifyData(
                                message = message,
                                type = NotifyType.Success,
                                duration = duration,
                                dismissible = dismissible
                            ),
                            onDismiss = onDismiss,
                            modifier = modifier
                        )
                    }

                    /**
                     * Warning Notify - Warning message
                     *
                     * Convenience function for creating a warning notification.
                     * Uses the warning color scheme (amber/orange) by default.
                     *
                     * @param visible Whether the notification is visible
                     * @param message The notification message
                     * @param onDismiss Callback when dismissed
                     * @param duration Auto-dismiss duration in milliseconds (null for no auto-dismiss)
                     * @param dismissible Whether manual dismiss is allowed
                     * @param modifier Modifier for the notification
                     */
                    @Composable
                    fun VetraNotifyWarning(
                        visible: Boolean,
                        message: String,
                        onDismiss: () -> Unit,
                        modifier: Modifier = Modifier,
                        duration: Long? = NotifyDefaultDuration,
                        dismissible: Boolean = true
                    ) {
                        VetraNotify(
                            visible = visible,
                            data = NotifyData(
                                message = message,
                                type = NotifyType.Warning,
                                duration = duration,
                                dismissible = dismissible
                            ),
                            onDismiss = onDismiss,
                            modifier = modifier
                        )
                    }

                    /**
                     * Danger Notify - Error or critical message
                     *
                     * Convenience function for creating an error/danger notification.
                     * Uses the danger color scheme (red) by default.
                     *
                     * @param visible Whether the notification is visible
                     * @param message The notification message
                     * @param onDismiss Callback when dismissed
                     * @param duration Auto-dismiss duration in milliseconds (null for no auto-dismiss)
                     * @param dismissible Whether manual dismiss is allowed
                     * @param modifier Modifier for the notification
                     */
                    @Composable
                    fun VetraNotifyDanger(
                        visible: Boolean,
                        message: String,
                        onDismiss: () -> Unit,
                        modifier: Modifier = Modifier,
                        duration: Long? = NotifyDefaultDuration,
                        dismissible: Boolean = true
                    ) {
                        VetraNotify(
                            visible = visible,
                            data = NotifyData(
                                message = message,
                                type = NotifyType.Danger,
                                duration = duration,
                                dismissible = dismissible
                            ),
                            onDismiss = onDismiss,
                            modifier = modifier
                        )
                    }

                    // ============================================================================
                    // Previews
                    // ============================================================================

                    @Preview
                    @Composable
                    private fun VetraNotifyPreview() {
                        VetraTheme {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(VetraTheme.colors.canvas)
                            ) {
                                Column(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    // Info notification
                                    VetraNotifyInfo(
                                        visible = true,
                                        message = "This is an informational message",
                                        onDismiss = {}
                                    )

                                    // Success notification
                                    VetraNotifySuccess(
                                        visible = true,
                                        message = "Action completed successfully!",
                                        onDismiss = {}
                                    )

                                    // Warning notification
                                    VetraNotifyWarning(
                                        visible = true,
                                        message = "Please check your input before continuing",
                                        onDismiss = {}
                                    )

                                    // Danger notification
                                    VetraNotifyDanger(
                                        visible = true,
                                        message = "An error occurred while processing your request",
                                        onDismiss = {}
                                    )
                                }
                            }
                        }
                    }

                    @Preview
                    @Composable
                    private fun VetraNotifyNonDismissiblePreview() {
                        VetraTheme {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(VetraTheme.colors.canvas)
                            ) {
                                Column(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    // Non-dismissible info
                                    VetraNotifyInfo(
                                        visible = true,
                                        message = "This notification cannot be manually dismissed",
                                        onDismiss = {},
                                        dismissible = false
                                    )

                                    // Non-dismissible with no auto-dismiss
                                    VetraNotifyWarning(
                                        visible = true,
                                        message = "This notification stays until programmatically dismissed",
                                        onDismiss = {},
                                        duration = null,
                                        dismissible = false
                                    )
                                }
                            }
                        }
                    }

                    @Preview
                    @Composable
                    private fun VetraNotifyLongMessagePreview() {
                        VetraTheme {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(VetraTheme.colors.canvas)
                            ) {
                                VetraNotifySuccess(
                                    visible = true,
                                    message = "This is a notification with a much longer message that might wrap to multiple lines in the notification banner",
                                    onDismiss = {}
                                )
                            }
                        }
                    }

                    @Preview
                    @Composable
                    private fun VetraNotifyDarkPreview() {
                        VetraTheme(darkMode = true) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(VetraTheme.colors.canvas)
                            ) {
                                Column(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    VetraNotifyInfo(
                                        visible = true,
                                        message = "Dark mode info notification",
                                        onDismiss = {}
                                    )

                                    VetraNotifySuccess(
                                        visible = true,
                                        message = "Dark mode success notification",
                                        onDismiss = {}
                                    )

                                    VetraNotifyWarning(
                                        visible = true,
                                        message = "Dark mode warning notification",
                                        onDismiss = {}
                                    )

                                    VetraNotifyDanger(
                                        visible = true,
                                        message = "Dark mode error notification",
                                        onDismiss = {}
                                    )
                                }
                            }
                        }
                    }

                    @Preview
                    @Composable
                    private fun VetraNotifyAnimationPreview() {
                        VetraTheme {
                            var visible by remember { mutableStateOf(false) }

                            LaunchedEffect(Unit) {
                                while (true) {
                                    delay(1000)
                                    visible = true
                                    delay(3000)
                                    visible = false
                                }
                            }

                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(VetraTheme.colors.canvas)
                            ) {
                                VetraNotifySuccess(
                                    visible = visible,
                                    message = "This notification demonstrates the slide-in animation",
                                    onDismiss = { visible = false }
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Scroll AppBar Show
                    //https://github.com/WAHID-QANDIL/BorutoAppVersion1

                    @OptIn(ExperimentalSharedTransitionApi::class)
                    @Composable
                    fun SharedTransitionScope.DetailScreen(
                        poster: Poster,
                        pressOnBack: () -> Unit,
                        animatedVisibilityScope: AnimatedVisibilityScope
                    ) {
                        val scrollState = rememberScrollState()
                        var detailScroller by remember {
                            mutableStateOf(DetailsScroller(scrollState, Float.MIN_VALUE))
                        }

                        val transitionState = remember(detailScroller) { detailScroller.toolbarTransitionState }
                        val toolbarState = detailScroller.getToolbarState(LocalDensity.current)

                        // Transition that fades in/out the header with the image and the Toolbar
                        val transition = rememberTransition(transitionState, label = "")
                        val contentAlpha = transition.animateFloat(
                            transitionSpec = { spring(stiffness = Spring.StiffnessLow) }, label = ""
                        ) { toolbarTransitionState ->
                            if (toolbarTransitionState == ToolbarState.HIDDEN) 1f else 0f
                        }

                        Box(Modifier.fillMaxSize().background(MaterialTheme.colors.background)) {
                            DetailsContent(
                                scrollState = scrollState,
                                onNamePosition = { newNamePosition ->
                                    // Comparing to Float.MIN_VALUE as we are just interested on the original
                                    // position of name on the screen
                                    if (detailScroller.namePosition == Float.MIN_VALUE) {
                                        detailScroller =
                                            detailScroller.copy(namePosition = newNamePosition)
                                    }
                                },
                                item = poster,
                                contentAlpha = { contentAlpha.value },
                                animatedVisibilityScope = animatedVisibilityScope
                            )
                            DetailsToolbar(
                                toolbarState, poster.name, pressOnBack,
                                contentAlpha = { contentAlpha.value }
                            )
                        }
                    }

                    @OptIn(ExperimentalSharedTransitionApi::class)
                    @Composable
                    private fun SharedTransitionScope.DetailsContent(
                        scrollState: ScrollState,
                        onNamePosition: (Float) -> Unit,
                        item: Poster,
                        contentAlpha: () -> Float,
                        animatedVisibilityScope: AnimatedVisibilityScope
                    ) {
                        val typography = MaterialTheme.typography

                        Column(
                            modifier = Modifier.verticalScroll(scrollState)
                        ) {
                            AsyncImage(
                                model = item.poster,
                                contentDescription = null,
                                modifier = Modifier.sharedElement(
                                    state = rememberSharedContentState(key = item.id),
                                    animatedVisibilityScope = animatedVisibilityScope
                                )
                                    .fillMaxWidth()
                                    .height(disney_448_dp)
                                    .alpha(contentAlpha()),
                                contentScale = ContentScale.Crop,
                            )
                            Spacer(
                                Modifier
                                    .height(disney_16_dp)
                                    .onGloballyPositioned { onNamePosition(it.positionInWindow().y) })
                            Text(
                                modifier = Modifier.padding(disney_8_dp),
                                text = item.description,
                                style = typography.body1,
                                fontSize = 18.sp,
                                color = MaterialTheme.colors.onSurface
                            )
                            Text(
                                modifier = Modifier.padding(disney_8_dp),
                                text = item.plot,
                                style = typography.body1,
                                fontSize = 18.sp,
                                color = MaterialTheme.colors.onSurface
                            )
                        }
                    }

                    @Composable
                    private fun DetailsToolbar(
                        toolbarState: ToolbarState,
                        name: String,
                        pressOnBack: () -> Unit,
                        contentAlpha: () -> Float
                    ) {
                        if (toolbarState.isShown) {
                            DetailsToolbar(
                                name = name,
                                onBackClick = pressOnBack,
                            )
                        } else {
                            HeaderActions(
                                onBackClick = pressOnBack,
                                modifier = Modifier.alpha(contentAlpha())
                            )
                        }
                    }

                    @Composable
                    private fun DetailsToolbar(
                        name: String,
                        onBackClick: () -> Unit
                    ) {
                        Surface {
                            TopAppBar(
                                backgroundColor = MaterialTheme.colors.surface
                            ) {
                                IconButton(
                                    onBackClick,
                                    Modifier.align(Alignment.CenterVertically)
                                ) {
                                    Icon(
                                        Icons.AutoMirrored.Filled.ArrowBack,
                                        contentDescription = "back",
                                        tint = MaterialTheme.colors.onSurface
                                    )
                                }
                                Text(
                                    text = name,
                                    style = MaterialTheme.typography.h6,
                                    // As title in TopAppBar has extra inset on the left, need to do this: b/158829169
                                    modifier = Modifier
                                        .weight(1f)
                                        .fillMaxSize()
                                        .wrapContentSize(Alignment.CenterStart)
                                )
                            }
                        }
                    }

                    @Composable
                    private fun HeaderActions(
                        onBackClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Row(
                            modifier = modifier
                                .fillMaxSize()
                                .padding(top = disney_12_dp),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            val iconModifier = Modifier
                                .sizeIn(
                                    maxWidth = disney_32_dp,
                                    maxHeight = disney_32_dp
                                )
                                .background(
                                    color = MaterialTheme.colors.surface,
                                    shape = CircleShape
                                )

                            IconButton(
                                onClick = onBackClick,
                                modifier = Modifier
                                    .padding(start = disney_12_dp)
                                    .then(iconModifier)
                            ) {
                                Icon(
                                    Icons.AutoMirrored.Filled.ArrowBack,
                                    contentDescription = "back",
                                    tint = MaterialTheme.colors.onSurface
                                )
                            }
                        }
                    }

                    //USE
                    DetailScreen(
                            Json.decodeFromString<Poster>(it),
                            navController::navigateUp,
                            animatedVisibilityScope = this@composable
                        )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Scroll SearchBar Hide / Show

                    @Composable
                        fun LazyListState.isScrollingUp(): Boolean {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }
                        return remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex > firstVisibleItemIndex
                                } else {
                                    previousScrollOffset >= firstVisibleItemScrollOffset
                                }.also {
                                    previousIndex = firstVisibleItemIndex
                                    previousScrollOffset = firstVisibleItemScrollOffset
                                }
                            }
                            }.value
                        }
                    """.trimIndent()
            )

            //https://github.com/abdulhamidrpn/Top-News-KMP
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun SearchAppView(
                        modifier: Modifier = Modifier,
                        hint: String = "Search...",
                        query: String,
                        isFocused: Boolean,
                        searchHistory: List<String> = emptyList(),
                        onQueryChange: (String) -> Unit,
                        onSearchClick: (String) -> Unit = {},
                        onRemoveHistory: () -> Unit = {},
                        leadingIcon: @Composable () -> Unit = {},
                        trailingIcon: @Composable () -> Unit = {}
                    ) {
                        var active by rememberSaveable { mutableStateOf(false) }

                        LaunchedEffect(isFocused) {
                            active = isFocused
                        }

                        Box(Modifier
                            .semantics { isTraversalGroup = true }
                            .zIndex(1f)
                            .fillMaxWidth()) {

                            if (searchHistory.isEmpty()) {
                                // Search bar
                                DockedSearchBar(
                                    modifier = modifier
                                        .align(Alignment.TopCenter)
                                        .padding(top = 8.dp),
                                    query = query,
                                    onQueryChange = { onQueryChange(it) },
                                    onSearch = {
                                        onSearchClick(query)
                                        active = false
                                    },
                                    placeholder = { Text(hint) },
                                    leadingIcon = leadingIcon,
                                    trailingIcon = trailingIcon,
                                    active = false,
                                    onActiveChange = {},
                                    content = {}
                                )
                            }
                            else {
                                DockedSearchBar(
                                    modifier = modifier
                                        .align(Alignment.TopCenter)
                                        .padding(top = 8.dp),
                                    placeholder = { Text(hint) },
                                    query = query,
                                    onQueryChange = { onQueryChange(it) },
                                    onSearch = {
                                        onSearchClick(query)
                                        active = false
                                    },
                                    active = active,
                                    onActiveChange = {  active = it },
                                    leadingIcon = leadingIcon,
                                    trailingIcon = trailingIcon,
                                ) {
                                    if (searchHistory.isNotEmpty()) {
                                        Text(
                                            text = "Recent Searches",
                                            style = MaterialTheme.typography.labelMedium,
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .padding(horizontal = 16.dp, vertical = 8.dp)
                                        )
                                        LazyColumn(
                                            modifier = Modifier.fillMaxWidth().fillMaxHeight(0.4f),
                                            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 4.dp),
                                            verticalArrangement = Arrangement.spacedBy(2.dp)
                                        ) {
                                            items(searchHistory) { history ->
                                                Row(
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .clip(MaterialTheme.shapes.medium)
                                                        .clickable {
                                                            onQueryChange(history)
                                                            onSearchClick(history)
                                                            active = false
                                                        }
                                                        .padding(vertical = 8.dp, horizontal = 16.dp),
                                                    verticalAlignment = Alignment.CenterVertically
                                                ) {
                                                    Icon(
                                                        painter = painterResource(Res.drawable.ic_history),
                                                        contentDescription = null,
                                                        tint = MaterialTheme.colorScheme.primary,
                                                        modifier = Modifier.padding(end = 8.dp)
                                                    )
                                                    Text(
                                                        text = history,
                                                        style = MaterialTheme.typography.titleMedium,
                                                    )
                                                }
                                            }
                                        }
                                        Text(
                                            text = "Clear history",
                                            style = MaterialTheme.typography.labelMedium,
                                            modifier = Modifier
                                                .align(Alignment.End)
                                                .clip(MaterialTheme.shapes.medium)
                                                .padding(horizontal = 16.dp, vertical = 16.dp)
                                                .clickable {
                                                    onRemoveHistory()
                                                },
                                            color = MaterialTheme.colorScheme.error
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/MahdiPorkar-github/AllTrailsUIClone.git
            KotlinCode(
                code = """
                        //ScrollingText SearchBar PlaceHolder (AnimationPlaceHolder)

                        @Composable
                        fun ScrollingText(
                            text: String,
                            modifier: Modifier = Modifier,
                        ) {
                            AnimatedContent(
                                targetState = text,
                                transitionSpec = { textChangeAnimation() },
                            ) { state ->
                                Text(
                                    text = state,
                                    modifier = modifier,
                                )
                            }
                        }

                        private fun textChangeAnimation(durationMillis: Int = 500): ContentTransform {
                            val enterTransition = slideInVertically(
                                animationSpec = tween(durationMillis),
                            ) { height ->
                                height
                            } + fadeIn(
                                animationSpec = tween(durationMillis),
                            )

                            val exitTransition = slideOutVertically(
                                animationSpec = tween(durationMillis),
                            ) { height ->
                                -height
                            } + fadeOut(
                                animationSpec = tween(durationMillis),
                            )

                            return enterTransition.togetherWith(exitTransition)
                        }

                        @OptIn(ExperimentalMaterial3Api::class)
                        @Composable
                        fun ATSearchInput(modifier: Modifier = Modifier) {
                            val typeOptions = listOf("trails", "parks", "cities")

                            var typeIndex by remember {
                                mutableStateOf(0)
                            }

                            LaunchedEffect(Unit) {
                                while (true) {
                                    delay(3000)

                                    typeIndex = (typeIndex + 1) % typeOptions.size
                                }
                            }

                            SearchBar(
                                query = "",
                                onQueryChange = {},
                                onSearch = {},
                                active = false,
                                onActiveChange = {},
                                leadingIcon = {
                                    Icon(
                                        imageVector = Icons.Default.Search,
                                        contentDescription = null,
                                    )
                                },
                                trailingIcon = {
                                    Icon(
                                        imageVector = Icons.Default.Tune,
                                        contentDescription = null,
                                        modifier = Modifier
                                            .background(
                                                color = MaterialTheme.colorScheme.background,
                                                shape = CircleShape,
                                            ).padding(6.dp),
                                    )
                                },
                                placeholder = {
                                    Row {
                                        Text(
                                            text = "Find ",
                                        )

                                        ScrollingText(
                                            text = typeOptions[typeIndex],
                                        )
                                    }
                                },
                                modifier = modifier,
                            ) {
                            }
                        }

                        @Preview(
                            name = "Day Mode",
                            uiMode = Configuration.UI_MODE_NIGHT_NO,
                        )
                        @Preview(
                            name = "Night Mode",
                            uiMode = Configuration.UI_MODE_NIGHT_YES,
                        )
                        @Composable
                        private fun ATSearchInputPreview() {
                            AllTrailsTheme {
                                ATSearchInput(
                                    modifier = Modifier
                                        .padding(16.dp)
                                        .fillMaxWidth(),
                                )
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        ////ScrollingText SearchBar PlaceHolder (AnimationPlaceHolder)

                        suspend fun <T> ListIterator<T>.doWhenHasNextOrPrevious(
                            delayMills: Long = 3000,
                            doWork: suspend (T) -> Unit
                        ) {
                            while (hasNext() || hasPrevious()) {
                                while (hasNext()) {
                                    delay(delayMills)
                                    doWork(next())
                                }
                                while (hasPrevious()) {
                                    delay(delayMills)
                                    doWork(previous())
                                }
                            }
                        }

                        @Composable
                        fun AnimatedPlaceholderComposable(
                            hints: List<String>
                        ) {
                            val iterator = hints.listIterator()

                            val target by produceState(initialValue = hints.first()) {
                                iterator.doWhenHasNextOrPrevious {
                                    value = it
                                }
                            }

                            AnimatedContent(
                                targetState = target,
                                transitionSpec = { ScrollAnimation() }
                            ) { str ->
                                Text(
                                    text = str,
                                    fontSize = 14.withScale(),
                                )
                            }
                        }

                        object ScrollAnimation {
                            @OptIn(ExperimentalAnimationApi::class)
                            operator fun invoke(): ContentTransform {
                                return (slideInVertically(
                                    initialOffsetY = { 50 },
                                    animationSpec = tween()
                                ) + fadeIn()).togetherWith(
                                    slideOutVertically(
                                        targetOffsetY = { -50 },
                                        animationSpec = tween()
                                    ) + fadeOut()
                                )
                            }
                        }

                        /**
                         * This prevents text size from enlarging when
                         * user increases their devices' font size
                         */

                        @Composable
                        fun Int.withScale(): TextUnit {
                            return with(LocalDensity.current) {
                                (this@withScale / fontScale).sp
                            }
                        }

                        /*
                         placeholder = {
                                        AnimatedPlaceholderComposable(
                                            hints = listOf(
                                                "Search your favourite topic",
                                                "Search your favourite auther name"
                                            ),
                                        )
                                    },
                        */
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Expand & Collapse Animation

                        @Composable
                        fun AccordionVariant3(modifier: Modifier = Modifier) {
                            var expanded by remember { mutableStateOf(false) }
                            val arrowRotation by animateFloatAsState(
                                targetValue = if (expanded) 90f else 0f,
                                label = "accordion-arrow"
                            )

                            Surface(
                                color = MaterialTheme.colorScheme.tertiary,
                                modifier = modifier
                                    // clip the ripple effect
                                    .clip(RoundedCornerShape(12.dp))
                                    .clickable { expanded = !expanded }
                            ) {
                                Column(
                                    verticalArrangement = Arrangement.spacedBy(4.dp),
                                    modifier = Modifier.padding(16.dp)
                                ) {
                                    // accordion header
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                                    ) {
                                        Text(
                                            text = LOREM_IPSUM_SHORT,
                                            style = MaterialTheme.typography.headlineSmall,
                                            fontWeight = FontWeight.Bold,
                                            // make sure the text fill the row
                                            modifier = Modifier.weight(1f)
                                        )
                                        Icon(
                                            imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                                            contentDescription = null,
                                            // arrow rotation based on the expanded state
                                            modifier = Modifier.rotate(arrowRotation)
                                        )
                                    }

                                    // accordion content
                                    Box(modifier = modifier) {
                                        // accordion content - collapsed item
                                        this@Column.AnimatedVisibility(
                                            visible = !expanded,
                                            // only used fadeIn and fadeOut animation for the collapsed item
                                            enter = fadeIn(animationSpec = tween(durationMillis = 0)),
                                            exit = fadeOut(animationSpec = tween())
                                        ) {
                                            Box {
                                                Text(
                                                    text = LOREM_IPSUM_VERY_LONG,
                                                    minLines = 2,
                                                    maxLines = 2,
                                                    overflow = TextOverflow.Ellipsis,
                                                    style = MaterialTheme.typography.bodyMedium,
                                                )
                                                // overlay above the text
                                                Box(
                                                    modifier = Modifier
                                                        .align(Alignment.BottomCenter)
                                                        .height(32.dp)
                                                        .fillMaxWidth()
                                                        .background(
                                                            // gradient color from bottom to top
                                                            brush = Brush.verticalGradient(
                                                                colors = listOf(
                                                                    Color.Transparent,
                                                                    MaterialTheme.colorScheme.tertiary
                                                                )
                                                            )
                                                        )
                                                )
                                            }
                                        }
                                        // accordion content - expanded item
                                        this@Column.AnimatedVisibility(
                                            visible = expanded,
                                            // animate expand vertically from the top when expanded
                                            enter = expandVertically(
                                                expandFrom = Alignment.Top,
                                                animationSpec = tween()
                                            ),
                                            // animate shrink vertically to the top when collapsed + fade out
                                            exit = shrinkVertically(
                                                shrinkTowards = Alignment.Top,
                                                animationSpec = tween()
                                            ) + fadeOut()
                                        ) {
                                            Text(
                                                text = LOREM_IPSUM_VERY_LONG,
                                                style = MaterialTheme.typography.bodyMedium
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //ExpandableText onTextLayout

                    @Composable
                    fun ExpandableText(
                        text: String,
                        maxLines: Int,
                        textAlign: TextAlign,
                        style: TextStyle,
                        paddingValues: PaddingValues,
                        modifier: Modifier = Modifier,
                        fontWeight: FontWeight = FontWeight.Normal
                    ) {
                        var isExpanded by remember { mutableStateOf(false) }
                        var hasOverflow by remember { mutableStateOf(false) }

                        val updatedText = if (containsHtml(text)) htmlTextFilter(text) else text

                        Row(
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(paddingValues)
                                .wrapContentHeight(Alignment.Bottom)
                                .animateContentSize(),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = updatedText,
                                maxLines = if (isExpanded) Int.MAX_VALUE else maxLines,
                                overflow = TextOverflow.Ellipsis,
                                onTextLayout = { textLayoutResult ->
                                    hasOverflow = textLayoutResult.hasVisualOverflow
                                },
                                textAlign = textAlign,
                                fontWeight = fontWeight,
                                softWrap = true,
                                color = MaterialTheme.colorScheme.secondary,
                                style = style,
                                modifier = Modifier
                                    .weight(1f)
                            )

                            // Show [See more] if text has overflow
                            // Show [See less] if text is expanded
                            if (hasOverflow || isExpanded) {
                                Text(
                                    text = if (isExpanded) "See less" else "See more",
                                    textAlign = TextAlign.Center,
                                    fontWeight = FontWeight.Bold,
                                    softWrap = true,
                                    color = Color.Gray,
                                    style = MaterialTheme.typography.titleSmall,
                                    modifier = modifier
                                        .clickable {
                                            isExpanded = !isExpanded
                                        }
                                        .padding(start = 2.dp)
                                        .align(Alignment.Bottom)
                                )
                            }
                        }
                    }

                    /** [containsHtml]:
                     * A function to check for HTML tags
                     * */
                    fun containsHtml(text: String): Boolean {
                        return text.contains(Regex("<.*?>"))
                    }

                    /**[htmlTextFilter]:
                     * A function responsible to properly filter descriptions from html tags.
                     * */
                    fun htmlTextFilter(text: String): String {
                        val plainText = text.replace(Regex("<.*?>"), "")
                        return plainText
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Custom Shadows Along X and Y Axes
                    fun Modifier.customShadow(
                        color: Color = Color.Black,
                        offsetX: Dp = 0.dp,
                        offsetY: Dp = 0.dp,
                        blurRadius: Dp = 0.dp,
                        shape: Shape = RectangleShape,
                    ) = this.drawBehind {
                        val paint = Paint().apply {
                            this.color = color
                            this.asFrameworkPaint().apply {
                                isAntiAlias = true
                                setShadowLayer(blurRadius.toPx(), offsetX.toPx(), offsetY.toPx(), color.toArgb())
                            }
                        }

                        drawIntoCanvas { canvas ->
                            when (val outline = shape.createOutline(size, layoutDirection, this)) {
                                is Outline.Rectangle -> {
                                    canvas.drawRect(outline.rect, paint)
                                }

                                is Outline.Rounded -> {
                                    val cornerRadius = outline.roundRect.bottomRightCornerRadius
                                    canvas.drawRoundRect(
                                        0f,
                                        0f,
                                        size.width,
                                        size.height,
                                        cornerRadius.x,
                                        cornerRadius.y,
                                        paint
                                    )
                                }

                                is Outline.Generic -> {
                                    canvas.drawPath(outline.path, paint)
                                }
                            }
                        }
                    }

                    Box(modifier = Modifier
                                .customShadow(
                                    color = ShadowGrey,
                                    offsetX = (2).dp,
                                    offsetY = (2).dp,
                                    blurRadius = 8.dp,
                                    shape = RoundedCornerShape(50.dp)
                                )
                                .customShadow(
                                    color = Color.White,
                                    offsetX = (-5).dp,
                                    offsetY = (-5).dp,
                                    blurRadius = 8.dp,
                                    shape = RoundedCornerShape(50.dp)
                                )
                                .background(color = backgroundColor, shape = RoundedCornerShape(50.dp))
                            ){
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //verticalScrollbar

                    import androidx.annotation.FloatRange
                    import androidx.compose.animation.core.animateFloatAsState
                    import androidx.compose.animation.core.tween
                    import androidx.compose.foundation.lazy.LazyListState
                    import androidx.compose.material3.MaterialTheme
                    import androidx.compose.runtime.getValue
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.composed
                    import androidx.compose.ui.draw.drawWithContent
                    import androidx.compose.ui.geometry.CornerRadius
                    import androidx.compose.ui.geometry.Offset
                    import androidx.compose.ui.geometry.Size
                    import androidx.compose.ui.graphics.Color
                    import androidx.compose.ui.unit.Dp
                    import androidx.compose.ui.unit.dp

                    fun Modifier.verticalScrollbar(
                        state: LazyListState,
                        width: Dp = 6.dp
                    ): Modifier = composed {
                        val targetAlpha = if (state.isScrollInProgress) 1f else 0f
                        val duration = if (state.isScrollInProgress) 150 else 500

                        val alpha by animateFloatAsState(
                            targetValue = targetAlpha,
                            animationSpec = tween(durationMillis = duration)
                        )

                        val color = MaterialTheme.colorScheme.onBackground.alpha(0.5f)

                        drawWithContent {
                            drawContent()

                            val firstVisibleElementIndex = state.layoutInfo.visibleItemsInfo.firstOrNull()?.index
                            val needDrawScrollbar = state.isScrollInProgress || alpha > 0.0f
                            if (needDrawScrollbar && firstVisibleElementIndex != null) {
                                val elementHeight = this.size.height / state.layoutInfo.totalItemsCount
                                val scrollbarOffsetY = firstVisibleElementIndex * elementHeight
                                val scrollbarHeight = state.layoutInfo.visibleItemsInfo.size * elementHeight

                                drawRoundRect(
                                    color = color,
                                    topLeft = Offset(this.size.width - width.toPx(), scrollbarOffsetY),
                                    size = Size(width.toPx(), scrollbarHeight),
                                    alpha = alpha,
                                    cornerRadius = CornerRadius(2.dp.toPx(), 2.dp.toPx()),
                                )
                            }
                        }
                    }

                    fun Color.alpha(@FloatRange(from = 0.0, to = 1.0) alpha: Float): Color = this.copy(alpha = alpha)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        @OptIn(ExperimentalFoundationApi::class)
                        @Composable
                        fun ChatListing(
                            modifier: Modifier = Modifier,
                            subtitle: String,
                            items: ImmutableList<ChatDisplayData>
                        ) {
                            val state = rememberLazyListState()
                            var initialScrollDone by remember {
                                mutableStateOf(false)
                            }
                            val itemsSize = remember(items) {
                                derivedStateOf {
                                    items.size
                                }
                            }
                            LaunchedEffect(itemsSize) {
                                if (itemsSize.value > 0) {
                                    if (initialScrollDone) {
                                        state.animateScrollToItem(itemsSize.value - 1)
                                    } else {
                                        initialScrollDone = true
                                        state.scrollToItem(itemsSize.value - 1)
                                    }
                                }
                            }
                            LazyColumn(modifier = modifier, state = state) {
                                stickyHeader {
                                    Text(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .background(MaterialTheme.colorScheme.background.alpha(0.2f))
                                            .padding(8.dp),
                                        text = subtitle,
                                        textAlign = TextAlign.Center,
                                        style = MaterialTheme.typography.labelSmall
                                    )
                                }
                                items(
                                    count = items.size,
                                    key = { items[it].id },
                                    itemContent = {
                                        ChatDisplay(data = items[it], modifier = Modifier.padding(horizontal = 8.dp))
                                    }
                                )
                            }
                        }

                        @AppThemePreviews
                        @Preview
                        @Composable
                        fun ChatListingPreview() {
                            ChatBackupTheme {
                                ChatListing(items = PreviewDataUtils.mockChats, subtitle = "Subtitle")
                            }
                        }

                        data class ChatDisplayData(
                            val id: String,
                            val content: String,
                            val date: String,
                            val alignedLeft: Boolean,
                            val avatar: AvatarData?,
                            val imageUri: String?,
                        )

                        @Composable
                        fun ChatDisplay(modifier: Modifier = Modifier, data: ChatDisplayData) {
                            val alignment = if (data.alignedLeft) Alignment.Start else Alignment.End
                            val backgroundColor = if (data.alignedLeft) Color.LightGray else Color.Blue
                            val textColor = if (data.alignedLeft) Color.Black else Color.White
                            Column(
                                modifier = modifier.fillMaxWidth(),
                                horizontalAlignment = alignment
                            ) {
                                Row(modifier = Modifier.wrapContentWidth()) {
                                    if (data.avatar != null) {
                                        AvatarBadge(
                                            data = data.avatar,
                                            modifier = Modifier
                                                .padding(top = 4.dp)
                                                .size(28.dp),
                                            textStyle = MaterialTheme.typography.labelMedium
                                        )
                                    }
                                    Column(
                                        modifier = Modifier
                                            .wrapContentWidth()
                                            .padding(4.dp)
                                    ) {
                                        Text(
                                            modifier = Modifier
                                                .background(backgroundColor, RoundedCornerShape(12.dp))
                                                .align(alignment)
                                                .widthIn(min = 20.dp, max = 220.dp)
                                                .padding(8.dp),
                                            text = data.content,
                                            style = MaterialTheme.typography.labelMedium,
                                            color = textColor,
                                        )
                                        if (data.imageUri != null) {
                                            Surface(
                                                modifier = modifier
                                                    .size(220.dp)
                                                    .padding(vertical = 8.dp)
                                                    .border(1.dp, MaterialTheme.colorScheme.background, CircleShape),
                                                color = MaterialTheme.colorScheme.background,
                                            ) {
                                                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                                    AsyncImage(model = Uri.parse(data.imageUri), contentDescription = null)
                                                }
                                            }
                                        }
                                        Text(
                                            modifier = Modifier.padding(2.dp),
                                            text = data.date,
                                            style = MaterialTheme.typography.labelSmall
                                        )
                                    }
                                }
                            }
                        }

                        @Preview
                        @Composable
                        fun ChatDisplayOtherPreview() {
                            ChatBackupTheme {
                                ChatDisplay(
                                    modifier = Modifier,
                                    data = ChatDisplayData(
                                        id = "id1",
                                        content = "Content Looong Content Looooong Looong Looong",
                                        date = "Sun 14th Dec 2022, 13:44:55",
                                        alignedLeft = true,
                                        avatar = AvatarData.Initials("AB", Color.Blue),
                                        imageUri = null,
                                    )
                                )
                            }
                        }

                        @Preview
                        @Composable
                        fun ChatDisplayMinePreview() {
                            ChatBackupTheme {
                                ChatDisplay(
                                    modifier = Modifier,
                                    data = ChatDisplayData(
                                        id = "id1",
                                        content = "Content",
                                        date = "Sun 14th Dec 2022, 13:44:55",
                                        alignedLeft = false,
                                        avatar = null,
                                        imageUri = null,
                                    )
                                )
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                //isScrollInProgress

                code = """
                    val headlinesListState = rememberLazyListState()
                    var showBackToTopButton by remember { mutableStateOf(false) }

                    LaunchedEffect(headlinesListState.isScrollInProgress) {
                        if (headlinesListState.isScrollInProgress) {
                            showBackToTopButton = true
                        } else {
                            coroutineScope.launch {
                                delay(2000)
                                showBackToTopButton = false
                            }
                        }
                    }

                    if (showBackToTopButton) {
                        BackToTopButton(modifier = Modifier.align(Alignment.TopCenter)) {
                            coroutineScope.launch { headlinesListState.animateScrollToItem(0) }
                        }
                    }

                    @Composable
                    private fun BackToTopButton(modifier: Modifier, onClick: ClickHandler) {
                        ElevatedButton(
                            modifier = modifier,
                            onClick = onClick
                        ) {
                            Icon(
                                modifier = Modifier.size(20.dp),
                                painter = painterResource(id = R.drawable.arrow_up),
                                contentDescription = BACK_TO_TOP
                            )

                            Text(modifier = Modifier.padding(start = 8.dp), text = BACK_TO_TOP)
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/oguzsout/WalliesCompose
            //https://github.com/oguzsout/WalliesCompose/tree/feature/SignInWithGoogleLoading
            KotlinCode(
                code = """
                    //Google Login Button Press Circle ProgressBar Animation

                    @Composable
                    fun ButtonGoogleSignIn(
                        onGoogleSignInButtonClick: () -> Unit, loading: Boolean = false, modifier: Modifier = Modifier
                    ) {
                        val isLoading by rememberUpdatedState(newValue = loading)
                        val transition = updateTransition(targetState = isLoading, label = "button state")
                        val primaryColorForArc by remember { mutableStateOf(LightColorPalette.primary) }
                        val buttonWidthFactor by transition.animateFloat(
                            transitionSpec = {
                                tween(
                                    durationMillis = 1000, easing = LinearEasing
                                )
                            }, label = "button width"
                        ) { state ->
                            if (state) 0f else 1f
                        }

                        val borderColor by transition.animateColor(
                            transitionSpec = {
                                tween(
                                    durationMillis = 700
                                )
                            }, label = "border color"
                        ) { state ->
                            if (state) MaterialTheme.colorScheme.secondaryContainer else Color.Gray
                        }

                        val infiniteTransition = rememberInfiniteTransition(label = "")
                        val animatedRotation by if (isLoading) {
                            infiniteTransition.animateFloat(
                                initialValue = 0f, targetValue = 360f, animationSpec = infiniteRepeatable(
                                    animation = tween(750, easing = FastOutSlowInEasing),
                                    repeatMode = RepeatMode.Restart
                                ), label = "rotation"
                            )
                        } else {
                            remember { mutableFloatStateOf(0f) }
                        }

                        val colorOfProgressArc by if (isLoading) {
                            infiniteTransition.animateColor(
                                initialValue = MaterialTheme.colorScheme.secondaryContainer,
                                targetValue = primaryColorForArc,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(durationMillis = 500, easing = LinearEasing),
                                    repeatMode = RepeatMode.Reverse
                                ),
                                label = "progress color"
                            )
                        } else {
                            remember { mutableStateOf(primaryColorForArc) }
                        }

                        Button(
                            onClick = {
                                onGoogleSignInButtonClick.invoke()
                            },
                            modifier = modifier
                                .padding(horizontal = 16.dp)
                                .fillMaxWidth(buttonWidthFactor)
                                .height(40.dp)
                                .then(
                                    if (buttonWidthFactor > 0.1f) Modifier.border(
                                        width = 2.dp, color = borderColor, shape = RoundedCornerShape(24.dp)
                                    ) else Modifier
                                )
                                .animatedButtonProgressBar(
                                    color = colorOfProgressArc,
                                    rotation = animatedRotation,
                                    stateOfLoading = isLoading,
                                    stateOfButtonWidth = buttonWidthFactor
                                ),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.background
                            )
                        ) {
                            AnimatedVisibility(
                                visible = buttonWidthFactor > 0.5f, enter = scaleIn(
                                    tween(750)
                                ), exit = scaleOut(
                                    animationSpec = tween(750)
                                )
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxSize(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.Center
                                ) {
                                    Image(
                                        painter = painterResource(id = R.drawable.google), contentDescription = null
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(
                                        text = "Sign in with Google",
                                        fontSize = 14.sp,
                                        fontFamily = medium,
                                        color = MaterialTheme.colorScheme.onPrimaryContainer
                                    )
                                }
                            }
                        }
                    }

                    @Stable
                    fun Modifier.animatedButtonProgressBar(
                        color: Color, rotation: Float, stateOfLoading: Boolean, stateOfButtonWidth: Float
                    ) = if (stateOfLoading && stateOfButtonWidth <= 0.1f) this.drawBehind {
                        drawIntoCanvas { canvas ->
                            val arcSize = 44.dp.toPx()
                            val arcOffsetX = (size.width - arcSize) / 2
                            val arcOffsetY = (size.height - arcSize) / 2
                            val arcRect = Rect(arcOffsetX, arcOffsetY, arcOffsetX + arcSize, arcOffsetY + arcSize)

                            val paint = Paint().apply {
                                this.color = color
                                style = PaintingStyle.Stroke
                                strokeWidth = 3.dp.toPx()
                                strokeCap = StrokeCap.Round
                            }

                            withTransform({
                                rotate(rotation, center)
                            }) {
                                canvas.drawArc(
                                    rect = arcRect,
                                    startAngle = 90f,
                                    sweepAngle = 280f,
                                    useCenter = false,
                                    paint = paint
                                )
                            }
                        }
                    } else this
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Button Shake Animation
                        @Composable
                        fun AuthButton(title: String, onClick: () -> Unit, loading: Boolean, success: Boolean) {
                            val enterAnimation = fadeIn(
                                animationSpec = tween(
                                    durationMillis = 1000,
                                )
                            ) + slideInVertically(
                                initialOffsetY = { 120.dp.value.toInt() },
                                animationSpec = tween(durationMillis = 1000)
                            )

                            val exitAnimation = fadeOut() + slideOutVertically { -80.dp.value.toInt() }

                            Button(
                                modifier = Modifier
                                    .height(60.dp)
                                    .padding(horizontal = 30.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = Color(0xFF4761B5),
                                    contentColor = Color.White
                                ),
                                shape = RoundedCornerShape(15),
                                onClick = onClick
                            ) {

                                AnimatedVisibility(
                                    visible = !loading && !success,
                                    exit = exitAnimation
                                ) {
                                    Text(
                                        text = title,
                                        color = Color.White,
                                        textAlign = TextAlign.Center,
                                        modifier = Modifier.fillMaxWidth()
                                    )
                                }
                                AnimatedVisibility(
                                    modifier = Modifier.align(Alignment.CenterVertically),
                                    visible = loading,
                                    enter = enterAnimation,
                                    exit = exitAnimation
                                ) {
                                    CircularProgress(isVisible = true)
                                }
                                AnimatedVisibility(
                                    visible = success,
                                    enter = enterAnimation
                                ) {
                                    Row(
                                        horizontalArrangement = Arrangement.SpaceEvenly,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(
                                            imageVector = Icons.Default.Check,
                                            contentDescription = "Check Icon",
                                            modifier = Modifier.size(22.dp)
                                        )
                                        Spacer(modifier = Modifier.width(5.dp))
                                        Text(text = "Done", color = Color.White)
                                    }
                                }
                            }
                        }


                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Favorite Button (animateFloatAsState)

                    @Serializable
                    @Immutable
                    data class FavoriteImages(
                        val id: String? = null,
                        val url: String? = null,
                        val profileImage: String? = null,
                        val name: String? = null,
                        val portfolioUrl: String? = null,
                        var isChecked: Boolean = false
                    )

                    @Composable
                    fun WalliesFavoriteButton(
                        modifier: Modifier = Modifier,
                        favoriteImages: FavoriteImages,
                        addPhotoToFavorites: (FavoriteImages) -> Unit,
                        removePhotoFromFavorites: (FavoriteImages) -> Unit
                    ) {
                        var isFavorite by rememberSaveable(favoriteImages) { mutableStateOf(favoriteImages.isChecked) }
                        val interactionSource = remember { MutableInteractionSource() }
                        val isPressed by interactionSource.collectIsPressedAsState()
                        val sizeScale by animateFloatAsState(if (isPressed) 0.5f else 1f, label = "")

                        IconButton(onClick = {
                            isFavorite = !isFavorite
                            if (isFavorite) {
                                addPhotoToFavorites.invoke(favoriteImages)
                            } else {
                                removePhotoFromFavorites.invoke(favoriteImages)
                            }
                        }, modifier = modifier.graphicsLayer {
                            scaleX = sizeScale
                            scaleY = sizeScale
                        },
                            interactionSource = interactionSource
                        ) {
                            val tintColor = if (isFavorite) Red else MaterialTheme.colorScheme.onPrimaryContainer
                            Icon(
                                modifier = Modifier.size(32.dp),
                                painter = rememberVectorPainter(Icons.TwoTone.Favorite),
                                contentDescription = null,
                                tint = tintColor
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                //https://github.com/skydoves/Balloon

                code = """
                    @OptIn(ExperimentalSharedTransitionApi::class)
                    @Composable
                    fun SharedTransitionScope.AuthenticatedUserWelcomeCard(
                        animatedVisibilityScope: AnimatedVisibilityScope,
                        modifier: Modifier = Modifier,
                        isGoogleSignIn: Boolean,
                        onChangeProfilePhotoClick: (Boolean) -> Unit
                    ) {
                        val openBottomSheetOfProfilePhotoChange by remember { mutableStateOf(false) }

                        var balloonWindow: BalloonWindow? by remember { mutableStateOf(null) }

                        val builder = rememberBalloonBuilder {
                            setArrowSize(10)
                            setArrowPosition(0.9f)
                            setWidth(BalloonSizeSpec.WRAP)
                            setHeight(BalloonSizeSpec.WRAP)
                            setPadding(12)
                            setMarginHorizontal(12)
                            setCornerRadius(8f)
                            setBackgroundColor(Color.DarkGray)
                            setBalloonAnimation(BalloonAnimation.NONE)
                        }
                        Card(modifier = modifier
                            .clickable {
                                balloonWindow?.dismiss()
                            }
                            .wrapContentHeight()
                            .fillMaxWidth(), shape = RoundedCornerShape(16.dp)) {
                            Box(modifier = modifier
                                .fillMaxWidth()
                                .wrapContentHeight(), contentAlignment = Alignment.Center) {
                                Balloon(
                                        onBalloonWindowInitialized = { balloonWindow = it },
                                        modifier = modifier
                                            .align(Alignment.CenterEnd)
                                            .padding(top = 24.dp),
                                        builder = builder,
                                        balloonContent = {
                                            Text(
                                                text = stringResource(id = R.string.show_info_edit_infos),
                                                fontFamily = medium,
                                                fontSize = 12.sp,
                                                color = Color.White
                                            )
                                        }
                                    ) {}

                                Row(
                                    modifier = modifier
                                        .padding(16.dp)
                                        .wrapContentWidth()
                                        .align(Alignment.CenterStart),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {

                                    Box(
                                        modifier = modifier.wrapContentSize(),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        AsyncImage(
                                            model = "",
                                            contentScale = ContentScale.FillBounds,
                                            contentDescription = "Profile Image",
                                            modifier = modifier
                                                .sharedBounds(
                                                    sharedContentState = rememberSharedContentState(key = "profileImage"),
                                                    animatedVisibilityScope = animatedVisibilityScope
                                                )
                                                .height(64.dp)
                                                .width(64.dp)
                                                .clip(RoundedCornerShape(64.dp))
                                                .clickable {
                                                    onChangeProfilePhotoClick.invoke(!openBottomSheetOfProfilePhotoChange)
                                                },
                                        )
                                    }

                                    Text(
                                        buildAnnotatedString {
                                            append(stringResource(id = R.string.welcome_profile))
                                            withStyle(style = SpanStyle(fontWeight = FontWeight.Bold)) {
                                                append(", Kamlesh Lakhani \uD83D\uDD90 ")
                                            }
                                        },
                                        fontSize = 14.sp,
                                        fontFamily = medium,
                                        modifier = modifier
                                    )
                                }
                                IconButton(
                                    onClick = { balloonWindow?.showAlignBottom()},
                                    modifier = modifier
                                        .wrapContentSize()
                                        .align(Alignment.CenterEnd)
                                        .padding(end = 8.dp)
                                ) {
                                    Icon(
                                        painter = painterResource(id = R.drawable.info),
                                        contentDescription = "",
                                        tint = MaterialTheme.colorScheme.onPrimaryContainer,
                                        modifier = modifier.wrapContentSize()
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val textAnimationState = remember { Animatable(0f) }

                            LaunchedEffect(Unit) {
                                launch {
                                    delay(100)
                                    textAnimationState.animateTo(
                                        1f, animationSpec = tween(
                                            500
                                        )
                                    )
                                }
                            }

                    @Composable
                    fun IntroText(
                        textAnimationState: Animatable<Float, AnimationVector1D>
                    ) {
                        //.scale(textAnimationState.value)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Card padding animation

                     @Composable
                     internal fun Timer(
                         modifier: Modifier
                     ) {
                         val dampingRatio = Spring.DampingRatioMediumBouncy
                         val stiffness = Spring.StiffnessMedium
                         val cardHorizontalPadding by animateDpAsState(
                             targetValue = if (isTimerStarted) 48.dp else 16.dp,
                             animationSpec = spring(
                                 dampingRatio = dampingRatio,
                                 stiffness = stiffness
                             )
                         )

                         Card(
                             elevation = CardDefaults.cardElevation(2.dp),
                             colors = CardDefaults.cardColors( containerColor = MaterialTheme.colorScheme.surface, contentColor = MaterialTheme.colorScheme.surface),
                             modifier = modifier
                                 .fillMaxWidth()
                                 .padding(horizontal = cardHorizontalPadding)
                         ) {
                             Column(
                                modifier = Modifier.padding(24.dp),
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                    Crossfade(
                                        targetState = ,
                                        animationSpec = tween(durationMillis = if (isTimerStarted) 0 else 350),
                                        modifier = Modifier.animateContentHeight()
                                    ) { }
                         }
                     }

                     private fun Modifier.animateContentHeight(): Modifier = composed(
                         inspectorInfo = debugInspectorInfo {
                             name = "animateContentSize"
                         }
                     ) {
                         val scope = rememberCoroutineScope()
                         val animModifier = remember(scope) { HeightAnimationModifier(scope) }
                         this
                             .clipToBounds()
                             .then(animModifier)
                     }

                     private class HeightAnimationModifier(val scope: CoroutineScope) : LayoutModifier {
                         val animSpec: AnimationSpec<Int> = spring(
                             dampingRatio = Spring.DampingRatioMediumBouncy,
                             stiffness = Spring.StiffnessLow
                         )

                         private data class AnimData(
                             val anim: Animatable<Int, AnimationVector1D>,
                             var startHeight: Int
                         )

                         private var animData: AnimData? = null

                         override fun MeasureScope.measure(
                             measurable: Measurable,
                             constraints: Constraints
                         ): MeasureResult {

                             val placeable = measurable.measure(constraints)

                             val measuredSize = IntSize(placeable.width, placeable.height)
                             val width = measuredSize.width
                             val height = animateTo(measuredSize.height)
                             return layout(width, height) {
                                 placeable.placeRelative(0, 0)
                             }
                         }

                         fun animateTo(targetHeight: Int): Int {
                             val data = animData?.apply {
                                 if (targetHeight != anim.targetValue) {
                                     startHeight = anim.value
                                     scope.launch {
                                         anim.animateTo(targetHeight, animSpec)
                                     }
                                 }
                             } ?: AnimData(Animatable(targetHeight, Int.VectorConverter, 1), targetHeight)
                             animData = data
                             return data.anim.value
                         }
                     }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //VerticalSelectionPopUp (Center item)
@Composable
fun VerticalSelectionPopUp(
    modifier: Modifier = Modifier,
    data: List<String>,
    placeHolder: String,
    enabled: Boolean = true,
    onDataSelected: (String) -> Unit,
    title: String,
    selected: String = ""
) {
    var showDialog by remember { mutableStateOf(false) }
    var selectedData by remember { mutableStateOf(selected) }
    val state = rememberLazyListState()

    val centerItem by remember {
        derivedStateOf {
            val layoutInfo = state.layoutInfo
            val centerOffset = layoutInfo.viewportStartOffset + layoutInfo.viewportEndOffset / 2
            layoutInfo.visibleItemsInfo.minByOrNull {
                val itemCenter = it.offset + it.size / 2
                abs(itemCenter - centerOffset)
            }?.index ?: 0
        }
    }

    LaunchedEffect(key1 = showDialog) {
        if (showDialog) state.scrollToItem(0)
    }

    if (showDialog) {
        Dialog(
            onDismissRequest = { showDialog = false }
        ) {
            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight(0.5f),
                color = MaterialTheme.colorScheme.background,
                shape = RoundedCornerShape(8.dp)
            ) {
                Column {
                    Text(
                        text = title,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = 16.dp),
                        textAlign = TextAlign.Center,
                        fontSize = 18.sp
                    )
                    LazyColumn(
                        state = state,
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(vertical = 16.dp),
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        item {
                            Spacer(modifier = Modifier.padding(((LocalConfiguration.current.screenHeightDp * 0.5) * 0.20).dp))
                        }
                        if (data.isEmpty()) {
                            item {
                                Text(
                                    text = "No data available",
                                    textAlign = TextAlign.Center,
                                    fontSize = 16.sp,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        }
                        items(data.size) { item ->
                            Text(
                                text = data[item],
                                textAlign = TextAlign.Center,
                                fontSize = 16.sp,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .graphicsLayer {
                                        rotationX = when (item) {
                                            centerItem -> 0f
                                            else -> (centerItem - item) * 7.25f
                                        }

                                    }
                                    .clickable {
                                        selectedData = data[item]
                                        onDataSelected(selectedData)
                                        showDialog = false
                                    }
                            )
                        }
                        item {
                            Spacer(modifier = Modifier.padding(((LocalConfiguration.current.screenHeightDp * 0.5) * 0.20).dp))
                        }
                    }
                }
            }
        }
    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun InstagramPager(
                        state: PagerState,
                        modifier: Modifier = Modifier,
                        @IntRange(10, 90) boxAngle: Int = 30,
                        reverse: Boolean = false,
                        contentPadding: PaddingValues = PaddingValues(0.dp),
                        pageSize: PageSize = PageSize.Fill,
                        pageSpacing: Dp = 0.dp,
                        verticalAlignment: Alignment.Vertical = Alignment.Top,
                        userScrollEnabled: Boolean = true,
                        pageContent: @Composable (index: Int, pageModifier: Modifier) -> Unit,
                    ) {
                        HorizontalPager(
                            state = state,
                            modifier,
                            verticalAlignment = verticalAlignment,
                            contentPadding = contentPadding,
                            pageSize = pageSize,
                            pageSpacing = pageSpacing,
                            userScrollEnabled = userScrollEnabled,
                        ) { pageIndex ->

                            val degreesIn by remember(state.currentPageOffsetFraction) {
                                derivedStateOf {
                                    if (pageIndex == state.currentPage) {
                                        state.currentPageOffsetFraction * boxAngle
                                    } else if (pageIndex > state.currentPage) {
                                        -boxAngle + abs(state.currentPageOffsetFraction * boxAngle)
                                    } else {
                                        boxAngle - abs(state.currentPageOffsetFraction * boxAngle)
                                    }
                                }
                            }

                            //alike instagram
                            val degreesOut by remember(state.currentPageOffsetFraction) {
                                derivedStateOf {
                                    if (pageIndex == state.currentPage) {
                                        -state.currentPageOffsetFraction * boxAngle
                                    } else if (pageIndex < state.currentPage) {
                                        -boxAngle + abs(state.currentPageOffsetFraction * boxAngle)
                                    } else {
                                        boxAngle - abs(state.currentPageOffsetFraction * boxAngle)
                                    }
                                }
                            }

                            val transformOriginX by remember(key1 = state.currentPage) {
                                derivedStateOf {
                                    if (pageIndex == state.currentPage) {
                                        if (state.currentPageOffsetFraction > 0f) {
                                            1f
                                        } else {
                                            0f
                                        }
                                    } else if (pageIndex < state.currentPage) {
                                        1f
                                    } else {
                                        0f
                                    }
                                }
                            }

                            pageContent(
                                pageIndex,
                                Modifier.graphicsLayer {
                                    rotationY = if (reverse) degreesIn else degreesOut
                                    transformOrigin = TransformOrigin(
                                        transformOriginX,
                                        .5f
                                    )
                                }
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/MostafaMohamed2002/GameVault

                    @OptIn(ExperimentalMaterial3Api::class)
                    @SuppressLint("UnusedMaterial3ScaffoldPaddingParameter")
                    @Composable
                    fun FreeGameDetailesScreen(
                        viewModel: FreeGameDetailesViewModel,
                    ) {
                        val state by viewModel.uiState.collectAsStateWithLifecycle()
                        val pagerState = rememberPagerState { state.game.screenshots.size }

                        Scaffold(snackbarHost = {

                        }, floatingActionButton = {}, content = {
                            HorizontalPager(
                                state = pagerState,
                                modifier = Modifier
                                    .wrapContentHeight()  // Adjust height as needed
                                    .fillMaxWidth(),
                                contentPadding = PaddingValues(horizontal = 8.dp),
                                verticalAlignment = Alignment.Top,
                                pageSpacing = 8.dp
                            ) { page ->
                                val pageOffset =
                                    (pagerState.currentPage - page) + pagerState.currentPageOffsetFraction
                                val imageSize by animateFloatAsState(
                                    targetValue = if (pageOffset != 0f) 0.75f else 1f,
                                    animationSpec = tween(300)
                                )
                                AsyncImage(model = state.game.screenshots[page].image,
                                    contentDescription = "Screenshot",
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(8.dp)
                                        .clip(RoundedCornerShape(8.dp))
                                        .graphicsLayer {
                                            scaleX = imageSize
                                            scaleY = imageSize
                                        })
                            }
                        })
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/ismailfatihbirak/PlantAppUi

                    @OptIn(ExperimentalFoundationApi::class)
                    @Composable
                    fun PlantPager(
                        pagerState: PagerState,
                        modifier: Modifier = Modifier,
                    ) {
                        HorizontalPager(
                            state = pagerState,
                            pageSpacing = (-100).dp,
                            contentPadding = PaddingValues(start = 54.dp, end = 54.dp, top = 30.dp),
                            modifier = modifier
                        ) { page ->
                            Box(
                                Modifier
                                    .graphicsLayer {
                                        val pageOffset = (
                                                (pagerState.currentPage - page) + pagerState
                                                    .currentPageOffsetFraction
                                                ).absoluteValue
                                        val rotationAngle = if (page < pagerState.currentPage) {
                                            lerp(start = -10f, stop = 0f, fraction = 1f - pageOffset.coerceIn(0f, 1f))
                                        } else {
                                            lerp(start = 10f, stop = 0f, fraction = 1f - pageOffset.coerceIn(0f, 1f))
                                        }

                                        rotationZ = rotationAngle

                                        val scale = lerp(
                                            start = 0.70f,
                                            stop = 1f,
                                            fraction = 1f - pageOffset.coerceIn(0f, 1f)
                                        )
                                        scaleX = scale
                                        scaleY = scale
                                    }
                                    .zIndex(if (page == pagerState.currentPage) 1f else 0f)
                                    .size(325.dp, 464.dp)
                                    .blur(
                                        radius = if (page == pagerState.currentPage) 0.dp else 5.dp
                                    )
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .clip(RoundedCornerShape(42.dp))
                                        .background(color = colorResource(R.color.white))
                                ) {

                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Swipe To Delete",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Delete CircularProgressIndicator

                    @OptIn(ExperimentalFoundationApi::class)
                    @Composable
                    private fun SwipeToDelete(
                        deleting: Boolean = false,
                        content: @Composable BoxScope.() -> Unit,
                        onDelete: () -> Unit = {}
                    ) {
                        val scope = rememberCoroutineScope()
                        val density = LocalDensity.current
                        val defaultActionSize = 80.dp
                        val endActionSizePx = with(density) { defaultActionSize.toPx() }
                        var showDeleteConfirmation by remember {
                            mutableStateOf(false)
                        }

                        val state = remember {
                            AnchoredDraggableState(
                                initialValue = DragAnchors.Center,
                                anchors = DraggableAnchors {
                                    DragAnchors.Center at 0f
                                    DragAnchors.End at endActionSizePx
                                },
                                positionalThreshold = { distance: Float -> distance * 0.5f },
                                velocityThreshold = { with(density) { 100.dp.toPx() } },
                                animationSpec = tween()
                            )
                        }
                        Box(
                            modifier = Modifier
                                .fillMaxSize()
                                .background(color = AppTheme.colorScheme.alertColor)
                                .clip(RectangleShape)
                        ) {
                            IconButton(
                                onClick = { showDeleteConfirmation = true },
                                modifier = Modifier
                                    .align(Alignment.CenterEnd)
                                    .padding(end = 16.dp),
                                enabled = !deleting
                            ) {
                                if (deleting) {
                                    AppProgressIndicator()
                                } else {
                                    Icon(
                                        imageVector = Icons.Default.Delete,
                                        contentDescription = null,
                                        tint = AppTheme.colorScheme.onPrimary
                                    )
                                }
                            }

                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .align(Alignment.CenterStart)
                                    .offset {
                                        IntOffset(
                                            x = -state
                                                .requireOffset()
                                                .roundToInt(),
                                            y = 0
                                        )
                                    }
                                    .anchoredDraggable(state, Orientation.Horizontal, reverseDirection = true),
                                content = content
                            )
                        }

                        if (showDeleteConfirmation) {
                            ShowDeleteThreadDialog(onDelete = {
                                onDelete()
                                showDeleteConfirmation = false
                            }) {
                                scope.launch { state.animateTo(DragAnchors.Center) }
                                showDeleteConfirmation = false
                            }
                        }
                    }

                    @Composable
                    fun ShowDeleteThreadDialog(onDelete: () -> Unit, onDismiss: () -> Unit) {
                        AppAlertDialog(
                            title = stringResource(R.string.threads_screen_delete_dialogue_title_text),
                            subTitle = stringResource(R.string.threads_screen_delete_dialogue_message_text),
                            confirmBtnText = stringResource(R.string.threads_screen_delete_dialogue_delete_btn),
                            dismissBtnText = stringResource(R.string.common_btn_cancel),
                            onConfirmClick = onDelete,
                            onDismissClick = onDismiss,
                            isConfirmDestructive = true
                        )
                    }

                    @Composable
                    fun AppAlertDialog(
                        title: String? = null,
                        subTitle: String,
                        confirmBtnText: String? = null,
                        dismissBtnText: String? = null,
                        onConfirmClick: (() -> Unit)? = null,
                        onDismissClick: (() -> Unit)? = null,
                        isConfirmDestructive: Boolean = false,
                        properties: DialogProperties? = null
                    ) {
                        AlertDialog(
                            onDismissRequest = {},
                            properties = properties ?: DialogProperties(),
                            containerColor = AppTheme.colorScheme.containerNormalOnSurface,
                            tonalElevation = 20.dp,
                            title = {
                                if (title != null) {
                                    Text(
                                        text = title,
                                        style = AppTheme.appTypography.header3
                                    )
                                }
                            },
                            text = {
                                Text(
                                    text = subTitle,
                                    style = AppTheme.appTypography.body1
                                )
                            },
                            confirmButton = {
                                if (confirmBtnText != null && onConfirmClick != null) {
                                    TextButton(
                                        onClick = (onConfirmClick)
                                    ) {
                                        Text(
                                            text = confirmBtnText,
                                            style = AppTheme.appTypography.button,
                                            color = if (isConfirmDestructive) AppTheme.colorScheme.alertColor else AppTheme.colorScheme.primary
                                        )
                                    }
                                }
                            },
                            dismissButton = {
                                if (dismissBtnText != null && onDismissClick != null) {
                                    TextButton(onClick = (onDismissClick)) {
                                        Text(
                                            text = dismissBtnText,
                                            style = AppTheme.appTypography.button,
                                            color = if (isConfirmDestructive) AppTheme.colorScheme.textSecondary else AppTheme.colorScheme.primary
                                        )
                                    }
                                }
                            }
                        )
                    }

                    @Composable
                    fun AppProgressIndicator(
                        color: Color = AppTheme.colorScheme.primary,
                        strokeWidth: Dp = ProgressIndicatorDefaults.CircularStrokeWidth
                    ) {
                        CircularProgressIndicator(
                            color = color,
                            strokeWidth = strokeWidth,
                            modifier = Modifier
                                .height(20.dp)
                                .width(20.dp)
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/usarican/Clean-Architecture-Movie-Application.git
            KotlinCode(
                code = """
                        @Composable
                        fun SwipeToDelete(
                            modifier: Modifier = Modifier,
                            movieClickAction: (movieId: Int) -> Unit = {},
                            onDelete: () -> Unit = {},
                            onInstantDelete: () -> Unit = {}
                        ) {

                            val offsetX = remember { Animatable(0f) }
                            var itemWidth by remember { mutableIntStateOf(1) }
                            var textViewWidth by remember { mutableFloatStateOf(1F) }
                            val cardShape = RoundedCornerShape(dimensionResource(R.dimen.medium_border))
                            val scope = rememberCoroutineScope()

                            val deleteTextPosition = remember {
                                Animatable(
                                    0F
                                )
                            }

                            Box(modifier = modifier
                                .fillMaxWidth()
                                .onGloballyPositioned { coords ->
                                    itemWidth = coords.size.width
                                }) {
                                Box(
                                    modifier = Modifier
                                        .matchParentSize()
                                        .clip(cardShape)
                                        .background(MaterialTheme.colorScheme.error)
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .graphicsLayer(translationX = deleteTextPosition.value)
                                            .onGloballyPositioned { coords ->
                                                textViewWidth = coords.size.width.toFloat()
                                            }
                                            .fillMaxHeight()
                                            .clip(cardShape)
                                            .background(MaterialTheme.colorScheme.error)
                                            .clickable {
                                                scope.launch {
                                                    onDelete()
                                                    delay(SNACK_BAR_WITH_ACTION_DELAY)
                                                    offsetX.animateTo(0f, animationSpec = spring())
                                                }
                                            }, contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            modifier = Modifier
                                                .padding(horizontal = dimensionResource(R.dimen.small_padding))
                                                .align(Alignment.Center),
                                            textAlign = TextAlign.Center,
                                            text = "Delete",
                                            style = MaterialTheme.typography.bodyMedium.copy(
                                                fontWeight = FontWeight.W700, color = MaterialTheme.colorScheme.onError
                                            )
                                        )
                                    }
                                }

                                Card(shape = cardShape, border = BorderStroke(
                                    dimensionResource(R.dimen.one_dp), MaterialTheme.colorScheme.outlineVariant
                                ),
                                    colors = CardDefaults.cardColors(
                                        containerColor = MaterialTheme.colorScheme.surface
                                    ), modifier = Modifier
                                        .offset { IntOffset(offsetX.value.roundToInt(), 0) }
                                        .fillMaxWidth()
                                        .draggable(orientation = Orientation.Horizontal,
                                            state = rememberDraggableState { delta ->
                                                val newOffset = offsetX.value + delta
                                                scope.launch {
                                                    launch {
                                                        offsetX.snapTo(newOffset.coerceIn(-itemWidth.toFloat(), 0f))
                                                    }
                                                    val percentSwiped = -offsetX.value / itemWidth

                                                    if (percentSwiped <= 0.7f) {
                                                        deleteTextPosition.animateTo(itemWidth - textViewWidth)

                                                    } else {
                                                        deleteTextPosition.animateTo((itemWidth + offsetX.value))
                                                    }
                                                }
                                            },
                                            onDragStopped = { velocity ->
                                                scope.launch {
                                                    if (velocity < -2000) {
                                                        offsetX.animateTo(
                                                            targetValue = -itemWidth.toFloat(), animationSpec = spring()
                                                        )
                                                        onInstantDelete()
                                                    } else {
                                                        val offsetVal = offsetX.value
                                                        val seventyFivePercentWidth = 3f * (-itemWidth / 4f)

                                                        when {
                                                            offsetVal <= seventyFivePercentWidth -> {
                                                                offsetX.animateTo(
                                                                    targetValue = -itemWidth.toFloat(),
                                                                    animationSpec = spring()
                                                                )
                                                                onInstantDelete()
                                                            }

                                                            offsetVal <= -(textViewWidth / 2) -> {
                                                                offsetX.animateTo(
                                                                    targetValue = -textViewWidth, animationSpec = spring()
                                                                )
                                                            }

                                                            else -> {
                                                                offsetX.animateTo(0f, animationSpec = spring())
                                                            }
                                                        }
                                                    }
                                                }
                                            })
                                        .clickable {

                                        }) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(dimensionResource(R.dimen.see_all_category_movie_width))
                                    ) {
                                        Card(
                                            shape = RoundedCornerShape(dimensionResource(R.dimen.medium_border))
                                        ) {
                                            MovieImage(
                                                imageUrl = "",
                                                modifier = Modifier
                                                    .width(dimensionResource(R.dimen.see_all_category_movie_width))
                                                    .height(dimensionResource(R.dimen.see_all_category_movie_width))
                                            )
                                        }
                                        Column(
                                            modifier = Modifier
                                                .fillMaxSize()
                                                .padding(start = dimensionResource(R.dimen.medium_padding)),
                                            verticalArrangement = Arrangement.Center,
                                        ) {
                                            Text(
                                                text = "Kotlin Multiplatform Developer", style = MaterialTheme.typography.titleMedium.copy(
                                                    fontSize = fontDimensionResource(R.dimen.movie_category_item_title_size),
                                                    fontWeight = FontWeight.Bold,
                                                    color = MaterialTheme.colorScheme.onSurface
                                                )
                                            )
                                            Spacer(modifier = Modifier.height(dimensionResource(R.dimen.small_padding)))
                                            Text(
                                                text = "Multiplatform developer is a versatile Software Developer proficient in Android, Jetpack Compose, KMM, KMP, kobWeb, Flutter, iOS, SwiftUI, PHP, .net, Web design, Wordpress and Gaming development. Additionally, I demonstrates a hands-on approach to crafting elegant User Interfaces, showcasing a talent for bug resolution and consistently delivering optimal results.",
                                                style = MaterialTheme.typography.bodySmall.copy(
                                                    fontSize = fontDimensionResource(R.dimen.see_all_movie_item_content_text_size),
                                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                                ),
                                                maxLines = 3,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                            Spacer(modifier = Modifier.height(dimensionResource(R.dimen.small_padding)))
                                            Row(verticalAlignment = Alignment.CenterVertically) {
                                                Icon(
                                                    imageVector = Icons.Filled.Star,
                                                    contentDescription = "Star Rating",
                                                    modifier = Modifier.size(16.dp),
                                                    tint = MaterialTheme.colorScheme.secondary
                                                )
                                                Spacer(modifier = Modifier.width(4.dp)) // Space between star and text
                                                Text(
                                                    text = "5.0",
                                                    style = MaterialTheme.typography.labelSmall.copy(
                                                        fontWeight = FontWeight.Bold,
                                                        color = MaterialTheme.colorScheme.onSurface
                                                    )
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    """.trimIndent()
            )


            KotlinCode(
                code = """
                        //Swipe To Delete Undo

                        @Composable
                        @Preview(showBackground = true)
                        fun MySnackBar(
                            modifier: Modifier = Modifier,
                            snackBarModel: MySnackBarModel = MySnackBarModel(
                                title = "Information",
                                message = "This is Information SnackBar",
                                actionLabel = null,
                                type = SnackBarType.SUCCESS,
                                action = {},
                                onDismiss = {},
                                clickActionDismiss = {}
                            ),
                            visible: Boolean = false,
                            isDarkMode: Boolean = false,
                        ) {
                            var visibility by remember { mutableStateOf(visible) }
                            val snackBarColors = snackBarModel.type.getColors(isDarkMode)

                            LaunchedEffect(Unit) {
                                visibility = true
                            }

                            LaunchedEffect(key1 = snackBarModel, key2 = snackBarModel.type, key3 = visibility) {
                                delay(SNACK_BAR_WITH_ACTION_DELAY)
                                visibility = false
                                snackBarModel.onDismiss?.invoke()
                            }

                            AnimatedVisibility(
                                visible = visibility, modifier = modifier,
                                enter = slideInVertically(
                                    animationSpec = spring(Spring.DampingRatioHighBouncy)
                                ) + fadeIn(),
                                exit = slideOutVertically(
                                    targetOffsetY = {it /2 }
                                ) + fadeOut()
                            ) {

                                Card(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(
                                            horizontal = dimensionResource(R.dimen.small_padding),
                                            vertical = dimensionResource(R.dimen.x_small_padding)
                                        ),
                                    shape = RoundedCornerShape(dimensionResource(R.dimen.small_border)),
                                    elevation = CardDefaults.elevatedCardElevation(dimensionResource(R.dimen.small_padding)),
                                    colors = CardDefaults.cardColors(
                                        containerColor = snackBarColors.containerColor,
                                        contentColor = snackBarColors.onContainerColor
                                    )
                                ) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable {
                                                visibility = false
                                                snackBarModel.clickActionDismiss?.invoke()
                                            }
                                            .padding(dimensionResource(R.dimen.small_padding)),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Image(
                                            painterResource(snackBarColors.iconId),
                                            contentDescription = snackBarModel.message,
                                            modifier = Modifier.size(dimensionResource(R.dimen.error_icon_size)),
                                            colorFilter = ColorFilter.tint(snackBarColors.lightColor)
                                        )
                                        Column(
                                            modifier = Modifier
                                                .weight(1F)
                                                .padding(horizontal = dimensionResource(R.dimen.small_padding))
                                        ) {
                                            if (snackBarModel.title != null) {
                                                Text(
                                                    text = snackBarModel.title,
                                                    style = MaterialTheme.typography.bodyMedium.copy(
                                                        color = snackBarColors.onContainerColor,
                                                        fontWeight = FontWeight.Bold
                                                    ),
                                                    maxLines = 1,
                                                )
                                            }
                                            Text(
                                                text = snackBarModel.message,
                                                style = MaterialTheme.typography.bodySmall.copy(
                                                    color = snackBarColors.onContainerColor
                                                )
                                            )
                                        }

                                        if (snackBarModel.action != null){
                                            Text(
                                                modifier = Modifier
                                                    .padding(end = dimensionResource(R.dimen.x_small_padding))
                                                    .clickable { snackBarModel.action.invoke() },
                                                text = snackBarModel.actionLabel ?: stringResource(R.string.retry),
                                                style = MaterialTheme.typography.labelLarge.copy(
                                                    color = snackBarColors.onContainerColor, fontWeight = FontWeight.Bold
                                                ),
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        @Composable
                        fun MySnackBarHost(hostState: SnackbarHostState, isDarkMode: Boolean) {
                            SnackbarHost(hostState = hostState) { snackBarData ->
                                MySnackBar(
                                    snackBarModel = snackBarData.visuals as MySnackBarModel,
                                    isDarkMode = isDarkMode
                                )
                            }
                        }

                        data class MySnackBarModel(
                            val title : String?,
                            val type : SnackBarType,
                            val movieId : Int? = null,
                            override val message : String,
                            override val actionLabel: String? = null,
                            override val duration: SnackbarDuration = if (actionLabel != null) SnackbarDuration.Long else SnackbarDuration.Short,
                            override val withDismissAction: Boolean = false,
                            val action: (() -> Unit)? = null,
                            val onDismiss: (() -> Unit)? = null,
                            val clickActionDismiss: (() -> Unit)? = null,
                        ) : SnackbarVisuals


                        enum class SnackBarType(
                            private val lightModeColors: SnackBarColors,
                            private val darkModeColors: SnackBarColors
                        ) {
                            SUCCESS(
                                lightModeColors = SnackBarColors(
                                    lightColor = Color(0xFF1E7D3A),
                                    containerColor = Color(0xFFD3F6E6),
                                    onContainerColor = Color(0xFF003214),
                                    iconId = R.drawable.ic_success_snack_bar
                                ),
                                darkModeColors = SnackBarColors(
                                    lightColor = Color(0xFF52C474),
                                    containerColor = Color(0xFF004D20),
                                    onContainerColor = Color(0xFFA8EABF),
                                    iconId = R.drawable.ic_success_snack_bar
                                )
                            ),
                            INFO(
                                lightModeColors = SnackBarColors(
                                    lightColor = Color(0xFF005FCC),
                                    containerColor = Color(0xFFD5E3FF),
                                    onContainerColor = Color(0xFF001A3C),
                                    iconId = R.drawable.ic_info_snack_bar
                                ),
                                darkModeColors = SnackBarColors(
                                    lightColor = Color(0xFF7ABFFF),
                                    containerColor = Color(0xFF002C6D),
                                    onContainerColor = Color(0xFFB3D4FF),
                                    iconId = R.drawable.ic_info_snack_bar
                                )
                            ),
                            WARNING(
                                lightModeColors = SnackBarColors(
                                    lightColor = Color(0xFFDC7800),
                                    containerColor = Color(0xFFFFE7C2),
                                    onContainerColor = Color(0xFF3B2100),
                                    iconId = R.drawable.ic_warning_snack_bar
                                ),
                                darkModeColors = SnackBarColors(
                                    lightColor = Color(0xFFFFAB40),
                                    containerColor = Color(0xFF4E2600),
                                    onContainerColor = Color(0xFFFFD8A8),
                                    iconId = R.drawable.ic_warning_snack_bar
                                )
                            ),
                            ERROR(
                                lightModeColors = SnackBarColors(
                                    lightColor = Color(0xFFBA1A1A),
                                    containerColor = Color(0xFFFFDAD6),
                                    onContainerColor = Color(0xFF410002),
                                    iconId = R.drawable.ic_error_outline
                                ),
                                darkModeColors = SnackBarColors(
                                    lightColor = Color(0xFFFF7676),
                                    containerColor = Color(0xFF690005),
                                    onContainerColor = Color(0xFFFFB4A9),
                                    iconId = R.drawable.ic_error_outline
                                )
                            );

                            fun getColors(isDarkMode: Boolean): SnackBarColors {
                                return if (isDarkMode) darkModeColors else lightModeColors
                            }
                        }

                        data class SnackBarColors(
                            val lightColor: Color,
                            val containerColor: Color,
                            val onContainerColor: Color,
                            @DrawableRes val iconId: Int
                        )
                    """.trimIndent()
            )

            SpanText(
                text = "Map Theme",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Map Night Theme

                    [
                      {
                        "elementType": "geometry",
                        "stylers": [
                          {
                            "color": "#242f3e"
                          }
                        ]
                      },
                      {
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#746855"
                          }
                        ]
                      },
                      {
                        "elementType": "labels.text.stroke",
                        "stylers": [
                          {
                            "color": "#242f3e"
                          }
                        ]
                      },
                      {
                        "featureType": "administrative.locality",
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#d59563"
                          }
                        ]
                      },
                      {
                        "featureType": "poi",
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#d59563"
                          }
                        ]
                      },
                      {
                        "featureType": "poi.park",
                        "elementType": "geometry",
                        "stylers": [
                          {
                            "color": "#263c3f"
                          }
                        ]
                      },
                      {
                        "featureType": "poi.park",
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#6b9a76"
                          }
                        ]
                      },
                      {
                        "featureType": "road",
                        "elementType": "geometry",
                        "stylers": [
                          {
                            "color": "#38414e"
                          }
                        ]
                      },
                      {
                        "featureType": "road",
                        "elementType": "geometry.stroke",
                        "stylers": [
                          {
                            "color": "#212a37"
                          }
                        ]
                      },
                      {
                        "featureType": "road",
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#9ca5b3"
                          }
                        ]
                      },
                      {
                        "featureType": "road.highway",
                        "elementType": "geometry",
                        "stylers": [
                          {
                            "color": "#746855"
                          }
                        ]
                      },
                      {
                        "featureType": "road.highway",
                        "elementType": "geometry.stroke",
                        "stylers": [
                          {
                            "color": "#1f2835"
                          }
                        ]
                      },
                      {
                        "featureType": "road.highway",
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#f3d19c"
                          }
                        ]
                      },
                      {
                        "featureType": "transit",
                        "elementType": "geometry",
                        "stylers": [
                          {
                            "color": "#2f3948"
                          }
                        ]
                      },
                      {
                        "featureType": "transit.station",
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#d59563"
                          }
                        ]
                      },
                      {
                        "featureType": "water",
                        "elementType": "geometry",
                        "stylers": [
                          {
                            "color": "#17263c"
                          }
                        ]
                      },
                      {
                        "featureType": "water",
                        "elementType": "labels.text.fill",
                        "stylers": [
                          {
                            "color": "#515c6d"
                          }
                        ]
                      },
                      {
                        "featureType": "water",
                        "elementType": "labels.text.stroke",
                        "stylers": [
                          {
                            "color": "#17263c"
                          }
                        ]
                      }
                    ]
                    """.trimIndent()
            )

            SpanText(
                text = "Popup",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class ServerException(message: String? = null) : Exception(message)

                    @Composable
                    fun AppBanner(
                        msg: String,
                        containerColor: Color? = null,
                        contentColor: Color? = null,
                        onDismiss: (() -> Unit)?
                    ) {
                        if (msg.isEmpty()) return

                        var show by remember { mutableStateOf(false) }
                        val backgroundColor = containerColor ?: AppTheme.colorScheme.primary
                        val textColor = contentColor ?: AppTheme.colorScheme.onPrimary
                        val textStyle = AppTheme.appTypography.subTitle1.copy(color = textColor)

                        LaunchedEffect(key1 = msg) {
                            delay(200)
                            show = true
                            delay(5000)
                            show = false
                            onDismiss?.invoke()
                        }

                        Popup(
                            properties = PopupProperties(
                                dismissOnBackPress = false,
                                dismissOnClickOutside = false
                            ),
                            onDismissRequest = { onDismiss?.invoke() }
                        ) {
                            AnimatedVisibility(
                                visible = show,
                                enter = fadeIn() + slideInVertically(
                                    initialOffsetY = { -it }
                                ),
                                exit = slideOutVertically(targetOffsetY = { -it })
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(80.dp)
                                        .background(color = backgroundColor)
                                        .padding(16.dp),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    Text(
                                        text = msg,
                                        style = textStyle,
                                        maxLines = 4,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    fun AppBanner(
                        msg: Exception,
                        customMsg: String? = null,
                        containerColor: Color? = null,
                        contentColor: Color? = null,
                        onDismiss: (() -> Unit)? = {}
                    ) {
                        var show by remember { mutableStateOf(false) }
                        val backgroundColor = containerColor ?: AppTheme.colorScheme.primary
                        val textColor = contentColor ?: AppTheme.colorScheme.onPrimary
                        val textStyle = AppTheme.appTypography.subTitle1.copy(color = textColor)

                        LaunchedEffect(key1 = Unit) {
                            delay(200)
                            show = true
                            delay(5000)
                            show = false
                            onDismiss?.invoke()
                        }

                        Popup(
                            properties = PopupProperties(
                                dismissOnBackPress = false,
                                dismissOnClickOutside = false
                            ),
                            onDismissRequest = { onDismiss?.invoke() }
                        ) {
                            AnimatedVisibility(
                                visible = show,
                                enter = fadeIn() + slideInVertically(
                                    initialOffsetY = { -it }
                                ),
                                exit = slideOutVertically(targetOffsetY = { -it })
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(80.dp)
                                        .background(color = backgroundColor)
                                        .padding(16.dp),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    when (msg) {
                                        is IOException -> {
                                            Text(
                                                text = stringResource(R.string.network_error_text),
                                                style = textStyle
                                            )
                                        }

                                        is ServerException -> {
                                            Text(
                                                text = stringResource(R.string.server_error_text),
                                                style = textStyle
                                            )
                                        }

                                        else -> {
                                            Text(
                                                text = customMsg ?: stringResource(R.string.unexpected_error_text),
                                                style = textStyle,
                                                maxLines = 4,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Swipe to Favorite / Delete

                    @OptIn(ExperimentalWearMaterialApi::class)
                    @Composable
                    fun SwipeToRevealItem(content: @Composable () -> Unit, onClick: () -> Unit, iconControl: Boolean) {
                        val swipeableState = rememberSwipeableState(initialValue = 0)
                        val size = with(LocalDensity.current) { 20.dp.toPx() }
                        val anchors = mapOf(0f to 0, -size to 1)

                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(MaterialTheme.colorScheme.surface)
                                .swipeable(
                                    state = swipeableState,
                                    anchors = anchors,
                                    thresholds = { _, _ -> FractionalThreshold(0.9f) },
                                    orientation = Orientation.Horizontal
                                )
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxHeight()
                                    .width(60.dp)
                                    .align(Alignment.CenterEnd)
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    IconButton(onClick = onClick) {
                                        Icon(
                                            imageVector = if (iconControl) {
                                                Icons.Default.Favorite
                                            } else {
                                                Icons.Default.Delete
                                            },
                                            contentDescription = null,
                                            tint = if (iconControl) {
                                                colorResource(id = R.color.green)
                                            } else {
                                                colorResource(id = R.color.red)
                                            }
                                        )
                                    }
                                }
                            }

                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .offset(x = swipeableState.offset.value.dp)
                                    .background(MaterialTheme.colorScheme.surface)
                            ) {
                                content()
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun gradientBrushColor(
                        vararg colorStops: Pair<Float, Color> = arrayOf(
                            0f to Blue,
                            0.4f to Purple,
                            1f to DarkOrange,
                        )
                    ) = Brush.linearGradient(
                        colorStops = colorStops,
                        start = Offset.Zero,
                        end = Offset.Infinite
                    )

                    @Composable
                    fun GlowingBorder(
                        modifier: Modifier = Modifier,
                        cornerRadius: Dp = 24.dp,
                        innerPadding: PaddingValues = PaddingValues(0.dp),
                        blur: Dp = 14.dp,
                        animationDuration: Int = 800
                    ) {
                        val infiniteTransition = rememberInfiniteTransition("glow")
                        val borderWidth by infiniteTransition.animateValue(
                            initialValue = 5.dp,
                            targetValue = 12.dp,
                            typeConverter = Dp.VectorConverter,
                            animationSpec = infiniteRepeatable(
                                animation = tween(
                                    durationMillis = animationDuration,
                                    easing = EaseInOut
                                ),
                                repeatMode = RepeatMode.Reverse
                            ),
                            label = "glowBorder"
                        )

                        Box(modifier) {
                            Box(
                                modifier = Modifier
                                    .matchParentSize()
                                    .blur(blur)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .padding(innerPadding)
                                        .matchParentSize()
                                        .clip(RoundedCornerShape(cornerRadius))
                                        .border(
                                            width = borderWidth,
                                            brush = gradientBrushColor(),
                                            shape = RoundedCornerShape(cornerRadius)
                                        )
                                )
                            }
                        }
                    }

                    @Preview(uiMode = Configuration.UI_MODE_NIGHT_YES)
                    @Composable
                    private fun GlowingBordersPreview() {
                        MyBrainTheme {
                            Surface {
                                GlowingBorder(
                                    modifier = Modifier.fillMaxSize(),
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    enum class Priority(val value: Int) {
                        LOW( 0),
                        MEDIUM(1),
                        HIGH(2)
                    }

                    @Composable
                    fun PriorityTabRow(
                        priorities: List<Priority>,
                        selectedPriority: Priority,
                        onChange: (Priority) -> Unit
                    ) {
                        val indicator = @Composable { tabPositions: List<TabPosition> ->
                            AnimatedTabIndicator(Modifier.tabIndicatorOffset(tabPositions[selectedPriority.value]))
                        }
                        TabRow(
                            selectedTabIndex = selectedPriority.value,
                            indicator = indicator,
                            modifier = Modifier.clip(RoundedCornerShape(14.dp))
                        ) {
                            priorities.forEach {
                                Tab(
                                    text = { Text(stringResource(it.titleRes)) },
                                    selected = selectedPriority == it,
                                    onClick = {
                                        onChange(it)
                                    },
                                    modifier = Modifier.background(it.color),
                                    unselectedContentColor = Color.White.copy(alpha = 0.7f),
                                    selectedContentColor = Color.White
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/umidjonkhasimov/CineMania
            KotlinCode(
                code = """
                        //Tab Indicator Bottom Line Animation

                        sealed class HomeTabs(
                            @StringRes
                            val nameRes: Int
                        ) {
                            data object MoviesTab : HomeTabs(nameRes = R.string.movies)
                            data object TvShowsTab : HomeTabs(nameRes = R.string.tv_shows)
                        }

                        @OptIn(ExperimentalFoundationApi::class)
                        @Composable
                        fun TabIndicator(
                            tabPositions: List<TabPosition>,
                            pagerState: PagerState
                        ) {
                            val transition = updateTransition(pagerState.currentPage, label = "transition")
                            val indicatorStart by transition.animateDp(
                                transitionSpec = {
                                    if (initialState < targetState) {
                                        spring(dampingRatio = 1f, stiffness = 50f)
                                    } else {
                                        spring(dampingRatio = 1f, stiffness = 1000f)
                                    }
                                }, label = "indicatorStart"
                            ) {
                                tabPositions[it].left
                            }

                            val indicatorEnd by transition.animateDp(
                                transitionSpec = {
                                    if (initialState < targetState) {
                                        spring(dampingRatio = 1f, stiffness = 1000f)
                                    } else {
                                        spring(dampingRatio = 1f, stiffness = 50f)
                                    }
                                }, label = "indicatorEnd"
                            ) {
                                tabPositions[it].right
                            }

                            Box(
                                Modifier
                                    .offset(x = indicatorStart)
                                    .wrapContentSize(align = Alignment.BottomStart)
                                    .width(indicatorEnd - indicatorStart)
                                    .height(4.dp)
                                    .padding(horizontal = (indicatorEnd - indicatorStart) / 3)
                                    .background(
                                        color = MaterialTheme.colorScheme.primary,
                                        shape = MaterialTheme.shapes.small
                                    )
                                    .zIndex(1f)
                            )
                        }

                        @OptIn(ExperimentalFoundationApi::class)
                        @Composable
                        private fun HomeScreenContent(
                            uiState: UiState,
                            onMovieItemClick: (Int) -> Unit,
                            onTvShowItemClick: (Int) -> Unit,
                            onSeeAllMoviesClick: (AllMoviesScreenParam) -> Unit,
                            onSeeAllTvShowsClick: (AllTvShowsScreenParam) -> Unit
                        ) {
                            Scaffold { paddingValues ->
                                val tabs = listOf(
                                    HomeTabs.MoviesTab,
                                    HomeTabs.TvShowsTab
                                )
                                val tabPagerState = rememberPagerState { tabs.size }
                                val scope = rememberCoroutineScope()

                                Column(
                                    modifier = Modifier.padding(paddingValues)
                                ) {
                                    CineManiaTabRow(
                                        tabs = tabs,
                                        tabPagerState = tabPagerState,
                                        scope = scope
                                    )

                                    if (uiState.isLoading)
                                        HomeShimmerEffect(modifier = Modifier.padding(paddingValues))
                                    else {
                                        HorizontalPager(
                                            state = tabPagerState
                                        ) { page ->
                                            when (tabs[page]) {
                                                is HomeTabs.MoviesTab -> {
                                                    MoviesTabContent(
                                                        uiState = uiState,
                                                        onMovieItemClick = onMovieItemClick,
                                                        onSeeAllClick = onSeeAllMoviesClick
                                                    )
                                                }

                                                is HomeTabs.TvShowsTab -> {
                                                    TvShowsTabContent(
                                                        uiState = uiState,
                                                        onTvShowClick = onTvShowItemClick,
                                                        onSeeAllClick = onSeeAllTvShowsClick
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        @OptIn(ExperimentalFoundationApi::class)
                        @Composable
                        fun CineManiaTabRow(
                            tabs: List<HomeTabs>,
                            tabPagerState: PagerState,
                            scope: CoroutineScope
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(start = 16.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                CineManiaGradientIcon(
                                    modifier = Modifier
                                        .size(32.dp)
                                        .align(Alignment.Bottom)
                                )

                                TabRow(
                                    modifier = Modifier
                                        .fillMaxWidth(.6f),
                                    selectedTabIndex = tabPagerState.currentPage,
                                    indicator = {
                                        TabIndicator(
                                            tabPositions = it,
                                            pagerState = tabPagerState
                                        )
                                    },
                                    divider = {}
                                ) {
                                    tabs.forEachIndexed { index, tab ->
                                        Tab(
                                            modifier = Modifier.clip(MaterialTheme.shapes.small),
                                            selected = tabPagerState.currentPage == index,
                                            onClick = {
                                                scope.launch {
                                                    tabPagerState.animateScrollToPage(index)
                                                }
                                            }
                                        ) {
                                            Text(
                                                modifier = Modifier.padding(8.dp),
                                                text = stringResource(tab.nameRes),
                                                style = MaterialTheme.typography.titleMedium
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/kaaneneskpc/Deliverr.git
            KotlinCode(
                code = """
                        val listOfTabs = listOf("Upcoming", "History")
                        val coroutineScope = rememberCoroutineScope()
                        val pagerState = rememberPagerState(pageCount = { listOfTabs.size }, initialPage = 0)

                        TabRow(selectedTabIndex = pagerState.currentPage,
                        modifier = Modifier.padding(16.dp).clip(RoundedCornerShape(32.dp))
                        .border(
                            width = 1.dp,
                            color = Color.LightGray,
                            shape = RoundedCornerShape(32.dp)
                        )
                        .padding(4.dp),
                        indicator = {},
                        divider = {}) {
                            listOfTabs.forEachIndexed { index, title ->
                                Tab(text = {
                                    Text(
                                        text = title,
                                        color = if (pagerState.currentPage == index) Color.White else Color.Gray
                                    )
                                }, selected = pagerState.currentPage == index, onClick = {
                                    coroutineScope.launch {
                                        pagerState.animateScrollToPage(index)
                                    }
                                }, modifier = Modifier
                                    .clip(
                                        RoundedCornerShape(32.dp)
                                    )
                                    .background(
                                        color = if (pagerState.currentPage == index) MaterialTheme.colorScheme.primary else Color.White
                                    )
                                )
                            }
                        }

                        HorizontalPager(state = pagerState) {
                            when (it) {
                                0 -> {
                                    OrderListInternal(list.filter { order -> order.status == "Pending" },
                                        onClick = { order ->

                                        })
                                }

                                1 -> {
                                    OrderListInternal(list.filter { order -> order.status != "Pending" },
                                        onClick = { order ->

                                        })
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //ImagePreview

                    @Composable
                    fun ImagePreviewDialog(photo: Photo?, onDismissCLicked: () -> Unit) {
                        Dialog(
                            onDismissRequest = onDismissCLicked,
                            properties = DialogProperties(
                                dismissOnBackPress = true,
                                dismissOnClickOutside = true
                            )
                        ) {
                            DialogContent(
                                imageUrl = photo?.urls?.regular,
                                imageColor = photo?.color,
                                blurHash = photo?.blurHash,
                                imageWidth = photo?.width,
                                imageHeight = photo?.height,
                                authorOrDescriptionText = photo?.description ?: photo?.alternateDescription
                                ?: photo?.user?.name
                            )
                        }
                    }

                    @OptIn(ExperimentalFoundationApi::class, ExperimentalMaterial3WindowSizeClassApi::class)
                    @Preview
                    @Composable
                    private fun DialogContent(
                        imageUrl: String? = null,
                        imageColor: String? = null,
                        blurHash: String? = null,
                        imageWidth: Int? = null,
                        imageHeight: Int? = null,
                        authorOrDescriptionText: String? = null,
                    ) {
                        val imageColorParsed = (imageColor?.color ?: Color(0xFF212121))
                        val imageColorParseComplementary = imageColorParsed.complementary()
                        val aspectRatio: Float by remember {
                            derivedStateOf { (imageWidth?.toFloat() ?: 1.0F) / (imageHeight?.toFloat() ?: 1.0F) }
                        }

                        val windowSizeClass = calculateWindowSizeClass()

                        val frameSize: Pair<Float, Float> by remember {
                            derivedStateOf {
                                when (windowSizeClass.widthSizeClass) {
                                    WindowWidthSizeClass.Compact -> 400F to 500F
                                    WindowWidthSizeClass.Medium -> 400F to 500F
                                    else -> 600F to 700F
                                }
                            }
                        }

                        val animatedSize by animateSizeAsState(
                            targetValue = Size(frameSize.first, frameSize.second),
                            animationSpec = tween(durationMillis = 1000)
                        )

                        Box(
                            modifier = Modifier
                                .size(width = animatedSize.width.dp, height = animatedSize.height.dp)
                        ) {

                            Card(
                                backgroundColor = imageColorParsed,
                                elevation = 0.dp,
                                shape = RoundedCornerShape(10.dp),
                                modifier = Modifier.fillMaxSize()
                            ) {
                                AsyncImage(
                                    model = imageUrl,
                                    contentDescription = null,
                                    contentScale = ContentScale.FillBounds,
                                    modifier = Modifier
                                        .aspectRatio(aspectRatio)
                                        .fillMaxWidth()
                                        .defaultMinSize(minHeight = 200.dp)
                                )
                            }

                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(40.dp)
                                    .background(
                                        imageColorParsed,
                                        shape = RoundedCornerShape(
                                            bottomStart = 10.dp,
                                            bottomEnd = 10.dp
                                        )
                                    )
                                    .align(Alignment.BottomCenter)
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.ic_image),
                                    contentScale = ContentScale.Crop,
                                    contentDescription = null,
                                    modifier = Modifier
                                        .padding(start = 10.dp)
                                        .align(Alignment.CenterVertically),
                                    colorFilter = ColorFilter.tint(
                                        color = imageColorParseComplementary
                                    )
                                )

                                Text(
                                    text = authorOrDescriptionText.orEmpty(),
                                    color = imageColorParseComplementary,
                                    fontSize = 14.sp,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    softWrap = false,
                                    fontStyle = FontStyle.Italic,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(start = 10.dp, end = 10.dp)
                                        .align(Alignment.CenterVertically)
                                        .basicMarquee()
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/dalafiarisamuel/Unsplash-KMP
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterialApi::class)
                    @Composable
                    fun PhotoLargeDisplay(
                        modifier: Modifier,
                        imageUrl: String,
                        imageColor: String? = null,
                        imageSize: Size,
                    ) {
                        var isScaled: Boolean by remember { mutableStateOf(false) }
                        var showShimmer: Boolean by remember { mutableStateOf(true) }
                        val scale: Float by animateFloatAsState(
                            targetValue = if (isScaled) 1.45f else 1f, animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessLow,
                                visibilityThreshold = 0.001f
                            )
                        )

                        val aspectRatio: Float by remember {
                            derivedStateOf { (imageSize.width / imageSize.height) }
                        }

                        val painter: AsyncImagePainter = rememberAsyncImagePainter(imageUrl)
                        if (painter.state is AsyncImagePainter.State.Success) { showShimmer = false }

                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .then(modifier))
                        {
                            Card(
                                elevation = 24.dp,
                                shape = RoundedCornerShape(10.dp),
                                modifier = modifier,
                                onClick = { isScaled = !isScaled }) {
                                Image(
                                    painter = painter,
                                    contentScale = ContentScale.Crop,
                                    contentDescription = null,
                                    modifier = Modifier
                                        .background(
                                            shimmerBrush(
                                                targetValue = 1300f,
                                                showShimmer = showShimmer,
                                                shimmerColorFrame = imageColor
                                            )
                                        )
                                        .aspectRatio(aspectRatio)
                                        .scale(scale)
                                        .width(350.dp)
                                        .height(500.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //BlueeredBox Button

                    @Composable
                    fun BlurredBox(modifier: Modifier = Modifier, content : @Composable () -> Unit) {
                        Box(
                            modifier = modifier
                        ) {
                            Box(
                                modifier = Modifier
                                    .matchParentSize() // Fill the entire box
                                    .background(Color.LightGray.copy(alpha = 0.35f)) // Background color with some transparency
                                    .blur(25.dp) // Apply blur to the background
                            )
                            content()
                        }
                    }

                    @Preview(apiLevel = 34, showSystemUi = true, showBackground = true,
                        wallpaper = Wallpapers.RED_DOMINATED_EXAMPLE
                    )

                    @Composable
                    fun BlurredBoxPreview() {
                        MaterialTheme {
                            BlurredBox(
                            ) {
                                Text("Hello, World!")
                            }
                        }
                    }

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun TooltipPopup(
                        modifier: Modifier = Modifier,
                        triggeredView: @Composable (Modifier) -> Unit,
                        tooltipContent: @Composable () -> Unit,
                    ) {
                        var isShowTooltip by remember { mutableStateOf(false) }
                        var position by remember { mutableStateOf(TooltipPopupPosition()) }

                        val view = LocalView.current.rootView

                        if (isShowTooltip) {
                            TooltipPopup(
                                onDismissRequest = {
                                    isShowTooltip = isShowTooltip.not()
                                },
                                position = position,
                            ) {
                                tooltipContent()
                            }
                        }
                        triggeredView(modifier
                            .clickableTooltip {
                                isShowTooltip = isShowTooltip.not()
                            }
                            .onGloballyPositioned { coordinates ->
                                position = calculateTooltipPopupPosition(view, coordinates)
                            }
                        )
                    }

                    @Composable
                    fun TooltipPopup(
                        position: TooltipPopupPosition,
                        backgroundShape: Shape = MaterialTheme.shapes.medium,
                        backgroundColor: Color = MaterialTheme.colorScheme.surfaceVariant,
                        arrowHeight: Dp = 8.dp,
                        horizontalPadding: Dp = 16.dp,
                        onDismissRequest: (() -> Unit)? = null,
                        content: @Composable () -> Unit
                    ) {
                        var alignment = Alignment.TopCenter
                        var offset = position.offset

                        val horizontalPaddingInPx = with(LocalDensity.current) { horizontalPadding.toPx() }
                        var arrowPositionX by remember { mutableFloatStateOf(position.centerPositionX) }

                        with(LocalDensity.current) {
                            val arrowPaddingPx = arrowHeight.toPx().roundToInt() * 2

                            when (position.alignment) {
                                TooltipAlignment.TopCenter -> {
                                    alignment = Alignment.TopCenter
                                    offset = offset.copy(
                                        y = position.offset.y + arrowPaddingPx
                                    )
                                }
                                TooltipAlignment.BottomCenter -> {
                                    alignment = Alignment.BottomCenter
                                    offset = offset.copy(
                                        y = position.offset.y - arrowPaddingPx
                                    )
                                }
                            }
                        }

                        val popupPositionProvider = remember(alignment, offset) {
                            mutableStateOf(TooltipAlignmentOffsetPositionProvider(
                                alignment = alignment,
                                offset = offset,
                                horizontalPaddingInPx = horizontalPaddingInPx,
                                centerPositionX = position.centerPositionX,
                            ) { position ->
                                arrowPositionX = position
                            })
                        }

                        Popup(
                            popupPositionProvider = popupPositionProvider.value,
                            onDismissRequest = onDismissRequest,
                            properties = PopupProperties(dismissOnBackPress = false),
                        ) {
                            TooltipMainLayout(
                                alignment = position.alignment,
                                arrowHeight = arrowHeight,
                                arrowPositionX = arrowPositionX,
                                modifier = Modifier
                                    .padding(horizontal = horizontalPadding)
                                    .background(
                                        color = backgroundColor,
                                        shape = backgroundShape,
                                    )
                            ) {
                                content()
                            }
                        }
                    }

                    @Stable
                    internal class TooltipAlignmentOffsetPositionProvider(
                        val alignment: Alignment,
                        val offset: IntOffset,
                        val centerPositionX: Float,
                        val horizontalPaddingInPx: Float,
                        private val onArrowPositionX: (Float) -> Unit,
                    ) : PopupPositionProvider {

                        override fun calculatePosition(
                            anchorBounds: IntRect,
                            windowSize: IntSize,
                            layoutDirection: LayoutDirection,
                            popupContentSize: IntSize
                        ): IntOffset {
                            var popupPosition = IntOffset(0, 0)

                            val parentAlignmentPoint = alignment.align(
                                IntSize.Zero,
                                IntSize(anchorBounds.width, anchorBounds.height),
                                layoutDirection
                            )

                            val relativePopupPos = alignment.align(
                                IntSize.Zero,
                                IntSize(popupContentSize.width, popupContentSize.height),
                                layoutDirection
                            )

                            popupPosition += IntOffset(anchorBounds.left, anchorBounds.top)

                            popupPosition += parentAlignmentPoint

                            popupPosition -= IntOffset(relativePopupPos.x, relativePopupPos.y)

                            val resolvedOffset = IntOffset(
                                offset.x * (if (layoutDirection == LayoutDirection.Ltr) 1 else -1),
                                offset.y
                            )

                            popupPosition += resolvedOffset

                            val leftSpace = centerPositionX - horizontalPaddingInPx
                            val rightSpace = windowSize.width - centerPositionX - horizontalPaddingInPx

                            val tooltipWidth = popupContentSize.width
                            val halfPopupContentSize = popupContentSize.center.x

                            val fullPadding = horizontalPaddingInPx * 2

                            val maxTooltipSize = windowSize.width - fullPadding

                            val isCentralPositionTooltip = halfPopupContentSize <= leftSpace && halfPopupContentSize <= rightSpace

                            when {
                                isCentralPositionTooltip -> {
                                    popupPosition = IntOffset(centerPositionX.toInt() - halfPopupContentSize, popupPosition.y)
                                    val arrowPosition = halfPopupContentSize.toFloat() - horizontalPaddingInPx
                                    onArrowPositionX.invoke(arrowPosition)
                                }
                                tooltipWidth >= maxTooltipSize -> {
                                    popupPosition = IntOffset(windowSize.center.x - halfPopupContentSize, popupPosition.y)
                                    val arrowPosition = centerPositionX - popupPosition.x - horizontalPaddingInPx
                                    onArrowPositionX.invoke(arrowPosition)
                                }
                                halfPopupContentSize > rightSpace -> {
                                    popupPosition = IntOffset(centerPositionX.toInt(), popupPosition.y)
                                    val arrowPosition = halfPopupContentSize + (halfPopupContentSize - rightSpace) - fullPadding

                                    onArrowPositionX.invoke(arrowPosition)
                                }
                                halfPopupContentSize > leftSpace -> {
                                    popupPosition = IntOffset(0, popupPosition.y)
                                    val arrowPosition = centerPositionX - horizontalPaddingInPx
                                    onArrowPositionX.invoke(arrowPosition)
                                }
                                else -> {
                                    val position = centerPositionX
                                    onArrowPositionX.invoke(position)
                                }
                            }

                            return popupPosition
                        }
                    }

                    @Composable
                    fun TooltipMainLayout(
                        modifier: Modifier = Modifier,
                        alignment: TooltipAlignment = TooltipAlignment.TopCenter,
                        arrowHeight: Dp,
                        arrowPositionX: Float,
                        content: @Composable () -> Unit
                    ) {

                        val arrowHeightPx = with(LocalDensity.current) {
                            arrowHeight.toPx()
                        }
                        val arrowColor = MaterialTheme.colorScheme.surfaceVariant
                        val animatedArrowPositionX by animateFloatAsState(
                            targetValue = arrowPositionX,
                            animationSpec = spring(
                                dampingRatio = Spring.DampingRatioMediumBouncy,
                                stiffness = Spring.StiffnessVeryLow
                            ), label = ""
                        )

                        Box(
                            modifier = modifier
                                .drawBehind {
                                    if (animatedArrowPositionX <= 0f) return@drawBehind

                                    val isTopCenter = alignment == TooltipAlignment.TopCenter

                                    val path = Path()

                                    if (isTopCenter) {
                                        val position = Offset(animatedArrowPositionX, 0f)
                                        path.apply {
                                            moveTo(x = position.x, y = position.y)
                                            lineTo(x = position.x - arrowHeightPx, y = position.y)
                                            lineTo(x = position.x, y = position.y - arrowHeightPx)
                                            lineTo(x = position.x + arrowHeightPx, y = position.y)
                                            lineTo(x = position.x, y = position.y)
                                        }
                                    } else {
                                        val arrowY = drawContext.size.height
                                        val position = Offset(animatedArrowPositionX, arrowY)
                                        path.apply {
                                            moveTo(x = position.x, y = position.y)
                                            lineTo(x = position.x + arrowHeightPx, y = position.y)
                                            lineTo(x = position.x, y = position.y + arrowHeightPx)
                                            lineTo(x = position.x - arrowHeightPx, y = position.y)
                                            lineTo(x = position.x, y = position.y)
                                        }
                                    }

                                    drawPath(
                                        path = path,
                                        color = arrowColor,
                                    )
                                    path.close()
                                }

                        ) {
                            AnimatedContent(
                                targetState = arrowPositionX,
                                transitionSpec = {
                                    slideIntoContainer(towards = AnimatedContentTransitionScope.SlideDirection.End, initialOffset = {
                                        it / 5
                                    }, animationSpec = spring(
                                        dampingRatio = Spring.DampingRatioMediumBouncy,
                                        stiffness = Spring.StiffnessVeryLow
                                    )).togetherWith(
                                        ExitTransition.None
                                    )
                                },
                                label = "",
                            ) { targetValue ->
                                if (targetValue > 30f) {
                                    content()
                                }
                            }
                        }
                    }

                    @Stable
                    data class TooltipPopupPosition(val alignment: TooltipAlignment = TooltipAlignment.TopCenter,
                        val offset: IntOffset = IntOffset(0, 0),
                        val centerPositionX: Float = 0f,
                    )

                    enum class TooltipAlignment {
                        TopCenter, BottomCenter
                    }

                    @Composable
                    fun Modifier.clickableTooltip(onClick: () -> Unit): Modifier = composed {
                        clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = onClick
                        )
                    }

                    fun calculateTooltipPopupPosition(
                        view: View,
                        coordinates: LayoutCoordinates,
                    ): TooltipPopupPosition {
                        val layoutPosition = IntArray(2)
                        view.getLocationOnScreen(layoutPosition)

                        val location = coordinates.positionInWindow()

                        val x = location.x + coordinates.size.width / 2f
                        val y = location.y - layoutPosition[1]

                        return if (y < view.height / 2) {
                            TooltipPopupPosition(
                                alignment = TooltipAlignment.TopCenter,
                                offset = IntOffset(0, coordinates.size.height),
                                centerPositionX = x
                            )
                        } else {
                            TooltipPopupPosition(
                                alignment = TooltipAlignment.BottomCenter,
                                offset = IntOffset(0, -coordinates.size.height),
                                centerPositionX = x
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                //Interactive Popup Menu

                code = """
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            enableEdgeToEdge()
                            setContent {
                                InteractivePopupMenuTheme {
                                    Surface() {
                                        ContactsList()
                                    }

                                }
                            }
                        }
                    }

                    @Preview(showBackground = true)
                    @Composable
                    fun ContactsList() {
                        var showPopup by remember { mutableStateOf(false) }
                        var applyOffest by remember { mutableStateOf(false) }
                        var selectedIndex by remember { mutableStateOf(-1) }
                        val lazyListState = rememberLazyListState()
                        val coroutineScope = rememberCoroutineScope()

                        val context = LocalContext.current

                        var contacts by remember { mutableStateOf(initContacts()) }

                        Box(
                            modifier =
                            Modifier
                                .fillMaxSize()
                                .background(Color.White),
                        ) {
                            LazyColumn(
                                state = lazyListState,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(10.dp),
                                contentPadding = PaddingValues(
                                    vertical = 40.dp,
                                ),
                            ) {

                                itemsIndexed(contacts) { index, contact ->
                                    val shadow = when {
                                        showPopup && selectedIndex != index -> 0.dp
                                        showPopup && selectedIndex == index -> 10.dp
                                        else -> 2.dp
                                    }

                                    Box {
                                        ElevatedButton(
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = Color.White,
                                            ),
                                            elevation = ButtonDefaults.elevatedButtonElevation(
                                                defaultElevation = shadow,
                                                pressedElevation = 0.dp,
                                                disabledElevation = 0.dp
                                            ),
                                            shape = RoundedCornerShape(50.dp),
                                            modifier = Modifier
                                                .fillMaxSize()
                                                .padding(2.dp),
                                            onClick = {
                                                selectedIndex = index
                                                coroutineScope.launch {
                                                    handleItemClick(lazyListState, selectedIndex) { showOffest ->
                                                        applyOffest = showOffest
                                                    }
                                                    showPopup = true
                                                }
                                            }) {

                                            Box(
                                                modifier = Modifier.fillMaxSize(),
                                                contentAlignment = Alignment.CenterStart
                                            ) {
                                                Row(
                                                    verticalAlignment = Alignment.CenterVertically,
                                                ) {
                                                    Avatar(contact.name)

                                                    Row(
                                                        modifier = Modifier
                                                            .fillMaxWidth()
                                                            .weight(1f),
                                                        verticalAlignment = Alignment.CenterVertically
                                                    ) {
                                                        Text(
                                                            contact.name,
                                                            fontSize = 16.sp,
                                                            color = Color.Black.copy(alpha = 0.9f),
                                                            fontWeight = FontWeight.Medium,
                                                            modifier = Modifier
                                                                .offset(x = -5.dp)
                                                                .wrapContentWidth(),
                                                        )

                                                        if (contact.isStarred) {
                                                            Icon(
                                                                painter = painterResource(id = R.drawable.ic_star_on),
                                                                contentDescription = null,
                                                                tint = Color(0xFFF7BB0A),
                                                                modifier = Modifier
                                                                    .size(18.dp)
                                                                    .offset(y = -1.dp)
                                                            )
                                                        }
                                                    }

                                                    Row(
                                                        modifier = Modifier.offset(x = 10.dp)
                                                    ) {

                                                        SmallButton(R.drawable.ic_call, Color(0xFF38AF48)) {}
                                                        Spacer(modifier = Modifier.width(3.dp))
                                                        SmallButton(R.drawable.ic_chat, Color(0XFF26A6DA)) {}
                                                        Spacer(modifier = Modifier.width(3.dp))
                                                    }
                                                }
                                            }
                                        }

                                        if (showPopup && selectedIndex == index) {
                                            Popup(
                                                offset = IntOffset(x = 0, y = if (applyOffest) -780 else 130),
                                                onDismissRequest = {
                                                    showPopup = false
                                                    selectedIndex = -1
                                                },
                                                properties = PopupProperties(focusable = true, clippingEnabled = false),
                                            ) {

                                                MyPopupMenu(contact, onEdit = { newName ->

                                                    contacts = contacts.toMutableList().apply {
                                                        this[index] = this[index].copy(name = newName)
                                                    }

                                                }, onStarred = { starred ->

                                                    contacts = contacts.toMutableList().apply {
                                                        this[index] = this[index].copy(isStarred = starred)
                                                    }

                                                }, onShare = {
                                                    val shareText = ""

                                                    val sendIntent: Intent = Intent().apply {
                                                        action = Intent.ACTION_SEND
                                                        putExtra(Intent.EXTRA_TEXT, shareText)
                                                        type = "text/plain"
                                                    }
                                                    val shareIntent = Intent.createChooser(sendIntent, null)

                                                    showPopup = false

                                                    context.startActivity(shareIntent)


                                                }, onDelete = {
                                                    contacts = contacts.toMutableList().apply {
                                                        removeAt(index)
                                                    }
                                                    showPopup = false

                                                })
                                            }
                                        }

                                    }

                                    Spacer(modifier = Modifier.height(10.dp))
                                }
                            }

                            Crossfade(targetState = showPopup, label = "") { showPopup ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .background(if (showPopup) Color.Black.copy(alpha = 0.1f) else Color.Transparent)
                                )
                            }
                        }
                    }

                    @Composable
                    private fun Avatar(contactName: String) {
                        val initials = contactName.split(" ").joinToString("") { it.first().uppercase() }

                        val bgColor = remember {
                            Color(
                                red = Random.nextFloat(),
                                green = Random.nextFloat(),
                                blue = Random.nextFloat(),
                                alpha = 0.2f
                            )
                        }
                        Box(
                            modifier = Modifier
                                .offset(x = -15.dp)
                                .size(50.dp)
                                .clip(CircleShape)
                                .border(1.dp, Color.Gray.copy(alpha = 0.3f), CircleShape)
                                .background(bgColor),
                            contentAlignment = Alignment.Center
                        ) {

                            Text(initials, fontSize = 18.sp, color = Color.Black, fontWeight = FontWeight.Medium)
                        }
                    }

                    @Composable
                    private fun SmallButton(@DrawableRes icon: Int, color: Color, onClick: () -> Unit) {
                        Box(
                            modifier = Modifier
                                .size(35.dp)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = ripple(bounded = false, radius = 25.dp),
                                    onClick = onClick
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                painter = painterResource(id = icon),
                                contentDescription = null,
                                tint = color,
                                modifier = Modifier.size(22.dp)
                            )
                        }
                    }

                    private fun handleItemClick(
                        lazyListState: LazyListState,
                        clickedIndex: Int,
                        onClick: (Boolean) -> Unit
                    ) {
                        val layoutInfo = lazyListState.layoutInfo
                        val visibleItemsInfo = layoutInfo.visibleItemsInfo
                        val totalVisibleItems = visibleItemsInfo.size
                        val clickedVisibleIndex = clickedIndex - visibleItemsInfo.first().index
                        if (clickedVisibleIndex > totalVisibleItems / 2) {
                            val targetIndex = clickedIndex - totalVisibleItems / 4
                            onClick(true)
                        } else onClick(false)
                    }

                    fun initContacts(): List<Contact> {
                        val names = listOf(
                            "John Smith",
                            "Mary Johnson",
                            "Michael Brown",
                            "Jennifer Davis",
                            "James Wilson",
                            "Elizabeth Taylor",
                            "David Anderson",
                            "Sarah Thomas",
                            "Robert Miller",
                            "Emily White",
                            "William Harris",
                            "Linda Martin",
                            "Charles King",
                            "Patricia Scott",
                            "Christopher Moore"
                        )

                        return names.map { name ->
                            Contact(name = name)
                        }
                    }

                    data class Contact(
                        val name: String,
                        val isStarred: Boolean = false,
                        val numbers: Array<String> = arrayOf("+91 9586331823", "+91 7990971929")
                    )

                    @Composable
                    fun MyPopupMenu(
                        contact: Contact,
                        onEdit: (String) -> Unit,
                        onStarred: (Boolean) -> Unit,
                        onShare: () -> Unit,
                        onDelete: () -> Unit
                    ) {
                        var conName by remember { mutableStateOf(contact.name) }
                        val context = LocalContext.current

                        var isExpanded by remember { mutableStateOf(false) }
                        val scale by animateFloatAsState(
                            targetValue = if (isExpanded) 1f else 0.9f,
                            animationSpec = tween(
                                durationMillis = 100, easing = FastOutSlowInEasing
                            )
                        )

                        LaunchedEffect(Unit) {
                            isExpanded = true
                        }

                        var isStared by remember { mutableStateOf(contact.isStarred) }
                        var isEditable by remember { mutableStateOf(false) }
                        var textFieldValue by remember { mutableStateOf(TextFieldValue(text = conName)) }
                        var areNumbersShown by remember { mutableStateOf(false) }
                        var areShareOptionsShown by remember { mutableStateOf(false) }
                        var enableDel by remember { mutableStateOf(false) }
                    //    var isChecked by remember { mutableStateOf(true) }

                        val focusRequester = remember { FocusRequester() }
                        val keyboardController = LocalSoftwareKeyboardController.current

                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .wrapContentHeight()
                                .graphicsLayer(
                                    scaleX = scale,
                                    scaleY = scale
                                )
                                .shadow(
                                    elevation = 30.dp,
                                    shape = RoundedCornerShape(17.dp),
                                    spotColor = Color(0x27000000),
                                    ambientColor = Color(0xFFFFFFFF)

                                )
                                .padding(top = 5.dp, bottom = 10.dp, start = 20.dp, end = 20.dp),
                            shape = RoundedCornerShape(17.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = Color.White,
                            ),
                            elevation = CardDefaults.cardElevation(defaultElevation = 5.dp),
                        ) {
                            Column {
                                // Star
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clip(
                                            RoundedCornerShape(
                                                topEnd = 17.dp,
                                                topStart = 17.dp,
                                                bottomEnd = 0.dp,
                                                bottomStart = 0.dp
                                            )
                                        )
                                        .clickable(
                                            indication = ripple(color = Color(0x03000000)),
                                            interactionSource = remember { MutableInteractionSource() }
                                        ) {
                                            isStared = !isStared
                                            onStarred(isStared)
                                        }
                                ) {

                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(vertical = 15.dp)
                                    ) {
                                        Text(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .weight(1f)
                                                .padding(horizontal = 20.dp),
                                            text = if (isStared) "Unstar" else "Star",
                    //                        fontFamily = FontFamily.Monospace,
                                            fontSize = 17.sp,
                    //                        fontWeight = FontWeight.Bold,
                                            textAlign = TextAlign.Start,
                                            color = Color(0xFF414141)
                                        )

                                        Icon(
                                            painterResource(id = if (isStared) R.drawable.ic_star_on else R.drawable.ic_star_off),
                                            contentDescription = "",
                                            tint = Color(0xFFF7BB0A),
                                            modifier = Modifier
                                                .padding(end = 20.dp)
                                                .size(26.dp)
                                        )
                                    }

                                    Spacer(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(1.dp)
                                            .background(Color.Gray.copy(alpha = 0.2f))
                                    )
                                }

                                //Edit
                                Column {
                                    LaunchedEffect(isEditable) {
                                        if (isEditable) {
                                            focusRequester.requestFocus()
                                            textFieldValue = TextFieldValue(
                                                text = textFieldValue.text,
                                                selection = TextRange(textFieldValue.text.length)
                                            )
                                            keyboardController?.show()
                                        } else {
                                            keyboardController?.hide()
                                        }
                                    }

                                    DisposableEffect(Unit) {
                                        onDispose {
                                            keyboardController?.hide()
                                        }
                                    }

                                    AnimatedContent(
                                        targetState = isEditable,
                                    ) { targetState ->

                                        if (targetState) {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(vertical = 10.dp),
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                BasicTextField(
                                                    value = textFieldValue,
                                                    onValueChange = { textFieldValue = it },
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .padding(start = 15.dp, end = 10.dp)
                                                        .background(
                                                            Color(0xFFF1F1F1),
                                                            shape = RoundedCornerShape(12.dp)
                                                        )
                                                        .padding(12.dp)
                                                        .weight(1f)
                                                        .focusRequester(focusRequester),
                                                    textStyle = TextStyle(
                                                        color = Color(0xFF505050),
                                                        fontSize = 16.sp,
                    //                                    fontFamily = FontFamily.Monospace
                                                    ),
                                                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                                                    decorationBox = { innerTextField ->
                                                        if (textFieldValue.text.isEmpty()) {
                                                            Text(
                                                                "Edit Name",
                                                                style = TextStyle(
                                                                    color = Color(0xFF9B9B9B),
                                                                    fontSize = 16.sp
                                                                )
                                                            )
                                                        }
                                                        innerTextField()
                                                    }
                                                )

                                                Row(
                                                    modifier = Modifier
                                                        .padding(end = 10.dp)
                                                ) {
                                                    ElevatedButton(
                                                        elevation = ButtonDefaults.elevatedButtonElevation(
                                                            defaultElevation = 4.dp,
                                                            pressedElevation = 1.dp,
                                                            disabledElevation = 4.dp
                                                        ),
                                                        colors = ButtonDefaults.buttonColors(
                                                            containerColor = Color.White,
                                                            contentColor = MaterialTheme.colorScheme.onSurface,
                                                            disabledContainerColor = Color.White
                                                        ),
                                                        shape = RoundedCornerShape(10.dp),
                                                        onClick = {
                                                            conName = textFieldValue.text
                                                            isEditable = false
                                                            onEdit(conName)
                                                        },
                                                        interactionSource = remember { MutableInteractionSource() },
                                                        modifier = Modifier
                                                            .size(43.dp)
                                                            .padding(5.dp),
                                                        contentPadding = PaddingValues(0.dp) // Optional: Remove padding to make icon fit better
                                                    ) {
                                                        Icon(
                                                            painterResource(id = R.drawable.ic_check),
                                                            contentDescription = "",
                                                            tint = Color(0xF44BC20B),
                                                            modifier = Modifier
                                                                .padding(5.dp)
                                                                .size(23.dp)
                                                        )
                                                    }

                                                    ElevatedButton(
                                                        elevation = ButtonDefaults.elevatedButtonElevation(
                                                            defaultElevation = 4.dp,
                                                            pressedElevation = 1.dp,
                                                            disabledElevation = 4.dp
                                                        ),
                                                        colors = ButtonDefaults.buttonColors(
                                                            containerColor = Color.White,
                                                            contentColor = MaterialTheme.colorScheme.onSurface,
                                                            disabledContainerColor = Color.White
                                                        ),
                                                        shape = RoundedCornerShape(10.dp),
                                                        onClick = {
                                                            isEditable = false
                                                        },
                                                        interactionSource = remember { MutableInteractionSource() },
                                                        modifier = Modifier
                                                            .size(43.dp)
                                                            .padding(5.dp),
                                                        contentPadding = PaddingValues(0.dp)
                                                    ) {
                                                        Icon(
                                                            painterResource(id = R.drawable.ic_x),
                                                            contentDescription = "",
                                                            tint = Color(0xF4E61414),
                                                            modifier = Modifier
                                                                .padding(5.dp)
                                                                .size(22.dp)
                                                        )
                                                    }
                                                }
                                            }
                                        } else {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .clickable(
                                                        indication = ripple(color = Color(0x03000000)),
                                                        interactionSource = remember { MutableInteractionSource() }
                                                    ) {
                                                        isEditable = true
                                                    },
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Text(
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .weight(1f)
                                                        .padding(horizontal = 20.dp, vertical = 17.dp),
                                                    text = "Edit Name",
                                                    fontSize = 17.sp,
                                                    textAlign = TextAlign.Start,
                                                    color = Color(0xFF414141)
                                                )

                                                Icon(
                                                    painterResource(id = R.drawable.ic_edit),
                                                    contentDescription = "",
                                                    tint = Color(0xFF414141),
                                                    modifier = Modifier
                                                        .padding(end = 20.dp)
                                                        .size(20.dp)
                                                )
                                            }
                                        }
                                    }

                                    Spacer(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(1.dp)
                                            .background(Color.Gray.copy(alpha = 0.2f))
                                    )
                                }

                                // Numbers
                                Column {
                                    AnimatedContent(
                                        targetState = areNumbersShown,
                                    ) { targetState ->

                                        if (targetState) {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(vertical = 10.dp),
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(
                                                    modifier = Modifier
                                                        .weight(1f)
                                                        .padding(start = 20.dp, end = 10.dp)
                                                ) {

                                                    Row(verticalAlignment = Alignment.CenterVertically) {

                                                        Text(
                                                            text = contact.numbers.first(),
                                                            color = Color(0xFF4376CE),
                                                            fontSize = 14.sp,
                                                            fontWeight = FontWeight.Medium,
                                                        )

                                                        IconButton(onClick = {
                                                            Toast.makeText(context, "Number Copied", Toast.LENGTH_SHORT)
                                                                .show()
                                                        }) {
                                                            Icon(
                                                                painterResource(id = R.drawable.ic_copy2),
                                                                contentDescription = "",
                                                                tint = Color.Black,
                                                                modifier = Modifier.size(20.dp)
                                                            )
                                                        }
                                                    }

                                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                                        Text(
                                                            text = contact.numbers.last(),
                                                            color = Color(0xFF4376CE),
                                                            fontSize = 14.sp,
                                                            fontWeight = FontWeight.Medium,
                                                        )

                                                        IconButton(onClick = {
                                                            Toast.makeText(context, "Number Copied", Toast.LENGTH_SHORT)
                                                                .show()
                                                        }) {
                                                            Icon(
                                                                painterResource(id = R.drawable.ic_copy2),
                                                                contentDescription = "",
                                                                tint = Color.Black,
                                                                modifier = Modifier.size(20.dp)
                                                            )
                                                        }
                                                    }
                                                }
                                                Row(
                                                    modifier = Modifier
                                                        .padding(end = 10.dp)
                                                ) {
                                                    ElevatedButton(
                                                        elevation = ButtonDefaults.elevatedButtonElevation(
                                                            defaultElevation = 4.dp,
                                                            pressedElevation = 1.dp,
                                                            disabledElevation = 4.dp
                                                        ),
                                                        colors = ButtonDefaults.buttonColors(
                                                            containerColor = Color.White,
                                                            contentColor = MaterialTheme.colorScheme.onSurface,
                                                            disabledContainerColor = Color.White
                                                        ),
                                                        shape = RoundedCornerShape(10.dp),
                                                        onClick = {
                                                            areNumbersShown = false
                                                        },
                                                        interactionSource = remember { MutableInteractionSource() },
                                                        modifier = Modifier
                                                            .size(43.dp)
                                                            .padding(5.dp),
                                                        contentPadding = PaddingValues(0.dp)
                                                    ) {
                                                        Icon(
                                                            painterResource(id = R.drawable.ic_x),
                                                            contentDescription = "",
                                                            tint = Color(0xF4E61414),
                                                            modifier = Modifier
                                                                .padding(5.dp)
                                                                .size(22.dp)
                                                        )
                                                    }
                                                }
                                            }
                                        } else {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .clickable(
                                                        indication = ripple(color = Color(0x03000000)),
                                                        interactionSource = remember { MutableInteractionSource() }
                                                    ) {
                                                        areNumbersShown = true
                                                    },
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Text(
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .weight(1f)
                                                        .padding(horizontal = 20.dp, vertical = 17.dp),
                                                    text = "Show Phone Numbers",
                                                    fontSize = 17.sp,
                                                    textAlign = TextAlign.Start,
                                                    color = Color(0xFF414141)
                                                )

                                                Icon(
                                                    painterResource(id = R.drawable.ic_con),
                                                    contentDescription = "",
                                                    tint = Color(0xFF414141),
                                                    modifier = Modifier
                                                        .padding(end = 20.dp)
                                                        .size(22.dp)
                                                )
                                            }
                                        }
                                    }

                                    Spacer(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(1.dp)
                                            .background(Color.Gray.copy(alpha = 0.2f))
                                    )
                                }

                                // Share
                                Column {
                                    AnimatedContent(
                                        targetState = areShareOptionsShown,
                                    ) { targetState ->

                                        if (targetState)
                                        {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(vertical = 10.dp),
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(
                                                    modifier = Modifier
                                                        .weight(1f)
                                                        .padding(
                                                            start = 20.dp,
                                                            end = 10.dp,
                                                            top = 10.dp,
                                                            bottom = 10.dp
                                                        )
                                                ) {
                                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                                        SmallCheckBox(false)

                                                        Text(
                                                            modifier = Modifier.padding(start = 10.dp),
                                                            text = conName,
                                                            color = Color(0xFF3F4B88),
                                                            fontSize = 17.sp,
                                                            fontWeight = FontWeight.Medium,
                                                        )
                                                    }

                                                    Spacer(modifier = Modifier.height(7.dp))

                                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                                        SmallCheckBox()

                                                        Text(
                                                            modifier = Modifier.padding(start = 10.dp),
                                                            text = contact.numbers.first(),
                                                            color = Color(0xFF3F4B88),
                                                            fontSize = 17.sp,
                                                            fontWeight = FontWeight.Medium,
                                                        )
                                                    }

                                                    Spacer(modifier = Modifier.height(7.dp))

                                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                                        SmallCheckBox()

                                                        Text(
                                                            modifier = Modifier.padding(start = 10.dp),
                                                            text = contact.numbers.last(),
                                                            color = Color(0xFF3F4B88),
                                                            fontSize = 17.sp,
                                                            fontWeight = FontWeight.Medium,
                                                        )
                                                    }
                                                }

                                                Column {

                                                    Row(
                                                        modifier = Modifier
                                                            .padding(end = 10.dp)
                                                    ) {

                                                        ElevatedButton(
                                                            elevation = ButtonDefaults.elevatedButtonElevation(
                                                                defaultElevation = 7.dp,
                                                                pressedElevation = 1.dp,
                                                                disabledElevation = 4.dp
                                                            ),
                                                            colors = ButtonDefaults.buttonColors(
                                                                containerColor = Color.White,
                                                                contentColor = MaterialTheme.colorScheme.onSurface,
                                                                disabledContainerColor = Color.White
                                                            ),
                                                            shape = RoundedCornerShape(10.dp),
                                                            onClick = {
                                                                areShareOptionsShown = false
                                                                onShare()

                                                            },
                                                            interactionSource = remember { MutableInteractionSource() },
                                                            modifier = Modifier
                                                                .size(50.dp)
                                                                .padding(5.dp),
                                                            contentPadding = PaddingValues(0.dp)
                                                        ) {
                                                            Icon(
                                                                painterResource(id = R.drawable.ic_share2),
                                                                contentDescription = "",
                                                                tint = Color(0xFF000000),
                                                                modifier = Modifier
                                                                    .size(27.dp)
                                                            )
                                                        }
                                                    }

                                                    Row(
                                                        modifier = Modifier
                                                            .padding(end = 10.dp)
                                                    ) {

                                                        ElevatedButton(
                                                            elevation = ButtonDefaults.elevatedButtonElevation(
                                                                defaultElevation = 7.dp,
                                                                pressedElevation = 1.dp,
                                                                disabledElevation = 4.dp
                                                            ),
                                                            colors = ButtonDefaults.buttonColors(
                                                                containerColor = Color.White,
                                                                contentColor = MaterialTheme.colorScheme.onSurface,
                                                                disabledContainerColor = Color.White
                                                            ),
                                                            shape = RoundedCornerShape(10.dp),
                                                            onClick = {
                                                                areShareOptionsShown = false
                                                            },
                                                            interactionSource = remember { MutableInteractionSource() },
                                                            modifier = Modifier
                                                                .size(50.dp)
                                                                .padding(5.dp),
                                                            contentPadding = PaddingValues(0.dp)
                                                        ) {
                                                            Icon(
                                                                painterResource(id = R.drawable.ic_x),
                                                                contentDescription = "",
                                                                tint = Color(0xF4E61414),
                                                                modifier = Modifier
                                                                    .size(27.dp)
                                                            )
                                                        }
                                                    }
                                                }
                                            }
                                        } else {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .clickable(
                                                        indication = ripple(color = Color(0x03000000)),
                                                        interactionSource = remember { MutableInteractionSource() }
                                                    ) {
                                                        areShareOptionsShown = true
                                                    },
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Text(
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .weight(1f)
                                                        .padding(horizontal = 20.dp, vertical = 17.dp),
                                                    text = "Share",
                                                    fontSize = 17.sp,
                                                    textAlign = TextAlign.Start,
                                                    color = Color(0xFF414141)
                                                )

                                                Icon(
                                                    painterResource(id = R.drawable.ic_share),
                                                    contentDescription = "",
                                                    tint = Color(0xFF414141),
                                                    modifier = Modifier
                                                        .padding(end = 20.dp)
                                                        .size(22.dp)
                                                )
                                            }
                                        }
                                    }

                                    Spacer(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(1.dp)
                                            .background(Color.Gray.copy(alpha = 0.2f))
                                    )
                                }

                                // Delete
                                Column {
                                    AnimatedContent(
                                        targetState = enableDel,
                                    ) { targetState ->
                                        if (targetState) {
                                            InteractiveDeleteRow(enableDel, onDeleteConfirmed = {
                                                enableDel = false
                                                Toast.makeText(context, "Contact Deleted", Toast.LENGTH_SHORT).show()
                                                onDelete()
                                            }) {

                                                Box{
                                                    Text(
                                                        modifier = Modifier
                                                            .fillMaxWidth()
                                                            .padding(horizontal = 10.dp, vertical = 12.dp),
                                                        text = "Hold to Confirm ",
                                                        fontSize = 17.sp,
                                                        fontWeight = FontWeight.Bold,
                                                        textAlign = TextAlign.Center,
                                                        color = Color(0xFFB91E1E)
                                                    )
                                                }
                                            }
                                        } else {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .height(55.dp)
                                                    .clickable(
                                                        indication = ripple(color = Color(0x03000000)),
                                                        interactionSource = remember { MutableInteractionSource() }
                                                    ) {
                                                        enableDel = true
                                                    },
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Text(
                                                    modifier = Modifier
                                                        .fillMaxWidth()
                                                        .weight(1f)
                                                        .padding(horizontal = 20.dp, vertical = 17.dp),
                                                    text = "Delete Contact",
                                                    fontSize = 17.sp,
                                                    textAlign = TextAlign.Start,
                                                    color = Color(0xFFB91E1E)
                                                )

                                                Icon(
                                                    painterResource(id = R.drawable.ic_remove),
                                                    contentDescription = "",
                                                    tint = Color(0xFFB91E1E),
                                                    modifier = Modifier
                                                        .padding(end = 20.dp)
                                                        .size(22.dp)

                                                )
                                            }
                                        }
                                    }

                                    Spacer(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(1.dp)
                                            .background(Color.Gray.copy(alpha = 0.2f))
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    @OptIn(ExperimentalMaterial3Api::class)
                    private fun SmallCheckBox(allowSelection: Boolean = true) {
                        CompositionLocalProvider(
                            LocalMinimumInteractiveComponentEnforcement provides false
                        ) {
                            var isChecked by remember { mutableStateOf(true) }

                            Checkbox(
                                enabled = if(allowSelection) true else false ,
                                checked = if(allowSelection) isChecked else true,
                                onCheckedChange = {
                                    isChecked = it
                                },
                                colors = CheckboxDefaults.colors(
                                    checkedColor = Color(0xFF3F4B88), // Custom checked color
                                    uncheckedColor = Color(0xFF3F4B88), // Custom unchecked color (optional)
                                )
                            )
                        }
                    }

                    @SuppressLint("RememberReturnType")
                    @Composable
                    fun InteractiveDeleteRow(
                        enableDel: Boolean,
                        onDeleteConfirmed: () -> Unit,
                        content: @Composable() (RowScope.() -> Unit)
                    ) {

                        var isPressed by remember { mutableStateOf(false) }
                        val animatedProgress = remember { Animatable(0f) }

                        LaunchedEffect(isPressed) {
                            if (isPressed) {
                                animatedProgress.animateTo(
                                    targetValue = 1f,
                                    animationSpec = tween(
                                        durationMillis = 1000,
                                        easing = LinearEasing
                                    )
                                )
                                if (animatedProgress.value >= 1f) {
                                    onDeleteConfirmed()
                                }
                            } else {
                                animatedProgress.snapTo(0f)
                            }
                        }

                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .wrapContentHeight()
                                .background(Color(0x0DB91E1E))
                                .pointerInput(Unit) {
                                    detectTapGestures(
                                        onPress = {
                                            isPressed = true
                                            tryAwaitRelease()
                                            isPressed = false
                                        }
                                    )
                                }

                        ) {
                            // Progress background
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth(animatedProgress.value)
                                    .height(55.dp)
                                    .background(Color.Red.copy(alpha = 0.25f))
                            )

                            // Content
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(55.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                content()
                            }
                        }
                    }

                    fun Int.toDp(): Dp = this.dp
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Hyperlinked

                    @Composable
                    fun HyperlinkedText(onClick: (String) -> Unit) {
                        val annotatedText = buildAnnotatedString {
                            append("Visit our ")
                            withStyle(
                                style = SpanStyle(
                                    color = Color.Blue,
                                    textDecoration = TextDecoration.Underline
                                )
                            ) {
                                pushStringAnnotation(tag = "URL", annotation = "https://multiplatformdeveloper.netlify.app")
                                append("website")
                                pop()
                            }
                            append(" for more details.")
                        }

                    ClickableText(
                            text = annotatedText,
                            onClick = { offset ->
                                annotatedText.getStringAnnotations(tag = "URL", start = offset, end = offset)
                                    .firstOrNull()?.let {
                                        if (it.item == "https://multiplatformdeveloper.netlify.app") {
                                            onClick(it.item)
                                        }
                                    }
                            }
                        )
                    }
                    """.trimIndent()
            )


            KotlinCode(
                code = """
                    //SpanStyle

                    @Composable
                    fun SpanStyleExample() {
                        val text = buildAnnotatedString {
                            append("Normal text, ")
                            withStyle(style = SpanStyle(color = Color.Blue, fontWeight = FontWeight.Bold)) {
                                append("then bold and blue.")
                            }
                        }
                        Text(text = text)
                    }
                    """.trimIndent()
            )

            //https://github.com/Let-s-build-something/client-app
            KotlinCode(
                code = """
                    //Build an AnnotatedString with given highlight 
                    
                    @Composable
                    fun highlightedText(
                        highlight: String?,
                        highLightColor: Color = LocalTheme.current.colors.brandMain,
                        annotatedString: AnnotatedString
                    ): AnnotatedString {
                        if (highlight.isNullOrBlank()) return annotatedString

                        val text = annotatedString.text
                        val lowerText = text.lowercase()
                        val lowerHighlight = highlight.lowercase()

                        if (!lowerText.contains(lowerHighlight)) return annotatedString

                        return buildAnnotatedString {
                            var startIndex = 0
                            while (startIndex < text.length) {
                                val matchIndex = lowerText.indexOf(lowerHighlight, startIndex)
                                if (matchIndex == -1) {
                                    append(annotatedString.subSequence(startIndex, text.length))
                                    break
                                }

                                if (matchIndex > startIndex) {
                                    append(annotatedString.subSequence(startIndex, matchIndex))
                                }

                                withStyle(
                                    style = SpanStyle(
                                        fontWeight = FontWeight.ExtraBold,
                                        color = highLightColor
                                    )
                                ) {
                                    append(annotatedString.subSequence(matchIndex, matchIndex + highlight.length))
                                }

                                startIndex = matchIndex + highlight.length
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedStar() {
                        var isLiked by remember { mutableStateOf(false) }

                        // Define the animation values
                        val transition = updateTransition(targetState = isLiked, label = "StarButtonTransition")

                        // Define the start and end values for the animation
                        val rotation by transition.animateFloat(
                            label = "StarRotation",
                            transitionSpec = {
                                tween(durationMillis = 500, easing = FastOutSlowInEasing)
                            }
                        ) { liked ->
                            if (liked) 360f else 0f
                        }

                        // Animate the color of the icon
                        val iconColor by transition.animateColor(
                            label = "StarColor",
                            transitionSpec = {
                                tween(durationMillis = 500, easing = FastOutSlowInEasing)
                            }
                        ) { liked ->
                            if (liked) Color.Red else Color.Gray
                        }

                        // Animate the size of the icon
                        val iconSize by transition.animateDp(
                            label = "StarSize",
                            transitionSpec = {
                                tween(durationMillis = 500, easing = FastOutSlowInEasing)
                            }
                        ) { liked ->
                            if (liked) 64.dp else 32.dp
                        }

                        Column(
                            modifier = Modifier
                                .padding(8.dp)
                                .clickable {
                                    isLiked = !isLiked
                                }
                        ) {
                            Icon(
                                imageVector = Icons.Default.Star,
                                contentDescription = null,
                                tint = iconColor,
                                modifier = Modifier
                                    .size(iconSize)
                                    .padding(4.dp)
                                    .rotate(rotation)
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Button press animation

                    val interactionSource = remember { MutableInteractionSource() }
                    val isPressed by interactionSource.collectIsPressedAsState()

                    val animatedValues = PaddingValues(
                        animateDpAsState(targetValue = if (isPressed) 32.dp else 0.dp).value,
                        animateDpAsState(targetValue = if (isPressed) 4.dp else 0.dp).value)

                    Button(
                    modifier = Modifier
                    .height(animateDpAsState(targetValue = if (isPressed) 56.dp else 64.dp).value)
                    .fillMaxWidth()
                    .padding(animatedValues),
                    shape = CircleShape,
                    border = BorderStroke(1.dp, Color.Blue),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = Color.Blue),
                    interactionSource = interactionSource,
                    onClick = { /* Action to Perform */ },
                    ) {

                        Text(
                            text = "Continue",
                            style = MaterialTheme.typography.labelLarge
                        )

                    }
                    """.trimIndent()
            )

            //Shape
            KotlinCode(
                code = """
                    //Hexagon Shape

                    class HexagonShape : Shape {
                        override fun createOutline(
                            size: Size,
                            layoutDirection: LayoutDirection,
                            density: Density
                        ): Outline {
                            return Outline.Generic(
                                path = drawCustomHexagonPath(size)
                            )
                        }
                    }

                    fun drawCustomHexagonPath(size: Size): Path {
                        return Path().apply {
                            val radius = min(size.width / 2f, size.height / 2f)
                            customHexagon(radius, size)
                        }
                    }

                    fun Path.customHexagon(radius: Float, size: Size) {
                        val triangleHeight = (sqrt(3.0) * radius / 2)
                        val centerX = size.width / 2
                        val centerY = size.height / 2

                        moveTo(centerX + radius, centerY)
                        lineTo((centerX + radius / 2), (centerY - triangleHeight).toFloat())
                        lineTo((centerX - radius / 2), (centerY - triangleHeight).toFloat())
                        lineTo(centerX - radius, centerY)
                        lineTo(centerX - radius / 2, (centerY + triangleHeight).toFloat())
                        lineTo(centerX + radius / 2, (centerY + triangleHeight).toFloat())

                        close()
                    }

                    @Composable
                    fun KeyPadButton(
                        title: String,
                        size: Dp,
                        modifier: Modifier = Modifier,
                        onClick: (title: String) -> Unit
                    ) {
                        ConstraintLayout(
                            modifier = modifier
                                .clip(HexagonShape())
                                .clickable {
                                    onClick.invoke(title)
                                }
                        ) {
                            val (btnBg, tvText) = createRefs()

                            Image(
                                painter = painterResource(id = R.drawable.ic_keypad_btn),
                                contentDescription = null,
                                modifier = Modifier
                                    .size(size)
                                    .constrainAs(btnBg) {
                                        top.linkTo(parent.top)
                                        bottom.linkTo(parent.bottom)
                                        start.linkTo(parent.start)
                                        end.linkTo(parent.end)
                                    }
                            )

                            Text(
                                text = title,
                                textAlign = TextAlign.Center,
                                color = Color.White,
                                fontSize = 22.sp,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier
                                    .constrainAs(tvText) {
                                        top.linkTo(parent.top)
                                        bottom.linkTo(parent.bottom)
                                        start.linkTo(parent.start)
                                        end.linkTo(parent.end)
                                    }
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class CartScreen() : Screen {
                        @OptIn(ExperimentalMaterial3Api::class)
                        @Composable
                        override fun Content() {
                            val navigator = LocalNavigator.currentOrThrow

                            Scaffold(
                                topBar = {
                                    CenterAlignedTopAppBar(
                                        title = { Text("Your Cart") },
                                        navigationIcon = {
                                            IconButton(onClick = {  }) {
                                                Icon(Icons.Default.ArrowBack, contentDescription = "Back")
                                            }
                                        },
                                        colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                                            containerColor = Color(
                                                0xFFFFA500
                                            )
                                        ) // Orange color
                                    )
                                }
                            ) { paddingValues ->

                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun Modifier.shimmerEffect(
                        progress: Float,
                        shimmerSize: Dp = 152.dp,
                        shimmerColor: Color = Color.White.copy(alpha = 0.25f),
                    ) = this.composed {
                        val shimmerSizePx = shimmerSize.toPx()
                        Modifier.drawWithContent {
                            drawContent()
                            val adjustedWidth = size.width + shimmerSizePx * 2
                            val x = adjustedWidth * progress - shimmerSizePx
                            drawRect(
                                brush = Brush.horizontalGradient(
                                    colors = listOf(
                                        Color.Transparent,
                                        shimmerColor,
                                        Color.Transparent,
                                    ),
                                    startX = x,
                                    endX = x + shimmerSizePx,
                                ),
                                topLeft = Offset(x = x, y = 0f),
                                size = Size(
                                    width = shimmerSizePx,
                                    height = size.height,
                                ),
                            )
                        }
                    }

                    @Composable
                    fun ShimmerTest() {
                        val infiniteTransition = rememberInfiniteTransition(label = "Shimmer")
                        val shimmerProgress by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(durationMillis = 1000),
                            ),
                            label = "ShimmerProgress",
                        )

                        Spacer(
                            modifier = Modifier
                                .padding(16.dp)
                                .fillMaxWidth()
                                .height(56.dp)
                                .clip(RoundedCornerShape(20))
                                .background(color = Color(0xFF42A5F5))
                                .shimmerEffect(progress = shimmerProgress)
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun Modifier.shimmerEffect(
                        shimmerSize: Dp = 152.dp,
                        shimmerColor: Color = Color.White.copy(alpha = 0.25f),
                        animationSpec: InfiniteRepeatableSpec<Float> = infiniteRepeatable(
                            animation = tween(
                                durationMillis = 1000,
                                delayMillis = 2000,
                                easing = EaseInOutQuad,
                            ),
                        ),
                    ) = this.composed {
                        val shimmerSizePx = shimmerSize.toPx()
                        val infiniteTransition = rememberInfiniteTransition(label = "Shimmer")
                        val progress by infiniteTransition.animateFloat(
                            initialValue = 0f,
                            targetValue = 1f,
                            animationSpec = animationSpec,
                            label = "ShimmerProgress",
                        )

                        Modifier.drawWithContent {
                            drawContent()
                            val adjustedWidth = size.width + shimmerSizePx * 2
                            val x = adjustedWidth * progress - shimmerSizePx
                            drawRect(
                                brush = Brush.horizontalGradient(
                                    colors = listOf(
                                        Color.Transparent,
                                        shimmerColor,
                                        Color.Transparent,
                                    ),
                                    startX = x,
                                    endX = x + shimmerSizePx,
                                ),
                                topLeft = Offset(x = x, y = 0f),
                                size = Size(
                                    width = shimmerSizePx,
                                    height = size.height,
                                ),
                            )
                        }
                    }

                    @Composable
                    fun ShimmerTest() {
                        Spacer(
                            modifier = Modifier
                                .padding(16.dp)
                                .fillMaxWidth()
                                .height(56.dp)
                                .clip(RoundedCornerShape(20))
                                .background(color = Color(0xFF42A5F5))
                                .shimmerEffect()
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        @Composable
                        fun SlidingBackgroundImage() {
                            var positionX by remember { mutableStateOf(0f) }
                            val infiniteTransition = rememberInfiniteTransition()

                            val gradientColor = Brush.verticalGradient(
                                listOf(
                                    Color(0x2000000),
                                    Color(0x80000000),
                                    Color(0xE6000000),
                                )
                            )

                            val slidingAnimation by infiniteTransition.animateFloat(
                                initialValue = 0f,
                                targetValue = -0.03f,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(
                                        durationMillis = 10000,
                                        easing = LinearEasing
                                    ),
                                    repeatMode = RepeatMode.Reverse
                                )
                            )

                            positionX = slidingAnimation * 1000

                            Image(
                                painter = painterResource(id = R.drawable.auth_bg_image),
                                contentDescription = "Background Image",
                                modifier = Modifier
                                    .fillMaxSize()
                                    .scale(1.4f)
                                    .graphicsLayer {
                                        translationX = positionX.dp.toPx()
                                    },
                                contentScale = ContentScale.Crop
                            )

                            Box(modifier = Modifier
                                .fillMaxSize()
                                .background(brush = gradientColor))
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //parallaxLayoutModifier

                    @Composable
                    internal fun BackgroundImage(
                        lazyListState: LazyListState,
                        imageModel: String
                    ) {
                        Box(
                            modifier = Modifier
                                .height(412.dp)
                                .fillMaxWidth()
                                .parallaxLayoutModifier(lazyListState, 2)
                        ) {
                            AsyncImage(
                                model = imageModel,
                                contentDescription = null,
                                contentScale = ContentScale.Crop,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .drawWithCache {
                                        onDrawWithContent {
                                            drawContent()
                                            drawRect(
                                                Brush.verticalGradient(
                                                    0f to VampireBlack.copy(alpha = 0F),
                                                    1F to VampireBlack
                                                )
                                            )
                                        }
                                    }
                            )
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .alpha(0.2f)
                                    .background(VampireBlack)
                            )
                        }
                    }

                    val backgroundImage = Res.getUri("drawable/img_main_typhoon_background.webp")

                    val lazyListState = rememberLazyListState()
                    LazyColumn(
                    state = lazyListState,
                    modifier = Modifier.fillMaxWidth()
                    ) {
                        item { BackgroundImage(lazyListState, backgroundImage) }
                    }
                    """.trimIndent()
            )

            //Custom GoogleMap Marker
            KotlinCode(
                code = """
                    //https://github.com/sergenes/restaurantdiscovery

                    private fun createCustomMarkerIcon(
                        context: Context,
                        vectorResId: Int
                    ): BitmapDescriptor {
                        return try {
                            val vectorDrawable = ContextCompat.getDrawable(context, vectorResId)
                                ?: return BitmapDescriptorFactory.defaultMarker()

                            val bitmap = Bitmap.createBitmap(
                                vectorDrawable.intrinsicWidth,
                                vectorDrawable.intrinsicHeight,
                                Bitmap.Config.ARGB_8888
                            )

                            android.graphics.Canvas(bitmap).apply {
                                vectorDrawable.setBounds(0, 0, width, height)
                                vectorDrawable.draw(this)
                            }

                            BitmapDescriptorFactory.fromBitmap(bitmap)
                        } catch (e: Exception) {
                            BitmapDescriptorFactory.defaultMarker()
                        }
                    }

                    val icon = createCustomMarkerIcon(context, R.mipmap.pins)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    internal fun GoogleMapView(
                        modifier: Modifier = Modifier,
                        cameraPositionState: CameraPositionState = rememberCameraPositionState(),
                        mapItems: List<UserStationResource>?,
                        locationPermissionGranted: Boolean,
                        onDetailClick: (String) -> Unit = {},
                        onRequirePermissions: () -> Unit = {},
                        onMyLocationClick: () -> Unit = {},
                        onMapLoaded: () -> Unit = {}
                    ) {
                        val uiSettings by remember {
                            mutableStateOf(
                                MapUiSettings(
                                    compassEnabled = false,
                                    zoomControlsEnabled = false,
                                    myLocationButtonEnabled = false
                                )
                            )
                        }

                        val mapProperties by remember {
                            mutableStateOf(
                                MapProperties(
                                    mapType = MapType.NORMAL,
                                    isMyLocationEnabled = locationPermissionGranted
                                )
                            )
                        }
                        val mapVisible by remember { mutableStateOf(true) }

                        if (mapVisible) {
                            Box(modifier.fillMaxSize()) {
                                GoogleMap(
                                    modifier = modifier,
                                    cameraPositionState = cameraPositionState,
                                    properties = mapProperties,
                                    uiSettings = uiSettings,
                                    onMapLoaded = { onMapLoaded() },
                                    onPOIClick = {

                                    }
                                ) {
                                    mapItems?.forEach {
                                        when {
                                            it.type == StationType.CLFS.typeName && it.isOperate == 1 -> {
                                                GoogleMapMarkerView(
                                                    cameraPositionState = cameraPositionState,
                                                    marker = it,
                                                    iconResourceId = MMIcons.StationClfs,
                                                    onMarkerClick = onDetailClick
                                                )
                                            }

                                            it.type == StationType.CNG.typeName && it.isOperate == 1 -> {
                                                GoogleMapMarkerView(
                                                    cameraPositionState = cameraPositionState,
                                                    marker = it,
                                                    iconResourceId = MMIcons.StationCng,
                                                    onMarkerClick = onDetailClick
                                                )
                                            }

                                            it.type == StationType.SERVICE.typeName && it.isOperate == 1 -> {
                                                GoogleMapMarkerView(
                                                    cameraPositionState = cameraPositionState,
                                                    marker = it,
                                                    iconResourceId = MMIcons.StationService,
                                                    onMarkerClick = onDetailClick
                                                )
                                            }

                                            else -> {
                                                GoogleMapMarkerView(
                                                    cameraPositionState = cameraPositionState,
                                                    marker = it,
                                                    iconResourceId = MMIcons.StationNotWorking,
                                                    onMarkerClick = onDetailClick
                                                )
                                            }
                                        }
                                    }
                                }
                                FloatingActionButton(
                                    onClick = {
                                        if (!locationPermissionGranted) {
                                            onRequirePermissions()
                                        } else {
                                            onMyLocationClick()
                                        }
                                    },
                                    modifier = Modifier
                                        .align(Alignment.BottomEnd)
                                        .padding(end = 16.dp, bottom = 24.dp),
                                    containerColor = MaterialTheme.colorScheme.secondary,
                                    contentColor = MaterialTheme.colorScheme.onSecondary
                                ) {
                                    Icon(
                                        imageVector = if (!locationPermissionGranted) {
                                            MMIcons.LocationDisabled
                                        } else {
                                            MMIcons.MyLocation
                                        },
                                        contentDescription = "Add FAB",
                                        tint = MMColors.toolbarIconColor,
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    private fun GoogleMapMarkerView(
                        cameraPositionState: CameraPositionState,
                        marker: UserStationResource,
                        @DrawableRes iconResourceId: Int,
                        onMarkerClick: (String) -> Unit = {},
                    ) {
                        val analyticsHelper = LocalAnalyticsHelper.current
                        val icon = bitmapDescriptor(LocalContext.current, iconResourceId)

                        val markerClick: (Marker) -> Boolean = {
                            cameraPositionState.projection?.let { _ ->
                                analyticsHelper.logStationResourceOpened(it.snippet.orEmpty())
                                onMarkerClick.invoke(it.snippet.orEmpty())
                            }
                            false
                        }
                        MarkerInfoWindowContent(
                            state = MarkerState(
                                position = LatLng(
                                    marker.latitude.toDouble(),
                                    marker.longitude.toDouble()
                                )
                            ),
                            icon = icon,
                            snippet = marker.code,
                            title = marker.title,
                            onClick = markerClick,
                            draggable = true,
                        ) {
                            Text(it.title ?: "Title", color = Color.Red)
                        }
                    }

                    private fun bitmapDescriptor(
                        context: Context,
                        vectorResId: Int,
                    ): BitmapDescriptor? {

                        // retrieve the actual drawable
                        val drawable = ContextCompat.getDrawable(context, vectorResId) ?: return null
                        drawable.setBounds(0, 0, drawable.intrinsicWidth, drawable.intrinsicHeight)
                        val bm = Bitmap.createBitmap(
                            drawable.intrinsicWidth,
                            drawable.intrinsicHeight,
                            Bitmap.Config.ARGB_8888
                        )

                        // draw it onto the bitmap
                        val canvas = android.graphics.Canvas(bm)
                        drawable.draw(canvas)
                        return BitmapDescriptorFactory.fromBitmap(bm)
                    }

                    @Composable
                    internal fun StationMapContent(
                        modifier: Modifier = Modifier,
                        stationList: List<UserStationResource>,
                        userLocation: LocationResource?,
                        onDetailClick: (String) -> Unit,
                    ) {
                        val permissionsState = LocalPermissionsState.current
                        val center = LatLng(53.90309661691656, 27.55363993274304)

                        val cameraPositionState = rememberCameraPositionState {
                            position = CameraPosition.fromLatLngZoom(center, 10f)
                        }

                        var shouldAnimateCamera by remember { mutableStateOf(false) }

                        LaunchedEffect(shouldAnimateCamera, userLocation) {
                            if (shouldAnimateCamera && permissionsState.hasLocationPermissions && userLocation != null) {
                                val location = LatLng(userLocation.latitude, userLocation.longitude)
                                cameraPositionState.animate(
                                    update = CameraUpdateFactory.newCameraPosition(
                                        CameraPosition(location, 15f, 0f, 0f)
                                    )
                                )
                                shouldAnimateCamera = false
                            }
                        }

                        GoogleMapView(
                            modifier = modifier,
                            cameraPositionState = cameraPositionState,
                            mapItems = stationList,
                            locationPermissionGranted = permissionsState.hasLocationPermissions,
                            onRequirePermissions = { permissionsState.requestPermissions() },
                            onMyLocationClick = {
                                val location = LatLng(userLocation?.latitude ?: 0.0, userLocation?.longitude ?: 0.0)
                                cameraPositionState.move(
                                    update = CameraUpdateFactory.newCameraPosition(
                                        CameraPosition(location, 15f, 0f, 0f)
                                    )
                                )
                            },
                            onDetailClick = onDetailClick,
                            onMapLoaded = { shouldAnimateCamera = true }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/ngapp-dev

                    import androidx.compose.foundation.gestures.detectDragGestures
                    import androidx.compose.foundation.gestures.detectDragGesturesAfterLongPress
                    import androidx.compose.foundation.interaction.DragInteraction
                    import androidx.compose.foundation.interaction.MutableInteractionSource
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.DisposableEffect
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.rememberCoroutineScope
                    import androidx.compose.runtime.setValue
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.composed
                    import androidx.compose.ui.geometry.Offset
                    import androidx.compose.ui.input.pointer.PointerInputChange
                    import androidx.compose.ui.input.pointer.pointerInput
                    import kotlinx.coroutines.launch

                    internal fun Modifier.draggable(
                        key1: Any?,
                        enabled: Boolean = true,
                        interactionSource: MutableInteractionSource? = null,
                        onDragStarted: (Offset) -> Unit = {},
                        onDragStopped: () -> Unit = {},
                        onDrag: (change: PointerInputChange, dragAmount: Offset) -> Unit,
                    ) = composed {
                        val coroutineScope = rememberCoroutineScope()
                        var dragInteractionStart by remember { mutableStateOf<DragInteraction.Start?>(null) }
                        var dragStarted by remember { mutableStateOf(false) }

                        DisposableEffect(key1) {
                            onDispose {
                                if (dragStarted) {
                                    dragInteractionStart?.also {
                                        coroutineScope.launch {
                                            interactionSource?.emit(DragInteraction.Cancel(it))
                                        }
                                    }
                                    if (dragStarted) onDragStopped()
                                    dragStarted = false
                                }
                            }
                        }

                        pointerInput(key1, enabled) {
                            if (enabled) {
                                detectDragGestures(
                                    onDragStart = {
                                        dragStarted = true
                                        dragInteractionStart = DragInteraction.Start().also {
                                            coroutineScope.launch {
                                                interactionSource?.emit(it)
                                            }
                                        }
                                        onDragStarted(it)
                                    },
                                    onDragEnd = {
                                        dragInteractionStart?.also {
                                            coroutineScope.launch {
                                                interactionSource?.emit(DragInteraction.Stop(it))
                                            }
                                        }
                                        if (dragStarted) onDragStopped()
                                        dragStarted = false
                                    },
                                    onDragCancel = {
                                        dragInteractionStart?.also {
                                            coroutineScope.launch {
                                                interactionSource?.emit(DragInteraction.Cancel(it))
                                            }
                                        }
                                        if (dragStarted) onDragStopped()
                                        dragStarted = false
                                    },
                                    onDrag = onDrag,
                                )
                            }
                        }
                    }

                    internal fun Modifier.longPressDraggable(
                        key1: Any?,
                        enabled: Boolean = true,
                        interactionSource: MutableInteractionSource? = null,
                        onDragStarted: (Offset) -> Unit = { },
                        onDragStopped: () -> Unit = { },
                        onDrag: (change: PointerInputChange, dragAmount: Offset) -> Unit,
                    ) = composed {
                        val coroutineScope = rememberCoroutineScope()
                        var dragInteractionStart by remember { mutableStateOf<DragInteraction.Start?>(null) }
                        var dragStarted by remember { mutableStateOf(false) }

                        DisposableEffect(key1) {
                            onDispose {
                                if (dragStarted) {
                                    dragInteractionStart?.also {
                                        coroutineScope.launch {
                                            interactionSource?.emit(DragInteraction.Cancel(it))
                                        }
                                    }
                                    if (dragStarted) onDragStopped()
                                    dragStarted = false
                                }
                            }
                        }

                        pointerInput(key1, enabled) {
                            if (enabled) {
                                detectDragGesturesAfterLongPress(
                                    onDragStart = {
                                        dragStarted = true
                                        dragInteractionStart = DragInteraction.Start().also {
                                            coroutineScope.launch {
                                                interactionSource?.emit(it)
                                            }
                                        }
                                        onDragStarted(it)
                                    },
                                    onDragEnd = {
                                        dragInteractionStart?.also {
                                            coroutineScope.launch {
                                                interactionSource?.emit(DragInteraction.Stop(it))
                                            }
                                        }
                                        if (dragStarted) onDragStopped()
                                        dragStarted = false
                                    },
                                    onDragCancel = {
                                        dragInteractionStart?.also {
                                            coroutineScope.launch {
                                                interactionSource?.emit(DragInteraction.Cancel(it))
                                            }
                                        }
                                        if (dragStarted) onDragStopped()
                                        dragStarted = false
                                    },
                                    onDrag = onDrag,
                                )
                            }
                        }
                    }

                    @Composable
                    fun ReorderableCollectionItemScope.reorderableItemModifier(
                        haptic: ReorderHapticFeedback,
                        interactionSource: MutableInteractionSource,
                    ): Modifier {
                        return Modifier
                            .draggableHandle(
                                onDragStarted = {
                                    haptic.performHapticFeedback(ReorderHapticFeedbackType.START)
                                },
                                onDragStopped = {
                                    haptic.performHapticFeedback(ReorderHapticFeedbackType.END)
                                },
                                interactionSource = interactionSource,
                            )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalFoundationApi::class)
                    @Composable
                    fun HorizontalPagerIndicator(
                        pagerState: PagerState,
                        pageCount: Int,
                        modifier: Modifier = Modifier,
                        pageIndexMapping: (Int) -> Int = { it },
                        activeColor: Color = Blue,
                        inactiveColor: Color = Gray300,
                        indicatorWidth: Dp = 8.dp,
                        indicatorHeight: Dp = indicatorWidth,
                        spacing: Dp = indicatorWidth,
                        indicatorShape: Shape = CircleShape,
                    ) {
                        val stateBridge = remember(pagerState) {
                            object : PagerStateBridge {
                                override val currentPage: Int
                                    get() = pagerState.currentPage
                                override val currentPageOffset: Float
                                    get() = pagerState.currentPageOffsetFraction
                            }
                        }

                        HorizontalPagerIndicator(
                            pagerState = stateBridge,
                            pageCount = pageCount,
                            modifier = modifier,
                            pageIndexMapping = pageIndexMapping,
                            activeColor = activeColor,
                            inactiveColor = inactiveColor,
                            indicatorHeight = indicatorHeight,
                            indicatorWidth = indicatorWidth,
                            spacing = spacing,
                            indicatorShape = indicatorShape
                        )
                    }

                    @Composable
                    private fun HorizontalPagerIndicator(
                        pagerState: PagerStateBridge,
                        pageCount: Int,
                        modifier: Modifier = Modifier,
                        pageIndexMapping: (Int) -> Int = { it },
                        activeColor: Color = Blue,
                        inactiveColor: Color = Gray300,
                        indicatorWidth: Dp = 8.dp,
                        indicatorHeight: Dp = indicatorWidth,
                        spacing: Dp = indicatorWidth,
                        indicatorShape: Shape = CircleShape,
                    ) {

                        val indicatorWidthPx = LocalDensity.current.run { indicatorWidth.roundToPx() }
                        val spacingPx = LocalDensity.current.run { spacing.roundToPx() }

                        Box(
                            modifier = modifier,
                            contentAlignment = Alignment.CenterStart
                        ) {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(spacing),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                val indicatorModifier = Modifier
                                    .size(width = indicatorWidth, height = indicatorHeight)
                                    .background(color = inactiveColor, shape = indicatorShape)

                                repeat(pageCount) {
                                    Box(indicatorModifier)
                                }
                            }

                            Box(
                                Modifier
                                    .offset {
                                        val position = pageIndexMapping(pagerState.currentPage)
                                        val offset = pagerState.currentPageOffset
                                        val next = pageIndexMapping(pagerState.currentPage + offset.sign.toInt())
                                        val scrollPosition = ((next - position) * offset.absoluteValue + position)
                                            .coerceIn(
                                                0f,
                                                (pageCount - 1)
                                                    .coerceAtLeast(0)
                                                    .toFloat()
                                            )

                                        IntOffset(
                                            x = ((spacingPx + indicatorWidthPx) * scrollPosition).toInt(),
                                            y = 0
                                        )
                                    }
                                    .size(width = indicatorWidth, height = indicatorHeight)
                                    .then(
                                        if (pageCount > 0) Modifier.background(
                                            color = activeColor,
                                            shape = indicatorShape,
                                        )
                                        else Modifier
                                    )
                            )
                        }
                    }

                    internal interface PagerStateBridge {
                        val currentPage: Int
                        val currentPageOffset: Float
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MMLoadingWheel(
                        contentDesc: String,
                        modifier: Modifier = Modifier,
                    ) {
                        val infiniteTransition = rememberInfiniteTransition(label = "wheel transition")

                        // Specifies the float animation for slowly drawing out the lines on entering
                        val startValue = if (LocalInspectionMode.current) 0F else 1F
                        val floatAnimValues = (0 until NUM_OF_LINES).map { remember { Animatable(startValue) } }
                        LaunchedEffect(floatAnimValues) {
                            (0 until NUM_OF_LINES).map { index ->
                                launch {
                                    floatAnimValues[index].animateTo(
                                        targetValue = 0F,
                                        animationSpec = tween(
                                            durationMillis = 100,
                                            easing = FastOutSlowInEasing,
                                            delayMillis = 40 * index,
                                        ),
                                    )
                                }
                            }
                        }

                        // Specifies the rotation animation of the entire Canvas composable
                        val rotationAnim by infiniteTransition.animateFloat(
                            initialValue = 0F,
                            targetValue = 360F,
                            animationSpec = infiniteRepeatable(
                                animation = tween(durationMillis = ROTATION_TIME, easing = LinearEasing),
                            ),
                            label = "wheel rotation animation",
                        )

                        // Specifies the color animation for the base-to-progress line color change
                        val baseLineColor = MaterialTheme.colorScheme.inverseSurface
                        val progressLineColor = Blue

                        val colorAnimValues = (0 until NUM_OF_LINES).map { index ->
                            infiniteTransition.animateColor(
                                initialValue = baseLineColor,
                                targetValue = baseLineColor,
                                animationSpec = infiniteRepeatable(
                                    animation = keyframes {
                                        durationMillis = ROTATION_TIME / 2
                                        progressLineColor at ROTATION_TIME / NUM_OF_LINES / 2 using LinearEasing
                                        baseLineColor at ROTATION_TIME / NUM_OF_LINES using LinearEasing
                                    },
                                    repeatMode = RepeatMode.Restart,
                                    initialStartOffset = StartOffset(ROTATION_TIME / NUM_OF_LINES / 2 * index),
                                ),
                                label = "wheel color animation",
                            )
                        }

                        // Draws out the LoadingWheel Canvas composable and sets the animations
                        Canvas(
                            modifier = modifier
                                .size(48.dp)
                                .padding(8.dp)
                                .graphicsLayer { rotationZ = rotationAnim }
                                .semantics { contentDescription = contentDesc }
                                .testTag("loadingWheel"),
                        ) {
                            repeat(NUM_OF_LINES) { index ->
                                rotate(degrees = index * 30f) {
                                    drawLine(
                                        color = colorAnimValues[index].value,
                                        // Animates the initially drawn 1 pixel alpha from 0 to 1
                                        alpha = if (floatAnimValues[index].value < 1f) 1f else 0f,
                                        strokeWidth = 4F,
                                        cap = StrokeCap.Round,
                                        start = Offset(size.width / 2, size.height / 4),
                                        end = Offset(size.width / 2, floatAnimValues[index].value * size.height / 4),
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    fun MMOverlayLoadingWheel(
                        contentDesc: String,
                        modifier: Modifier = Modifier,
                    ) {
                        Surface(
                            shape = RoundedCornerShape(60.dp),
                            shadowElevation = 8.dp,
                            color = MaterialTheme.colorScheme.surface,
                            modifier = modifier.size(60.dp),
                        ) {
                            MMLoadingWheel(contentDesc = contentDesc)
                        }
                    }

                    @ThemePreviews
                    @Composable
                    fun MMLoadingWheelPreview() {
                        MMTheme {
                            Surface {
                                MMLoadingWheel(contentDesc = "LoadingWheel")
                            }
                        }
                    }

                    @ThemePreviews
                    @Composable
                    fun MMOverlayLoadingWheelPreview() {
                        MMTheme {
                            Surface {
                                MMOverlayLoadingWheel(contentDesc = "LoadingWheel")
                            }
                        }
                    }

                    private const val ROTATION_TIME = 12000
                    private const val NUM_OF_LINES = 12
                    """.trimIndent()
            )

            //5. https://github.com/Beb3r/OpalTest
            KotlinCode(
                code = """
                        //1. Dash Border 1.9.0
                        private fun Modifier.dashedBorder(
                          color: ColorProducer,
                        ) = drawBehind {
                          val inset = 3.dp.toPx()
                          val outline = TileShape.createOutline(
                            size = size.copy(size.width - inset, size.height - inset),
                            layoutDirection = layoutDirection,
                            density = this
                          )
                          val dashedStroke = Stroke(
                            cap = StrokeCap.Round,
                            width = 3.dp.toPx(),
                            pathEffect = PathEffect.dashPathEffect(
                              intervals = floatArrayOf(4.dp.toPx(), 8.dp.toPx())
                            )
                          )

                          inset(inset / 2f) {
                            drawOutline(
                              outline = outline,
                              style = dashedStroke,
                              color = color(),
                            )
                          }
                        }

                        val TileShape: CornerBasedShape
                            get() = ContinuousRoundedCornerShape(size = 6.dp)
                        
                        //2. Dash Border Divider 
                        @Composable
                        private fun DashedDivider(
                            color: Color = MaterialTheme.colorScheme.tertiary,
                            thickness: Dp = 1.dp,
                            dashLength: Dp = 4.dp,
                            gapLength: Dp = 4.dp,
                            modifier: Modifier = Modifier.fillMaxWidth()
                        ) {
                            val stroke = with(LocalDensity.current) { thickness.toPx() }
                            val dash = with(LocalDensity.current) { dashLength.toPx() }
                            val gap = with(LocalDensity.current) { gapLength.toPx() }

                            Canvas(modifier = modifier.height(thickness)) {
                                drawLine(
                                    color = color,
                                    start = Offset(0f, 0f),
                                    end = Offset(size.width, 0f),
                                    strokeWidth = stroke,
                                    pathEffect = PathEffect.dashPathEffect(floatArrayOf(dash, gap), 0f)
                                )
                            }
                        }

                        //3. Dash Border
                        fun Modifier.dashedBorder(width: Dp, brush: Brush, shape: Shape, on: Dp = 5.dp, off: Dp = 5.dp): Modifier =
                            composed(
                                factory = {
                                    this.then(
                                        Modifier.drawWithCache {
                                            val outline: Outline = shape.createOutline(size, layoutDirection, this)
                                            val borderSize = if (width == Dp.Hairline) 1f else width.toPx()

                                            var insetOutline: Outline? = null // outline used for roundrect/generic shapes
                                            var stroke: Stroke? = null // stroke to draw border for all outline types
                                            var pathClip: Path? = null // path to clip roundrect/generic shapes
                                            var inset = 0f // inset to translate before drawing the inset outline
                                            // path to draw generic shapes or roundrects with different corner radii
                                            var insetPath: Path? = null
                                            if (borderSize > 0 && size.minDimension > 0f) {
                                                if (outline is Outline.Rectangle) {
                                                    stroke = Stroke(
                                                        borderSize, pathEffect = PathEffect.dashPathEffect(
                                                            floatArrayOf(on.toPx(), off.toPx())
                                                        )
                                                    )
                                                } else {
                                                    // Multiplier to apply to the border size to get a stroke width that is
                                                    // large enough to cover the corners while not being too large to overly
                                                    // square off the internal shape. The resultant shape will be
                                                    // clipped to the desired shape. Any value lower will show artifacts in
                                                    // the corners of shapes. A value too large will always square off
                                                    // the internal shape corners. For example, for a rounded rect border
                                                    // a large multiplier will always have squared off edges within the
                                                    // inner section of the stroke, however, having a smaller multiplier
                                                    // will still keep the rounded effect for the inner section of the
                                                    // border
                                                    val strokeWidth = 1.2f * borderSize
                                                    inset = borderSize - strokeWidth / 2
                                                    val insetSize = Size(
                                                        size.width - inset * 2,
                                                        size.height - inset * 2
                                                    )
                                                    insetOutline = shape.createOutline(insetSize, layoutDirection, this)
                                                    stroke = Stroke(
                                                        strokeWidth, pathEffect = PathEffect.dashPathEffect(
                                                            floatArrayOf(on.toPx(), off.toPx())
                                                        )
                                                    )
                                                    pathClip = if (outline is Outline.Rounded) {
                                                        Path().apply { addRoundRect(outline.roundRect) }
                                                    } else if (outline is Outline.Generic) {
                                                        outline.path
                                                    } else {
                                                        // should not get here because we check for Outline.Rectangle
                                                        // above
                                                        null
                                                    }

                                                    insetPath =
                                                        if (insetOutline is Outline.Rounded &&
                                                            !insetOutline.roundRect.isSimple
                                                        ) {
                                                            // Rounded rect with non equal corner radii needs a path
                                                            // to be pre-translated
                                                            Path().apply {
                                                                addRoundRect(insetOutline.roundRect)
                                                                translate(Offset(inset, inset))
                                                            }
                                                        } else if (insetOutline is Outline.Generic) {
                                                            // Generic paths must be created and pre-translated
                                                            Path().apply {
                                                                addPath(insetOutline.path, Offset(inset, inset))
                                                            }
                                                        } else {
                                                            // Drawing a round rect with equal corner radii without
                                                            // usage of a path
                                                            null
                                                        }
                                                }
                                            }

                                            onDrawWithContent {
                                                drawContent()
                                                // Only draw the border if a have a valid stroke parameter. If we have
                                                // an invalid border size we will just draw the content
                                                if (stroke != null) {
                                                    if (insetOutline != null && pathClip != null) {
                                                        val isSimpleRoundRect = insetOutline is Outline.Rounded &&
                                                                insetOutline.roundRect.isSimple
                                                        withTransform({
                                                            clipPath(pathClip)
                                                            // we are drawing the round rect not as a path so we must
                                                            // translate ourselves othe
                                                            if (isSimpleRoundRect) {
                                                                translate(inset, inset)
                                                            }
                                                        }) {
                                                            if (isSimpleRoundRect) {
                                                                // If we don't have an insetPath then we are drawing
                                                                // a simple round rect with the corner radii all identical
                                                                val rrect = (insetOutline as Outline.Rounded).roundRect
                                                                drawRoundRect(
                                                                    brush = brush,
                                                                    topLeft = Offset(rrect.left, rrect.top),
                                                                    size = Size(rrect.width, rrect.height),
                                                                    cornerRadius = rrect.topLeftCornerRadius,
                                                                    style = stroke
                                                                )
                                                            } else if (insetPath != null) {
                                                                drawPath(insetPath, brush, style = stroke)
                                                            }
                                                        }
                                                        // Clip rect to ensure the stroke does not extend the bounds
                                                        // of the composable.
                                                        clipRect {
                                                            // Draw a hairline stroke to cover up non-anti-aliased pixels
                                                            // generated from the clip
                                                            if (isSimpleRoundRect) {
                                                                val rrect = (outline as Outline.Rounded).roundRect
                                                                drawRoundRect(
                                                                    brush = brush,
                                                                    topLeft = Offset(rrect.left, rrect.top),
                                                                    size = Size(rrect.width, rrect.height),
                                                                    cornerRadius = rrect.topLeftCornerRadius,
                                                                    style = Stroke(
                                                                        Stroke.HairlineWidth,
                                                                        pathEffect = PathEffect.dashPathEffect(
                                                                            floatArrayOf(on.toPx(), off.toPx())
                                                                        )
                                                                    )
                                                                )
                                                            } else {
                                                                drawPath(
                                                                    pathClip, brush = brush, style = Stroke(
                                                                        Stroke.HairlineWidth,
                                                                        pathEffect = PathEffect.dashPathEffect(
                                                                            floatArrayOf(on.toPx(), off.toPx())
                                                                        )
                                                                    )
                                                                )
                                                            }
                                                        }
                                                    } else {
                                                        // Rectangular border fast path
                                                        val strokeWidth = stroke.width
                                                        val halfStrokeWidth = strokeWidth / 2
                                                        drawRect(
                                                            brush = brush,
                                                            topLeft = Offset(halfStrokeWidth, halfStrokeWidth),
                                                            size = Size(
                                                                size.width - strokeWidth,
                                                                size.height - strokeWidth
                                                            ),
                                                            style = stroke
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    )
                                },
                                inspectorInfo = debugInspectorInfo {
                                    name = "border"
                                    properties["width"] = width
                                    if (brush is SolidColor) {
                                        properties["color"] = brush.value
                                        value = brush.value
                                    } else {
                                        properties["brush"] = brush
                                    }
                                    properties["shape"] = shape
                                }
                            )
                            
                            //4. HorizontalDivider
                            @Composable
                            fun HorizontalDivider(
                                modifier: Modifier = Modifier,
                                thickness: Dp = 1.dp,
                                color: Color = AppTheme.color.stroke,
                            ) {
                                Canvas(
                                    modifier
                                        .fillMaxWidth()
                                        .height(thickness),
                                ) {
                                    drawLine(
                                        color = color,
                                        strokeWidth = thickness.toPx(),
                                        start = Offset(0f, thickness.toPx() / 2),
                                        end = Offset(size.width, thickness.toPx() / 2),
                                    )
                                }
                            }
                            
                            //5. DashedBorder
                            fun Modifier.dashedBorder(
                                brush: Brush,
                                shape: Shape,
                                strokeWidth: Dp = 2.dp,
                                dashLength: Dp = 3.dp,
                                gapLength: Dp = 4.dp,
                                cap: StrokeCap = StrokeCap.Round
                            ) = this.drawWithContent {

                                val outline = shape.createOutline(size, layoutDirection, density = this)

                                val dashedStroke = Stroke(
                                    cap = cap,
                                    width = strokeWidth.toPx(),
                                    pathEffect = PathEffect.dashPathEffect(
                                        intervals = floatArrayOf(dashLength.toPx(), gapLength.toPx())
                                    )
                                )

                                drawContent()

                                drawOutline(
                                    outline = outline,
                                    style = dashedStroke,
                                    brush = brush
                                )
                            }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //RattingBar Animation

                    @Composable
                    fun RateView(modifier: Modifier, rate: Double?) {
                        var width by mutableRem(0)
                        val shape = remember { Rate }
                        var anim by mutableRem(false)
                        val w by animateDpAsState(
                            if (anim) (width.pxToDp / 10 * (rate ?: 0.0)).dp else 0.dp,
                            tween(1000),
                            label = ""
                        )
                        Box(modifier.onSizeChanged {
                            width = it.width
                        }) {
                            Row(
                                Modifier
                                    .fillMaxWidth()
                                    .aspectRatio(shape.ratio)
                                    .border(1.dp, SecondaryColor, shape.v)
                                    .clip(shape.v)
                            ) {
                                Box(
                                    Modifier
                                        .fillMaxHeight()
                                        .width(w)
                                        .background(PrimaryColor)
                                )
                            }
                        }

                        Launch {
                            anim = true
                        }
                    }

                    val Rate = Shape(
                        multiPaths(
                            "M259,1c1.86,1.53,2.88,3.5,3.62,5.81c18.08,55.93,36.22,111.84,54.35,167.75c3.52,10.84,6.64,13.08,18.18,13.1 c57.81,0.09,115.62,0.15,173.44,0.31c2.63,0.01,6.37-1.42,7.54,2.02c1.23,3.6-2.86,4.42-4.91,5.93 c-46.24,34.13-92.56,68.16-138.86,102.21c-9.95,7.31-11.47,11.4-7.72,23.07c15.62,48.67,31.33,97.32,47.02,145.97 c1.89,5.86,3.88,11.69,5.78,17.55c0.41,1.26,0.76,2.57,0.83,3.88c0.08,1.6,0.66,3.64-0.96,4.63c-1.75,1.07-3.07-0.69-4.35-1.62 c-47.28-34.4-95.17-67.96-141.92-103.09c-0.53-0.4-1.06-0.81-1.6-1.2c-7.39-5.48-11.53-5.65-19.02-0.3 c-13.42,9.59-26.7,19.36-40.09,28.99c-35.43,25.49-70.88,50.95-106.31,76.43c-1.07,0.77-2.02,1.72-3.02,2.58c-1,0-2,0-3,0 c3.08-9.85,6.1-19.72,9.26-29.55c15.73-48.99,31.51-97.97,47.25-146.96c2.92-9.08,1.9-12.35-5.75-18.02 c-11.51-8.53-23.1-16.96-34.63-25.46c-35-25.81-69.96-51.66-105.02-77.39c-3.29-2.42-6.28-4.9-8.11-8.6c0-0.33,0-0.67,0-1 c2.49-0.1,4.99-0.28,7.48-0.28c57.83-0.03,115.66-0.05,173.49-0.07c12.85,0,16.01-2.26,19.9-14.38 c15.29-47.57,30.58-95.14,45.84-142.72c3.15-9.83,6.19-19.7,9.28-29.55C257.67,1,258.33,1,259,1z",
                            "M614,495c2.12-10.13,5.86-19.79,8.99-29.61c15.62-49.03,31.44-98,47.21-146.98c3.32-10.33,2.51-13.11-6.26-19.57 c-46.96-34.58-93.93-69.14-140.9-103.7c-0.27-0.2-0.55-0.38-0.81-0.59c-2.07-1.71-4.93-3.67-4.22-6.3 c0.82-3.01,4.31-1.47,6.56-1.47c57.65-0.11,115.31-0.11,172.96-0.13c13.45,0,16.38-2.11,20.51-14.92 C736.38,114.83,754.69,57.91,773,1c1.33,0,2.67,0,4,0c18.41,56.71,36.81,113.42,55.22,170.13c0.36,1.11,0.67,2.23,1.04,3.34 c3.31,9.72,6.63,12.17,16.85,12.18c32.16,0.04,64.32,0.02,96.49,0.07c26.33,0.04,52.66,0.13,78.99,0.21 c1.49,0,3.08-0.14,4.47,0.28c2.75,0.84,3.42,2.57,1.19,4.76c-0.95,0.92-2.1,1.64-3.17,2.43 c-46.45,34.16-92.89,68.31-139.34,102.46c-10.5,7.72-11.91,11.45-7.91,23.86c17.5,54.38,35.1,108.72,52.66,163.08 c0.26,0.79,0.63,1.59,0.66,2.39c0.08,2.09,1.17,4.76-0.94,6.02c-2.01,1.19-3.58-1.14-5.11-2.26 c-47.71-34.68-96.02-68.54-143.16-104.01c-7.08-5.33-11.4-5.11-19.45,0.7c-34.05,24.57-68.06,49.19-102.12,73.75 C647.28,471.98,631.12,483.46,615,495C614.67,495,614.33,495,614,495z",
                            "M1293,1c18.41,56.71,36.81,113.42,55.22,170.13c0.36,1.11,0.67,2.23,1.04,3.34c3.31,9.72,6.63,12.17,16.85,12.18 c32.16,0.04,64.32,0.02,96.49,0.07c26.33,0.04,52.66,0.13,78.99,0.21c1.49,0,3.08-0.14,4.47,0.28c2.75,0.84,3.42,2.57,1.19,4.75 c-0.95,0.92-2.1,1.64-3.17,2.43c-46.45,34.16-92.89,68.31-139.34,102.46c-10.5,7.72-11.91,11.45-7.91,23.86 c17.5,54.38,35.1,108.72,52.66,163.08c0.26,0.79,0.63,1.59,0.66,2.39c0.08,2.09,1.17,4.76-0.94,6.02 c-2.01,1.19-3.58-1.14-5.11-2.26c-47.71-34.68-96.02-68.54-143.16-104.01c-7.08-5.33-11.4-5.11-19.45,0.7 c-34.05,24.57-68.06,49.19-102.12,73.75c-16.08,11.59-32.24,23.08-48.36,34.62c-0.33,0-0.67,0-1,0 c2.12-10.13,5.86-19.79,8.99-29.61c15.62-49.03,31.44-98,47.21-146.98c3.32-10.33,2.51-13.11-6.26-19.57 c-46.96-34.58-93.93-69.14-140.9-103.7c-0.27-0.2-0.55-0.38-0.81-0.59c-2.07-1.71-4.93-3.67-4.22-6.3 c0.82-3.01,4.31-1.47,6.56-1.47c57.65-0.11,115.31-0.11,172.96-0.13c13.45,0,16.38-2.11,20.51-14.92 C1252.38,114.83,1270.69,57.91,1289,1C1290.33,1,1291.67,1,1293,1z",
                            "M1809,1c18.41,56.71,36.81,113.42,55.22,170.13c0.36,1.11,0.67,2.23,1.04,3.34c3.31,9.72,6.63,12.17,16.85,12.18 c32.16,0.04,64.32,0.02,96.49,0.07c26.33,0.04,52.66,0.13,78.99,0.21c1.49,0,3.08-0.14,4.47,0.28c2.75,0.84,3.42,2.57,1.19,4.75 c-0.95,0.92-2.1,1.64-3.17,2.43c-46.45,34.16-92.89,68.31-139.34,102.46c-10.5,7.72-11.91,11.45-7.91,23.86 c17.5,54.38,35.1,108.72,52.66,163.08c0.26,0.79,0.63,1.59,0.66,2.39c0.08,2.09,1.17,4.76-0.94,6.02 c-2.01,1.19-3.58-1.14-5.11-2.26c-47.71-34.68-96.02-68.54-143.16-104.01c-7.08-5.33-11.4-5.11-19.45,0.7 c-34.05,24.57-68.06,49.19-102.12,73.75c-16.08,11.59-32.24,23.08-48.36,34.62c-0.33,0-0.67,0-1,0 c2.12-10.13,5.86-19.79,8.99-29.61c15.62-49.03,31.44-98,47.21-146.98c3.32-10.33,2.51-13.11-6.26-19.57 c-46.96-34.58-93.93-69.14-140.9-103.7c-0.27-0.2-0.55-0.38-0.81-0.59c-2.07-1.71-4.93-3.67-4.22-6.3 c0.82-3.01,4.31-1.47,6.56-1.47c57.65-0.11,115.31-0.11,172.96-0.13c13.45,0,16.38-2.11,20.51-14.92 C1768.38,114.83,1786.69,57.91,1805,1C1806.33,1,1807.67,1,1809,1z",
                            "M2162,495c2.12-10.13,5.86-19.79,8.99-29.61c15.62-49.03,31.44-98,47.21-146.98c3.32-10.33,2.52-13.11-6.26-19.57 c-46.96-34.58-93.93-69.14-140.9-103.7c-0.27-0.2-0.55-0.38-0.81-0.59c-2.07-1.71-4.93-3.67-4.22-6.3 c0.82-3.01,4.31-1.47,6.56-1.47c57.65-0.11,115.31-0.11,172.96-0.13c13.44,0,16.39-2.11,20.51-14.92 C2284.38,114.82,2302.69,57.91,2321,1c1.33,0,2.67,0,4,0c1.99,6.18,3.96,12.37,5.97,18.55c16.61,51.17,33.22,102.34,49.82,153.52 c3.69,11.38,6.69,13.58,18.85,13.59c57.83,0.08,115.66,0.16,173.49,0.26c2.78,0,5.76-0.56,7.87,2.08c0,0.67,0,1.33,0,2 c-1.51,1.04-3.05,2.03-4.52,3.12c-46.59,34.24-93.17,68.5-139.75,102.74c-10.5,7.72-11.89,11.45-7.9,23.86 c15.56,48.53,31.24,97.02,46.86,145.53c2.24,6.96,4.52,13.92,6.52,20.96c0.46,1.6,0.87,3.95-0.98,5.04 c-1.72,1.02-3.06-0.78-4.34-1.71c-47.69-34.7-96.01-68.55-143.15-104.02c-7.68-5.78-11.76-5.97-19.43-0.45 C2263.85,422.35,2213.43,458.68,2163,495C2162.67,495,2162.33,495,2162,495z"
                        ),
                        2580F, 494F
                    )
                    """.trimIndent()
            )

            //https://github.com/Joker-x-dev/CoolMallKotlin.git
            KotlinCode(
                code = """
                    //RattingBar Animation
                     
                    @Composable
                    fun WeRate(
                        value: Int,
                        count: Int = 5,
                        size: Dp = 26.dp,
                        onChange: ((Int) -> Unit)? = null,
                        animationEnabled: Boolean = true
                    ) {
                        var starWidth by remember { mutableIntStateOf(0) }
                        var clickedIndex by remember { mutableIntStateOf(-1) }
                        var animationTrigger by remember { mutableIntStateOf(0) }

                        LaunchedEffect(clickedIndex) {
                            if (clickedIndex >= 0) {
                                delay(200)
                                clickedIndex = -1
                            }
                        }

                        Row(
                            modifier = Modifier
                                .pointerInput(starWidth) {
                                    onChange?.let {
                                        detectHorizontalDragGestures { change, _ ->
                                            val newValue = change.position
                                                .calculateRateValue(starWidth, count)
                                            onChange(newValue)
                                        }
                                    }
                                }
                        ) {
                            repeat(count) { index ->
                                StarItem(
                                    isActive = index < value,
                                    isClicked = clickedIndex == index,
                                    animationEnabled = animationEnabled,
                                    animationDelay = if (animationEnabled) index * 50L else 0L,
                                    animationTrigger = animationTrigger,
                                    modifier = Modifier
                                        .size(size)
                                        .onSizeChanged {
                                            starWidth = it.width
                                        }
                                        .pointerInput(Unit) {
                                            onChange?.let {
                                                detectTapGestures {
                                                    clickedIndex = index
                                                    animationTrigger++
                                                    onChange(index + 1)
                                                }
                                            }
                                        }
                                )
                            }
                        }
                    }

                  
                    @Composable
                    private fun StarItem(
                        isActive: Boolean,
                        isClicked: Boolean,
                        animationEnabled: Boolean,
                        animationDelay: Long,
                        animationTrigger: Int,
                        modifier: Modifier
                    ) {
                        val scale by animateFloatAsState(
                            targetValue = if (isClicked && animationEnabled) 1.3f else 1f,
                            animationSpec = spring(
                                dampingRatio = 0.6f,
                                stiffness = 800f
                            ),
                            label = "star_scale"
                        )

                        val tintColor by animateColorAsState(
                            targetValue = if (isActive) {
                                Color(0xffFF6700)
                            } else {
                                Color(0xffC0C0C0) // 灰色
                            },
                            animationSpec = tween(
                                durationMillis = if (animationEnabled) 300 else 0,
                                delayMillis = if (animationEnabled) animationDelay.toInt() else 0
                            ),
                            label = "star_color"
                        )

                        var scaleAnimation by remember { mutableFloatStateOf(1f) }
                        val animatedScale by animateFloatAsState(
                            targetValue = scaleAnimation,
                            animationSpec = spring(
                                dampingRatio = 0.5f,
                                stiffness = 600f
                            ),
                            label = "star_fill_animation"
                        )

                        LaunchedEffect(isActive, animationTrigger) {
                            if (isActive && animationEnabled) {
                                delay(animationDelay)
                                scaleAnimation = 1.2f
                                delay(150)
                                scaleAnimation = 1f
                            }
                        }

                        Icon(
                            painter = painterResource(
                                id = R.drawable.ic_star_fill
                            ),
                            contentDescription = stringResource(id = R.string.rate_star),
                            tint = tintColor,
                            modifier = modifier
                                .scale(scale * animatedScale)
                        )
                    }

                    private fun Offset.calculateRateValue(starWidth: Int, count: Int): Int {
                        val newRating = (this.x / starWidth)
                            .coerceIn(0f, count.toFloat())

                        return newRating
                            .roundToInt()
                            .coerceIn(0, count)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/kaaneneskpc/AndroidXRF1Tracks

                    //Sliding Animation Image
                    private fun getSlideAnimation(
                        targetState: Int,
                        initialState: Int,
                        durationMillis: Int = 500
                    ) = if (targetState > initialState) {
                        (slideInHorizontally(
                            initialOffsetX = { fullWidth -> fullWidth },
                            animationSpec = tween(durationMillis = durationMillis)
                        ) + fadeIn(animationSpec = tween(durationMillis))).togetherWith(
                            slideOutHorizontally(
                                targetOffsetX = { fullWidth -> -fullWidth },
                                animationSpec = tween(durationMillis = durationMillis)
                            ) + fadeOut(animationSpec = tween(durationMillis))
                        )
                    } else {
                        (slideInHorizontally(
                            initialOffsetX = { fullWidth -> -fullWidth },
                            animationSpec = tween(durationMillis = durationMillis)
                        ) + fadeIn(animationSpec = tween(durationMillis))).togetherWith(
                            slideOutHorizontally(
                                targetOffsetX = { fullWidth -> fullWidth },
                                animationSpec = tween(durationMillis = durationMillis)
                            ) + fadeOut(animationSpec = tween(durationMillis))
                        )
                    }

                    Surface {
                        AnimatedContent(
                            targetState = currentTrackIndex,
                            transitionSpec = { getSlideAnimation(targetState, initialState) }
                        ) { targetIndex ->

                        }
                    }

                    private fun updateTrackIndex(currentIndex: Int, forward: Boolean, totalTracks: Int): Int =
                        when {
                                forward -> (currentIndex + 1) % totalTracks
                                currentIndex == 0 -> totalTracks - 1
                                else -> currentIndex - 1
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Zoom Image

                    @Composable
                    @Preview
                    fun ZoomableImage(imageResource: DrawableResource) {
                        // State for zoom level and offset.
                        val scale: MutableState<Float> = remember { mutableStateOf(1f) }
                        val offset: MutableState<Offset> = remember {
                            mutableStateOf(Offset(0f, 0f))
                        }

                        // Define minimum and maximum scale factors.
                        // Prevent zooming out further than screen width.
                        val minScale = 1f
                        // Maximum zoom level.
                        val maxScale = 20f

                        val transformableState: TransformableState =
                            rememberTransformableState { zoomChange: Float, panChange: Offset, _ ->
                                val newScale =
                                    (scale.value * zoomChange).coerceIn(minScale, maxScale)
                                scale.value = newScale
                                offset.value = if (newScale == minScale || newScale == maxScale) {
                                    Offset.Zero
                                } else {
                                    offset.value + panChange
                                }
                            }

                        Box(
                            Modifier
                                .fillMaxSize()
                                .background(Color(0xFFf0e7d8))
                                // Handles zoom and pan gestures.
                                .transformable(state = transformableState)
                                .pointerInput(Unit) {
                                    detectTapGestures(
                                        onDoubleTap = {
                                            // Reset zoom on double-tap.
                                            scale.value = 1f
                                            offset.value = Offset.Zero
                                        }
                                    )
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Image(
                                painter = painterResource(imageResource),
                                contentDescription = "Розворот книги",
                                modifier = Modifier
                                    .graphicsLayer(
                                        scaleX = scale.value,
                                        scaleY = scale.value,
                                        translationX = offset.value.x,
                                        translationY = offset.value.y
                                    )
                                    .fillMaxSize()
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Kevn-Block/JuliaSets-KMP
            KotlinCode(
                code = """
                    //Zoom Image Rectangle

                    @Composable
                    fun JuliaSetContent(
                        modifier: Modifier,
                        image: ImageBitmap?,
                        canvasSize: Size,
                        zoomRange: Float,
                        center: Offset,
                        rectStrokeColor: Color,
                        onCanvasSizeChanged: (Size) -> Unit,
                        onCenterChanged: (Offset) -> Unit,
                        onZoomRangeChanged: (Float) -> Unit,
                    ) {
                        Canvas(
                            modifier = modifier
                                .rectZoomModifier(
                                    canvasSize = canvasSize,
                                    currentZoomRange = zoomRange,
                                    currentCenter = center,
                                    strokeColor = rectStrokeColor,
                                    onDragEnd = { newCenter, newZoomRange ->
                                        onCenterChanged(newCenter)
                                        onZoomRangeChanged(newZoomRange)
                                    }
                                )
                                .onSizeChanged {
                                    onCanvasSizeChanged(it.toSize())
                                }
                        ) {
                            image?.let {
                                drawImage(it)
                            }
                        }
                    }

                    fun Modifier.rectZoomModifier(
                        canvasSize: Size,
                        currentCenter: Offset,
                        currentZoomRange: Float,
                        strokeColor: Color,
                        onDragEnd: (center: Offset, zoomRange: Float) -> Unit,
                    ): Modifier =
                        composed {
                            var initialPoint by remember { mutableStateOf(Offset.Unspecified) }
                            var drawnRect by remember { mutableStateOf(Rect.Zero) }
                            pointerInput(canvasSize, currentCenter) {
                                detectDragGestures(
                                    onDragStart = { clickPoint ->
                                        initialPoint = clickPoint
                                    },
                                    onDragEnd = {
                                        val w = canvasSize.width
                                        val h = canvasSize.height
                                        val aspectRatio = canvasSize.aspectRatio
                                        val oldZoomRangeY = currentZoomRange
                                        val oldZoomRangeX = oldZoomRangeY * aspectRatio

                                        fun getNewCenter(pixel: Offset): Offset {
                                            val real = (pixel.x / w) * oldZoomRangeX + (currentCenter.x - oldZoomRangeX / 2f)
                                            val imag = (pixel.y / h) * oldZoomRangeY + (currentCenter.y - oldZoomRangeY / 2f)
                                            return Offset(real, imag)
                                        }

                                        val newCenter = getNewCenter(drawnRect.center)
                                        val newRange = oldZoomRangeY * (drawnRect.height / h)

                                        drawnRect = Rect.Zero
                                        onDragEnd(
                                            newCenter,
                                            newRange
                                        )
                                    },
                                    onDragCancel = {
                                        drawnRect = Rect.Zero
                                    },
                                    onDrag = { change, dragAmount ->
                                        change.consume()
                                        drawnRect = Rect.Companion.fromOffsets(initialPoint, change.position)
                                    }
                                )
                            }
                            .drawWithContent {
                                drawContent()
                                if (drawnRect != Rect.Zero) {
                                    drawRect(
                                        color = strokeColor,
                                        topLeft = drawnRect.topLeft,
                                        size = drawnRect.size,
                                        style = Stroke(width = 3f)
                                    )
                                }
                            }
                        }

                       //GeometryUtil.kt
                       fun Rect.Companion.fromOffsets(firstOffset: Offset, secondOffset: Offset): Rect {
                            val left = minOf(firstOffset.x, secondOffset.x)
                            val top = minOf(firstOffset.y, secondOffset.y)
                            val right = maxOf(firstOffset.x, secondOffset.x)
                            val bottom = maxOf(firstOffset.y, secondOffset.y)

                        return Rect(
                            topLeft = Offset(left, top),
                            bottomRight = Offset(right, bottom)
                        )
                      }

                      val Size.aspectRatio get() = width / height
                    """.trimIndent()
            )


            KotlinCode(
                code = """
                    @Composable
                    fun AnimatedExample() {
                        var visible by remember { mutableStateOf(false) }
                        val density = LocalDensity.current

                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(16.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            // Button to toggle visibility
                            Button(onClick = { visible = !visible },
                                Modifier.height(200.dp).width(200.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = color1,
                                    contentColor = textColor
                                ),
                                shape = RoundedCornerShape(16.dp)
                            ) {
                                Text(if (visible) "Hello" else "Hi",
                                    fontSize = 20.sp)
                            }

                            Spacer(modifier = Modifier.height(16.dp))

                            // AnimatedVisibility with animation effects
                            AnimatedVisibility(
                                visible = visible,
                                enter = slideInVertically {
                                    // Convert -40.dp to pixels using LocalDensity
                                    with(density) { -40.dp.toPx() }.toInt()
                                } + expandVertically(
                                    expandFrom = Alignment.Top
                                ) + fadeIn(
                                    initialAlpha = 0.1f
                                ),
                                exit = slideOutVertically() + shrinkVertically() + fadeOut()
                            ) {
                                Example1()
                            }
                        }
                    }


                    @Composable
                    fun Example1() {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(200.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center,
                        ) {

                            Button(onClick = {  },
                                Modifier.height(200.dp).width(200.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = color2,
                                    contentColor = textColor
                                ),
                                shape = RoundedCornerShape(16.dp)
                            ) {
                                Text(text = "World", fontSize = 20.sp,
                                )
                            }

                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // breathing animation
                    val scale by rememberInfiniteTransition().animateFloat(
                        initialValue = 0.96f,
                        targetValue = 1.04f,
                        animationSpec = infiniteRepeatable(
                            animation = tween(600, easing = EaseInOut),
                            repeatMode = RepeatMode.Reverse
                        )
                    )
                    """.trimIndent()
            )

            //decodeToImageBitmap
            KotlinCode(
                code = """
                    sealed interface ImageEnhanceState {
                        data object Loading : ImageEnhanceState
                        data class Success(val imageBitmap: ImageBitmap) : ImageEnhanceState
                    }

                    @OptIn(ExperimentalResourceApi::class)
                    @Composable
                    fun ImageEnhanceScreen(
                        uiState: StateFlow<ImageEnhanceState>
                    ) {

                        val state by uiState.collectAsStateWithLifecycle()

                        var imageBitmap by remember {
                            mutableStateOf<ImageBitmap?>(null)
                        }

                        if (state is ImageEnhanceState.Success) {
                            imageBitmap = (state as ImageEnhanceState.Success).imageBitmap
                        }

                        Scaffold {
                            Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                                imageBitmap?.let { bitmap ->
                                    Image(bitmap = bitmap, contentDescription = null)
                                }
                            }
                        }
                    }

                    class ImageEnhanceViewModel() : ViewModel() {
                        private val _uiState: MutableStateFlow<ImageEnhanceState> = MutableStateFlow(ImageEnhanceState.Loading)
                        val uiState: StateFlow<ImageEnhanceState> = _uiState.asStateFlow()

                        init {
                            observeImage()
                        }

                        @OptIn(ExperimentalResourceApi::class)
                        private fun observeImage() {
                            viewModelScope.launch(Dispatchers.IO) {
                                Res.readBytes("files/image_01.jpg").decodeToImageBitmap()
                                    .also { imageBitmap ->
                                        _uiState.update {
                                            ImageEnhanceState.Success(imageBitmap = imageBitmap)
                                        }
                                    }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        .animatedBorder(
                            borderColors = listOf(
                                SpikartTheme.colors.accentColor,
                                SpikartTheme.colors.blueColor,
                                SpikartTheme.colors.greenAccentColor,
                            ),
                            backgroundColor = MaterialTheme.colorScheme.background)

                        @Composable
                        fun Modifier.animatedBorder(
                            borderColors: List<Color>,
                            backgroundColor: Color,
                            shape: Shape = RoundedCornerShape(4.dp),
                            borderWidth: Dp = 2.dp,
                            animationDurationInMillis: Int = 12000,
                            easing: Easing = EaseInCubic
                        ): Modifier {
                            var isFocused by remember { mutableStateOf(false) }

                            val brush = Brush.sweepGradient(borderColors)
                            val infiniteTransition = rememberInfiniteTransition(label = "animatedBorder")
                            val angle by infiniteTransition.animateFloat(
                                initialValue = 0f,
                                targetValue = 360f,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(durationMillis = animationDurationInMillis, easing = easing),
                                    repeatMode = RepeatMode.Reverse
                                ), label = "angleAnimation"
                            )

                            return this
                                .onFocusChanged { focusState ->
                                    isFocused = focusState.isFocused
                                }
                                .then(
                                    if (isFocused) {
                                        this.clip(shape)
                                            .padding(borderWidth)
                                            .drawWithContent {
                                                rotate(angle) {
                                                    drawCircle(
                                                        brush = brush,
                                                        radius = size.width,
                                                        blendMode = BlendMode.SrcIn,
                                                    )
                                                }
                                                drawContent()
                                            }
                                            .background(color = backgroundColor, shape = shape)
                                    } else {
                                        this
                                    }
                                )
                        }
                    """.trimIndent()
            )

            //SHAPE
            //https://github.com/bingbong0098/Coach
            KotlinCode(
                code = """
                        //SHAPE 1 Oval BottomShape Background

                        @Composable
                        fun CustomOvalBottomShapeContainer(
                            modifier: Modifier = Modifier,
                            backgroundColor: Color = Color.White,
                            ovalHeight: Float? = null,
                            content: @Composable BoxScope.() -> Unit,
                        ) {
                            Box(
                                modifier = modifier
                                    .drawBehind {
                                        drawPath(
                                            path = createOvalBottomPath(ovalHeight ?: size.height),
                                            color = backgroundColor
                                        )
                                    }
                            ) {
                                content()
                            }
                        }

                        private fun DrawScope.createOvalBottomPath(
                            ovalHeight: Float,
                        ): Path {
                            val constantOffsetPx = size.width / 6
                            val path = Path()
                            path.moveTo(0f, 0f) // Top-left corner
                            path.lineTo(size.width, 0f) // Top-right corner
                            path.lineTo(size.width, ovalHeight - constantOffsetPx) // Bottom-right corner (before oval)
                            path.quadraticTo(
                                x1 = size.width / 2,
                                y1 = ovalHeight + constantOffsetPx,
                                x2 = 0f,
                                y2 = ovalHeight - constantOffsetPx
                            ) // Oval bottom-left
                            path.close()
                            return path
                        }

                        //@Preview
                        //@Composable
                        //fun QuadraticPathDemo() {
                        //    Canvas(modifier = Modifier.fillMaxSize()) {
                        //        val controlPoint = Size(300f, 900f)
                        //        val endPoint = Size(500f, 600f)
                        //        val path = Path().apply {
                        //            // Start at (100, 600)
                        //            moveTo(100f, 600f)
                        //            // Create a quadratic Bézier curve with control point at (300, 200) and endpoint at (500, 600)
                        //            quadraticTo(controlPoint.width, controlPoint.height, endPoint.width, endPoint.height)
                        //        }
                        //
                        //        // Draw the path with a stroke
                        //        drawPath(
                        //            path = path,
                        //            color = Color.Blue,
                        //            style = Stroke(width = 5.dp.toPx())
                        //        )
                        //
                        //        // Draw control and endpoint markers for clarity
                        //        drawCircle(
                        //            color = Color.Red,
                        //            center = Offset(controlPoint.width, controlPoint.height),
                        //            radius = 10f
                        //        )
                        //        drawCircle(
                        //            color = Color.Green,
                        //            center = Offset(endPoint.width, endPoint.height),
                        //            radius = 10f
                        //        )
                        //    }
                        //}
                    """.trimIndent()
            )

            //https://github.com/bingbong0098/Coach
            KotlinCode(
                code = """
                         //SHAPE 2 Card corner green corner strokes

                         Box(
                            modifier = Modifier.fillMaxSize()
                         ) {
                                 // Draw top-left and bottom-right green corner strokes
                                 CornerAccent(
                                     modifier = Modifier
                                     .fillMaxSize()
                                     .padding(space.small),
                                         cornerColor = Color(0xFF00C853), // bright green
                                         cornerStrokeWidth = 2.dp
                                 )
                         }

                         // Draw top-left and bottom-right green corner strokes
                         CornerAccent(
                             modifier = Modifier
                                 .fillMaxSize()
                                 .padding(14.dp),
                             cornerColor = Color(0xFF00C853), // bright green
                             cornerStrokeWidth = 2.dp
                         )

                         @Composable
                         fun CornerAccent(
                             cornerColor: Color,
                             cornerStrokeWidth: Dp,
                             modifier: Modifier = Modifier,
                         ) {
                             Canvas(
                                 modifier
                                     .fillMaxWidth()
                                     .fillMaxHeight()
                             ) {
                                 val strokePx = cornerStrokeWidth.toPx()
                                 val cornerRadius = 24.dp.toPx()  // how "curved" each corner arc is

                                 // Top-left arc
                                 drawArc(
                                     color = cornerColor,
                                     startAngle = 180f, // half circle to the left
                                     sweepAngle = 90f,  // 90° arc
                                     useCenter = false,
                                     style = Stroke(width = strokePx),
                                     topLeft = Offset(-cornerRadius / 2, -cornerRadius / 2),
                                     size = Size(cornerRadius, cornerRadius)
                                 )

                                 // Bottom-right arc
                                 drawArc(
                                     color = cornerColor,
                                     startAngle = 0f,
                                     sweepAngle = 90f,
                                     useCenter = false,
                                     style = Stroke(width = strokePx),
                                     topLeft = Offset(size.width - cornerRadius / 2, size.height - cornerRadius / 2),
                                     size = Size(cornerRadius, cornerRadius)
                                 )
                             }
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //SHAPE 3 GridLines Triangle Caps

                        @Composable
                        fun HighlightsSection(items: List<HighlightItem>, modifier: Modifier = Modifier) {
                            Box(
                                modifier = modifier
                                    .padding(horizontal = 40.dp)
                                    // Force the box to be tall enough to show 2 rows
                                    // while staying symmetrical; for example a square or 3:2 ratio:
                                    .aspectRatio(3f / 2f),
                                contentAlignment = Alignment.Center
                            ) {
                                // 1) Draw your grid lines with triangular caps
                                Canvas(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(130.dp)
                                ) {
                                    drawGridLinesWithTriangleCaps(
                                        gridRow = 2,
                                        gridColumn = 3,
                                        color = Color.LightGray,
                                        strokeWidth = 5f
                                    )
                                }

                                // 2) Overlay your icons/text using a LazyVerticalGrid with 3 columns
                                LazyVerticalGrid(
                                    columns = GridCells.Fixed(3),
                                    horizontalArrangement = Arrangement.Center,
                                    verticalArrangement = Arrangement.Center,
                                    modifier = Modifier
                                        .fillMaxWidth()

                                ) {
                                    items(items) { item ->
                                        Column(
                                            horizontalAlignment = Alignment.CenterHorizontally,
                                            verticalArrangement = Arrangement.Center,
                                            modifier = Modifier.padding(8.dp)
                                        ) {
                                            Icon(
                                                painter = painterResource(id = item.icon),
                                                contentDescription = item.label,
                                                tint = if (item.isSelected) HomeConstants.HomeColors.CoachGreen else Color.Gray,
                                                modifier = Modifier.size(30.dp)
                                            )
                                            Spacer(modifier = Modifier.height(4.dp))

                                            Text(
                                                text = item.label,
                                                style = TextStyle(
                                                    fontFamily = HomeConstants.HomeFont.inter,
                                                    fontWeight = FontWeight.Normal,
                                                    fontSize = 12.sp,
                                                    color = if (item.isSelected) Color.Black else Color.Gray
                                                ),
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        private fun DrawScope.drawGridLinesWithTriangleCaps(
                            gridRow: Int,
                            gridColumn: Int,
                            color: Color,
                            strokeWidth: Float,
                        ) {
                            val cellWidth = size.width / gridColumn
                            val cellHeight = size.height / gridRow
                            val triangleSize = strokeWidth * 4  // Triangle size based on the line thickness

                            // Function to draw a triangle cap at a specific position
                            fun drawTriangleCap(center: Offset, directionX: Float, directionY: Float, isVertical: Boolean) {
                                val path = Path().apply {
                                    if (isVertical) {
                                        moveTo(center.x, center.y)  // Triangle tip
                                        lineTo(
                                            center.x + directionX - triangleSize / 2,
                                            center.y + directionY * triangleSize
                                        )  // Bottom-left
                                        lineTo(
                                            center.x - directionX + triangleSize / 2,
                                            center.y + directionY * triangleSize
                                        )  // Bottom-right // Right corner
                                        close()
                                    } else {
                                        moveTo(center.x, center.y)  // Triangle tip
                                        lineTo(
                                            center.x + directionX * triangleSize,
                                            center.y - (triangleSize / 2)
                                        )
                                        lineTo(
                                            center.x + directionX * triangleSize,
                                            center.y + (triangleSize / 2)
                                        )
                                        close()
                                    }
                                }
                                drawPath(path, color)
                            }

                            val lineOffset = 70f

                            // Draw vertical lines with triangle caps
                            for (i in 0..gridColumn) {
                                val x = i * cellWidth
                                drawLine(
                                    color = color,
                                    start = Offset(x, -lineOffset),
                                    end = Offset(x, size.height + lineOffset),
                                    strokeWidth = strokeWidth,
                                    cap = StrokeCap.Round
                                )
                                // Top and bottom triangle caps
                                drawTriangleCap(
                                    center = Offset(x, -lineOffset),
                                    directionX = 0f,
                                    directionY = -1f,
                                    isVertical = true
                                )  // Pointing down
                                drawTriangleCap(
                                    center = Offset(x, size.height + lineOffset),
                                    directionX = 0f,
                                    directionY = 1f,
                                    isVertical = true
                                )  // Pointing up
                            }

                            // Draw horizontal lines with triangle caps
                            for (i in 0..gridRow) {
                                val y = i * cellHeight
                                drawLine(
                                    color = color,
                                    start = Offset(-lineOffset, y),
                                    end = Offset(size.width + lineOffset, y),
                                    strokeWidth = strokeWidth,
                                    cap = StrokeCap.Round
                                )
                                // Left and right triangle caps
                                drawTriangleCap(
                                    center = Offset(-lineOffset, y),
                                    directionX = -1f,
                                    directionY = 0f,
                                    isVertical = false
                                )  // Pointing right
                                drawTriangleCap(
                                    center = Offset(size.width + lineOffset, y),
                                    directionX = 1f,
                                    directionY = 0f,
                                    isVertical = false
                                )  // Pointing left
                            }
                        }

                        @Preview(showBackground = true)
                        @Composable
                        fun TriangleCapPreview() {
                            Canvas(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(24.dp)
                            ) {
                                val canvasWidth = size.width
                                val canvasHeight = size.height
                                val gridSize = 3
                                val cellWidth = canvasWidth / gridSize
                                val cellHeight = canvasHeight / gridSize
                                val triangleSize = 20.dp.toPx()

                                // Function to draw a triangle cap at a specific position
                                fun drawTriangleCap(
                                    center: Offset,
                                    directionX: Float,
                                    directionY: Float,
                                    isVertical: Boolean,
                                ) {
                                    val path = Path().apply {
                                        if (isVertical) {
                                            moveTo(center.x, center.y)  // Triangle tip
                                            lineTo(
                                                center.x + directionX - triangleSize / 2,
                                                center.y + directionY * triangleSize
                                            )  // Bottom-left
                                            lineTo(
                                                center.x - directionX + triangleSize / 2,
                                                center.y + directionY * triangleSize
                                            )  // Bottom-right // Right corner
                                            close()
                                        } else {
                                            moveTo(center.x, center.y)  // Triangle tip
                                            lineTo(
                                                center.x + directionX * triangleSize,
                                                center.y - (triangleSize / 2)
                                            )
                                            lineTo(
                                                center.x + directionX * triangleSize,
                                                center.y + (triangleSize / 2)
                                            )
                                            close()
                                        }
                                    }
                                    drawPath(path, Color.Red)
                                }

                                // Draw vertical lines with upward/downward-facing triangles
                                for (i in 1 until gridSize) {
                                    val x = i * cellWidth
                                    drawLine(
                                        color = Color.Gray,
                                        start = Offset(x, 0f),
                                        end = Offset(x, canvasHeight),
                                        strokeWidth = 5.dp.toPx()
                                    )
                                    // Top and bottom triangle caps
                                    drawTriangleCap(
                                        center = Offset(x, 0f),
                                        directionX = 0f,
                                        directionY = -1f,
                                        isVertical = true
                                    )  // Pointing down
                                    drawTriangleCap(
                                        center = Offset(x, canvasHeight),
                                        directionX = 0f,
                                        directionY = 1f,
                                        isVertical = true
                                    )  // Pointing up
                                }

                                // Draw horizontal lines with inward-facing triangles
                                for (i in 1 until gridSize) {
                                    val y = i * cellHeight
                                    drawLine(
                                        color = Color.Gray,
                                        start = Offset(0f, y),
                                        end = Offset(canvasWidth, y),
                                        strokeWidth = 5.dp.toPx()
                                    )
                                    // Left and right triangle caps
                                    drawTriangleCap(
                                        center = Offset(0f, y),
                                        directionX = -1f,
                                        directionY = 0f,
                                        isVertical = false
                                    )  // Pointing right
                                    drawTriangleCap(
                                        center = Offset(canvasWidth, y),
                                        directionX = 1f,
                                        directionY = 0f,
                                        isVertical = false
                                    )  // Pointing left
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Circle Indicator

                        @Composable
                        fun Modifier.circleIndicator(isVisible: Boolean, block: DrawScope.() -> Unit): Modifier {
                            if (!isVisible) return this.drawBehind { block() }
                            val color = Pond.localColors.content
                            val density = LocalDensity.current
                            val infiniteTransition = rememberInfiniteTransition(label = "infinite")
                            val phase by infiniteTransition.animateFloat(
                                initialValue = 0f,
                                targetValue = 50f * density.density,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(2000, easing = EaseInOutCubic),
                                    repeatMode = RepeatMode.Reverse
                                ),
                                label = "color"
                            )
                            val dotSize = 3f * density.density
                            return this.drawBehind {
                                this.block()
                                drawCircle(
                                    color = color.copy(.5f),
                                    radius = size.width / 2f + 4f * density.density,
                                    style = Stroke(
                                        width = dotSize,
                                        pathEffect = PathEffect.dashPathEffect(floatArrayOf(dotSize, dotSize), phase) // Dash pattern
                                    )
                                )
                            }
                        }`
                    """.trimIndent()
            )

            //https://github.com/StarkDroid/WallStreet
            KotlinCode(
                code = """
                        //TextList Animation (TextField Hint Animation, AnimatedPlaceholderTextField)

                        @OptIn(ExperimentalMaterial3Api::class)
                        @Composable
                        fun AnimatedHeaderText(scrollBehavior: TopAppBarScrollBehavior) {

                            val headerTexts = stringArrayResource(Res.array.subtitle_texts)

                            var currentSubtitleIndex by remember { mutableStateOf(0) }

                            val coroutineScope = rememberCoroutineScope()

                            LaunchedEffect(headerTexts.size) {
                                coroutineScope.launch {
                                    while (true) {
                                        delay(3000)
                                        currentSubtitleIndex = (currentSubtitleIndex + 1) % headerTexts.size
                                    }
                                }
                            }

                            val isVisible = remember { derivedStateOf { scrollBehavior.state.collapsedFraction < 0.1f } }

                            AnimatedVisibility(
                                visible = isVisible.value,
                                enter = fadeIn() + expandVertically(),
                                exit = fadeOut() + shrinkVertically(),
                                modifier = Modifier.padding(WindowInsets.statusBars.asPaddingValues())
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(top = TopAppBarDefaults.TopAppBarExpandedHeight / 2),
                                    contentAlignment = Alignment.Center
                                ) {
                                    AnimatedContent(
                                        targetState = currentSubtitleIndex,
                                        transitionSpec = {
                                            (slideInVertically { it } + fadeIn()) togetherWith
                                                    (slideOutVertically { it } + fadeOut())
                                        }
                                    ) { index ->
                                        Text(
                                            text = headerTexts[index],
                                            style = MaterialTheme.typography.bodyLarge,
                                            color = MaterialTheme.colorScheme.primary,
                                        )
                                    }
                                }
                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/ksharma-xyz/KRAIL.git
            KotlinCode(
                code = $$"""
                    @Composable
                    fun AnimatedPlaceholderTextField(modifier: Modifier) {
                        val transportModes = listOf(
                            TransportMode.Bus(),
                            TransportMode.Train(),
                            TransportMode.Metro(),
                            TransportMode.Ferry(),
                            TransportMode.LightRail()
                        )

                        var currentModeIndex by remember { mutableStateOf(0) }
                        var currentText by remember { mutableStateOf("Search here") }
                        var isDeleting by remember { mutableStateOf(false) }

                        LaunchedEffect(currentText, isDeleting) {
                            val targetText = when {
                                isDeleting -> "Search"
                                else -> when (currentModeIndex) {
                                    0 -> "Search bus stop id"
                                    1 -> "Search train station"
                                    2 -> "Search metro station"
                                    3 -> "Search ferry wharf"
                                    4 -> "Search light rail stop"
                                    else -> "Search here"
                                }
                            }

                            if (currentText != targetText) {
                                delay(100) // Typing speed
                                currentText = if (isDeleting) {
                                    currentText.dropLast(1)
                                } else {
                                    targetText.take(currentText.length + 1)
                                }
                            } else {
                                if (isDeleting) {
                                    isDeleting = false
                                } else {
                                    delay(1500) // Pause before starting delete animation
                                    isDeleting = true
                                    currentModeIndex = (currentModeIndex + 1) % transportModes.size
                                }
                            }
                        }

                        Column(
                            modifier = modifier
                                .padding(16.dp)
                                .fillMaxWidth()
                        ) {
                            Text(
                                text = "Mode: ${transportModes[currentModeIndex].name}",
                                color = transportModes[currentModeIndex].colorCode.hexToComposeColor(),
                                modifier = Modifier.padding(bottom = 8.dp)
                            )
                            TextField(
                                placeholder = currentText,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/StarkDroid/WallStreet
            KotlinCode(
                code = """
                        //CardView Corner Animation Hover

                        @OptIn(ExperimentalComposeUiApi::class)
                        @Composable
                        actual fun CardView(wallpapers: Model, onImageClick: (String) -> Unit) {
                            var cornerRadius by remember { mutableStateOf(8.dp) }
                            var elevation by remember { mutableStateOf(4.dp) }
                            var isHovering by remember { mutableStateOf(false) }

                            val offset by animateFloatAsState(
                                targetValue = if (isHovering) -8f else 0f,
                                animationSpec = tween(durationMillis = 300)
                            )

                            val hoverModifier = Modifier
                                .pointerHoverIcon(icon = PointerIcon.Hand)
                                .onPointerEvent(PointerEventType.Enter) {
                                    cornerRadius = 16.dp
                                    elevation = 20.dp
                                    isHovering = true
                                }
                                .onPointerEvent(PointerEventType.Exit) {
                                    cornerRadius = 8.dp
                                    elevation = 4.dp
                                    isHovering = false
                                }
                                .graphicsLayer {
                                    translationY = offset
                                }

                            Card(
                                modifier = Modifier
                                    .width(500.dp)
                                    .height(250.dp)
                                    .padding(8.dp)
                                    .then(hoverModifier)
                                    .clickable {
                                        if (wallpapers.imageUrl.isNotEmpty()) {
                                            setWallpaper(wallpapers)
                                        }
                                    },
                                shape = RoundedCornerShape(cornerRadius),
                                elevation = CardDefaults.cardElevation(defaultElevation = elevation),
                            ) {

                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/WAHID-QANDIL/BorutoAppVersion1
            KotlinCode(
                code =
                    $$"""
                //Image fraction Detail Page

                @OptIn(ExperimentalMaterial3Api::class)
                @Composable
                fun DetailsContent(
                ) {
                    val scaffoldState = rememberBottomSheetScaffoldState(
                        bottomSheetState = rememberStandardBottomSheetState(initialValue = SheetValue.Expanded)
                    )

                    val currentSheetFraction = scaffoldState.currentSheetFraction
                    val imageFractionAnim by animateFloatAsState(
                        targetValue = currentSheetFraction,
                        animationSpec = tween(
                            durationMillis = 10,
                            delayMillis = 0,
                            easing = EaseInOut
                        )
                    )
                    val reduceAnim by animateDpAsState(
                        targetValue = if (currentSheetFraction == 1f) LARGE_ROUNDED_CORNER_SIZE else 0.dp
                    )

                    BottomSheetScaffold(
                        scaffoldState = scaffoldState,
                        sheetPeekHeight = SHEET_PEEK_HEIGHT,
                        sheetContent = {

                        },
                        content = {

                        },
                        sheetShadowElevation = 4.dp,
                        sheetShape = RoundedCornerShape(
                            topStart = reduceAnim,
                            topEnd = reduceAnim,
                        ),
                    )
                }

                @OptIn(ExperimentalMaterial3Api::class)
                val BottomSheetScaffoldState.currentSheetFraction: Float
                get() {
                    val currentValue = bottomSheetState.currentValue
                    val targetValue = bottomSheetState.targetValue

                    Log.d("BottomSheetScaffoldState", "currentValue: $currentValue")
                    Log.d("BottomSheetScaffoldState", "targetValue: $targetValue")
                    return when {
                        currentValue == SheetValue.Hidden && targetValue == SheetValue.Hidden -> 1f
                        currentValue == SheetValue.Expanded && targetValue == SheetValue.Expanded -> 0f
                        currentValue == SheetValue.Hidden && targetValue == SheetValue.Expanded -> 1f
                        currentValue == SheetValue.Expanded && targetValue == SheetValue.Hidden -> 0f
                        else -> 1f
                    }
                }

                      @Composable
                      fun BackgroundContent(
                          heroImage: String,
                          imageFraction: Float = 1f,
                          backgroundColor: Color = MaterialTheme.colorScheme.surface,
                          onCloseClicked: () -> Unit,
                      ) {
                          val painter = rememberAsyncImagePainter(
                              model = ImageRequest.Builder(
                                  LocalContext.current
                              ).data(
                                  "imageUrl"
                              ).placeholder(
                                  R.drawable.place_holder
                              ).error(R.drawable.place_holder).build()
                          )
                          Box(
                              modifier = Modifier
                                  .fillMaxSize()
                                  .background(color = backgroundColor),

                              ) {
                              Image(
                                  modifier = Modifier
                                      .fillMaxWidth()
                                      .fillMaxHeight(fraction = if (imageFraction < 1) MIN_BACKGROUND_SIZE else imageFraction ),
                                  painter = painter,
                                  contentDescription = stringResource(R.string.hero_image),
                                  contentScale = ContentScale.Crop,
                              )
                          }
                      }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //LazyColumn Timer List

                        @OptIn(ExperimentalResourceApi::class)
                        @Composable
                        @Preview
                        fun App() {
                            MaterialTheme {
                                FancyCountDownTimer()
                            }
                        }

                        const val total = 100
                        const val itemsPerRulerHeight = 30
                        val fontSize = 60.sp

                        // TODO: still work in progress, it requires some fine-tuning and adjustments
                        @OptIn(ExperimentalFoundationApi::class)
                        @Composable
                        fun FancyCountDownTimer() {
                            val scrollState = rememberLazyListState()
                            val snappingLayout = remember(scrollState) { SnapLayoutInfoProvider(scrollState) }
                            val flingBehavior = rememberSnapFlingBehavior(snappingLayout)
                            val count by remember(scrollState) {
                                derivedStateOf {
                                    (total - scrollState.firstVisibleItemIndex)
                                }
                            }

                            val localDensity = LocalDensity.current

                            var columnHeightDp by remember {
                                mutableStateOf(0.dp)
                            }

                            var itemHeight by remember {
                                mutableStateOf(0.dp)
                            }
                            var topEmptyCount by remember {
                                mutableFloatStateOf(0f)
                            }

                            var bottomEmptyCount by remember {
                                mutableFloatStateOf(0f)
                            }
                            LaunchedEffect(columnHeightDp) {
                                itemHeight = columnHeightDp / itemsPerRulerHeight
                                topEmptyCount = ((columnHeightDp / 2f) / itemHeight)
                                bottomEmptyCount = topEmptyCount - 10
                            }

                            var minutesState by remember {
                                mutableIntStateOf(0)
                            }

                            var secondsState by remember {
                                mutableIntStateOf(0)
                            }

                            var isRunning by remember {
                                mutableStateOf(false)
                            }

                            LaunchedEffect(isRunning) {
                                var minutes = count
                                var seconds = 0

                                while (minutes >= 0 && isRunning) {
                                    minutesState = minutes
                                    secondsState = seconds
                                    delay(1000)
                                    if (seconds == 0) {
                                        minutes--
                                        seconds = 59
                                    } else {
                                        seconds--
                                    }
                                }
                            }

                            Column(modifier = Modifier.fillMaxSize()) {

                                Text(
                                    fontWeight = FontWeight.Medium,
                                    fontSize = 16.sp,
                                    text = "Countdown Timer",
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(16.dp),
                                    textAlign = TextAlign.Center,
                                    color = Color(0xFF73849C)
                                )

                                if (!isRunning) {
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                                        AnimatedCounter(
                                            count
                                        )
                                        Text(
                                            fontWeight = FontWeight.Medium,
                                            fontSize = fontSize,
                                            text = ":00",
                                            style = MaterialTheme.typography.h3,
                                            softWrap = false, color = Color(0xFF429CFF),
                                        )
                                    }
                                } else {
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                                        AnimatedCounter(
                                            minutesState
                                        )
                                        Text(
                                            fontWeight = FontWeight.Medium,
                                            fontSize = fontSize,
                                            text = ":",
                                            style = MaterialTheme.typography.h3,
                                            softWrap = false, color = Color(0xFF429CFF),
                                        )
                                        AnimatedCounter(
                                            secondsState
                                        )
                                    }
                                }
                                Column(Modifier.weight(1f)) {

                                    Box(
                                        Modifier
                                            .fillMaxSize()
                                            .onGloballyPositioned { coordinates ->
                                                // Set column height using the LayoutCoordinates
                                                columnHeightDp = with(localDensity) { coordinates.size.height.toDp() }
                                            }) {

                                        LazyColumn(state = scrollState, flingBehavior = flingBehavior) {

                                            items(topEmptyCount.toInt()) {
                                                Row(
                                                    verticalAlignment = Alignment.CenterVertically,
                                                    modifier = Modifier
                                                        .height(itemHeight)
                                                ) {

                                                    Spacer(modifier = Modifier.weight(1f))


                                                    Box(
                                                        Modifier
                                                            .background(Color(0xD273849C))
                                                            .width(20.dp)
                                                            .height(1.dp)

                                                    )

                                                    Spacer(modifier = Modifier.weight(1f))
                                                }
                                            }

                                            itemsIndexed(constructCounter()) { _, item ->
                                                Row(
                                                    verticalAlignment = Alignment.CenterVertically,
                                                    modifier = Modifier
                                                        .height(itemHeight)

                                                ) {
                                                    if (item % 10 == 0 && (item in 10..total)) {
                                                        Text(
                                                            fontWeight = FontWeight.Medium,
                                                            text = item.toString(),
                                                            fontSize = 18.sp,
                                                            modifier = Modifier
                                                                .weight(1f)
                                                                .padding(horizontal = 16.dp),
                                                            textAlign = TextAlign.Start,
                                                            color = if (item == count) Color(0xFF429CFF) else Color(
                                                                0xFF73849C
                                                            ),
                                                        )

                                                    } else {
                                                        Spacer(modifier = Modifier.weight(1f))
                                                    }

                                                    Box(
                                                        Modifier
                                                            .background(Color(0xFF73849C))
                                                            .width(20.dp)
                                                            .height(1.dp)

                                                    )

                                                    Spacer(modifier = Modifier.weight(1f))
                                                }

                                            }


                                            items(bottomEmptyCount.toInt()) {
                                                Row(
                                                    verticalAlignment = Alignment.CenterVertically,
                                                    modifier = Modifier
                                                        .height(itemHeight)

                                                ) {
                                                    Spacer(modifier = Modifier.weight(1f)
                                                    )
                                                    Box(
                                                        Modifier
                                                            .background(Color(0xFF73849C))
                                                            .width(20.dp)
                                                            .height(1.dp)

                                                    )

                                                    Spacer(modifier = Modifier.weight(1f))
                                                }
                                            }
                                        }

                                        //top shadow
                                        Box(
                                            Modifier
                                                .fillMaxWidth()
                                                .height(columnHeightDp.value.roundToInt().dp / 2)
                                                .background(
                                                    brush = Brush.verticalGradient(
                                                        listOf(Color(0xFFEFF7FF), Color.Transparent)
                                                    )
                                                )
                                                .align(Alignment.TopCenter)
                                        )

                                        //bottom shadow
                                        Box(
                                            Modifier
                                                .fillMaxWidth()
                                                .height(columnHeightDp.value.roundToInt().dp / 2)
                                                .background(
                                                    brush = Brush.verticalGradient(
                                                        listOf(Color.Transparent, Color(0xFFEFF7FF))
                                                    )
                                                )
                                                .align(Alignment.BottomCenter)
                                        )

                                        //blue indicator
                                        Box(
                                            Modifier
                                                .offset(y = 1.dp)
                                                .align(Alignment.Center)
                                                .background(Color(0xFF429CFF))
                                                .width(50.dp)
                                                .height((2).dp)

                                        )
                                    }
                                }

                                //Start, Pause and Stop
                                AnimatedVisibility(visible = isRunning) {
                                    Row(Modifier.fillMaxWidth()) {
                                        Text(
                                            text = "Pause",
                                            modifier = Modifier
                                                .clickable {
                                                    isRunning = false
                                                }
                                                .background(Color(0xFF73849C))
                                                .weight(1f)
                                                .padding(16.dp),
                                            textAlign = TextAlign.Center,
                                            color = Color.White
                                        )

                                        Text(
                                            text = "Delete",
                                            modifier = Modifier
                                                .clickable {
                                                    isRunning = false
                                                }
                                                .background(Color(0xFFFF7373))
                                                .weight(1f)
                                                .padding(16.dp),
                                            textAlign = TextAlign.Center,
                                            color = Color.White
                                        )
                                    }
                                }
                                AnimatedVisibility(visible = !isRunning) {
                                    Text(
                                        text = "Start",
                                        modifier = Modifier
                                            .clickable {
                                                isRunning = true
                                            }
                                            .background(Color(0xFF429CFF))
                                            .fillMaxWidth()
                                            .padding(16.dp),
                                        textAlign = TextAlign.Center, color = Color.White
                                    )
                                }
                            }
                        }

                        /**
                         * https://github.com/philipplackner/AnimatedCounterCompose/blob/master/app/src/main/java/com/plcoding/animatedcountercompose/AnimatedCounter.kt
                         */
                        @Composable
                        fun AnimatedCounter(
                            count: Int,
                            modifier: Modifier = Modifier,
                            style: TextStyle = MaterialTheme.typography.h1
                        ) {
                            var oldCount by remember {
                                mutableIntStateOf(count)
                            }
                            SideEffect {
                                oldCount = count
                            }
                            Row(modifier = modifier, horizontalArrangement = Arrangement.Center) {
                                val countString = count.toString()
                                val oldCountString = oldCount.toString()
                                for (i in countString.indices) {
                                    val oldChar = oldCountString.getOrNull(i)
                                    val newChar = countString[i]
                                    val char = if (oldChar == newChar) {
                                        oldCountString[i]
                                    } else {
                                        countString[i]
                                    }
                                    AnimatedContent(
                                        targetState = char,
                                        transitionSpec = {
                                            slideInVertically { it } togetherWith slideOutVertically { -it }
                                        }, label = ""
                                    ) { char ->

                                        Text(
                                            fontWeight = FontWeight.Medium,
                                            fontSize = fontSize,
                                            text = char.toString(),
                                            style = style,
                                            softWrap = false, color = Color(0xFF429CFF),
                                        )
                                    }
                                }
                            }
                        }

                        private fun constructCounter(): List<Int> {
                            val list = mutableListOf<Int>()
                            repeat(total) {
                                list.add(total - it)
                            }

                            return list
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Desktop File Choose

                        internal fun openFileBrowser(
                                            window: ComposeWindow
                                        ): File? {
                                            val chooser = FileDialog(window).apply {
                                                title = "Select file"
                                                mode = FileDialog.LOAD
                                                isMultipleMode = false
                                                filenameFilter = FilenameFilter { _, name ->
                                                    val extensionAllowList = SupportedFiles.entries.map { it.extension }

                                                    extensionAllowList.any { extension ->
                                                        name.endsWith(extension, ignoreCase = true)
                                                    }
                                                }
                                            }

                                            chooser.isVisible = true

                                            return chooser.files.firstOrNull()
                                        }
                    """.trimIndent()
            )

            //https://github.com/delacrixmorgan/twilight-kmp
            KotlinCode(
                code = """
                    @OptIn(ExperimentalFoundationApi::class)
                    @Composable
                    fun VerticalScrollWheel(
                        modifier: Modifier,
                        listState: LazyListState,
                        onScrolled: (Int, Boolean) -> Unit,
                        buffer: Int = 2,
                    ) {
                        val items = remember { mutableStateListOf<Int>() }

                        LazyColumn(
                            modifier = modifier,
                            verticalArrangement = Arrangement.spacedBy(24.dp),
                            horizontalAlignment = Alignment.End,
                            state = listState,
                            flingBehavior = rememberSnapFlingBehavior(lazyListState = listState)
                        ) {
                            items(items, key = { it }) { index ->
                                val width = if (index % 3 == 0) 32.dp else 16.dp
                                HorizontalDivider(
                                    Modifier.width(width),
                                    color = MaterialTheme.colorScheme.onSurface,
                                )
                            }
                        }

                        val listStateListener: State<InfiniteListContent> = remember {
                            derivedStateOf {
                                val layoutInfo = listState.layoutInfo
                                val totalItemsNumber = layoutInfo.totalItemsCount
                                val lastVisibleItemIndex = (layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0) + 1

                                InfiniteListContent(
                                    firstVisibleIndex = listState.firstVisibleItemIndex,
                                    isFirstItemVisible = listState.firstVisibleItemIndex == 0,
                                    reachedBottom = lastVisibleItemIndex > (totalItemsNumber - buffer)
                                )
                            }
                        }

                        LaunchedEffect(listState) {
                            snapshotFlow { listStateListener.value }
                                .distinctUntilChanged()
                                .collect { content ->
                                    onScrolled(content.firstVisibleIndex, content.isFirstItemVisible)
                                    if (content.reachedBottom) items.addAll((items.size..items.size + SCROLL_WHEEL_PAGE_SIZE))
                                }
                        }
                    }

                    private data class InfiniteListContent(
                        val firstVisibleIndex: Int,
                        val isFirstItemVisible: Boolean,
                        val reachedBottom: Boolean,
                    )
                    """.trimIndent()
            )

            //LazyColumn scroll transparent list
            KotlinCode(
                code = """
                    object DefaultColors {
                        @Composable
                        fun bottomScaffoldBrush() = Brush.verticalGradient(
                            colors = listOf(
                                Color.Transparent,
                                MaterialTheme.colorScheme.surfaceDim,
                            ),
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Timeline Divider Length Size Of Text

                    @Composable
                    fun TimelineEventCard(
                        year: Int, text: String,
                        darkMode: Boolean = false,
                        modifier: Modifier = Modifier
                    ) {
                        val backgroundColor = if (darkMode) greyStrong else white
                        val contentColor = if (darkMode) white else black

                        Card(
                            modifier = modifier,
                            colors = CardDefaults.cardColors(backgroundColor, contentColor),
                            elevation = CardDefaults.cardElevation(0.dp),
                        ) {
                            Row(
                                modifier = Modifier.height(IntrinsicSize.Min)
                                    .padding(10.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                // Date
                                Column(
                                    modifier = Modifier
                                        .width(75.dp)
                                        .fillMaxHeight(),
                                    verticalArrangement = Arrangement.Top
                                ) {
                                    Text(
                                        text = "${year.absoluteValue}",
                                        style = MaterialTheme.typography.titleMedium.copy(
                                            fontWeight = FontWeight.W500,
                                            lineHeight = 20.sp,
                                            fontFamily = TenorSans
                                        )
                                    )
                                    Text(
                                        text = getYrSuffix(year),
                                        style = MaterialTheme.typography.bodySmall.copy(
                                            fontFamily = TenorSans,
                                            fontWeight = FontWeight.W500
                                        )
                                    )
                                }

                                // Divider
                                Divider(
                                    modifier = Modifier
                                        .padding(horizontal = 8.dp)
                                        .width(1.dp)
                                        .fillMaxHeight(),
                                    color = contentColor
                                )

                                // Text content
                                Text(
                                    text = text,
                                    style = MaterialTheme.typography.bodyMedium.copy(
                                        lineHeight = 20.sp
                                    )
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Error Animation Shaker

                    const val privacy_policy = "Privacy Policy"
                    const val terms_conditions = "Terms & Conditions"
                    const val txt_accept_privacy_policy_and_terms = "I have read and agree to the "
                    const val and = "and"

                    @Composable
                    fun AcceptPrivacyPolicyTermsConditionsText(
                        modifier: Modifier,
                        isChecked: Boolean,
                        onClickPrivacyPolicy: () -> Unit,
                        onClickTermsService: () -> Unit,
                        onCheckPrivacyPolicy: () -> Unit,
                    ) {
                        Row(modifier = modifier, verticalAlignment = Alignment.CenterVertically) {
                            Checkbox(
                                checked = isChecked,
                                onCheckedChange = { onCheckPrivacyPolicy() },
                                colors = CheckboxDefaults.colors(
                                    checkedColor = MaterialTheme.colorScheme.secondary,
                                    checkmarkColor = MaterialTheme.colorScheme.onSecondary
                                )
                            )
                            Spacer(modifier = Modifier.width(4.dp))
                            val privacyPolicy = Strings.privacy_policy
                            val termsConditions = Strings.terms_conditions
                            val annotatedString = buildAnnotatedString {
                                append(Strings.txt_accept_privacy_policy_and_terms)
                                withStyle(
                                    style = SpanStyle(
                                        fontWeight = FontWeight.SemiBold,
                                        color = MaterialTheme.colorScheme.secondary,
                                        textDecoration = TextDecoration.Underline
                                    ),
                                ) {
                                    pushStringAnnotation(
                                        tag = privacyPolicy,
                                        annotation = privacyPolicy
                                    )
                                    append(privacyPolicy)
                                }
                                append(" and ")
                                withStyle(
                                    style = SpanStyle(
                                        fontWeight = FontWeight.SemiBold,
                                        color = MaterialTheme.colorScheme.secondary,
                                        textDecoration = TextDecoration.Underline
                                    ),
                                ) {
                                    pushStringAnnotation(
                                        tag = termsConditions,
                                        annotation = termsConditions
                                    )// AnnotatedString.Builder
                                    append(termsConditions)
                                }

                            }
                            ClickableText(
                                text = annotatedString,
                                style = MaterialTheme.typography.bodySmall.copy(color = Black_28),

                                ) { offset ->
                                annotatedString.getStringAnnotations(offset, offset)
                                    .firstOrNull()?.let { span ->
                                        when (span.item) {
                                            privacyPolicy -> onClickPrivacyPolicy()
                                            termsConditions -> onClickTermsService()
                                        }
                                    }
                            }
                        }
                    }

                    val offsetX = remember { Animatable(0f) }
                    
                    AcceptPrivacyPolicyTermsConditionsText(
                                   modifier = Modifier
                                       .wrapContentWidth()
                                       .offset(offsetX.value.dp, 0.dp)
                                       .padding(bottom = 10.dp),
                                   isChecked = uiState.isPrivacyPolicyChecked,
                                   onClickPrivacyPolicy = onClickPrivacyPolicy,
                                   onClickTermsService = onClickTermsService,
                                   onCheckPrivacyPolicy = onCheckPrivacyPolicy,
                               )

                    private fun CoroutineScope.shakePrivacyPolicyText(offset: Animatable<Float, AnimationVector1D>) {
                        val shakeKeyframes: AnimationSpec<Float> = keyframes {
                            durationMillis = 800
                            val easing = FastOutLinearInEasing
                            // generate 8 keyframes
                            for (i in 1..8) {
                                val x = when (i % 3) {
                                    0 -> 4f
                                    1 -> -4f
                                    else -> 0f
                                }
                                x at durationMillis / 10 * i with easing
                            }
                        }

                        this.launch {
                            offset.animateTo(
                                targetValue = 0f,
                                animationSpec = shakeKeyframes,
                            )
                        }
                    }

                    //Button click add
                    coroutineScope.shakePrivacyPolicyText(offsetX)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Terms and Conditions

                    @Composable
                    fun TermsAndRulesText(
                        fullText: String,
                        textColor: Color,
                        underlinedText: List<String>,
                        underlinedTextFontWeight: FontWeight = FontWeight.Medium,
                        underlinedTextDecoration: TextDecoration = TextDecoration.Underline,
                        fontSize: TextUnit,
                        textAlign: TextAlign,
                    ){
                        Text(
                            text = buildAnnotatedString {
                                append(fullText)
                                underlinedText.forEach { text ->
                                    val startIndex = fullText.indexOf(text)
                                    val endIndex = startIndex + text.length
                                    addStyle(
                                        style = SpanStyle(
                                            fontSize = fontSize,
                                            fontWeight = underlinedTextFontWeight,
                                            textDecoration = underlinedTextDecoration
                                        ),
                                        start = startIndex,
                                        end = endIndex
                                    )
                                    addStyle(
                                        style = SpanStyle(
                                            fontSize = fontSize,
                                            fontFamily = font_standard,
                                            color = textColor
                                        ),
                                        start = 0,
                                        end = fullText.length
                                    )
                                }
                            },
                            modifier = Modifier.padding(
                                horizontal = LocalSpacing.current.small,
                                vertical = LocalSpacing.current.medium
                            ),
                            textAlign = textAlign
                        )
                    }

                    TermsAndRulesText(
                        fullText = stringResource(id = R.string.terms_and_rules_full),
                        underlinedText = listOf(
                            stringResource(id = R.string.terms_and_rules),
                            stringResource(id = R.string.privacy_and_rules)
                        ),
                        textColor = MaterialTheme.colors.semiDarkText,
                        fontSize = 10.sp,
                        textAlign = TextAlign.Center
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Two Text Divider Line Size Of Text

                    @Composable
                    fun TwoTexts(modifier: Modifier = Modifier, text1: String, text2: String) {
                        Row(modifier = modifier.height(IntrinsicSize.Min)) {
                            Text(
                                modifier = Modifier
                                    .weight(1f)
                                    .padding(start = 4.dp)
                                    .wrapContentWidth(Alignment.Start),
                                text = text1
                            )
                            Divider(
                                color = Color.Black,
                                modifier = Modifier.fillMaxHeight().width(1.dp)
                            )
                            Text(
                                modifier = Modifier
                                    .weight(1f)
                                    .padding(end = 4.dp)
                                    .wrapContentWidth(Alignment.End),

                                text = text2
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Search Text Highlight

                    @Composable
                    internal fun buildHighlightedString(
                        text: String,
                        highlights: List<IntRange>,
                    ): AnnotatedString = if (highlights.isEmpty()) {
                        AnnotatedString(text)
                    } else {
                        buildAnnotatedString {
                            append(text)
                            highlights.forEach { range ->
                                // Ignore invalid ranges
                                if (!range.isEmpty()) {
                                    addStyle(
                                        style = SpanStyle(
                                            color = KotlinConfTheme.colors.primaryTextInverted,
                                            background = KotlinConfTheme.colors.primaryBackground,
                                        ),
                                        start = range.first,
                                        end = range.last + 1,
                                    )
                                }
                            }
                        }
                    }
                """.trimIndent()
            )

            //Validation
            KotlinCode(
                code = """
                    enum class AddActivityError {
                        Unknown,
                        EmptyDescription
                    }

                    fun AddActivityError.localizedMessage(context: Context): String {
                        return when (this) {
                            AddActivityError.Unknown -> context.getString(R.string.add_activity_error)
                            AddActivityError.EmptyDescription -> context.getString(R.string.add_activity_error_empty_description)
                        }
                    }

                    @Serializable
                    data class ActivityModel(
                        @SerialName("id")
                        val id: Long = AUTOINCREMENT_ID,

                        @SerialName("description")
                        val description: String,

                        @SerialName("images")
                        val images: List<String>,

                        @SerialName("date")
                        val date: Timestamp,

                        @SerialName("metadata")
                        val metadata: Metadata = Metadata()
                    )

                    internal class ActivityValidator : ModelValidator<ActivityModel, AddActivityError?> {
                        override fun checkErrors(model: ActivityModel): AddActivityError? {
                            return if (model.description.isBlank()) {
                                AddActivityError.EmptyDescription
                            } else {
                                null
                            }
                        }
                    }

                    interface ModelValidator<M, E> {
                        fun checkErrors(model: M): E
                    }
                    """.trimIndent()
            )

            //https://github.com/rookiecoder910/pixelpholio
            KotlinCode(
                code = """
                    //Button Bottom to Top and Shimmer Animation
                    
                    @Composable
                    private fun MenuButton(
                        text: String,
                        onClick: () -> Unit
                    ) {
                        val interactionSource = remember { MutableInteractionSource() }
                        val isPressed by interactionSource.collectIsPressedAsState()

                        // --- Animations for the button ---
                        val scale = animateFloatAsState(
                            targetValue = if (isPressed) 0.95f else 1f,
                            animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy),
                            label = "scale"
                        )

                        val infiniteTransition = rememberInfiniteTransition(label = "glow")
                        val animatedBorderColor by infiniteTransition.animateColor(
                            initialValue = Color.White.copy(alpha = 0.5f),
                            targetValue = Color.Cyan.copy(alpha = 0.8f),
                            animationSpec = infiniteRepeatable(
                                animation = tween(durationMillis = 1500, easing = FastOutSlowInEasing),
                                repeatMode = RepeatMode.Reverse
                            ),
                            label = "glowColor"
                        )
                        val shimmerTranslate by infiniteTransition.animateFloat(
                            initialValue = -1f,
                            targetValue = 2f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(durationMillis = 2000, delayMillis = 500, easing = LinearEasing),
                                repeatMode = RepeatMode.Restart
                            ), label = "shimmer"
                        )

                        // --- Button Layout ---
                        Button(
                            onClick = onClick,
                            modifier = Modifier
                                .fillMaxWidth(0.6f)
                                .height(60.dp)
                                .scale(scale.value), // Apply press-down scale effect
                            interactionSource = interactionSource,
                            shape = RoundedCornerShape(16.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent),
                            contentPadding = PaddingValues(0.dp),
                            elevation = ButtonDefaults.buttonElevation(defaultElevation = 8.dp, pressedElevation = 4.dp),
                            border = BorderStroke(2.dp, animatedBorderColor) // Glowing border
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .clip(RoundedCornerShape(16.dp)),
                                contentAlignment = Alignment.Center
                            ) {
                                // 1. Brick Texture Background
                                Image(
                                    painter = painterResource(R.drawable.brick_texture),
                                    contentDescription = null,
                                    contentScale = ContentScale.Crop,
                                    modifier = Modifier.matchParentSize()
                                )

                                // ✅ 2. CORRECTED Shimmer Effect Overlay
                                // The Brush is now created inside the drawBehind modifier,
                                // which provides the correct `size`.
                                Box(
                                    modifier = Modifier
                                        .matchParentSize()
                                        .drawBehind {
                                            val brush = Brush.horizontalGradient(
                                                colors = listOf(
                                                    Color.White.copy(alpha = 0.0f),
                                                    Color.White.copy(alpha = 0.4f),
                                                    Color.White.copy(alpha = 0.0f)
                                                ),
                                                startX = (shimmerTranslate * size.width) - size.width,
                                                endX = (shimmerTranslate * size.width)
                                            )
                                            drawRect(brush = brush)
                                        }
                                )

                                // 3. Button Text
                                Text(
                                    text = text,
                                    fontSize = 20.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color.White,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Rome-Squad/CineVerse-App
            KotlinCode(
                code = """
                    //Scroll Image Shape to Circle 
                    
                    @Composable
                    fun CastDetailsContent(
                        state: CastDetailsUiState,
                        interaction: CastDetailsInteractionListener,
                        modifier: Modifier = Modifier,
                    ) {
                        val padding16 = 16.dp
                        val bottomSpacingHeight = 90
                        val innerColumnSpacing = 18.dp
                        val scrollState = rememberLazyListState()

                        var imageWidth by rememberSaveable { mutableIntStateOf(64) }
                        var imageHeight by rememberSaveable { mutableIntStateOf(80) }
                        var consumedX by rememberSaveable { mutableIntStateOf(0) }
                        var consumedY by rememberSaveable { mutableIntStateOf(0) }
                        var animationProgress by rememberSaveable { mutableFloatStateOf(0f) }

                        val nestedScrollConnection = remember {
                            object : NestedScrollConnection {
                                override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                                    val delta = available.y.toInt()
                                    if (
                                        (scrollState.firstVisibleItemIndex != 0 || scrollState.firstVisibleItemScrollOffset != 0)
                                        && delta > 0
                                    ) {
                                        return Offset(consumedX.toFloat(), consumedY.toFloat())
                                    }
                                    val newImageWidth = imageWidth + delta
                                    val previousImageWidth = imageWidth
                                    imageWidth = newImageWidth.coerceIn(40, 64)
                                    val newImageHeight = imageHeight + delta
                                    val previousImageHeight = imageHeight
                                    imageHeight = newImageHeight.coerceIn(40, 80)
                                    animationProgress = 1f - (imageHeight - 40) / 40f
                                    consumedX = imageWidth - previousImageWidth
                                    consumedY = imageHeight - previousImageHeight
                                    return Offset(consumedX.toFloat(), consumedY.toFloat())
                                }
                            }
                        }

                        Box(
                            modifier = modifier
                                .fillMaxSize()
                                .background(Theme.color.background.screen)
                                .systemBarsPadding()
                                .nestedScroll(nestedScrollConnection)
                        ) {
                            LazyColumn(
                                state = scrollState,
                                verticalArrangement = Arrangement.spacedBy(innerColumnSpacing)
                            ) {
                                stickyHeader {
                                    Box(Modifier.background(Theme.color.background.screen)) {
                                        //AppBar

                                        MainDetailsAnimatedContent(
                                            actorImageUrl = state.actorImageUrl,
                                            actorName = state.actorName,
                                            actorBirthday = state.actorBirth,
                                            actorPlaceOfBirth = state.actorPlace,
                                            socialMediaUiList = state.socialMediaUiList,
                                            onSocialMediaLinkClick = interaction::onSocialMediaLinkClick,
                                            animationProgress = animationProgress
                                        )

                                        if (animationProgress == 1f) {
                                            Box(
                                                modifier = Modifier
                                                    .height(1.dp)
                                                    .fillMaxWidth()
                                                    .background(Theme.color.stroke.primary)
                                                    .align(Alignment.BottomStart)
                                            )
                                        }
                                    }
                                }

                                item {

                                }
                            }
                        }
                    }
                    
                    @Composable
                    private fun MainDetailsAnimatedContent(
                        actorName: String,
                        actorBirthday: String,
                        actorPlaceOfBirth: String,
                        actorImageUrl: String?,
                        socialMediaUiList: List<SocialMediaUi>,
                        onSocialMediaLinkClick: (String) -> Unit,
                        animationProgress: Float
                    ) {
                        MainDetails(
                            actorName = actorName,
                            actorBirthday = actorBirthday,
                            actorPlaceOfBirth = actorPlaceOfBirth,
                            socialMediaUiList = socialMediaUiList,
                            onLinkClick = onSocialMediaLinkClick,
                            actorImageUrl = actorImageUrl,
                            animationProgress = animationProgress,
                            modifier = Modifier
                                .padding(horizontal = 16.dp)
                                .padding(top = 72.dp - 72.dp * animationProgress)
                        )
                    }
                    
                    @Composable
                    fun MainDetails(
                        actorName: String,
                        actorBirthday: String,
                        actorPlaceOfBirth: String,
                        socialMediaUiList: List<SocialMediaUi>,
                        onLinkClick: (String) -> Unit,
                        actorImageUrl: String?,
                        modifier: Modifier = Modifier,
                        animationProgress: Float = 0f
                    ) {
                        val radiusXl = Theme.radius.xl
                        val radiusS = Theme.radius.s
                        val shapeImage = remember(animationProgress) {
                            RoundedCornerShape(
                                topStart = radiusXl + (4.dp * animationProgress),
                                topEnd = radiusS + (12.dp * animationProgress),
                                bottomStart = radiusS + (12.dp * animationProgress),
                                bottomEnd = radiusXl + (4.dp * animationProgress)
                            )
                        }
                        val singleSpace = remember { " " }

                        Column(
                            modifier = modifier
                                .fillMaxWidth()
                                .wrapContentHeight()
                                .clip(shape = RoundedCornerShape(Theme.radius.xxl))
                                .background(Theme.color.background.card.copy(1f - animationProgress))
                                .padding(vertical = 16.dp * (1 - animationProgress)),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .padding(start = 16.dp)
                                    .animateContentSize()
                            ) {
                                actorImageUrl?.let {
                                    SafeIslamicImage(
                                        imageUrl = it,
                                        contentDescription = actorName,
                                        contentScale = ContentScale.Crop,
                                        hasSensitiveText = false,
                                        modifier = Modifier
                                            .padding(
                                                top = 8.dp * animationProgress,
                                                bottom = 8.dp * animationProgress,
                                                end = 12.dp - 4.dp * animationProgress,
                                                start = 16.dp * animationProgress
                                            )
                                            .size(
                                                height = 80.dp - (40.dp * animationProgress),
                                                width = 64.dp - (24.dp * animationProgress)
                                            )
                                            .clip(shape = shapeImage),
                                        placeHolderTint = Theme.color.brand.secondary,
                                        placeholderModifier = Modifier
                                            .fillMaxSize()
                                            .border(
                                                width = 1.dp,
                                                color = Theme.color.stroke.primary,
                                                shape = shapeImage
                                            )
                                    )
                                }

                                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                    Text(
                                        style = Theme.textStyle.title.md,
                                        color = Theme.color.shade.primary,
                                        text = actorName
                                    )

                                    AnimatedVisibility(
                                        visible = actorBirthday.isNotBlank() && animationProgress == 0f,
                                        enter = fadeIn(),
                                        exit = fadeOut()
                                    ) {
                                        IconTextBox(
                                            icon = painterResource(Theme.icons.outline.cake),
                                            contentDescription = stringResource(R.string.birthday_cake_icon),
                                            text = stringResource(R.string.actor_birthday) + singleSpace + actorBirthday,
                                        )
                                    }

                                    AnimatedVisibility(
                                        visible = actorPlaceOfBirth.isNotBlank() && animationProgress == 0f,
                                        enter = fadeIn(),
                                        exit = fadeOut()
                                    ) {
                                        IconTextBox(
                                            icon = painterResource(Theme.icons.outline.location),
                                            contentDescription = stringResource(R.string.location_icon),
                                            text = stringResource(R.string.place_of_birth) + singleSpace + actorPlaceOfBirth,
                                        )
                                    }
                                }
                            }

                            AnimatedVisibility(
                                visible = socialMediaUiList.isNotEmpty() && animationProgress == 0f,
                                enter = fadeIn(),
                                exit = fadeOut()
                            ) {
                                LazyRow(
                                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                                    contentPadding = PaddingValues(horizontal = 16.dp)
                                ) {
                                    items(socialMediaUiList) {
                                        SocialMediaComponent(
                                            image = it.platform.getIcon(),
                                            name = stringResource(it.name),
                                            contentDescription = stringResource(it.contentDescription),
                                            onClick = { onLinkClick(it.url) }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    fun SocialMediaComponent(
                        modifier: Modifier = Modifier,
                        image: Painter,
                        name: String,
                        contentDescription: String,
                        onClick: () -> Unit
                    ) {
                        Row(
                            modifier = modifier
                                .clip(shape = RoundedCornerShape(Theme.radius.full))
                                .background(Theme.color.shade.quinary)
                                .clickable(onClick = onClick)
                                .wrapContentSize()
                                .padding(
                                    start = 10.dp,
                                    end = 12.dp,
                                    top = 8.dp,
                                    bottom = 8.dp
                                ),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Image(
                                painter = image,
                                contentDescription = contentDescription
                            )
                            Text(
                                text = name,
                                style = Theme.textStyle.label.md.medium,
                                color = Theme.color.shade.primary
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/TheChance101/MENA-mobile.git
            //https://github.com/jesuslcorominas/teamflowmanager.git
            KotlinCode(
                code = """
                    @Composable
                    fun WheelDatePicker(
                        selectedDayIndex: Int,
                        selectedMonthIndex: Int,
                        selectedYearIndex: Int,
                        days: List<String>,
                        months: List<String>,
                        years: List<String>,
                        modifier: Modifier = Modifier,
                        onDateChange: (dayIndex: Int, monthIndex: Int, yearIndex: Int) -> Unit,
                    ) {
                        val dayPagerState = rememberPagerState(
                            initialPage = selectedDayIndex.coerceIn(0, days.size - 1),
                            pageCount = { days.size }
                        )

                        val monthPagerState = rememberPagerState(
                            initialPage = selectedMonthIndex.coerceIn(0, months.size - 1),
                            pageCount = { months.size }
                        )

                        val yearPagerState = rememberPagerState(
                            initialPage = selectedYearIndex.coerceIn(0, years.size - 1),
                            pageCount = { years.size }
                        )

                        var rowWidth by remember { mutableStateOf(0) }

                        LaunchedEffect(selectedDayIndex, selectedMonthIndex, selectedYearIndex) {
                            val targetDayPage = selectedDayIndex.coerceIn(0, days.size - 1)
                            val targetMonthPage = selectedMonthIndex.coerceIn(0, months.size - 1)
                            val targetYearPage = selectedYearIndex.coerceIn(0, years.size - 1)

                            coroutineScope {
                                if (yearPagerState.currentPage != targetYearPage) {
                                    launch { yearPagerState.scrollToPage(targetYearPage) }
                                }
                                if (monthPagerState.currentPage != targetMonthPage) {
                                    launch { monthPagerState.scrollToPage(targetMonthPage) }
                                }
                                if (dayPagerState.currentPage != targetDayPage) {
                                    launch { dayPagerState.scrollToPage(targetDayPage) }
                                }
                            }
                        }

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .height(244.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            ChoiceIndicator(
                                modifier = Modifier
                                    .width(with(LocalDensity.current) { rowWidth.toDp() + 20.dp })
                                    .align(Alignment.Center)
                            )

                            Row(
                                modifier = Modifier
                                    .onSizeChanged { rowWidth = it.width }
                                    .align(Alignment.Center),
                                horizontalArrangement = Arrangement.Center,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                VerticalPicker(
                                    state = monthPagerState,
                                    options = months,
                                    modifier = Modifier.padding(end = Theme.spacing._32),
                                )

                                VerticalPicker(
                                    state = dayPagerState,
                                    options = days,
                                    modifier = Modifier.padding(end = 52.dp)
                                )

                                VerticalPicker(
                                    state = yearPagerState,
                                    options = years,
                                    isYearPicker = true
                                )
                            }
                        }

                        LaunchedEffect(dayPagerState, monthPagerState, yearPagerState) {
                            snapshotFlow {
                                Triple(
                                    dayPagerState.currentPage,
                                    monthPagerState.currentPage,
                                    yearPagerState.currentPage
                                )
                            }
                                .distinctUntilChanged()
                                .collect { (dayIndex, monthIndex, yearIndex) ->
                                    val userIsScrolling = dayPagerState.isScrollInProgress ||
                                            monthPagerState.isScrollInProgress ||
                                            yearPagerState.isScrollInProgress

                                    if (userIsScrolling) {
                                        onDateChange(dayIndex, monthIndex, yearIndex)
                                    }
                                }
                        }
                    }

                    @Composable
                    private fun ChoiceIndicator(modifier: Modifier = Modifier) {
                        Box(
                            modifier = modifier
                                .clip(SquircleShape(Theme.radius.md))
                                .height(28.dp)
                                .background(Theme.colorScheme.background.surfaceHigh)
                        )
                    }

                    @Composable
                    private fun VerticalPicker(
                        state: PagerState,
                        options: List<String>,
                        isYearPicker: Boolean = false,
                        modifier: Modifier = Modifier
                    ) {
                        val textMeasurer = rememberTextMeasurer()
                        val baseStyle = Theme.typography.body.large

                        val maxWidth = remember(options) {
                            options.maxOf { option ->
                                textMeasurer.measure(
                                    text = option,
                                    style = baseStyle
                                ).size.width
                            }
                        }

                        Box(
                            modifier = modifier
                                .width(with(LocalDensity.current) { maxWidth.toDp() })
                                .fillMaxHeight()
                        ) {
                            VerticalPager(
                                state = state,
                                modifier = Modifier.fillMaxHeight().wrapContentWidth(),
                                beyondViewportPageCount = 5,
                                horizontalAlignment = Alignment.CenterHorizontally,
                                pageSpacing = 4.dp,
                                contentPadding = PaddingValues(vertical = 108.dp),
                                pageSize = PageSize.Fixed(28.dp),
                                flingBehavior = PagerDefaults.flingBehavior(
                                    state = state,
                                    pagerSnapDistance = PagerSnapDistance.atMost(state.pageCount),
                                    snapPositionalThreshold = 0f,
                                    snapAnimationSpec = tween(durationMillis = 200, easing = LinearOutSlowInEasing)
                                )
                            ) { page ->
                                val distance = (page - state.currentPage).absoluteValue

                                val alpha = when (distance) {
                                    0 -> 1f
                                    1 -> 0.9f
                                    2 -> 0.5f
                                    else -> 0.2f
                                }

                                Box(
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .wrapContentWidth()
                                        .graphicsLayer {
                                            this.alpha = alpha
                                        },
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = options[page],
                                        style = Theme.typography.label.medium,
                                        modifier = Modifier.fillMaxWidth(),
                                        textAlign = if (isYearPicker) TextAlign.End else TextAlign.Start,
                                        color = Theme.colorScheme.shadePrimary
                                    )
                                }
                            }
                        }
                    }

                    @Preview(showBackground = true,backgroundColor = 0xFFF2F4F7)

                    @Composable
                    private fun WheelDatePickerPreview() {
                        MenaTheme {
                            WheelDatePicker(
                                selectedDayIndex = 14,
                                selectedMonthIndex = 5,
                                selectedYearIndex = 104,
                                days = (1..31).map { it.toString().padStart(2, '0') },
                                months = listOf(
                                    "January", "February", "March", "April", "May", "June",
                                    "July", "August", "September", "October", "November", "December"
                                ),
                                years = (1920..2024).map { it.toString() },
                                onDateChange = { _, _, _ -> },
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Pulse Indicator
                    
                    @Composable
                    private fun PulseIndicator(
                        @DrawableRes icon: Int,
                        modifier: Modifier = Modifier
                    ) {
                        val periodMs = 3600L
                        val offsetsMs = longArrayOf(0L, 1200L, 2400L)

                        val startNs = remember { System.nanoTime() }
                        var frameTimeNs by remember { mutableLongStateOf(startNs) }

                        LaunchedEffect(Unit) {
                            while (true) {
                                withFrameNanos { now -> frameTimeNs = now }
                            }
                        }

                        fun phase(offsetMs: Long): Float {
                            val elapsedMs = (frameTimeNs - startNs) / 1_000_000L + offsetMs
                            return ((elapsedMs % periodMs).toFloat() / periodMs.toFloat())
                        }

                        Box(modifier.size(80.dp), contentAlignment = Alignment.Center) {
                            @Composable
                            fun Ring(p: Float) = Box(
                                Modifier
                                    .matchParentSize()
                                    .graphicsLayer {
                                        scaleX = 1f + 0.8f * p
                                        scaleY = 1f + 0.8f * p
                                        alpha = 1f - p
                                    }
                                    .border(1.5.dp, Color.White.copy(alpha = 0.9f), CircleShape)
                            )

                            Ring(phase(offsetsMs[0]))
                            Ring(phase(offsetsMs[1]))
                            Ring(phase(offsetsMs[2]))

                            Box(
                                Modifier
                                    .size(80.dp)
                                    .background(Color.White, CircleShape),
                                contentAlignment = Alignment.Center
                            ) {
                                Image(
                                    painter = painterResource(icon),
                                    contentDescription = null,
                                    modifier = Modifier.size(32.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/evirgenoguz/Flights.git
            KotlinCode(
                code = """
                      //Google Map Marker Drawable Icon
                      
                      val planeIcon = createPlaneIconFromDrawable(
                            context = LocalContext.current,
                            onGround = false
                      )
                    
                     fun createPlaneIconFromDrawable(context: Context, onGround: Boolean): BitmapDescriptor {
                        val drawable = ContextCompat.getDrawable(context, R.drawable.ic_plane)

                        val color = if (onGround) Color.Red else Color.Blue
                        drawable?.setTint(color.toArgb())

                        val bitmap = createBitmap(
                            width = drawable?.intrinsicWidth ?: 100,
                            height = drawable?.intrinsicHeight ?: 100
                        )

                        val canvas = Canvas(bitmap)
                        drawable?.setBounds(0, 0, canvas.width, canvas.height)
                        drawable?.draw(canvas)

                        return BitmapDescriptorFactory.fromBitmap(bitmap)
                    }
                    """.trimIndent()
            )
        }
    }
}

//Chat GPT
//upgrade project to use agp 9

//https://medium.com/@sokainabch2021/from-figma-to-jetpack-compose-creating-complex-shapes-with-shape-composer-451c6a573abd

//Breadcrumbs display the path from the code element where your caret is placed to the root element of the file. They help you track your location within a file's hierarchy.

//ExtendedFloatingActionButton
//https://gist.github.com/Kyriakos-Georgiopoulos (Best For UI Animation)
//https://github.com/yuriyskulskiy/android-compose-playground (liquidBar tab bar)
//https://github.com/ahmedzer/LiquidGlass (LiquidGlass)
//https://github.com/Aouni19/Liquid-UI.git (liquid bottom bar)
//https://www.sinasamaki.com (glow-search-box, custom-pull-to-refresh)
//https://github.com/pitampoudel/komposeauth.git (State - Event Profile Edit, OTP Good)
//https://github.com/team2681/CollapsingAppBar-Demo-Jetpack
//https://medium.com/@aysegulbozdag/jetpack-composeda-lifecycle-entegre-etme-1e50f592ca01
//Drawer: https://github.com/fsfaysalcse/DiscoverBangladesh
//Animation: https://www.sinasamaki.com/author/sinasamaki/
//https://github.com/styropyr0/NowBar
//https://github.com/StarkDroid/WallStreet (Desktop download Image, TextList Animation)
//https://github.com/OnTrack-Organization/OnTrack (Firebase Notification, Social Login)
//https://github.com/NicosNicolaou16/Live_Update_Notifcation_Android.git (Firebase Notification Live Tracking)
//https://github.com/bingbong0098/Coach
//https://github.com/waqas028/Ktor-Upload-File-With-Progress (File Upload ProgressBar)
//https://github.com/bonzaiferroni/pond-compose (Tab Select, RadioGroup, CircleIndicator)
//https://github.com/austin17ng/Austin_SegmentedControl
//https://github.com/Codekan-Labs/Notes
//https://github.com/Abhay-cloud/JsonVisualizer
//https://github.com/bonzaiferroni/pond-compose
//https://github.com/rishadappat/ChargerApp (Good UI)
//https://www.zoommarket.net/app (UI)
//https://github.com/3wiida/SkySense
//https://gitlab.rsnlab.com/rsn/animax-backend
//https://www.pixelmatters.com/work/jumia-ecommerce-mobile-app-design
//https://github.com/thisishisham1/CoinTracker (LazyColum List Animation Left to Right)
//https://github.com/StarkDroid/kmp-winget (Desktop File Download)
//https://github.com/nandaiqbalh/muppi-kmp.git (Dialog Animation Open Bottom, Error Left to Right Animation, Plus Animation, Click Search Button Visible Right Side TextField Animation)
//https://github.com/WAHID-QANDIL/BorutoAppVersion1 (AppBar Hide Show, Detail Fraction)
//https://github.com/mejdi14/Lean-Dialog
//https://ui8.net/toko-design-206804/products
//https://github.com/usarican/Clean-Architecture-Movie-Application.git (Pagination Generic 3.3.5, SnackBar Animation)
//https://github.com/waqas028/Ktor-Koin-KMP (Pagination Generic)
//https://github.com/umidjonkhasimov/CineMania (Introduction Good)
//https://github.com/MostafaMohamed2002/GameVault (Bluer ImageColor Background from network, Filter List Animation)
//https://github.com/MahmoudElsayedEssa/SharedElementTransition (Click ListView Change GridView)
//https://github.com/eduruesta/movies_series_share (Drawer Animation, Search Animation (Up - Down))
//https://github.com/mnebot-6/Riptide.git (Drawer top to Bottom Animation)
//https://gist.github.com/danimahardhika (Collapsing AppBar with Scroll Effect, SlidingDialog)
//https://github.com/boguszpawlowski/ComposeCalendar (Calendar)
//https://github.com/Dev-Husnain/LiveTracker
//https://github.com/kagg886/Pixiv-MultiPlatform (Favourite With Progressbar)
//https://github.com/XDdevv/PLMayMiniChallenges2025 (LazyColumn Scroll Horizontal ProgressBar and Horizontal Tab to Scroll Header Title and Item Move Category)
//https://github.com/XDdevv/AugustMiniChallenges
//https://github.com/XDdevv/PLJulyMiniChallenges (Comment View Good)
//https://github.com/rainxchzed/NovemberMiniChallenges.git (Sticky List Create)
//https://github.com/rainxchzed/FebruaryMiniChallenges.git (Multiple ViewModel, KMM Flow Using internet connection check)
//https://github.com/codewithbilalahmad/Fansonic (Weekly Calendar)
//https://github.com/JetBrains/kotlinconf-app (Tab to Scroll, Metro DI)
//https://github.com/alexandregpereira/Monster-Compendium
//https://github.com/Cyril-Pina/TravelCase
//https://github.com/ilyasipek/dope-compose-animations.git (Animation)
//https://github.com/kylixeza/beu-cmp //(Voyger)
//https://github.com/dimrnhhh/moneytopia
//https://github.com/haidrrrry/Jetpackanimation (Roated Circle Users List Animation)
//https://github.com/haidrrrry/Authenticator (Click List/Grid Show Animation, Animation Dialog, Theme Change)
//https://github.com/rockyhappy/ken (Best UI)
//https://github.com/enesokurterzi/choice-dial
//https://github.com/Kevn-Block/JuliaSets-KMP (Zoom Image Rectangle)
//https://gist.github.com/Kyriakos-Georgiopoulos (DiagonalPager,  AnimatedSelectableList)
//https://github.com/chris-horner/Reverse-Rainbow (Game Position Selection Animation)
//https://github.com/faridGuliyew/compose-world
//https://github.com/AhmedOmara14/Multiplatform-Book-Manager (Delete Scale Animation)
//https://github.com/theapache64/retrosheet (Google Drive Sheet)
//https://github.com/Shoaibkhalid65/NavigationDrawer (Drawer open Left to right slide)
//https://github.com/Deepak-4551819/CryptoTracker_CoinCap_V3
//https://github.com/Vishesh-Rao/Cipher-Chat-App- (Chat Application)
//https://github.com/adityakar0808/MindEase.git (Chat, WebRTC, Login From Animation)
//https://gist.github.com/Kyriakos-Georgiopoulos (CircularImageRevealScreen, GymSessions, AnimatedSelectableList)
//https://github.com/danimontoya/TheMovieDbKmpApp (Arrow Library)
//https://github.com/beHqo/MockStore (Best API calling, stateIn, combine, flatMapLatest, Chip Filter)
//https://github.com/Mahnoor55/SwapGridCompose
//https://github.com/gsrathoreniks/Scratchify
//https://github.com/farimarwat/ResponsiveApp (Scroll to Profile Animation)
//https://github.com/amit4app/Work-Manager-Video-Upload
//https://github.com/delacrixmorgan/twilight-kmp (Horizontal Weight Slider)
//https://github.com/kanha321/Upastithi.git (Calendar (List), LazyColumn Generic with Border Animation)
//https://github.com/3wiida/Taskii (Dynamic LazyColumn, Timer LazyColumn)
//https://github.com/Cairo-Squad/Yalla-Tasks.git (LazyRow Center Element Scroll User, Theme Change Button Animation)
//https://github.com/shalenMathew/Quotes-app (Card Swipe Tinder App)
//https://github.com/eevajonnapanula/cats/ (Card Swipe Tinder App)
//https://github.com/yesshreyes/one-badge (Card Swipe Tinder)
//https://github.com/makzimi/scroll-effect-lazy ()
//https://github.com/makzimi/SwipingCards (Swipe Card)
//https://github.com/codewithbilalahmad/Fansonic (Status Change Animation)
//https://github.com/xfqwdsj/capsule (A Compose Multiplatform library that provides smooth rounded corner shapes.)
//https://github.com/isaruff/ComposePlayground (LazyColumn / LazyRow Auto Scroll)
//https://github.com/isaruff/ComposePlayground (auto_scroll_carousel, collapsing_toolbar, custom tooltip (good))
//https://github.com/furkanaskin/ComposePlayground (Card Pager Animation, Shadow Chain, Side panel custom layout, ExplodableChips Delete Animation, Calendar Grid, Bottom Bar Morph)
//https://github.com/cinkhangin/composable (Ticket Card, Ratting Star, Stacking Card, StepBar, Analog Clock)
//https://github.com/codewithbilalahmad/Fansonic
//https://github.com/developerchunk/JetCo (Pie Chart, Column Bar Chart, Extended Column Bar Chart, Extended Column Bar Chart, Stepper, Ticket Card, SwitchButton, Horizontal Stepper, Curved Card)
//https://github.com/ahmadsufyan455/subscription-manager.git (CoroutineWorker, Koin, Scheduler Notification)
//https://github.com/r-qocayevv/Hanged-Game.git (Hilt, Socket IO, Game UI)
//https://github.com/borey-roun99/Android-QR-Scanner-With-ML-Kit
//https://github.com/livingstonantony/KMPNumberIncrementCleanArchitecture
//https://github.com/composablehorizons/compose-ripple-indication
//https://github.com/furkanaskin/ComposePlayground.git (Toolbar Transition, Credit Card Selection Animation, Circular Carousel with LazyRow, Side Panel Custom Layout Add To Cart)
//https://github.com/mohammadsml/CardScanner
//https://github.com/MehdiSekoba/Banimode.git (Navigation3, NavigationRailItem, Shooping UI best)
//https://github.com/yasanglass/kepko
//https://github.com/pascaladitia/Smart-Notification-Listener
//https://github.com/codewithdipesh/Habitized.git (Image Top to Bottom Animation, Text Middle Line Animation)
//https://github.com/mohdaquib/Realtime-Price-Tracker.git (Websocket, with Price Tag Color Change Every Second)
//https://github.com/love-yuri/journal-kmp.git (Swipe to Refresh / Pull To Refresh Custom UI using pointerInput, LazyColumn Animation)
//https://github.com/codewithbilalahmad/PillTime.git (Notification Permission, Weekly Calendar, Multiple Select DatePicker, WorkManager using Schedule)
//https://github.com/sai-charan2003/Habit-Diary.git (Calendar picker, Material3 (SegmentedListItem, MediumFlexibleTopAppBar, Circle Shape Radio Button Animation, Notification))
//https://github.com/Ali0092/ComposeReceipt
//https://github.com/sai-charan2003/Habit-Diary.git
//https://gist.github.com/Rohit-554 (Animated DateCarousel)
//https://github.com/Rohit-554/CMP-TODO-LIST (Bottom NavigationBar Good)
//https://github.com/RoushanKhalid/Meal-Manager (Calendar (Moth Year Selection))
//https://github.com/EvgenyMeltsaykin/SwipeButton
//https://github.com/kartollikaa/ComposeFastPaymentSlider
//https://github.com/maulikdadhaniya/stock_ticker_compose
//https://github.com/shamil-RS/Gemini
//https://github.com/ranjeetchouhan/compose-code-viewer
//https://github.com/shaikmohammed8/LocalMind.git (Audio Recorder)
//https://github.com/arifamsar/iFlix.git (hilt, test cases, detail scroll image good)
//https://github.com/patrykandpatrick/liftapp.git (Divider Wavy, RequestState Good, SavedState Good, ReadOnly padding good)
//https://github.com/uragiristereo/Mikansei (Search to List Bold Text)
//https://github.com/tunjid/Tiler (Pagination library good)
//https://github.com/mofeejegi/compose-alert-banner
//https://github.com/PiotrPrus/ComposeWeatherCard
//https://medium.com/@supsabhi/mastering-pagination-in-jetpack-compose-paging-3-vs-snapshotflow-8193410eb991
//https://dev.to/midopooler/what-happens-if-two-android-phones-edit-the-same-data-offline-3ok2
//https://github.com/lainovic/StrayCat.git (Google Map)
//https://github.com/khodorkin-dmitrii/ParcelOnMap (Google Map, Market Connected Line Animatation)
//https://github.com/saltpay/lemonade-design-system.git (UI Kit)
//https://github.com/RoyalTaxi/yalla-components.git (UI Kit)
//https://github.com/flyfishxu/vetra-ui (UI Kit)
//https://github.com/ItamiOMW/CalorieTracker.git (BottomBar Animation)
//https://github.com/MohammadAliUstad/Quill.git (Calendar View)
//https://github.com/DeFerence3/broke-compose.git (Sliding Drawer, Month Picker)
//https://github.com/filipebatista/compose-schedule-calendar
//https://github.com/mvk059/Portfolio
//https://github.com/surajpsp/CustomList.git (Section List Programmatically Good)
//https://github.com/Housseinch17/VibePlayer (Music Player Koin Using)
//https://github.com/T8RIN/ImageToolbox
//https://github.com/RomanK2311/Pdf_Tools
//https://github.com/oleksandrbalan/pagecurl (New App Animation)
//https://github.com/aungkhanthtoo/ComposeReels
//https://github.com/BitMavrick/People.git (Drag & Drop Item (ReorderableLazyColumn))
//https://github.com/amirroid/chatguard.git (Chat App, Good Encryption)
//https://github.com/surajpsp/CustomList.git (Section List Animation)
//https://github.com/fsfaysalcse/Zenify.git (Music App, Slide Drawer Good)
//https://github.com/ShanuDevCodes/KritiqueRevemp (Material3 SearchBar Good, BottomBar)
//https://github.com/stoyan-vuchev/magic-message.git (Bottom Bar Hide/Show Animation Good, Setting List Good Animation, Theme Change, SharedTransitionLayout)
//https://github.com/ShanuDevCodes/KITO.git (Best Navigation3 Animation, Find Dynamic Position, Bottom Hide/Show Animation, hazeEffect, Calendar, Auto Scroll Index)
//https://github.com/iamrv-sachan/MatchMate
//https://github.com/rafal-mi/MP-animations
//https://github.com/Abdelilah27/Plan-Feed-Animation (Position Animation)
//https://github.com/LazyIonEs/WanAndroid-ComposeMultiplatform
//https://github.com/codewithbilalahmad/PillTime.git (Onboarding Animation, Calendar Picker Multiple Date Selected)
//https://www.linkedin.com/feed
//https://github.com/theadityatiwari/RulerKit (Good)
//https://github.com/theadityatiwari/leetflow-android
//https://github.com/ADMusab12/Finora-Dashboard.git (Scroll AppBar & Header Animation Good)
//https://github.com/UmairOye/fluid_splash_kotlin_multiplatform (Onboarding Wave Animation)
//https://github.com/Peakmain/CmComposeUI.git (Auto Slider Horizontal - Vertical)
//https://github.com/rajdeepvaghela/ValuePickerSlider
//https://github.com/lumkit/SweetEditor-Compose (Kotlin Code Editor)
//https://medium.com/@ramadan123sayed/building-a-fully-animated-multi-stop-custom-bottom-sheet-in-jetpack-compose-peek-half-full-efe1e11de0f1 (BottomSheet Draggable)
//https://github.com/codewithdipesh/Habitized.git
//https://github.com/codewithdipesh/TicketBooking
//https://github.com/sosauce/Chocola (Music app)
//https://github.com/oguzsout/WalliesCompose (Press Button Circle Progress, TextField Border Animation)
//https://github.com/ImChabs/DontForgetApp.git (Rood DB MVI)
//https://github.com/olubunmialegbeleye/DragToReveal
//https://github.com/rroohit/ImageCropView (Crop Image)
//https://kirupakaran094.medium.com/day-24-building-the-quiz-engine-part-1-data-scoring-70b68e7ca483
//https://github.com/Dev-Aditya-More/PokeVerse.git (GroupBy Good, Filter option list top to bottom animation)
//https://github.com/david-simba/vint-beats (Music App)
//https://github.com/enmanuel52/Pop-Corns.git (Movie App, KTORFIT, LazyColumn Animation)
//https://github.com/tbahlai/animations
//https://github.com/tbahlai/animations.git
//https://github.com/BetuelRS/Antares.git (Barcode Scanner)
//https://github.com/alexbr-alves/Jetpack-Compose-Labs (Waller Animation, Boarding Pass Animation, Music Animation)
//https://github.com/iprashantpanwar/JellyFab
//https://github.com/Ali0092/ComposeCarouselSlider (WaterRippleEffect(), PageCurlEffect(), MorphEffect(), GlitchEffect(), ChromaticAberrationEffect(), NoiseDissolveEffect(), PixelateEffect(), MotionBlurEffect())
//https://github.com/Ali0092/ComposeReceipt (Receipt UI)
//https://github.com/hpnightowl/CabinClimate
//https://github.com/hpnightowl/NightowlUI

//NEO
//https://github.com/PrasadVennam1005/NeoCompose

//Best E-Commerce App
//https://github.com/PocketShopiTi/pocket-shop.git (AI Chat, Good OnBoarding, SplashScreen Animation, Error Shake Animation, Card Added Animation Text, 2 Option Selection Good)
//https://github.com/Big-OO/carto.git (AI Chat)
//https://github.com/Troves-eCommerce/Troves.git

//Background Hide/Show Animation
//https://github.com/saheermk/no-sleep

//Dynamic App Icon
//https://github.com/AhmedMokhtarAli/DynamicAppIcon

//Payment
//https://github.com/Sibghat65/InAppOneTimeBilling

//Free API
//https://dog.ceo/api/breeds/image/random/10
//https://meme-api.com/gimme/50
//https://api.jolpi.ca/ergast/f1/current/driverstandings.json
//https://api.jolpi.ca/ergast/f1/current/constructorstandings.json
//https://api.jolpi.ca/ergast/f1/current/races.json

//Constraint Layout
//https://github.com/ARPITSINGH088/SkySeat/tree/master (Ticket UI)

//Multiple URL / HttpClient or GraphQL
//https://github.com/matthieucoisne/YelpExplorer-CMP

//DESKTOP
//https://github.com/itxaliraza/Localizer.git (Desktop, Custom Scrollbar)
//https://github.com/orioneee/Axer (Table View, IsolatedContext koin)
//https://github.com/julesmeister/deutschcraft-compose-multiplatform.git (Chat UI Animation List)

//CHAT
//https://github.com/MahmoudElsayedEssa/CircularReveal (Chat UI, CircularReveal Animation)

//Detail Screen
//https://github.com/tunjid/heron
//https://github.com/arifamsar/iFlix.git (Good Detail Screen)
//https://github.com/rishadappat/ChargerApp (Good UI)
//https://github.com/MahmoudElsayedEssa/AbsorbableCompose (Profile Image Scroll Animation)
//https://github.com/omer-ok/TouristVibes
//https://github.com/WAHID-QANDIL/BorutoAppVersion1 (AppBar Hide Show, Detail Fraction)
//https://github.com/Beb3r/OpalTest.git (AGSL, Animated Background, Weight Picker Slider, Button Spring Animation)
//https://github.com/GuilhE/KitchenTimer (Weight Picker Slider Round Shape)
//https://github.com/xephosbot/AnilibriaRefresh (Good KMM, Pagination 3.3.0 Generic, Advance Layout, SplitButton)
//https://github.com/Dev-Aditya-More/PokeVerse.git (LazyColumn Animation, Scroll detail page .graphicsLayer using hide/show image, WavySlider)

//The Chance
//https://github.com/TheChance101/Honey-Mart-Android-Client (Client - Backend)
//https://github.com/Rome-Squad/CineVerse-App (The Chance, Scroll to Circle Shape)
//https://github.com/Moscow-Squad/CineVerse.git (The Chance - CommonViewModel)
//https://github.com/Amsterdam-Team/Aflami (The Chance - CommonViewModel, BottomBar Animation Hide/Show, Quiz)
//https://github.com/team-berlin/Aflami (Same Link)
//https://github.com/poula22/the_chance_caffeine
//https://github.com/LondonSquad/Novix (The Chance - BottomBar Dot Animation) (https://londonsquad.github.io/Novix/)
//https://github.com/Cairo-Squad/Movio (Slide to Button, Tab Row, Slider)
//https://github.com/Rome-Squad/Tudee-App (The Chance)
//https://github.com/TheChance101/MENA-mobile
//https://github.com/raouf-kamel/chat-app-with-webssocket

//PHOTO EDITOR
//https://github.com/codewithbilalahmad/Lumina.git (Toast Good, Photo Edit, Success photo edit overlay animation)

//Download Social Video
//https://github.com/GitForYasir/Instagram-Video-Downloader-Android-App.git

//TimeLine
//https://github.com/ferPrieto/TimelineView
//https://github.com/ksharma-xyz/KRAIL

//Wifi
//https://github.com/ADMusab12/Wifi-Analyzer

//Room Database
//https://github.com/harikrishnanponath/FreshKeeper.git (Insert, Update, Delete, StateIn)

//Quiz Option Animation
//https://github.com/YazanAesmael/AndroidQuizApp
//https://github.com/fluttertutorialin/Quizy
//https://github.com/nyinyiz/DailyChallenge (Good Animation, Tinder Style, Single Quiz, Multiplat Quiz)

//Interview
//https://github.com/ahmedNaser7/The-Chance-Tasks
//https://www.interview.micro1.ai/intro/micro1 //AI Based
//https://github.com/cheonjaeung/gridlayout-compose (Fixed GridView)
//composeTestRule

//Chart
//https://github.com/ehsannarmani/ComposeCharts
//https://github.com/majid2851/Charts

//UI
//https://github.com/kotlin-flex/FlexUI
//https://composables.com/primitives

//CALENDAR EVENT LIST
//https://github.com/matiz22/UeKatPlan (AssistChip Composable, LazyColum Border Corner Animation)
//https://github.com/climbeyond/beyondcalendar

//BLOG
//https://medium.com/@cenkeraydin14/managing-side-effects-in-jetpack-compose-6adafff83b59
//https://www.rajendhiraneasu.in/2022

//https://medium.com/@monikabhasin.sd17/key-in-jetpack-compose-the-most-ignored-line-that-saves-performance-7e171890612f

//plugin best
//https://plugins.jetbrains.com/plugin/13121-http-client
//Compose Stability Analyze

//WASMJs Website
//https://github.com/Egi10/personal
//https://github.com/RogerCorcoles/kotlin-wasm-wasi-echo

//Firebase Notification
//https://github.com/muhammadnoman11/EasyFirebaseNotifier

//Good Application No Free With Document
//https://bitepay.in/
//https://www.krail.app/

//Interview Questions
//https://www.linkedin.com/in/aliasheer/recent-activity/documents/

//AssistChip, OutlinedToggleButton, SplitButtonLayout, LoadingIndicator

//https://www.linkedin.com/in/kajal0121/recent-activity/all/
//implementation(project(":core:design"))

//Android Studio Plugin
//https://firebender.com/

//Git
//https://itnext.io/coordinated-polyrepo-pattern-managing-multiple-git-repositories-with-submodules-1610d6ee857a

//networkquality checknetwork


//Color.Transparent = an actual color with alpha 0 (it draws, but is invisible).
//Color.Unspecified = “don’t set a color.” Many components will inherit the current LocalContentColor or skip the draw.
//MutableTransitionState
//Modifier.safeContentPadding().fillMaxSize()
//Material3: CircularWavyProgressIndicator(), HorizontalCenteredHeroCarousel
//Compose 1.10  - Modifier.scrollableArea()
//Compose — VerbatimTtsAnnotation, SwiftUl — speechSpellsOutCharacters)
//https://androidpoet.medium.com/compose-rules-enforcing-best-practices-for-jetpack-compose-through-static-analysis-ee1763a99df3

//android.r8.optimizedResourceShrinking=true
//https://www.youtube.com/watch?v=lKuPqQxU374 //Navigation3
//https://medium.com/@om.teastallstudio/jetpack-compose-paging-3-lazy-list-navigation-3-with-mvi-load-states-shimmer-placeholders-a4ced8224bf3

//val bottomPadding = WindowInsets.statusBars.asPaddingValues().calculateBottomPadding()
//https://github.com/ardon-dev/kmp-wasm-crud
//https://vm.ketoy.dev/blogs (Best Website UI)

/*
ContentTransform
LocalTextStyle.current,
LocalContentColor.current,

@Composable
fun CommonIconButton(
    modifier: Modifier = Modifier,
    imageVector: ImageVector,
    contentDescription: String,
    tint: Color = LocalContentColor.current,
    onClick: () -> Unit,
) {
    IconButton(
        modifier = modifier,
        onClick = onClick,
    ) {
        Icon(
            imageVector = imageVector,
            contentDescription = contentDescription,
            tint = tint,
        )
    }
}*/

//val iconButtonColors = IconButtonDefaults.iconButtonColors(contentColor = colorScheme.onPrimaryContainer)
//val menuItemColor = MenuDefaults.itemColors(leadingIconColor = colorScheme.onPrimaryContainer)
//https://composables.com/docs/compose-unstyled/tooltip

/*
modifier = Modifier.align(Alignment.CenterHorizontally).onPreviewKeyEvent {event->
          if (event.key == Key.Enter&& event.type == KeyEventType.KeyUp) {

            return@onPreviewKeyEvent true
          }
          return@onPreviewKeyEvent false
        }
*/

//val dropdownHeight = LocalConfiguration.current.screenHeightDp.dp * 0.4f //DropDown Height

/*
    val imeHeight = WindowInsets.ime.getBottom(LocalDensity.current)
    val isKeyboardVisible = imeHeight > 0
    val configuration = currentDeviceConfiguration()

    val shouldHideHeader = configuration == DeviceConfiguration.MOBILE_LANDSCAPE
            || (isKeyboardVisible && configuration != DeviceConfiguration.DESKTOP) || isTextFieldFocused
*/

//https://medium.com/@priteshnikam/building-custom-segmented-controllers-in-jetpack-compose-67bd663f00b5?postPublishedType=initial
//https://medium.com/@fadibouteraa/scaling-kotlin-multiplatform-a-feature-oriented-clean-architecture-for-real-products-2a838ddf1314
//https://exam.plusiunedevelopers.com/register
//http://nameisjayant.medium.com/best-ways-to-perform-search-on-the-list-in-jetpack-compose-0f573a48b9ec
//https://blog.shreyaspatil.dev/solving-the-mystery-of-recompositions-in-composes-lazylist (composed Modifier)
//https://abifarhan.medium.com/state-management-strategies-in-large-scale-compose-apps-the-2026-complete-guide-de51b324749b
//https://www.linkedin.com/pulse/jetpack-compose-kotlin-coroutines-flow-2025-interview-komilov-1jbtf/?trackingId=9TMpLnMUQVKI%2B0I0JNwEUA%3D%3D


//Custom components
//https://github.com/yasanglass/kepko
//https://github.com/dev778g-me/Kore

/*
import androidx.compose.material3.RadioButton as Material3RadioButton
import androidx.compose.material3.RadioButtonDefaults as Material3RadioButtonDefaults

https://thecodingshef.in/master-android
https://www.androidengineers.in/questions
https://github.com/orgs/ThecodingShef/repositories
https://www.youtube.com/watch?v=R0uxUxnMbIE&t=1173s
*/

/*
ADB Idea, Rainbow Brackets, Kotlin Fill Class,
*/

// Performance optimizations
//implementation("androidx.compose.runtime:runtime-tracing:1.0.0-beta01")
//implementation("androidx.profileinstaller:profileinstaller:1.3.1")
//testImplementation(libs.junit)

//MATERIAL 4.0
//https://bahadireray.medium.com/material-4-0-blur-effects-modern-uilere-derinlik-ve-sihir-kat%C4%B1n-c182c52f3cd6

//LinkedIn Compose
//Konstantin Klassen

//Today Post
//https://medium.com/@sandeep.jan19/from-senior-to-architect-10-clean-architecture-trade-offs-that-define-your-level-9a9bfb954d84
//https://github.com/kotakvishal/GroceryAppUI-Compose.git
//https://medium.com/@nicholausmichael51/advanced-slot-patterns-in-jetpack-compose-scoped-slots-48447dd53722
//https://www.linkedin.com/video/live/urn:li:ugcPost:7417537333652221952/
//https://codefarm0.medium.com/designing-a-upi-payment-system-real-time-processing-bank-integration-transaction-routing-0bb4bb93e12a#810a

//Plugin
//CodeMetrics With Kotlin

//AI Tools
//https://github.com/ioannisa/AppFunctions-Demo-App.git

//https://skydoves.github.io/
//getorchidshygiene
//https://tallytimer.github.io/#showcase
//https://proandroiddev.com/why-modifier-scrollable-doesnt-scroll-in-jetpack-compose-and-what-to-use-instead-d81807abaaf0
//https://kamaldeepkakkar.medium.com/jetpack-compose-tutorial-understanding-rememberlauncherforactivityresult-e93194c5e198
//https://proandroiddev.com/why-your-lazycolumn-drops-frames-part-2-hidden-patterns-78622d10ca40
//https://christophybarth.medium.com/custom-bottom-sheets-in-compose-without-the-hassle-f8e35474ed3f
//https://christophybarth.medium.com/state-hoisting-in-jetpack-compose-when-and-why-f72df96bed99
//https://syedovaiss.medium.com/mastering-ui-testing-in-jetpack-compose-with-real-examples-6f647452d38c
//https://pl-coding.com/2024/10/28/credential-manager
//https://itnext.io/server-driven-ui-in-compose-multiplatform-contracts-schema-first-codegen-partial-updates-b1b185a9d38c
//https://medium.com/@tamersarioglu/the-anatomy-of-data-transfer-in-android-a-deep-dive-into-serializable-parcelable-and-ipc-7ed7fa2d9d1a
//https://proandroiddev.com/jetpack-compose-why-you-shouldnt-use-localcontext-for-strings-4d4c372b14ab
//https://medium.com/@hyunjine/compose-internal-6-%EC%83%81%ED%83%9C-%EC%8A%A4%EB%83%85%EC%83%B7-%EC%8B%9C%EC%8A%A4%ED%85%9C-4d4743fd0d00
//https://medium.com/@radoslaw.tywanek/escaping-ifology-advanced-control-flow-in-kotlin-f851048f5ee8
//https://medium.com/@guruprasadhegde4/android-jetpack-compose-part-1-3a5b21092bbe
//https://medium.com/@ramadan123sayed/building-a-fully-animated-smart-tooltip-in-jetpack-compose-auto-positioning-that-adapts-to-screen-f40f62cd4a66
//https://christophybarth.medium.com/compose-state-explained-like-youre-five-and-it-s-bedtime-d7e679d3000e

//  implementation(libs.kotlinx.serialization.protobuf)
//